/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

/*!
 * Vue.js v2.7.14
 * (c) 2014-2022 Evan You
 * Released under the MIT License.
 */
var emptyObject = Object.freeze({});
var isArray = Array.isArray;
// These helpers produce better VM code in JS engines due to their
// explicitness and function inlining.
function isUndef(v) {
    return v === undefined || v === null;
}
function isDef(v) {
    return v !== undefined && v !== null;
}
function isTrue(v) {
    return v === true;
}
function isFalse(v) {
    return v === false;
}
/**
 * Check if value is primitive.
 */
function isPrimitive$1(value) {
    return (typeof value === 'string' ||
        typeof value === 'number' ||
        // $flow-disable-line
        typeof value === 'symbol' ||
        typeof value === 'boolean');
}
function isFunction(value) {
    return typeof value === 'function';
}
/**
 * Quick object check - this is primarily used to tell
 * objects from primitive values when we know the value
 * is a JSON-compliant type.
 */
function isObject(obj) {
    return obj !== null && typeof obj === 'object';
}
/**
 * Get the raw type string of a value, e.g., [object Object].
 */
var _toString = Object.prototype.toString;
function toRawType(value) {
    return _toString.call(value).slice(8, -1);
}
/**
 * Strict object type check. Only returns true
 * for plain JavaScript objects.
 */
function isPlainObject(obj) {
    return _toString.call(obj) === '[object Object]';
}
function isRegExp(v) {
    return _toString.call(v) === '[object RegExp]';
}
/**
 * Check if val is a valid array index.
 */
function isValidArrayIndex(val) {
    var n = parseFloat(String(val));
    return n >= 0 && Math.floor(n) === n && isFinite(val);
}
function isPromise(val) {
    return (isDef(val) &&
        typeof val.then === 'function' &&
        typeof val.catch === 'function');
}
/**
 * Convert a value to a string that is actually rendered.
 */
function toString(val) {
    return val == null
        ? ''
        : Array.isArray(val) || (isPlainObject(val) && val.toString === _toString)
            ? JSON.stringify(val, null, 2)
            : String(val);
}
/**
 * Convert an input value to a number for persistence.
 * If the conversion fails, return original string.
 */
function toNumber(val) {
    var n = parseFloat(val);
    return isNaN(n) ? val : n;
}
/**
 * Make a map and return a function for checking if a key
 * is in that map.
 */
function makeMap(str, expectsLowerCase) {
    var map = Object.create(null);
    var list = str.split(',');
    for (var i = 0; i < list.length; i++) {
        map[list[i]] = true;
    }
    return expectsLowerCase ? function (val) { return map[val.toLowerCase()]; } : function (val) { return map[val]; };
}
/**
 * Check if a tag is a built-in tag.
 */
makeMap('slot,component', true);
/**
 * Check if an attribute is a reserved attribute.
 */
var isReservedAttribute = makeMap('key,ref,slot,slot-scope,is');
/**
 * Remove an item from an array.
 */
function remove$2(arr, item) {
    var len = arr.length;
    if (len) {
        // fast path for the only / last item
        if (item === arr[len - 1]) {
            arr.length = len - 1;
            return;
        }
        var index = arr.indexOf(item);
        if (index > -1) {
            return arr.splice(index, 1);
        }
    }
}
/**
 * Check whether an object has the property.
 */
var hasOwnProperty = Object.prototype.hasOwnProperty;
function hasOwn(obj, key) {
    return hasOwnProperty.call(obj, key);
}
/**
 * Create a cached version of a pure function.
 */
function cached(fn) {
    var cache = Object.create(null);
    return function cachedFn(str) {
        var hit = cache[str];
        return hit || (cache[str] = fn(str));
    };
}
/**
 * Camelize a hyphen-delimited string.
 */
var camelizeRE = /-(\w)/g;
var camelize = cached(function (str) {
    return str.replace(camelizeRE, function (_, c) { return (c ? c.toUpperCase() : ''); });
});
/**
 * Capitalize a string.
 */
var capitalize = cached(function (str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
});
/**
 * Hyphenate a camelCase string.
 */
var hyphenateRE = /\B([A-Z])/g;
var hyphenate = cached(function (str) {
    return str.replace(hyphenateRE, '-$1').toLowerCase();
});
/**
 * Simple bind polyfill for environments that do not support it,
 * e.g., PhantomJS 1.x. Technically, we don't need this anymore
 * since native bind is now performant enough in most browsers.
 * But removing it would mean breaking code that was able to run in
 * PhantomJS 1.x, so this must be kept for backward compatibility.
 */
/* istanbul ignore next */
function polyfillBind(fn, ctx) {
    function boundFn(a) {
        var l = arguments.length;
        return l
            ? l > 1
                ? fn.apply(ctx, arguments)
                : fn.call(ctx, a)
            : fn.call(ctx);
    }
    boundFn._length = fn.length;
    return boundFn;
}
function nativeBind(fn, ctx) {
    return fn.bind(ctx);
}
// @ts-expect-error bind cannot be `undefined`
var bind = Function.prototype.bind ? nativeBind : polyfillBind;
/**
 * Convert an Array-like object to a real Array.
 */
function toArray(list, start) {
    start = start || 0;
    var i = list.length - start;
    var ret = new Array(i);
    while (i--) {
        ret[i] = list[i + start];
    }
    return ret;
}
/**
 * Mix properties into target object.
 */
function extend(to, _from) {
    for (var key in _from) {
        to[key] = _from[key];
    }
    return to;
}
/**
 * Merge an Array of Objects into a single Object.
 */
function toObject(arr) {
    var res = {};
    for (var i = 0; i < arr.length; i++) {
        if (arr[i]) {
            extend(res, arr[i]);
        }
    }
    return res;
}
/* eslint-disable no-unused-vars */
/**
 * Perform no operation.
 * Stubbing args to make Flow happy without leaving useless transpiled code
 * with ...rest (https://flow.org/blog/2017/05/07/Strict-Function-Call-Arity/).
 */
function noop(a, b, c) { }
/**
 * Always return false.
 */
var no = function (a, b, c) { return false; };
/* eslint-enable no-unused-vars */
/**
 * Return the same value.
 */
var identity = function (_) { return _; };
/**
 * Check if two values are loosely equal - that is,
 * if they are plain objects, do they have the same shape?
 */
function looseEqual(a, b) {
    if (a === b)
        return true;
    var isObjectA = isObject(a);
    var isObjectB = isObject(b);
    if (isObjectA && isObjectB) {
        try {
            var isArrayA = Array.isArray(a);
            var isArrayB = Array.isArray(b);
            if (isArrayA && isArrayB) {
                return (a.length === b.length &&
                    a.every(function (e, i) {
                        return looseEqual(e, b[i]);
                    }));
            }
            else if (a instanceof Date && b instanceof Date) {
                return a.getTime() === b.getTime();
            }
            else if (!isArrayA && !isArrayB) {
                var keysA = Object.keys(a);
                var keysB = Object.keys(b);
                return (keysA.length === keysB.length &&
                    keysA.every(function (key) {
                        return looseEqual(a[key], b[key]);
                    }));
            }
            else {
                /* istanbul ignore next */
                return false;
            }
        }
        catch (e) {
            /* istanbul ignore next */
            return false;
        }
    }
    else if (!isObjectA && !isObjectB) {
        return String(a) === String(b);
    }
    else {
        return false;
    }
}
/**
 * Return the first index at which a loosely equal value can be
 * found in the array (if value is a plain object, the array must
 * contain an object of the same shape), or -1 if it is not present.
 */
function looseIndexOf(arr, val) {
    for (var i = 0; i < arr.length; i++) {
        if (looseEqual(arr[i], val))
            return i;
    }
    return -1;
}
/**
 * Ensure a function is called only once.
 */
function once(fn) {
    var called = false;
    return function () {
        if (!called) {
            called = true;
            fn.apply(this, arguments);
        }
    };
}
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/is#polyfill
function hasChanged(x, y) {
    if (x === y) {
        return x === 0 && 1 / x !== 1 / y;
    }
    else {
        return x === x || y === y;
    }
}

var SSR_ATTR = 'data-server-rendered';
var ASSET_TYPES = ['component', 'directive', 'filter'];
var LIFECYCLE_HOOKS = [
    'beforeCreate',
    'created',
    'beforeMount',
    'mounted',
    'beforeUpdate',
    'updated',
    'beforeDestroy',
    'destroyed',
    'activated',
    'deactivated',
    'errorCaptured',
    'serverPrefetch',
    'renderTracked',
    'renderTriggered'
];

var config = {
    /**
     * Option merge strategies (used in core/util/options)
     */
    // $flow-disable-line
    optionMergeStrategies: Object.create(null),
    /**
     * Whether to suppress warnings.
     */
    silent: false,
    /**
     * Show production mode tip message on boot?
     */
    productionTip: "production" !== 'production',
    /**
     * Whether to enable devtools
     */
    devtools: "production" !== 'production',
    /**
     * Whether to record perf
     */
    performance: false,
    /**
     * Error handler for watcher errors
     */
    errorHandler: null,
    /**
     * Warn handler for watcher warns
     */
    warnHandler: null,
    /**
     * Ignore certain custom elements
     */
    ignoredElements: [],
    /**
     * Custom user key aliases for v-on
     */
    // $flow-disable-line
    keyCodes: Object.create(null),
    /**
     * Check if a tag is reserved so that it cannot be registered as a
     * component. This is platform-dependent and may be overwritten.
     */
    isReservedTag: no,
    /**
     * Check if an attribute is reserved so that it cannot be used as a component
     * prop. This is platform-dependent and may be overwritten.
     */
    isReservedAttr: no,
    /**
     * Check if a tag is an unknown element.
     * Platform-dependent.
     */
    isUnknownElement: no,
    /**
     * Get the namespace of an element
     */
    getTagNamespace: noop,
    /**
     * Parse the real tag name for the specific platform.
     */
    parsePlatformTagName: identity,
    /**
     * Check if an attribute must be bound using property, e.g. value
     * Platform-dependent.
     */
    mustUseProp: no,
    /**
     * Perform updates asynchronously. Intended to be used by Vue Test Utils
     * This will significantly reduce performance if set to false.
     */
    async: true,
    /**
     * Exposed for legacy reasons
     */
    _lifecycleHooks: LIFECYCLE_HOOKS
};

/**
 * unicode letters used for parsing html tags, component names and property paths.
 * using https://www.w3.org/TR/html53/semantics-scripting.html#potentialcustomelementname
 * skipping \u10000-\uEFFFF due to it freezing up PhantomJS
 */
var unicodeRegExp = /a-zA-Z\u00B7\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u037D\u037F-\u1FFF\u200C-\u200D\u203F-\u2040\u2070-\u218F\u2C00-\u2FEF\u3001-\uD7FF\uF900-\uFDCF\uFDF0-\uFFFD/;
/**
 * Check if a string starts with $ or _
 */
function isReserved(str) {
    var c = (str + '').charCodeAt(0);
    return c === 0x24 || c === 0x5f;
}
/**
 * Define a property.
 */
function def(obj, key, val, enumerable) {
    Object.defineProperty(obj, key, {
        value: val,
        enumerable: !!enumerable,
        writable: true,
        configurable: true
    });
}
/**
 * Parse simple path.
 */
var bailRE = new RegExp("[^".concat(unicodeRegExp.source, ".$_\\d]"));
function parsePath(path) {
    if (bailRE.test(path)) {
        return;
    }
    var segments = path.split('.');
    return function (obj) {
        for (var i = 0; i < segments.length; i++) {
            if (!obj)
                return;
            obj = obj[segments[i]];
        }
        return obj;
    };
}

// can we use __proto__?
var hasProto$1 = '__proto__' in {};
// Browser environment sniffing
var inBrowser = typeof window !== 'undefined';
var UA = inBrowser && window.navigator.userAgent.toLowerCase();
var isIE = UA && /msie|trident/.test(UA);
var isIE9 = UA && UA.indexOf('msie 9.0') > 0;
var isEdge = UA && UA.indexOf('edge/') > 0;
UA && UA.indexOf('android') > 0;
var isIOS = UA && /iphone|ipad|ipod|ios/.test(UA);
UA && /chrome\/\d+/.test(UA) && !isEdge;
UA && /phantomjs/.test(UA);
var isFF = UA && UA.match(/firefox\/(\d+)/);
// Firefox has a "watch" function on Object.prototype...
// @ts-expect-error firebox support
var nativeWatch = {}.watch;
var supportsPassive = false;
if (inBrowser) {
    try {
        var opts = {};
        Object.defineProperty(opts, 'passive', {
            get: function () {
                /* istanbul ignore next */
                supportsPassive = true;
            }
        }); // https://github.com/facebook/flow/issues/285
        window.addEventListener('test-passive', null, opts);
    }
    catch (e) { }
}
// this needs to be lazy-evaled because vue may be required before
// vue-server-renderer can set VUE_ENV
var _isServer;
var isServerRendering = function () {
    if (_isServer === undefined) {
        /* istanbul ignore if */
        if (!inBrowser && typeof global !== 'undefined') {
            // detect presence of vue-server-renderer and avoid
            // Webpack shimming the process
            _isServer =
                global['process'] && global['process'].env.VUE_ENV === 'server';
        }
        else {
            _isServer = false;
        }
    }
    return _isServer;
};
// detect devtools
var devtools = inBrowser && window.__VUE_DEVTOOLS_GLOBAL_HOOK__;
/* istanbul ignore next */
function isNative(Ctor) {
    return typeof Ctor === 'function' && /native code/.test(Ctor.toString());
}
var hasSymbol = typeof Symbol !== 'undefined' &&
    isNative(Symbol) &&
    typeof Reflect !== 'undefined' &&
    isNative(Reflect.ownKeys);
var _Set; // $flow-disable-line
/* istanbul ignore if */ if (typeof Set !== 'undefined' && isNative(Set)) {
    // use native Set when available.
    _Set = Set;
}
else {
    // a non-standard Set polyfill that only works with primitive keys.
    _Set = /** @class */ (function () {
        function Set() {
            this.set = Object.create(null);
        }
        Set.prototype.has = function (key) {
            return this.set[key] === true;
        };
        Set.prototype.add = function (key) {
            this.set[key] = true;
        };
        Set.prototype.clear = function () {
            this.set = Object.create(null);
        };
        return Set;
    }());
}

var currentInstance = null;
/**
 * @internal
 */
function setCurrentInstance(vm) {
    if (vm === void 0) { vm = null; }
    if (!vm)
        currentInstance && currentInstance._scope.off();
    currentInstance = vm;
    vm && vm._scope.on();
}

/**
 * @internal
 */
var VNode = /** @class */ (function () {
    function VNode(tag, data, children, text, elm, context, componentOptions, asyncFactory) {
        this.tag = tag;
        this.data = data;
        this.children = children;
        this.text = text;
        this.elm = elm;
        this.ns = undefined;
        this.context = context;
        this.fnContext = undefined;
        this.fnOptions = undefined;
        this.fnScopeId = undefined;
        this.key = data && data.key;
        this.componentOptions = componentOptions;
        this.componentInstance = undefined;
        this.parent = undefined;
        this.raw = false;
        this.isStatic = false;
        this.isRootInsert = true;
        this.isComment = false;
        this.isCloned = false;
        this.isOnce = false;
        this.asyncFactory = asyncFactory;
        this.asyncMeta = undefined;
        this.isAsyncPlaceholder = false;
    }
    Object.defineProperty(VNode.prototype, "child", {
        // DEPRECATED: alias for componentInstance for backwards compat.
        /* istanbul ignore next */
        get: function () {
            return this.componentInstance;
        },
        enumerable: false,
        configurable: true
    });
    return VNode;
}());
var createEmptyVNode = function (text) {
    if (text === void 0) { text = ''; }
    var node = new VNode();
    node.text = text;
    node.isComment = true;
    return node;
};
function createTextVNode(val) {
    return new VNode(undefined, undefined, undefined, String(val));
}
// optimized shallow clone
// used for static nodes and slot nodes because they may be reused across
// multiple renders, cloning them avoids errors when DOM manipulations rely
// on their elm reference.
function cloneVNode(vnode) {
    var cloned = new VNode(vnode.tag, vnode.data, 
    // #7975
    // clone children array to avoid mutating original in case of cloning
    // a child.
    vnode.children && vnode.children.slice(), vnode.text, vnode.elm, vnode.context, vnode.componentOptions, vnode.asyncFactory);
    cloned.ns = vnode.ns;
    cloned.isStatic = vnode.isStatic;
    cloned.key = vnode.key;
    cloned.isComment = vnode.isComment;
    cloned.fnContext = vnode.fnContext;
    cloned.fnOptions = vnode.fnOptions;
    cloned.fnScopeId = vnode.fnScopeId;
    cloned.asyncMeta = vnode.asyncMeta;
    cloned.isCloned = true;
    return cloned;
}

var uid$2 = 0;
var pendingCleanupDeps = [];
var cleanupDeps = function () {
    for (var i = 0; i < pendingCleanupDeps.length; i++) {
        var dep = pendingCleanupDeps[i];
        dep.subs = dep.subs.filter(function (s) { return s; });
        dep._pending = false;
    }
    pendingCleanupDeps.length = 0;
};
/**
 * A dep is an observable that can have multiple
 * directives subscribing to it.
 * @internal
 */
var Dep = /** @class */ (function () {
    function Dep() {
        // pending subs cleanup
        this._pending = false;
        this.id = uid$2++;
        this.subs = [];
    }
    Dep.prototype.addSub = function (sub) {
        this.subs.push(sub);
    };
    Dep.prototype.removeSub = function (sub) {
        // #12696 deps with massive amount of subscribers are extremely slow to
        // clean up in Chromium
        // to workaround this, we unset the sub for now, and clear them on
        // next scheduler flush.
        this.subs[this.subs.indexOf(sub)] = null;
        if (!this._pending) {
            this._pending = true;
            pendingCleanupDeps.push(this);
        }
    };
    Dep.prototype.depend = function (info) {
        if (Dep.target) {
            Dep.target.addDep(this);
        }
    };
    Dep.prototype.notify = function (info) {
        // stabilize the subscriber list first
        var subs = this.subs.filter(function (s) { return s; });
        for (var i = 0, l = subs.length; i < l; i++) {
            var sub = subs[i];
            sub.update();
        }
    };
    return Dep;
}());
// The current target watcher being evaluated.
// This is globally unique because only one watcher
// can be evaluated at a time.
Dep.target = null;
var targetStack = [];
function pushTarget(target) {
    targetStack.push(target);
    Dep.target = target;
}
function popTarget() {
    targetStack.pop();
    Dep.target = targetStack[targetStack.length - 1];
}

/*
 * not type checking this file because flow doesn't play well with
 * dynamically accessing methods on Array prototype
 */
var arrayProto = Array.prototype;
var arrayMethods = Object.create(arrayProto);
var methodsToPatch = [
    'push',
    'pop',
    'shift',
    'unshift',
    'splice',
    'sort',
    'reverse'
];
/**
 * Intercept mutating methods and emit events
 */
methodsToPatch.forEach(function (method) {
    // cache original method
    var original = arrayProto[method];
    def(arrayMethods, method, function mutator() {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        var result = original.apply(this, args);
        var ob = this.__ob__;
        var inserted;
        switch (method) {
            case 'push':
            case 'unshift':
                inserted = args;
                break;
            case 'splice':
                inserted = args.slice(2);
                break;
        }
        if (inserted)
            ob.observeArray(inserted);
        // notify change
        {
            ob.dep.notify();
        }
        return result;
    });
});

var arrayKeys = Object.getOwnPropertyNames(arrayMethods);
var NO_INIITIAL_VALUE = {};
/**
 * In some cases we may want to disable observation inside a component's
 * update computation.
 */
var shouldObserve = true;
function toggleObserving(value) {
    shouldObserve = value;
}
// ssr mock dep
var mockDep = {
    notify: noop,
    depend: noop,
    addSub: noop,
    removeSub: noop
};
/**
 * Observer class that is attached to each observed
 * object. Once attached, the observer converts the target
 * object's property keys into getter/setters that
 * collect dependencies and dispatch updates.
 */
var Observer = /** @class */ (function () {
    function Observer(value, shallow, mock) {
        if (shallow === void 0) { shallow = false; }
        if (mock === void 0) { mock = false; }
        this.value = value;
        this.shallow = shallow;
        this.mock = mock;
        // this.value = value
        this.dep = mock ? mockDep : new Dep();
        this.vmCount = 0;
        def(value, '__ob__', this);
        if (isArray(value)) {
            if (!mock) {
                if (hasProto$1) {
                    value.__proto__ = arrayMethods;
                    /* eslint-enable no-proto */
                }
                else {
                    for (var i = 0, l = arrayKeys.length; i < l; i++) {
                        var key = arrayKeys[i];
                        def(value, key, arrayMethods[key]);
                    }
                }
            }
            if (!shallow) {
                this.observeArray(value);
            }
        }
        else {
            /**
             * Walk through all properties and convert them into
             * getter/setters. This method should only be called when
             * value type is Object.
             */
            var keys = Object.keys(value);
            for (var i = 0; i < keys.length; i++) {
                var key = keys[i];
                defineReactive(value, key, NO_INIITIAL_VALUE, undefined, shallow, mock);
            }
        }
    }
    /**
     * Observe a list of Array items.
     */
    Observer.prototype.observeArray = function (value) {
        for (var i = 0, l = value.length; i < l; i++) {
            observe(value[i], false, this.mock);
        }
    };
    return Observer;
}());
// helpers
/**
 * Attempt to create an observer instance for a value,
 * returns the new observer if successfully observed,
 * or the existing observer if the value already has one.
 */
function observe(value, shallow, ssrMockReactivity) {
    if (value && hasOwn(value, '__ob__') && value.__ob__ instanceof Observer) {
        return value.__ob__;
    }
    if (shouldObserve &&
        (ssrMockReactivity || !isServerRendering()) &&
        (isArray(value) || isPlainObject(value)) &&
        Object.isExtensible(value) &&
        !value.__v_skip /* ReactiveFlags.SKIP */ &&
        !isRef(value) &&
        !(value instanceof VNode)) {
        return new Observer(value, shallow, ssrMockReactivity);
    }
}
/**
 * Define a reactive property on an Object.
 */
function defineReactive(obj, key, val, customSetter, shallow, mock) {
    var dep = new Dep();
    var property = Object.getOwnPropertyDescriptor(obj, key);
    if (property && property.configurable === false) {
        return;
    }
    // cater for pre-defined getter/setters
    var getter = property && property.get;
    var setter = property && property.set;
    if ((!getter || setter) &&
        (val === NO_INIITIAL_VALUE || arguments.length === 2)) {
        val = obj[key];
    }
    var childOb = !shallow && observe(val, false, mock);
    Object.defineProperty(obj, key, {
        enumerable: true,
        configurable: true,
        get: function reactiveGetter() {
            var value = getter ? getter.call(obj) : val;
            if (Dep.target) {
                {
                    dep.depend();
                }
                if (childOb) {
                    childOb.dep.depend();
                    if (isArray(value)) {
                        dependArray(value);
                    }
                }
            }
            return isRef(value) && !shallow ? value.value : value;
        },
        set: function reactiveSetter(newVal) {
            var value = getter ? getter.call(obj) : val;
            if (!hasChanged(value, newVal)) {
                return;
            }
            if (setter) {
                setter.call(obj, newVal);
            }
            else if (getter) {
                // #7981: for accessor properties without setter
                return;
            }
            else if (!shallow && isRef(value) && !isRef(newVal)) {
                value.value = newVal;
                return;
            }
            else {
                val = newVal;
            }
            childOb = !shallow && observe(newVal, false, mock);
            {
                dep.notify();
            }
        }
    });
    return dep;
}
function set(target, key, val) {
    if (isReadonly(target)) {
        return;
    }
    var ob = target.__ob__;
    if (isArray(target) && isValidArrayIndex(key)) {
        target.length = Math.max(target.length, key);
        target.splice(key, 1, val);
        // when mocking for SSR, array methods are not hijacked
        if (ob && !ob.shallow && ob.mock) {
            observe(val, false, true);
        }
        return val;
    }
    if (key in target && !(key in Object.prototype)) {
        target[key] = val;
        return val;
    }
    if (target._isVue || (ob && ob.vmCount)) {
        return val;
    }
    if (!ob) {
        target[key] = val;
        return val;
    }
    defineReactive(ob.value, key, val, undefined, ob.shallow, ob.mock);
    {
        ob.dep.notify();
    }
    return val;
}
function del(target, key) {
    if (isArray(target) && isValidArrayIndex(key)) {
        target.splice(key, 1);
        return;
    }
    var ob = target.__ob__;
    if (target._isVue || (ob && ob.vmCount)) {
        return;
    }
    if (isReadonly(target)) {
        return;
    }
    if (!hasOwn(target, key)) {
        return;
    }
    delete target[key];
    if (!ob) {
        return;
    }
    {
        ob.dep.notify();
    }
}
/**
 * Collect dependencies on array elements when the array is touched, since
 * we cannot intercept array element access like property getters.
 */
function dependArray(value) {
    for (var e = void 0, i = 0, l = value.length; i < l; i++) {
        e = value[i];
        if (e && e.__ob__) {
            e.__ob__.dep.depend();
        }
        if (isArray(e)) {
            dependArray(e);
        }
    }
}
/**
 * Return a shallowly-reactive copy of the original object, where only the root
 * level properties are reactive. It also does not auto-unwrap refs (even at the
 * root level).
 */
function shallowReactive(target) {
    makeReactive(target, true);
    def(target, "__v_isShallow" /* ReactiveFlags.IS_SHALLOW */, true);
    return target;
}
function makeReactive(target, shallow) {
    // if trying to observe a readonly proxy, return the readonly version.
    if (!isReadonly(target)) {
        observe(target, shallow, isServerRendering() /* ssr mock reactivity */);
    }
}
function isReadonly(value) {
    return !!(value && value.__v_isReadonly);
}
function isRef(r) {
    return !!(r && r.__v_isRef === true);
}
function proxyWithRefUnwrap(target, source, key) {
    Object.defineProperty(target, key, {
        enumerable: true,
        configurable: true,
        get: function () {
            var val = source[key];
            if (isRef(val)) {
                return val.value;
            }
            else {
                var ob = val && val.__ob__;
                if (ob)
                    ob.dep.depend();
                return val;
            }
        },
        set: function (value) {
            var oldValue = source[key];
            if (isRef(oldValue) && !isRef(value)) {
                oldValue.value = value;
            }
            else {
                source[key] = value;
            }
        }
    });
}

var activeEffectScope;
var EffectScope = /** @class */ (function () {
    function EffectScope(detached) {
        if (detached === void 0) { detached = false; }
        this.detached = detached;
        /**
         * @internal
         */
        this.active = true;
        /**
         * @internal
         */
        this.effects = [];
        /**
         * @internal
         */
        this.cleanups = [];
        this.parent = activeEffectScope;
        if (!detached && activeEffectScope) {
            this.index =
                (activeEffectScope.scopes || (activeEffectScope.scopes = [])).push(this) - 1;
        }
    }
    EffectScope.prototype.run = function (fn) {
        if (this.active) {
            var currentEffectScope = activeEffectScope;
            try {
                activeEffectScope = this;
                return fn();
            }
            finally {
                activeEffectScope = currentEffectScope;
            }
        }
    };
    /**
     * This should only be called on non-detached scopes
     * @internal
     */
    EffectScope.prototype.on = function () {
        activeEffectScope = this;
    };
    /**
     * This should only be called on non-detached scopes
     * @internal
     */
    EffectScope.prototype.off = function () {
        activeEffectScope = this.parent;
    };
    EffectScope.prototype.stop = function (fromParent) {
        if (this.active) {
            var i = void 0, l = void 0;
            for (i = 0, l = this.effects.length; i < l; i++) {
                this.effects[i].teardown();
            }
            for (i = 0, l = this.cleanups.length; i < l; i++) {
                this.cleanups[i]();
            }
            if (this.scopes) {
                for (i = 0, l = this.scopes.length; i < l; i++) {
                    this.scopes[i].stop(true);
                }
            }
            // nested scope, dereference from parent to avoid memory leaks
            if (!this.detached && this.parent && !fromParent) {
                // optimized O(1) removal
                var last = this.parent.scopes.pop();
                if (last && last !== this) {
                    this.parent.scopes[this.index] = last;
                    last.index = this.index;
                }
            }
            this.parent = undefined;
            this.active = false;
        }
    };
    return EffectScope;
}());
/**
 * @internal
 */
function recordEffectScope(effect, scope) {
    if (scope === void 0) { scope = activeEffectScope; }
    if (scope && scope.active) {
        scope.effects.push(effect);
    }
}
function resolveProvided(vm) {
    // by default an instance inherits its parent's provides object
    // but when it needs to provide values of its own, it creates its
    // own provides object using parent provides object as prototype.
    // this way in `inject` we can simply look up injections from direct
    // parent and let the prototype chain do the work.
    var existing = vm._provided;
    var parentProvides = vm.$parent && vm.$parent._provided;
    if (parentProvides === existing) {
        return (vm._provided = Object.create(parentProvides));
    }
    else {
        return existing;
    }
}

var normalizeEvent = cached(function (name) {
    var passive = name.charAt(0) === '&';
    name = passive ? name.slice(1) : name;
    var once = name.charAt(0) === '~'; // Prefixed last, checked first
    name = once ? name.slice(1) : name;
    var capture = name.charAt(0) === '!';
    name = capture ? name.slice(1) : name;
    return {
        name: name,
        once: once,
        capture: capture,
        passive: passive
    };
});
function createFnInvoker(fns, vm) {
    function invoker() {
        var fns = invoker.fns;
        if (isArray(fns)) {
            var cloned = fns.slice();
            for (var i = 0; i < cloned.length; i++) {
                invokeWithErrorHandling(cloned[i], null, arguments, vm, "v-on handler");
            }
        }
        else {
            // return handler return value for single handlers
            return invokeWithErrorHandling(fns, null, arguments, vm, "v-on handler");
        }
    }
    invoker.fns = fns;
    return invoker;
}
function updateListeners(on, oldOn, add, remove, createOnceHandler, vm) {
    var name, cur, old, event;
    for (name in on) {
        cur = on[name];
        old = oldOn[name];
        event = normalizeEvent(name);
        if (isUndef(cur)) ;
        else if (isUndef(old)) {
            if (isUndef(cur.fns)) {
                cur = on[name] = createFnInvoker(cur, vm);
            }
            if (isTrue(event.once)) {
                cur = on[name] = createOnceHandler(event.name, cur, event.capture);
            }
            add(event.name, cur, event.capture, event.passive, event.params);
        }
        else if (cur !== old) {
            old.fns = cur;
            on[name] = old;
        }
    }
    for (name in oldOn) {
        if (isUndef(on[name])) {
            event = normalizeEvent(name);
            remove(event.name, oldOn[name], event.capture);
        }
    }
}

function mergeVNodeHook(def, hookKey, hook) {
    if (def instanceof VNode) {
        def = def.data.hook || (def.data.hook = {});
    }
    var invoker;
    var oldHook = def[hookKey];
    function wrappedHook() {
        hook.apply(this, arguments);
        // important: remove merged hook to ensure it's called only once
        // and prevent memory leak
        remove$2(invoker.fns, wrappedHook);
    }
    if (isUndef(oldHook)) {
        // no existing hook
        invoker = createFnInvoker([wrappedHook]);
    }
    else {
        /* istanbul ignore if */
        if (isDef(oldHook.fns) && isTrue(oldHook.merged)) {
            // already a merged invoker
            invoker = oldHook;
            invoker.fns.push(wrappedHook);
        }
        else {
            // existing plain hook
            invoker = createFnInvoker([oldHook, wrappedHook]);
        }
    }
    invoker.merged = true;
    def[hookKey] = invoker;
}

function extractPropsFromVNodeData(data, Ctor, tag) {
    // we are only extracting raw values here.
    // validation and default values are handled in the child
    // component itself.
    var propOptions = Ctor.options.props;
    if (isUndef(propOptions)) {
        return;
    }
    var res = {};
    var attrs = data.attrs, props = data.props;
    if (isDef(attrs) || isDef(props)) {
        for (var key in propOptions) {
            var altKey = hyphenate(key);
            checkProp(res, props, key, altKey, true) ||
                checkProp(res, attrs, key, altKey, false);
        }
    }
    return res;
}
function checkProp(res, hash, key, altKey, preserve) {
    if (isDef(hash)) {
        if (hasOwn(hash, key)) {
            res[key] = hash[key];
            if (!preserve) {
                delete hash[key];
            }
            return true;
        }
        else if (hasOwn(hash, altKey)) {
            res[key] = hash[altKey];
            if (!preserve) {
                delete hash[altKey];
            }
            return true;
        }
    }
    return false;
}

// The template compiler attempts to minimize the need for normalization by
// statically analyzing the template at compile time.
//
// For plain HTML markup, normalization can be completely skipped because the
// generated render function is guaranteed to return Array<VNode>. There are
// two cases where extra normalization is needed:
// 1. When the children contains components - because a functional component
// may return an Array instead of a single root. In this case, just a simple
// normalization is needed - if any child is an Array, we flatten the whole
// thing with Array.prototype.concat. It is guaranteed to be only 1-level deep
// because functional components already normalize their own children.
function simpleNormalizeChildren(children) {
    for (var i = 0; i < children.length; i++) {
        if (isArray(children[i])) {
            return Array.prototype.concat.apply([], children);
        }
    }
    return children;
}
// 2. When the children contains constructs that always generated nested Arrays,
// e.g. <template>, <slot>, v-for, or when the children is provided by user
// with hand-written render functions / JSX. In such cases a full normalization
// is needed to cater to all possible types of children values.
function normalizeChildren(children) {
    return isPrimitive$1(children)
        ? [createTextVNode(children)]
        : isArray(children)
            ? normalizeArrayChildren(children)
            : undefined;
}
function isTextNode(node) {
    return isDef(node) && isDef(node.text) && isFalse(node.isComment);
}
function normalizeArrayChildren(children, nestedIndex) {
    var res = [];
    var i, c, lastIndex, last;
    for (i = 0; i < children.length; i++) {
        c = children[i];
        if (isUndef(c) || typeof c === 'boolean')
            continue;
        lastIndex = res.length - 1;
        last = res[lastIndex];
        //  nested
        if (isArray(c)) {
            if (c.length > 0) {
                c = normalizeArrayChildren(c, "".concat(nestedIndex || '', "_").concat(i));
                // merge adjacent text nodes
                if (isTextNode(c[0]) && isTextNode(last)) {
                    res[lastIndex] = createTextVNode(last.text + c[0].text);
                    c.shift();
                }
                res.push.apply(res, c);
            }
        }
        else if (isPrimitive$1(c)) {
            if (isTextNode(last)) {
                // merge adjacent text nodes
                // this is necessary for SSR hydration because text nodes are
                // essentially merged when rendered to HTML strings
                res[lastIndex] = createTextVNode(last.text + c);
            }
            else if (c !== '') {
                // convert primitive to vnode
                res.push(createTextVNode(c));
            }
        }
        else {
            if (isTextNode(c) && isTextNode(last)) {
                // merge adjacent text nodes
                res[lastIndex] = createTextVNode(last.text + c.text);
            }
            else {
                // default key for nested array children (likely generated by v-for)
                if (isTrue(children._isVList) &&
                    isDef(c.tag) &&
                    isUndef(c.key) &&
                    isDef(nestedIndex)) {
                    c.key = "__vlist".concat(nestedIndex, "_").concat(i, "__");
                }
                res.push(c);
            }
        }
    }
    return res;
}

/**
 * Runtime helper for rendering v-for lists.
 */
function renderList(val, render) {
    var ret = null, i, l, keys, key;
    if (isArray(val) || typeof val === 'string') {
        ret = new Array(val.length);
        for (i = 0, l = val.length; i < l; i++) {
            ret[i] = render(val[i], i);
        }
    }
    else if (typeof val === 'number') {
        ret = new Array(val);
        for (i = 0; i < val; i++) {
            ret[i] = render(i + 1, i);
        }
    }
    else if (isObject(val)) {
        if (hasSymbol && val[Symbol.iterator]) {
            ret = [];
            var iterator = val[Symbol.iterator]();
            var result = iterator.next();
            while (!result.done) {
                ret.push(render(result.value, ret.length));
                result = iterator.next();
            }
        }
        else {
            keys = Object.keys(val);
            ret = new Array(keys.length);
            for (i = 0, l = keys.length; i < l; i++) {
                key = keys[i];
                ret[i] = render(val[key], key, i);
            }
        }
    }
    if (!isDef(ret)) {
        ret = [];
    }
    ret._isVList = true;
    return ret;
}

/**
 * Runtime helper for rendering <slot>
 */
function renderSlot(name, fallbackRender, props, bindObject) {
    var scopedSlotFn = this.$scopedSlots[name];
    var nodes;
    if (scopedSlotFn) {
        // scoped slot
        props = props || {};
        if (bindObject) {
            props = extend(extend({}, bindObject), props);
        }
        nodes =
            scopedSlotFn(props) ||
                (isFunction(fallbackRender) ? fallbackRender() : fallbackRender);
    }
    else {
        nodes =
            this.$slots[name] ||
                (isFunction(fallbackRender) ? fallbackRender() : fallbackRender);
    }
    var target = props && props.slot;
    if (target) {
        return this.$createElement('template', { slot: target }, nodes);
    }
    else {
        return nodes;
    }
}

/**
 * Runtime helper for resolving filters
 */
function resolveFilter(id) {
    return resolveAsset(this.$options, 'filters', id) || identity;
}

function isKeyNotMatch(expect, actual) {
    if (isArray(expect)) {
        return expect.indexOf(actual) === -1;
    }
    else {
        return expect !== actual;
    }
}
/**
 * Runtime helper for checking keyCodes from config.
 * exposed as Vue.prototype._k
 * passing in eventKeyName as last argument separately for backwards compat
 */
function checkKeyCodes(eventKeyCode, key, builtInKeyCode, eventKeyName, builtInKeyName) {
    var mappedKeyCode = config.keyCodes[key] || builtInKeyCode;
    if (builtInKeyName && eventKeyName && !config.keyCodes[key]) {
        return isKeyNotMatch(builtInKeyName, eventKeyName);
    }
    else if (mappedKeyCode) {
        return isKeyNotMatch(mappedKeyCode, eventKeyCode);
    }
    else if (eventKeyName) {
        return hyphenate(eventKeyName) !== key;
    }
    return eventKeyCode === undefined;
}

/**
 * Runtime helper for merging v-bind="object" into a VNode's data.
 */
function bindObjectProps(data, tag, value, asProp, isSync) {
    if (value) {
        if (!isObject(value)) ;
        else {
            if (isArray(value)) {
                value = toObject(value);
            }
            var hash = void 0;
            var _loop_1 = function (key) {
                if (key === 'class' || key === 'style' || isReservedAttribute(key)) {
                    hash = data;
                }
                else {
                    var type = data.attrs && data.attrs.type;
                    hash =
                        asProp || config.mustUseProp(tag, type, key)
                            ? data.domProps || (data.domProps = {})
                            : data.attrs || (data.attrs = {});
                }
                var camelizedKey = camelize(key);
                var hyphenatedKey = hyphenate(key);
                if (!(camelizedKey in hash) && !(hyphenatedKey in hash)) {
                    hash[key] = value[key];
                    if (isSync) {
                        var on = data.on || (data.on = {});
                        on["update:".concat(key)] = function ($event) {
                            value[key] = $event;
                        };
                    }
                }
            };
            for (var key in value) {
                _loop_1(key);
            }
        }
    }
    return data;
}

/**
 * Runtime helper for rendering static trees.
 */
function renderStatic(index, isInFor) {
    var cached = this._staticTrees || (this._staticTrees = []);
    var tree = cached[index];
    // if has already-rendered static tree and not inside v-for,
    // we can reuse the same tree.
    if (tree && !isInFor) {
        return tree;
    }
    // otherwise, render a fresh tree.
    tree = cached[index] = this.$options.staticRenderFns[index].call(this._renderProxy, this._c, this // for render fns generated for functional component templates
    );
    markStatic(tree, "__static__".concat(index), false);
    return tree;
}
/**
 * Runtime helper for v-once.
 * Effectively it means marking the node as static with a unique key.
 */
function markOnce(tree, index, key) {
    markStatic(tree, "__once__".concat(index).concat(key ? "_".concat(key) : ""), true);
    return tree;
}
function markStatic(tree, key, isOnce) {
    if (isArray(tree)) {
        for (var i = 0; i < tree.length; i++) {
            if (tree[i] && typeof tree[i] !== 'string') {
                markStaticNode(tree[i], "".concat(key, "_").concat(i), isOnce);
            }
        }
    }
    else {
        markStaticNode(tree, key, isOnce);
    }
}
function markStaticNode(node, key, isOnce) {
    node.isStatic = true;
    node.key = key;
    node.isOnce = isOnce;
}

function bindObjectListeners(data, value) {
    if (value) {
        if (!isPlainObject(value)) ;
        else {
            var on = (data.on = data.on ? extend({}, data.on) : {});
            for (var key in value) {
                var existing = on[key];
                var ours = value[key];
                on[key] = existing ? [].concat(existing, ours) : ours;
            }
        }
    }
    return data;
}

function resolveScopedSlots(fns, res, 
// the following are added in 2.6
hasDynamicKeys, contentHashKey) {
    res = res || { $stable: !hasDynamicKeys };
    for (var i = 0; i < fns.length; i++) {
        var slot = fns[i];
        if (isArray(slot)) {
            resolveScopedSlots(slot, res, hasDynamicKeys);
        }
        else if (slot) {
            // marker for reverse proxying v-slot without scope on this.$slots
            // @ts-expect-error
            if (slot.proxy) {
                // @ts-expect-error
                slot.fn.proxy = true;
            }
            res[slot.key] = slot.fn;
        }
    }
    if (contentHashKey) {
        res.$key = contentHashKey;
    }
    return res;
}

// helper to process dynamic keys for dynamic arguments in v-bind and v-on.
function bindDynamicKeys(baseObj, values) {
    for (var i = 0; i < values.length; i += 2) {
        var key = values[i];
        if (typeof key === 'string' && key) {
            baseObj[values[i]] = values[i + 1];
        }
    }
    return baseObj;
}
// helper to dynamically append modifier runtime markers to event names.
// ensure only append when value is already string, otherwise it will be cast
// to string and cause the type check to miss.
function prependModifier(value, symbol) {
    return typeof value === 'string' ? symbol + value : value;
}

function installRenderHelpers(target) {
    target._o = markOnce;
    target._n = toNumber;
    target._s = toString;
    target._l = renderList;
    target._t = renderSlot;
    target._q = looseEqual;
    target._i = looseIndexOf;
    target._m = renderStatic;
    target._f = resolveFilter;
    target._k = checkKeyCodes;
    target._b = bindObjectProps;
    target._v = createTextVNode;
    target._e = createEmptyVNode;
    target._u = resolveScopedSlots;
    target._g = bindObjectListeners;
    target._d = bindDynamicKeys;
    target._p = prependModifier;
}

/**
 * Runtime helper for resolving raw children VNodes into a slot object.
 */
function resolveSlots(children, context) {
    if (!children || !children.length) {
        return {};
    }
    var slots = {};
    for (var i = 0, l = children.length; i < l; i++) {
        var child = children[i];
        var data = child.data;
        // remove slot attribute if the node is resolved as a Vue slot node
        if (data && data.attrs && data.attrs.slot) {
            delete data.attrs.slot;
        }
        // named slots should only be respected if the vnode was rendered in the
        // same context.
        if ((child.context === context || child.fnContext === context) &&
            data &&
            data.slot != null) {
            var name_1 = data.slot;
            var slot = slots[name_1] || (slots[name_1] = []);
            if (child.tag === 'template') {
                slot.push.apply(slot, child.children || []);
            }
            else {
                slot.push(child);
            }
        }
        else {
            (slots.default || (slots.default = [])).push(child);
        }
    }
    // ignore slots that contains only whitespace
    for (var name_2 in slots) {
        if (slots[name_2].every(isWhitespace)) {
            delete slots[name_2];
        }
    }
    return slots;
}
function isWhitespace(node) {
    return (node.isComment && !node.asyncFactory) || node.text === ' ';
}

function isAsyncPlaceholder(node) {
    // @ts-expect-error not really boolean type
    return node.isComment && node.asyncFactory;
}

function normalizeScopedSlots(ownerVm, scopedSlots, normalSlots, prevScopedSlots) {
    var res;
    var hasNormalSlots = Object.keys(normalSlots).length > 0;
    var isStable = scopedSlots ? !!scopedSlots.$stable : !hasNormalSlots;
    var key = scopedSlots && scopedSlots.$key;
    if (!scopedSlots) {
        res = {};
    }
    else if (scopedSlots._normalized) {
        // fast path 1: child component re-render only, parent did not change
        return scopedSlots._normalized;
    }
    else if (isStable &&
        prevScopedSlots &&
        prevScopedSlots !== emptyObject &&
        key === prevScopedSlots.$key &&
        !hasNormalSlots &&
        !prevScopedSlots.$hasNormal) {
        // fast path 2: stable scoped slots w/ no normal slots to proxy,
        // only need to normalize once
        return prevScopedSlots;
    }
    else {
        res = {};
        for (var key_1 in scopedSlots) {
            if (scopedSlots[key_1] && key_1[0] !== '$') {
                res[key_1] = normalizeScopedSlot(ownerVm, normalSlots, key_1, scopedSlots[key_1]);
            }
        }
    }
    // expose normal slots on scopedSlots
    for (var key_2 in normalSlots) {
        if (!(key_2 in res)) {
            res[key_2] = proxyNormalSlot(normalSlots, key_2);
        }
    }
    // avoriaz seems to mock a non-extensible $scopedSlots object
    // and when that is passed down this would cause an error
    if (scopedSlots && Object.isExtensible(scopedSlots)) {
        scopedSlots._normalized = res;
    }
    def(res, '$stable', isStable);
    def(res, '$key', key);
    def(res, '$hasNormal', hasNormalSlots);
    return res;
}
function normalizeScopedSlot(vm, normalSlots, key, fn) {
    var normalized = function () {
        var cur = currentInstance;
        setCurrentInstance(vm);
        var res = arguments.length ? fn.apply(null, arguments) : fn({});
        res =
            res && typeof res === 'object' && !isArray(res)
                ? [res] // single vnode
                : normalizeChildren(res);
        var vnode = res && res[0];
        setCurrentInstance(cur);
        return res &&
            (!vnode ||
                (res.length === 1 && vnode.isComment && !isAsyncPlaceholder(vnode))) // #9658, #10391
            ? undefined
            : res;
    };
    // this is a slot using the new v-slot syntax without scope. although it is
    // compiled as a scoped slot, render fn users would expect it to be present
    // on this.$slots because the usage is semantically a normal slot.
    if (fn.proxy) {
        Object.defineProperty(normalSlots, key, {
            get: normalized,
            enumerable: true,
            configurable: true
        });
    }
    return normalized;
}
function proxyNormalSlot(slots, key) {
    return function () { return slots[key]; };
}

function initSetup(vm) {
    var options = vm.$options;
    var setup = options.setup;
    if (setup) {
        var ctx = (vm._setupContext = createSetupContext(vm));
        setCurrentInstance(vm);
        pushTarget();
        var setupResult = invokeWithErrorHandling(setup, null, [vm._props || shallowReactive({}), ctx], vm, "setup");
        popTarget();
        setCurrentInstance();
        if (isFunction(setupResult)) {
            // render function
            // @ts-ignore
            options.render = setupResult;
        }
        else if (isObject(setupResult)) {
            vm._setupState = setupResult;
            // __sfc indicates compiled bindings from <script setup>
            if (!setupResult.__sfc) {
                for (var key in setupResult) {
                    if (!isReserved(key)) {
                        proxyWithRefUnwrap(vm, setupResult, key);
                    }
                }
            }
            else {
                // exposed for compiled render fn
                var proxy = (vm._setupProxy = {});
                for (var key in setupResult) {
                    if (key !== '__sfc') {
                        proxyWithRefUnwrap(proxy, setupResult, key);
                    }
                }
            }
        }
        else ;
    }
}
function createSetupContext(vm) {
    return {
        get attrs() {
            if (!vm._attrsProxy) {
                var proxy = (vm._attrsProxy = {});
                def(proxy, '_v_attr_proxy', true);
                syncSetupProxy(proxy, vm.$attrs, emptyObject, vm, '$attrs');
            }
            return vm._attrsProxy;
        },
        get listeners() {
            if (!vm._listenersProxy) {
                var proxy = (vm._listenersProxy = {});
                syncSetupProxy(proxy, vm.$listeners, emptyObject, vm, '$listeners');
            }
            return vm._listenersProxy;
        },
        get slots() {
            return initSlotsProxy(vm);
        },
        emit: bind(vm.$emit, vm),
        expose: function (exposed) {
            if (exposed) {
                Object.keys(exposed).forEach(function (key) {
                    return proxyWithRefUnwrap(vm, exposed, key);
                });
            }
        }
    };
}
function syncSetupProxy(to, from, prev, instance, type) {
    var changed = false;
    for (var key in from) {
        if (!(key in to)) {
            changed = true;
            defineProxyAttr(to, key, instance, type);
        }
        else if (from[key] !== prev[key]) {
            changed = true;
        }
    }
    for (var key in to) {
        if (!(key in from)) {
            changed = true;
            delete to[key];
        }
    }
    return changed;
}
function defineProxyAttr(proxy, key, instance, type) {
    Object.defineProperty(proxy, key, {
        enumerable: true,
        configurable: true,
        get: function () {
            return instance[type][key];
        }
    });
}
function initSlotsProxy(vm) {
    if (!vm._slotsProxy) {
        syncSetupSlots((vm._slotsProxy = {}), vm.$scopedSlots);
    }
    return vm._slotsProxy;
}
function syncSetupSlots(to, from) {
    for (var key in from) {
        to[key] = from[key];
    }
    for (var key in to) {
        if (!(key in from)) {
            delete to[key];
        }
    }
}

function initRender(vm) {
    vm._vnode = null; // the root of the child tree
    vm._staticTrees = null; // v-once cached trees
    var options = vm.$options;
    var parentVnode = (vm.$vnode = options._parentVnode); // the placeholder node in parent tree
    var renderContext = parentVnode && parentVnode.context;
    vm.$slots = resolveSlots(options._renderChildren, renderContext);
    vm.$scopedSlots = parentVnode
        ? normalizeScopedSlots(vm.$parent, parentVnode.data.scopedSlots, vm.$slots)
        : emptyObject;
    // bind the createElement fn to this instance
    // so that we get proper render context inside it.
    // args order: tag, data, children, normalizationType, alwaysNormalize
    // internal version is used by render functions compiled from templates
    // @ts-expect-error
    vm._c = function (a, b, c, d) { return createElement$1(vm, a, b, c, d, false); };
    // normalization is always applied for the public version, used in
    // user-written render functions.
    // @ts-expect-error
    vm.$createElement = function (a, b, c, d) { return createElement$1(vm, a, b, c, d, true); };
    // $attrs & $listeners are exposed for easier HOC creation.
    // they need to be reactive so that HOCs using them are always updated
    var parentData = parentVnode && parentVnode.data;
    /* istanbul ignore else */
    {
        defineReactive(vm, '$attrs', (parentData && parentData.attrs) || emptyObject, null, true);
        defineReactive(vm, '$listeners', options._parentListeners || emptyObject, null, true);
    }
}
var currentRenderingInstance = null;
function renderMixin(Vue) {
    // install runtime convenience helpers
    installRenderHelpers(Vue.prototype);
    Vue.prototype.$nextTick = function (fn) {
        return nextTick(fn, this);
    };
    Vue.prototype._render = function () {
        var vm = this;
        var _a = vm.$options, render = _a.render, _parentVnode = _a._parentVnode;
        if (_parentVnode && vm._isMounted) {
            vm.$scopedSlots = normalizeScopedSlots(vm.$parent, _parentVnode.data.scopedSlots, vm.$slots, vm.$scopedSlots);
            if (vm._slotsProxy) {
                syncSetupSlots(vm._slotsProxy, vm.$scopedSlots);
            }
        }
        // set parent vnode. this allows render functions to have access
        // to the data on the placeholder node.
        vm.$vnode = _parentVnode;
        // render self
        var vnode;
        try {
            // There's no need to maintain a stack because all render fns are called
            // separately from one another. Nested component's render fns are called
            // when parent component is patched.
            setCurrentInstance(vm);
            currentRenderingInstance = vm;
            vnode = render.call(vm._renderProxy, vm.$createElement);
        }
        catch (e) {
            handleError(e, vm, "render");
            // return error render result,
            // or previous vnode to prevent render error causing blank component
            /* istanbul ignore else */
            {
                vnode = vm._vnode;
            }
        }
        finally {
            currentRenderingInstance = null;
            setCurrentInstance();
        }
        // if the returned array contains only a single node, allow it
        if (isArray(vnode) && vnode.length === 1) {
            vnode = vnode[0];
        }
        // return empty vnode in case the render function errored out
        if (!(vnode instanceof VNode)) {
            vnode = createEmptyVNode();
        }
        // set parent
        vnode.parent = _parentVnode;
        return vnode;
    };
}

function ensureCtor(comp, base) {
    if (comp.__esModule || (hasSymbol && comp[Symbol.toStringTag] === 'Module')) {
        comp = comp.default;
    }
    return isObject(comp) ? base.extend(comp) : comp;
}
function createAsyncPlaceholder(factory, data, context, children, tag) {
    var node = createEmptyVNode();
    node.asyncFactory = factory;
    node.asyncMeta = { data: data, context: context, children: children, tag: tag };
    return node;
}
function resolveAsyncComponent(factory, baseCtor) {
    if (isTrue(factory.error) && isDef(factory.errorComp)) {
        return factory.errorComp;
    }
    if (isDef(factory.resolved)) {
        return factory.resolved;
    }
    var owner = currentRenderingInstance;
    if (owner && isDef(factory.owners) && factory.owners.indexOf(owner) === -1) {
        // already pending
        factory.owners.push(owner);
    }
    if (isTrue(factory.loading) && isDef(factory.loadingComp)) {
        return factory.loadingComp;
    }
    if (owner && !isDef(factory.owners)) {
        var owners_1 = (factory.owners = [owner]);
        var sync_1 = true;
        var timerLoading_1 = null;
        var timerTimeout_1 = null;
        owner.$on('hook:destroyed', function () { return remove$2(owners_1, owner); });
        var forceRender_1 = function (renderCompleted) {
            for (var i = 0, l = owners_1.length; i < l; i++) {
                owners_1[i].$forceUpdate();
            }
            if (renderCompleted) {
                owners_1.length = 0;
                if (timerLoading_1 !== null) {
                    clearTimeout(timerLoading_1);
                    timerLoading_1 = null;
                }
                if (timerTimeout_1 !== null) {
                    clearTimeout(timerTimeout_1);
                    timerTimeout_1 = null;
                }
            }
        };
        var resolve = once(function (res) {
            // cache resolved
            factory.resolved = ensureCtor(res, baseCtor);
            // invoke callbacks only if this is not a synchronous resolve
            // (async resolves are shimmed as synchronous during SSR)
            if (!sync_1) {
                forceRender_1(true);
            }
            else {
                owners_1.length = 0;
            }
        });
        var reject_1 = once(function (reason) {
            if (isDef(factory.errorComp)) {
                factory.error = true;
                forceRender_1(true);
            }
        });
        var res_1 = factory(resolve, reject_1);
        if (isObject(res_1)) {
            if (isPromise(res_1)) {
                // () => Promise
                if (isUndef(factory.resolved)) {
                    res_1.then(resolve, reject_1);
                }
            }
            else if (isPromise(res_1.component)) {
                res_1.component.then(resolve, reject_1);
                if (isDef(res_1.error)) {
                    factory.errorComp = ensureCtor(res_1.error, baseCtor);
                }
                if (isDef(res_1.loading)) {
                    factory.loadingComp = ensureCtor(res_1.loading, baseCtor);
                    if (res_1.delay === 0) {
                        factory.loading = true;
                    }
                    else {
                        // @ts-expect-error NodeJS timeout type
                        timerLoading_1 = setTimeout(function () {
                            timerLoading_1 = null;
                            if (isUndef(factory.resolved) && isUndef(factory.error)) {
                                factory.loading = true;
                                forceRender_1(false);
                            }
                        }, res_1.delay || 200);
                    }
                }
                if (isDef(res_1.timeout)) {
                    // @ts-expect-error NodeJS timeout type
                    timerTimeout_1 = setTimeout(function () {
                        timerTimeout_1 = null;
                        if (isUndef(factory.resolved)) {
                            reject_1(null);
                        }
                    }, res_1.timeout);
                }
            }
        }
        sync_1 = false;
        // return in case resolved synchronously
        return factory.loading ? factory.loadingComp : factory.resolved;
    }
}

function getFirstComponentChild(children) {
    if (isArray(children)) {
        for (var i = 0; i < children.length; i++) {
            var c = children[i];
            if (isDef(c) && (isDef(c.componentOptions) || isAsyncPlaceholder(c))) {
                return c;
            }
        }
    }
}

var SIMPLE_NORMALIZE = 1;
var ALWAYS_NORMALIZE = 2;
// wrapper function for providing a more flexible interface
// without getting yelled at by flow
function createElement$1(context, tag, data, children, normalizationType, alwaysNormalize) {
    if (isArray(data) || isPrimitive$1(data)) {
        normalizationType = children;
        children = data;
        data = undefined;
    }
    if (isTrue(alwaysNormalize)) {
        normalizationType = ALWAYS_NORMALIZE;
    }
    return _createElement(context, tag, data, children, normalizationType);
}
function _createElement(context, tag, data, children, normalizationType) {
    if (isDef(data) && isDef(data.__ob__)) {
        return createEmptyVNode();
    }
    // object syntax in v-bind
    if (isDef(data) && isDef(data.is)) {
        tag = data.is;
    }
    if (!tag) {
        // in case of component :is set to falsy value
        return createEmptyVNode();
    }
    // support single function children as default scoped slot
    if (isArray(children) && isFunction(children[0])) {
        data = data || {};
        data.scopedSlots = { default: children[0] };
        children.length = 0;
    }
    if (normalizationType === ALWAYS_NORMALIZE) {
        children = normalizeChildren(children);
    }
    else if (normalizationType === SIMPLE_NORMALIZE) {
        children = simpleNormalizeChildren(children);
    }
    var vnode, ns;
    if (typeof tag === 'string') {
        var Ctor = void 0;
        ns = (context.$vnode && context.$vnode.ns) || config.getTagNamespace(tag);
        if (config.isReservedTag(tag)) {
            vnode = new VNode(config.parsePlatformTagName(tag), data, children, undefined, undefined, context);
        }
        else if ((!data || !data.pre) &&
            isDef((Ctor = resolveAsset(context.$options, 'components', tag)))) {
            // component
            vnode = createComponent(Ctor, data, context, children, tag);
        }
        else {
            // unknown or unlisted namespaced elements
            // check at runtime because it may get assigned a namespace when its
            // parent normalizes children
            vnode = new VNode(tag, data, children, undefined, undefined, context);
        }
    }
    else {
        // direct component options / constructor
        vnode = createComponent(tag, data, context, children);
    }
    if (isArray(vnode)) {
        return vnode;
    }
    else if (isDef(vnode)) {
        if (isDef(ns))
            applyNS(vnode, ns);
        if (isDef(data))
            registerDeepBindings(data);
        return vnode;
    }
    else {
        return createEmptyVNode();
    }
}
function applyNS(vnode, ns, force) {
    vnode.ns = ns;
    if (vnode.tag === 'foreignObject') {
        // use default namespace inside foreignObject
        ns = undefined;
        force = true;
    }
    if (isDef(vnode.children)) {
        for (var i = 0, l = vnode.children.length; i < l; i++) {
            var child = vnode.children[i];
            if (isDef(child.tag) &&
                (isUndef(child.ns) || (isTrue(force) && child.tag !== 'svg'))) {
                applyNS(child, ns, force);
            }
        }
    }
}
// ref #5318
// necessary to ensure parent re-render when deep bindings like :style and
// :class are used on slot nodes
function registerDeepBindings(data) {
    if (isObject(data.style)) {
        traverse(data.style);
    }
    if (isObject(data.class)) {
        traverse(data.class);
    }
}

function handleError(err, vm, info) {
    // Deactivate deps tracking while processing error handler to avoid possible infinite rendering.
    // See: https://github.com/vuejs/vuex/issues/1505
    pushTarget();
    try {
        if (vm) {
            var cur = vm;
            while ((cur = cur.$parent)) {
                var hooks = cur.$options.errorCaptured;
                if (hooks) {
                    for (var i = 0; i < hooks.length; i++) {
                        try {
                            var capture = hooks[i].call(cur, err, vm, info) === false;
                            if (capture)
                                return;
                        }
                        catch (e) {
                            globalHandleError(e, cur, 'errorCaptured hook');
                        }
                    }
                }
            }
        }
        globalHandleError(err, vm, info);
    }
    finally {
        popTarget();
    }
}
function invokeWithErrorHandling(handler, context, args, vm, info) {
    var res;
    try {
        res = args ? handler.apply(context, args) : handler.call(context);
        if (res && !res._isVue && isPromise(res) && !res._handled) {
            res.catch(function (e) { return handleError(e, vm, info + " (Promise/async)"); });
            res._handled = true;
        }
    }
    catch (e) {
        handleError(e, vm, info);
    }
    return res;
}
function globalHandleError(err, vm, info) {
    if (config.errorHandler) {
        try {
            return config.errorHandler.call(null, err, vm, info);
        }
        catch (e) {
            // if the user intentionally throws the original error in the handler,
            // do not log it twice
            if (e !== err) {
                logError(e);
            }
        }
    }
    logError(err);
}
function logError(err, vm, info) {
    /* istanbul ignore else */
    if (inBrowser && typeof console !== 'undefined') {
        console.error(err);
    }
    else {
        throw err;
    }
}

/* globals MutationObserver */
var isUsingMicroTask = false;
var callbacks = [];
var pending = false;
function flushCallbacks() {
    pending = false;
    var copies = callbacks.slice(0);
    callbacks.length = 0;
    for (var i = 0; i < copies.length; i++) {
        copies[i]();
    }
}
// Here we have async deferring wrappers using microtasks.
// In 2.5 we used (macro) tasks (in combination with microtasks).
// However, it has subtle problems when state is changed right before repaint
// (e.g. #6813, out-in transitions).
// Also, using (macro) tasks in event handler would cause some weird behaviors
// that cannot be circumvented (e.g. #7109, #7153, #7546, #7834, #8109).
// So we now use microtasks everywhere, again.
// A major drawback of this tradeoff is that there are some scenarios
// where microtasks have too high a priority and fire in between supposedly
// sequential events (e.g. #4521, #6690, which have workarounds)
// or even between bubbling of the same event (#6566).
var timerFunc;
// The nextTick behavior leverages the microtask queue, which can be accessed
// via either native Promise.then or MutationObserver.
// MutationObserver has wider support, however it is seriously bugged in
// UIWebView in iOS >= 9.3.3 when triggered in touch event handlers. It
// completely stops working after triggering a few times... so, if native
// Promise is available, we will use it:
/* istanbul ignore next, $flow-disable-line */
if (typeof Promise !== 'undefined' && isNative(Promise)) {
    var p_1 = Promise.resolve();
    timerFunc = function () {
        p_1.then(flushCallbacks);
        // In problematic UIWebViews, Promise.then doesn't completely break, but
        // it can get stuck in a weird state where callbacks are pushed into the
        // microtask queue but the queue isn't being flushed, until the browser
        // needs to do some other work, e.g. handle a timer. Therefore we can
        // "force" the microtask queue to be flushed by adding an empty timer.
        if (isIOS)
            setTimeout(noop);
    };
    isUsingMicroTask = true;
}
else if (!isIE &&
    typeof MutationObserver !== 'undefined' &&
    (isNative(MutationObserver) ||
        // PhantomJS and iOS 7.x
        MutationObserver.toString() === '[object MutationObserverConstructor]')) {
    // Use MutationObserver where native Promise is not available,
    // e.g. PhantomJS, iOS7, Android 4.4
    // (#6466 MutationObserver is unreliable in IE11)
    var counter_1 = 1;
    var observer = new MutationObserver(flushCallbacks);
    var textNode_1 = document.createTextNode(String(counter_1));
    observer.observe(textNode_1, {
        characterData: true
    });
    timerFunc = function () {
        counter_1 = (counter_1 + 1) % 2;
        textNode_1.data = String(counter_1);
    };
    isUsingMicroTask = true;
}
else if (typeof setImmediate !== 'undefined' && isNative(setImmediate)) {
    // Fallback to setImmediate.
    // Technically it leverages the (macro) task queue,
    // but it is still a better choice than setTimeout.
    timerFunc = function () {
        setImmediate(flushCallbacks);
    };
}
else {
    // Fallback to setTimeout.
    timerFunc = function () {
        setTimeout(flushCallbacks, 0);
    };
}
/**
 * @internal
 */
function nextTick(cb, ctx) {
    var _resolve;
    callbacks.push(function () {
        if (cb) {
            try {
                cb.call(ctx);
            }
            catch (e) {
                handleError(e, ctx, 'nextTick');
            }
        }
        else if (_resolve) {
            _resolve(ctx);
        }
    });
    if (!pending) {
        pending = true;
        timerFunc();
    }
    // $flow-disable-line
    if (!cb && typeof Promise !== 'undefined') {
        return new Promise(function (resolve) {
            _resolve = resolve;
        });
    }
}

/**
 * Note: also update dist/vue.runtime.mjs when adding new exports to this file.
 */
var version = '2.7.14';

var seenObjects = new _Set();
/**
 * Recursively traverse an object to evoke all converted
 * getters, so that every nested property inside the object
 * is collected as a "deep" dependency.
 */
function traverse(val) {
    _traverse(val, seenObjects);
    seenObjects.clear();
    return val;
}
function _traverse(val, seen) {
    var i, keys;
    var isA = isArray(val);
    if ((!isA && !isObject(val)) ||
        val.__v_skip /* ReactiveFlags.SKIP */ ||
        Object.isFrozen(val) ||
        val instanceof VNode) {
        return;
    }
    if (val.__ob__) {
        var depId = val.__ob__.dep.id;
        if (seen.has(depId)) {
            return;
        }
        seen.add(depId);
    }
    if (isA) {
        i = val.length;
        while (i--)
            _traverse(val[i], seen);
    }
    else if (isRef(val)) {
        _traverse(val.value, seen);
    }
    else {
        keys = Object.keys(val);
        i = keys.length;
        while (i--)
            _traverse(val[keys[i]], seen);
    }
}

var uid$1 = 0;
/**
 * A watcher parses an expression, collects dependencies,
 * and fires callback when the expression value changes.
 * This is used for both the $watch() api and directives.
 * @internal
 */
var Watcher = /** @class */ (function () {
    function Watcher(vm, expOrFn, cb, options, isRenderWatcher) {
        recordEffectScope(this, 
        // if the active effect scope is manually created (not a component scope),
        // prioritize it
        activeEffectScope && !activeEffectScope._vm
            ? activeEffectScope
            : vm
                ? vm._scope
                : undefined);
        if ((this.vm = vm) && isRenderWatcher) {
            vm._watcher = this;
        }
        // options
        if (options) {
            this.deep = !!options.deep;
            this.user = !!options.user;
            this.lazy = !!options.lazy;
            this.sync = !!options.sync;
            this.before = options.before;
        }
        else {
            this.deep = this.user = this.lazy = this.sync = false;
        }
        this.cb = cb;
        this.id = ++uid$1; // uid for batching
        this.active = true;
        this.post = false;
        this.dirty = this.lazy; // for lazy watchers
        this.deps = [];
        this.newDeps = [];
        this.depIds = new _Set();
        this.newDepIds = new _Set();
        this.expression = '';
        // parse expression for getter
        if (isFunction(expOrFn)) {
            this.getter = expOrFn;
        }
        else {
            this.getter = parsePath(expOrFn);
            if (!this.getter) {
                this.getter = noop;
            }
        }
        this.value = this.lazy ? undefined : this.get();
    }
    /**
     * Evaluate the getter, and re-collect dependencies.
     */
    Watcher.prototype.get = function () {
        pushTarget(this);
        var value;
        var vm = this.vm;
        try {
            value = this.getter.call(vm, vm);
        }
        catch (e) {
            if (this.user) {
                handleError(e, vm, "getter for watcher \"".concat(this.expression, "\""));
            }
            else {
                throw e;
            }
        }
        finally {
            // "touch" every property so they are all tracked as
            // dependencies for deep watching
            if (this.deep) {
                traverse(value);
            }
            popTarget();
            this.cleanupDeps();
        }
        return value;
    };
    /**
     * Add a dependency to this directive.
     */
    Watcher.prototype.addDep = function (dep) {
        var id = dep.id;
        if (!this.newDepIds.has(id)) {
            this.newDepIds.add(id);
            this.newDeps.push(dep);
            if (!this.depIds.has(id)) {
                dep.addSub(this);
            }
        }
    };
    /**
     * Clean up for dependency collection.
     */
    Watcher.prototype.cleanupDeps = function () {
        var i = this.deps.length;
        while (i--) {
            var dep = this.deps[i];
            if (!this.newDepIds.has(dep.id)) {
                dep.removeSub(this);
            }
        }
        var tmp = this.depIds;
        this.depIds = this.newDepIds;
        this.newDepIds = tmp;
        this.newDepIds.clear();
        tmp = this.deps;
        this.deps = this.newDeps;
        this.newDeps = tmp;
        this.newDeps.length = 0;
    };
    /**
     * Subscriber interface.
     * Will be called when a dependency changes.
     */
    Watcher.prototype.update = function () {
        /* istanbul ignore else */
        if (this.lazy) {
            this.dirty = true;
        }
        else if (this.sync) {
            this.run();
        }
        else {
            queueWatcher(this);
        }
    };
    /**
     * Scheduler job interface.
     * Will be called by the scheduler.
     */
    Watcher.prototype.run = function () {
        if (this.active) {
            var value = this.get();
            if (value !== this.value ||
                // Deep watchers and watchers on Object/Arrays should fire even
                // when the value is the same, because the value may
                // have mutated.
                isObject(value) ||
                this.deep) {
                // set new value
                var oldValue = this.value;
                this.value = value;
                if (this.user) {
                    var info = "callback for watcher \"".concat(this.expression, "\"");
                    invokeWithErrorHandling(this.cb, this.vm, [value, oldValue], this.vm, info);
                }
                else {
                    this.cb.call(this.vm, value, oldValue);
                }
            }
        }
    };
    /**
     * Evaluate the value of the watcher.
     * This only gets called for lazy watchers.
     */
    Watcher.prototype.evaluate = function () {
        this.value = this.get();
        this.dirty = false;
    };
    /**
     * Depend on all deps collected by this watcher.
     */
    Watcher.prototype.depend = function () {
        var i = this.deps.length;
        while (i--) {
            this.deps[i].depend();
        }
    };
    /**
     * Remove self from all dependencies' subscriber list.
     */
    Watcher.prototype.teardown = function () {
        if (this.vm && !this.vm._isBeingDestroyed) {
            remove$2(this.vm._scope.effects, this);
        }
        if (this.active) {
            var i = this.deps.length;
            while (i--) {
                this.deps[i].removeSub(this);
            }
            this.active = false;
            if (this.onStop) {
                this.onStop();
            }
        }
    };
    return Watcher;
}());

function initEvents(vm) {
    vm._events = Object.create(null);
    vm._hasHookEvent = false;
    // init parent attached events
    var listeners = vm.$options._parentListeners;
    if (listeners) {
        updateComponentListeners(vm, listeners);
    }
}
var target$1;
function add$1(event, fn) {
    target$1.$on(event, fn);
}
function remove$1(event, fn) {
    target$1.$off(event, fn);
}
function createOnceHandler$1(event, fn) {
    var _target = target$1;
    return function onceHandler() {
        var res = fn.apply(null, arguments);
        if (res !== null) {
            _target.$off(event, onceHandler);
        }
    };
}
function updateComponentListeners(vm, listeners, oldListeners) {
    target$1 = vm;
    updateListeners(listeners, oldListeners || {}, add$1, remove$1, createOnceHandler$1, vm);
    target$1 = undefined;
}
function eventsMixin(Vue) {
    var hookRE = /^hook:/;
    Vue.prototype.$on = function (event, fn) {
        var vm = this;
        if (isArray(event)) {
            for (var i = 0, l = event.length; i < l; i++) {
                vm.$on(event[i], fn);
            }
        }
        else {
            (vm._events[event] || (vm._events[event] = [])).push(fn);
            // optimize hook:event cost by using a boolean flag marked at registration
            // instead of a hash lookup
            if (hookRE.test(event)) {
                vm._hasHookEvent = true;
            }
        }
        return vm;
    };
    Vue.prototype.$once = function (event, fn) {
        var vm = this;
        function on() {
            vm.$off(event, on);
            fn.apply(vm, arguments);
        }
        on.fn = fn;
        vm.$on(event, on);
        return vm;
    };
    Vue.prototype.$off = function (event, fn) {
        var vm = this;
        // all
        if (!arguments.length) {
            vm._events = Object.create(null);
            return vm;
        }
        // array of events
        if (isArray(event)) {
            for (var i_1 = 0, l = event.length; i_1 < l; i_1++) {
                vm.$off(event[i_1], fn);
            }
            return vm;
        }
        // specific event
        var cbs = vm._events[event];
        if (!cbs) {
            return vm;
        }
        if (!fn) {
            vm._events[event] = null;
            return vm;
        }
        // specific handler
        var cb;
        var i = cbs.length;
        while (i--) {
            cb = cbs[i];
            if (cb === fn || cb.fn === fn) {
                cbs.splice(i, 1);
                break;
            }
        }
        return vm;
    };
    Vue.prototype.$emit = function (event) {
        var vm = this;
        var cbs = vm._events[event];
        if (cbs) {
            cbs = cbs.length > 1 ? toArray(cbs) : cbs;
            var args = toArray(arguments, 1);
            var info = "event handler for \"".concat(event, "\"");
            for (var i = 0, l = cbs.length; i < l; i++) {
                invokeWithErrorHandling(cbs[i], vm, args, vm, info);
            }
        }
        return vm;
    };
}

var activeInstance = null;
function setActiveInstance(vm) {
    var prevActiveInstance = activeInstance;
    activeInstance = vm;
    return function () {
        activeInstance = prevActiveInstance;
    };
}
function initLifecycle(vm) {
    var options = vm.$options;
    // locate first non-abstract parent
    var parent = options.parent;
    if (parent && !options.abstract) {
        while (parent.$options.abstract && parent.$parent) {
            parent = parent.$parent;
        }
        parent.$children.push(vm);
    }
    vm.$parent = parent;
    vm.$root = parent ? parent.$root : vm;
    vm.$children = [];
    vm.$refs = {};
    vm._provided = parent ? parent._provided : Object.create(null);
    vm._watcher = null;
    vm._inactive = null;
    vm._directInactive = false;
    vm._isMounted = false;
    vm._isDestroyed = false;
    vm._isBeingDestroyed = false;
}
function lifecycleMixin(Vue) {
    Vue.prototype._update = function (vnode, hydrating) {
        var vm = this;
        var prevEl = vm.$el;
        var prevVnode = vm._vnode;
        var restoreActiveInstance = setActiveInstance(vm);
        vm._vnode = vnode;
        // Vue.prototype.__patch__ is injected in entry points
        // based on the rendering backend used.
        if (!prevVnode) {
            // initial render
            vm.$el = vm.__patch__(vm.$el, vnode, hydrating, false /* removeOnly */);
        }
        else {
            // updates
            vm.$el = vm.__patch__(prevVnode, vnode);
        }
        restoreActiveInstance();
        // update __vue__ reference
        if (prevEl) {
            prevEl.__vue__ = null;
        }
        if (vm.$el) {
            vm.$el.__vue__ = vm;
        }
        // if parent is an HOC, update its $el as well
        var wrapper = vm;
        while (wrapper &&
            wrapper.$vnode &&
            wrapper.$parent &&
            wrapper.$vnode === wrapper.$parent._vnode) {
            wrapper.$parent.$el = wrapper.$el;
            wrapper = wrapper.$parent;
        }
        // updated hook is called by the scheduler to ensure that children are
        // updated in a parent's updated hook.
    };
    Vue.prototype.$forceUpdate = function () {
        var vm = this;
        if (vm._watcher) {
            vm._watcher.update();
        }
    };
    Vue.prototype.$destroy = function () {
        var vm = this;
        if (vm._isBeingDestroyed) {
            return;
        }
        callHook$1(vm, 'beforeDestroy');
        vm._isBeingDestroyed = true;
        // remove self from parent
        var parent = vm.$parent;
        if (parent && !parent._isBeingDestroyed && !vm.$options.abstract) {
            remove$2(parent.$children, vm);
        }
        // teardown scope. this includes both the render watcher and other
        // watchers created
        vm._scope.stop();
        // remove reference from data ob
        // frozen object may not have observer.
        if (vm._data.__ob__) {
            vm._data.__ob__.vmCount--;
        }
        // call the last hook...
        vm._isDestroyed = true;
        // invoke destroy hooks on current rendered tree
        vm.__patch__(vm._vnode, null);
        // fire destroyed hook
        callHook$1(vm, 'destroyed');
        // turn off all instance listeners.
        vm.$off();
        // remove __vue__ reference
        if (vm.$el) {
            vm.$el.__vue__ = null;
        }
        // release circular reference (#6759)
        if (vm.$vnode) {
            vm.$vnode.parent = null;
        }
    };
}
function mountComponent(vm, el, hydrating) {
    vm.$el = el;
    if (!vm.$options.render) {
        // @ts-expect-error invalid type
        vm.$options.render = createEmptyVNode;
    }
    callHook$1(vm, 'beforeMount');
    var updateComponent;
    /* istanbul ignore if */
    {
        updateComponent = function () {
            vm._update(vm._render(), hydrating);
        };
    }
    var watcherOptions = {
        before: function () {
            if (vm._isMounted && !vm._isDestroyed) {
                callHook$1(vm, 'beforeUpdate');
            }
        }
    };
    // we set this to vm._watcher inside the watcher's constructor
    // since the watcher's initial patch may call $forceUpdate (e.g. inside child
    // component's mounted hook), which relies on vm._watcher being already defined
    new Watcher(vm, updateComponent, noop, watcherOptions, true /* isRenderWatcher */);
    hydrating = false;
    // flush buffer for flush: "pre" watchers queued in setup()
    var preWatchers = vm._preWatchers;
    if (preWatchers) {
        for (var i = 0; i < preWatchers.length; i++) {
            preWatchers[i].run();
        }
    }
    // manually mounted instance, call mounted on self
    // mounted is called for render-created child components in its inserted hook
    if (vm.$vnode == null) {
        vm._isMounted = true;
        callHook$1(vm, 'mounted');
    }
    return vm;
}
function updateChildComponent(vm, propsData, listeners, parentVnode, renderChildren) {
    // determine whether component has slot children
    // we need to do this before overwriting $options._renderChildren.
    // check if there are dynamic scopedSlots (hand-written or compiled but with
    // dynamic slot names). Static scoped slots compiled from template has the
    // "$stable" marker.
    var newScopedSlots = parentVnode.data.scopedSlots;
    var oldScopedSlots = vm.$scopedSlots;
    var hasDynamicScopedSlot = !!((newScopedSlots && !newScopedSlots.$stable) ||
        (oldScopedSlots !== emptyObject && !oldScopedSlots.$stable) ||
        (newScopedSlots && vm.$scopedSlots.$key !== newScopedSlots.$key) ||
        (!newScopedSlots && vm.$scopedSlots.$key));
    // Any static slot children from the parent may have changed during parent's
    // update. Dynamic scoped slots may also have changed. In such cases, a forced
    // update is necessary to ensure correctness.
    var needsForceUpdate = !!(renderChildren || // has new static slots
        vm.$options._renderChildren || // has old static slots
        hasDynamicScopedSlot);
    var prevVNode = vm.$vnode;
    vm.$options._parentVnode = parentVnode;
    vm.$vnode = parentVnode; // update vm's placeholder node without re-render
    if (vm._vnode) {
        // update child tree's parent
        vm._vnode.parent = parentVnode;
    }
    vm.$options._renderChildren = renderChildren;
    // update $attrs and $listeners hash
    // these are also reactive so they may trigger child update if the child
    // used them during render
    var attrs = parentVnode.data.attrs || emptyObject;
    if (vm._attrsProxy) {
        // force update if attrs are accessed and has changed since it may be
        // passed to a child component.
        if (syncSetupProxy(vm._attrsProxy, attrs, (prevVNode.data && prevVNode.data.attrs) || emptyObject, vm, '$attrs')) {
            needsForceUpdate = true;
        }
    }
    vm.$attrs = attrs;
    // update listeners
    listeners = listeners || emptyObject;
    var prevListeners = vm.$options._parentListeners;
    if (vm._listenersProxy) {
        syncSetupProxy(vm._listenersProxy, listeners, prevListeners || emptyObject, vm, '$listeners');
    }
    vm.$listeners = vm.$options._parentListeners = listeners;
    updateComponentListeners(vm, listeners, prevListeners);
    // update props
    if (propsData && vm.$options.props) {
        toggleObserving(false);
        var props = vm._props;
        var propKeys = vm.$options._propKeys || [];
        for (var i = 0; i < propKeys.length; i++) {
            var key = propKeys[i];
            var propOptions = vm.$options.props; // wtf flow?
            props[key] = validateProp(key, propOptions, propsData, vm);
        }
        toggleObserving(true);
        // keep a copy of raw propsData
        vm.$options.propsData = propsData;
    }
    // resolve slots + force update if has children
    if (needsForceUpdate) {
        vm.$slots = resolveSlots(renderChildren, parentVnode.context);
        vm.$forceUpdate();
    }
}
function isInInactiveTree(vm) {
    while (vm && (vm = vm.$parent)) {
        if (vm._inactive)
            return true;
    }
    return false;
}
function activateChildComponent(vm, direct) {
    if (direct) {
        vm._directInactive = false;
        if (isInInactiveTree(vm)) {
            return;
        }
    }
    else if (vm._directInactive) {
        return;
    }
    if (vm._inactive || vm._inactive === null) {
        vm._inactive = false;
        for (var i = 0; i < vm.$children.length; i++) {
            activateChildComponent(vm.$children[i]);
        }
        callHook$1(vm, 'activated');
    }
}
function deactivateChildComponent(vm, direct) {
    if (direct) {
        vm._directInactive = true;
        if (isInInactiveTree(vm)) {
            return;
        }
    }
    if (!vm._inactive) {
        vm._inactive = true;
        for (var i = 0; i < vm.$children.length; i++) {
            deactivateChildComponent(vm.$children[i]);
        }
        callHook$1(vm, 'deactivated');
    }
}
function callHook$1(vm, hook, args, setContext) {
    if (setContext === void 0) { setContext = true; }
    // #7573 disable dep collection when invoking lifecycle hooks
    pushTarget();
    var prev = currentInstance;
    setContext && setCurrentInstance(vm);
    var handlers = vm.$options[hook];
    var info = "".concat(hook, " hook");
    if (handlers) {
        for (var i = 0, j = handlers.length; i < j; i++) {
            invokeWithErrorHandling(handlers[i], vm, args || null, vm, info);
        }
    }
    if (vm._hasHookEvent) {
        vm.$emit('hook:' + hook);
    }
    setContext && setCurrentInstance(prev);
    popTarget();
}
var queue = [];
var activatedChildren = [];
var has = {};
var waiting = false;
var flushing = false;
var index = 0;
/**
 * Reset the scheduler's state.
 */
function resetSchedulerState() {
    index = queue.length = activatedChildren.length = 0;
    has = {};
    waiting = flushing = false;
}
// Async edge case #6566 requires saving the timestamp when event listeners are
// attached. However, calling performance.now() has a perf overhead especially
// if the page has thousands of event listeners. Instead, we take a timestamp
// every time the scheduler flushes and use that for all event listeners
// attached during that flush.
var currentFlushTimestamp = 0;
// Async edge case fix requires storing an event listener's attach timestamp.
var getNow = Date.now;
// Determine what event timestamp the browser is using. Annoyingly, the
// timestamp can either be hi-res (relative to page load) or low-res
// (relative to UNIX epoch), so in order to compare time we have to use the
// same timestamp type when saving the flush timestamp.
// All IE versions use low-res event timestamps, and have problematic clock
// implementations (#9632)
if (inBrowser && !isIE) {
    var performance_1 = window.performance;
    if (performance_1 &&
        typeof performance_1.now === 'function' &&
        getNow() > document.createEvent('Event').timeStamp) {
        // if the event timestamp, although evaluated AFTER the Date.now(), is
        // smaller than it, it means the event is using a hi-res timestamp,
        // and we need to use the hi-res version for event listener timestamps as
        // well.
        getNow = function () { return performance_1.now(); };
    }
}
var sortCompareFn = function (a, b) {
    if (a.post) {
        if (!b.post)
            return 1;
    }
    else if (b.post) {
        return -1;
    }
    return a.id - b.id;
};
/**
 * Flush both queues and run the watchers.
 */
function flushSchedulerQueue() {
    currentFlushTimestamp = getNow();
    flushing = true;
    var watcher, id;
    // Sort queue before flush.
    // This ensures that:
    // 1. Components are updated from parent to child. (because parent is always
    //    created before the child)
    // 2. A component's user watchers are run before its render watcher (because
    //    user watchers are created before the render watcher)
    // 3. If a component is destroyed during a parent component's watcher run,
    //    its watchers can be skipped.
    queue.sort(sortCompareFn);
    // do not cache length because more watchers might be pushed
    // as we run existing watchers
    for (index = 0; index < queue.length; index++) {
        watcher = queue[index];
        if (watcher.before) {
            watcher.before();
        }
        id = watcher.id;
        has[id] = null;
        watcher.run();
    }
    // keep copies of post queues before resetting state
    var activatedQueue = activatedChildren.slice();
    var updatedQueue = queue.slice();
    resetSchedulerState();
    // call component updated and activated hooks
    callActivatedHooks(activatedQueue);
    callUpdatedHooks(updatedQueue);
    cleanupDeps();
    // devtool hook
    /* istanbul ignore if */
    if (devtools && config.devtools) {
        devtools.emit('flush');
    }
}
function callUpdatedHooks(queue) {
    var i = queue.length;
    while (i--) {
        var watcher = queue[i];
        var vm = watcher.vm;
        if (vm && vm._watcher === watcher && vm._isMounted && !vm._isDestroyed) {
            callHook$1(vm, 'updated');
        }
    }
}
/**
 * Queue a kept-alive component that was activated during patch.
 * The queue will be processed after the entire tree has been patched.
 */
function queueActivatedComponent(vm) {
    // setting _inactive to false here so that a render function can
    // rely on checking whether it's in an inactive tree (e.g. router-view)
    vm._inactive = false;
    activatedChildren.push(vm);
}
function callActivatedHooks(queue) {
    for (var i = 0; i < queue.length; i++) {
        queue[i]._inactive = true;
        activateChildComponent(queue[i], true /* true */);
    }
}
/**
 * Push a watcher into the watcher queue.
 * Jobs with duplicate IDs will be skipped unless it's
 * pushed when the queue is being flushed.
 */
function queueWatcher(watcher) {
    var id = watcher.id;
    if (has[id] != null) {
        return;
    }
    if (watcher === Dep.target && watcher.noRecurse) {
        return;
    }
    has[id] = true;
    if (!flushing) {
        queue.push(watcher);
    }
    else {
        // if already flushing, splice the watcher based on its id
        // if already past its id, it will be run next immediately.
        var i = queue.length - 1;
        while (i > index && queue[i].id > watcher.id) {
            i--;
        }
        queue.splice(i + 1, 0, watcher);
    }
    // queue the flush
    if (!waiting) {
        waiting = true;
        nextTick(flushSchedulerQueue);
    }
}

function initProvide(vm) {
    var provideOption = vm.$options.provide;
    if (provideOption) {
        var provided = isFunction(provideOption)
            ? provideOption.call(vm)
            : provideOption;
        if (!isObject(provided)) {
            return;
        }
        var source = resolveProvided(vm);
        // IE9 doesn't support Object.getOwnPropertyDescriptors so we have to
        // iterate the keys ourselves.
        var keys = hasSymbol ? Reflect.ownKeys(provided) : Object.keys(provided);
        for (var i = 0; i < keys.length; i++) {
            var key = keys[i];
            Object.defineProperty(source, key, Object.getOwnPropertyDescriptor(provided, key));
        }
    }
}
function initInjections(vm) {
    var result = resolveInject(vm.$options.inject, vm);
    if (result) {
        toggleObserving(false);
        Object.keys(result).forEach(function (key) {
            /* istanbul ignore else */
            {
                defineReactive(vm, key, result[key]);
            }
        });
        toggleObserving(true);
    }
}
function resolveInject(inject, vm) {
    if (inject) {
        // inject is :any because flow is not smart enough to figure out cached
        var result = Object.create(null);
        var keys = hasSymbol ? Reflect.ownKeys(inject) : Object.keys(inject);
        for (var i = 0; i < keys.length; i++) {
            var key = keys[i];
            // #6574 in case the inject object is observed...
            if (key === '__ob__')
                continue;
            var provideKey = inject[key].from;
            if (provideKey in vm._provided) {
                result[key] = vm._provided[provideKey];
            }
            else if ('default' in inject[key]) {
                var provideDefault = inject[key].default;
                result[key] = isFunction(provideDefault)
                    ? provideDefault.call(vm)
                    : provideDefault;
            }
            else ;
        }
        return result;
    }
}

function FunctionalRenderContext(data, props, children, parent, Ctor) {
    var _this = this;
    var options = Ctor.options;
    // ensure the createElement function in functional components
    // gets a unique context - this is necessary for correct named slot check
    var contextVm;
    if (hasOwn(parent, '_uid')) {
        contextVm = Object.create(parent);
        contextVm._original = parent;
    }
    else {
        // the context vm passed in is a functional context as well.
        // in this case we want to make sure we are able to get a hold to the
        // real context instance.
        contextVm = parent;
        // @ts-ignore
        parent = parent._original;
    }
    var isCompiled = isTrue(options._compiled);
    var needNormalization = !isCompiled;
    this.data = data;
    this.props = props;
    this.children = children;
    this.parent = parent;
    this.listeners = data.on || emptyObject;
    this.injections = resolveInject(options.inject, parent);
    this.slots = function () {
        if (!_this.$slots) {
            normalizeScopedSlots(parent, data.scopedSlots, (_this.$slots = resolveSlots(children, parent)));
        }
        return _this.$slots;
    };
    Object.defineProperty(this, 'scopedSlots', {
        enumerable: true,
        get: function () {
            return normalizeScopedSlots(parent, data.scopedSlots, this.slots());
        }
    });
    // support for compiled functional template
    if (isCompiled) {
        // exposing $options for renderStatic()
        this.$options = options;
        // pre-resolve slots for renderSlot()
        this.$slots = this.slots();
        this.$scopedSlots = normalizeScopedSlots(parent, data.scopedSlots, this.$slots);
    }
    if (options._scopeId) {
        this._c = function (a, b, c, d) {
            var vnode = createElement$1(contextVm, a, b, c, d, needNormalization);
            if (vnode && !isArray(vnode)) {
                vnode.fnScopeId = options._scopeId;
                vnode.fnContext = parent;
            }
            return vnode;
        };
    }
    else {
        this._c = function (a, b, c, d) {
            return createElement$1(contextVm, a, b, c, d, needNormalization);
        };
    }
}
installRenderHelpers(FunctionalRenderContext.prototype);
function createFunctionalComponent(Ctor, propsData, data, contextVm, children) {
    var options = Ctor.options;
    var props = {};
    var propOptions = options.props;
    if (isDef(propOptions)) {
        for (var key in propOptions) {
            props[key] = validateProp(key, propOptions, propsData || emptyObject);
        }
    }
    else {
        if (isDef(data.attrs))
            mergeProps(props, data.attrs);
        if (isDef(data.props))
            mergeProps(props, data.props);
    }
    var renderContext = new FunctionalRenderContext(data, props, children, contextVm, Ctor);
    var vnode = options.render.call(null, renderContext._c, renderContext);
    if (vnode instanceof VNode) {
        return cloneAndMarkFunctionalResult(vnode, data, renderContext.parent, options);
    }
    else if (isArray(vnode)) {
        var vnodes = normalizeChildren(vnode) || [];
        var res = new Array(vnodes.length);
        for (var i = 0; i < vnodes.length; i++) {
            res[i] = cloneAndMarkFunctionalResult(vnodes[i], data, renderContext.parent, options);
        }
        return res;
    }
}
function cloneAndMarkFunctionalResult(vnode, data, contextVm, options, renderContext) {
    // #7817 clone node before setting fnContext, otherwise if the node is reused
    // (e.g. it was from a cached normal slot) the fnContext causes named slots
    // that should not be matched to match.
    var clone = cloneVNode(vnode);
    clone.fnContext = contextVm;
    clone.fnOptions = options;
    if (data.slot) {
        (clone.data || (clone.data = {})).slot = data.slot;
    }
    return clone;
}
function mergeProps(to, from) {
    for (var key in from) {
        to[camelize(key)] = from[key];
    }
}

function getComponentName(options) {
    return options.name || options.__name || options._componentTag;
}
// inline hooks to be invoked on component VNodes during patch
var componentVNodeHooks = {
    init: function (vnode, hydrating) {
        if (vnode.componentInstance &&
            !vnode.componentInstance._isDestroyed &&
            vnode.data.keepAlive) {
            // kept-alive components, treat as a patch
            var mountedNode = vnode; // work around flow
            componentVNodeHooks.prepatch(mountedNode, mountedNode);
        }
        else {
            var child = (vnode.componentInstance = createComponentInstanceForVnode(vnode, activeInstance));
            child.$mount(hydrating ? vnode.elm : undefined, hydrating);
        }
    },
    prepatch: function (oldVnode, vnode) {
        var options = vnode.componentOptions;
        var child = (vnode.componentInstance = oldVnode.componentInstance);
        updateChildComponent(child, options.propsData, // updated props
        options.listeners, // updated listeners
        vnode, // new parent vnode
        options.children // new children
        );
    },
    insert: function (vnode) {
        var context = vnode.context, componentInstance = vnode.componentInstance;
        if (!componentInstance._isMounted) {
            componentInstance._isMounted = true;
            callHook$1(componentInstance, 'mounted');
        }
        if (vnode.data.keepAlive) {
            if (context._isMounted) {
                // vue-router#1212
                // During updates, a kept-alive component's child components may
                // change, so directly walking the tree here may call activated hooks
                // on incorrect children. Instead we push them into a queue which will
                // be processed after the whole patch process ended.
                queueActivatedComponent(componentInstance);
            }
            else {
                activateChildComponent(componentInstance, true /* direct */);
            }
        }
    },
    destroy: function (vnode) {
        var componentInstance = vnode.componentInstance;
        if (!componentInstance._isDestroyed) {
            if (!vnode.data.keepAlive) {
                componentInstance.$destroy();
            }
            else {
                deactivateChildComponent(componentInstance, true /* direct */);
            }
        }
    }
};
var hooksToMerge = Object.keys(componentVNodeHooks);
function createComponent(Ctor, data, context, children, tag) {
    if (isUndef(Ctor)) {
        return;
    }
    var baseCtor = context.$options._base;
    // plain options object: turn it into a constructor
    if (isObject(Ctor)) {
        Ctor = baseCtor.extend(Ctor);
    }
    // if at this stage it's not a constructor or an async component factory,
    // reject.
    if (typeof Ctor !== 'function') {
        return;
    }
    // async component
    var asyncFactory;
    // @ts-expect-error
    if (isUndef(Ctor.cid)) {
        asyncFactory = Ctor;
        Ctor = resolveAsyncComponent(asyncFactory, baseCtor);
        if (Ctor === undefined) {
            // return a placeholder node for async component, which is rendered
            // as a comment node but preserves all the raw information for the node.
            // the information will be used for async server-rendering and hydration.
            return createAsyncPlaceholder(asyncFactory, data, context, children, tag);
        }
    }
    data = data || {};
    // resolve constructor options in case global mixins are applied after
    // component constructor creation
    resolveConstructorOptions(Ctor);
    // transform component v-model data into props & events
    if (isDef(data.model)) {
        // @ts-expect-error
        transformModel(Ctor.options, data);
    }
    // extract props
    // @ts-expect-error
    var propsData = extractPropsFromVNodeData(data, Ctor);
    // functional component
    // @ts-expect-error
    if (isTrue(Ctor.options.functional)) {
        return createFunctionalComponent(Ctor, propsData, data, context, children);
    }
    // extract listeners, since these needs to be treated as
    // child component listeners instead of DOM listeners
    var listeners = data.on;
    // replace with listeners with .native modifier
    // so it gets processed during parent component patch.
    data.on = data.nativeOn;
    // @ts-expect-error
    if (isTrue(Ctor.options.abstract)) {
        // abstract components do not keep anything
        // other than props & listeners & slot
        // work around flow
        var slot = data.slot;
        data = {};
        if (slot) {
            data.slot = slot;
        }
    }
    // install component management hooks onto the placeholder node
    installComponentHooks(data);
    // return a placeholder vnode
    // @ts-expect-error
    var name = getComponentName(Ctor.options) || tag;
    var vnode = new VNode(
    // @ts-expect-error
    "vue-component-".concat(Ctor.cid).concat(name ? "-".concat(name) : ''), data, undefined, undefined, undefined, context, 
    // @ts-expect-error
    { Ctor: Ctor, propsData: propsData, listeners: listeners, tag: tag, children: children }, asyncFactory);
    return vnode;
}
function createComponentInstanceForVnode(
// we know it's MountedComponentVNode but flow doesn't
vnode, 
// activeInstance in lifecycle state
parent) {
    var options = {
        _isComponent: true,
        _parentVnode: vnode,
        parent: parent
    };
    // check inline-template render functions
    var inlineTemplate = vnode.data.inlineTemplate;
    if (isDef(inlineTemplate)) {
        options.render = inlineTemplate.render;
        options.staticRenderFns = inlineTemplate.staticRenderFns;
    }
    return new vnode.componentOptions.Ctor(options);
}
function installComponentHooks(data) {
    var hooks = data.hook || (data.hook = {});
    for (var i = 0; i < hooksToMerge.length; i++) {
        var key = hooksToMerge[i];
        var existing = hooks[key];
        var toMerge = componentVNodeHooks[key];
        // @ts-expect-error
        if (existing !== toMerge && !(existing && existing._merged)) {
            hooks[key] = existing ? mergeHook(toMerge, existing) : toMerge;
        }
    }
}
function mergeHook(f1, f2) {
    var merged = function (a, b) {
        // flow complains about extra args which is why we use any
        f1(a, b);
        f2(a, b);
    };
    merged._merged = true;
    return merged;
}
// transform component v-model info (value and callback) into
// prop and event handler respectively.
function transformModel(options, data) {
    var prop = (options.model && options.model.prop) || 'value';
    var event = (options.model && options.model.event) || 'input';
    (data.attrs || (data.attrs = {}))[prop] = data.model.value;
    var on = data.on || (data.on = {});
    var existing = on[event];
    var callback = data.model.callback;
    if (isDef(existing)) {
        if (isArray(existing)
            ? existing.indexOf(callback) === -1
            : existing !== callback) {
            on[event] = [callback].concat(existing);
        }
    }
    else {
        on[event] = callback;
    }
}

var warn = noop;

/**
 * Option overwriting strategies are functions that handle
 * how to merge a parent option value and a child option
 * value into the final value.
 */
var strats = config.optionMergeStrategies;
/**
 * Helper that recursively merges two data objects together.
 */
function mergeData(to, from, recursive) {
    if (recursive === void 0) { recursive = true; }
    if (!from)
        return to;
    var key, toVal, fromVal;
    var keys = hasSymbol
        ? Reflect.ownKeys(from)
        : Object.keys(from);
    for (var i = 0; i < keys.length; i++) {
        key = keys[i];
        // in case the object is already observed...
        if (key === '__ob__')
            continue;
        toVal = to[key];
        fromVal = from[key];
        if (!recursive || !hasOwn(to, key)) {
            set(to, key, fromVal);
        }
        else if (toVal !== fromVal &&
            isPlainObject(toVal) &&
            isPlainObject(fromVal)) {
            mergeData(toVal, fromVal);
        }
    }
    return to;
}
/**
 * Data
 */
function mergeDataOrFn(parentVal, childVal, vm) {
    if (!vm) {
        // in a Vue.extend merge, both should be functions
        if (!childVal) {
            return parentVal;
        }
        if (!parentVal) {
            return childVal;
        }
        // when parentVal & childVal are both present,
        // we need to return a function that returns the
        // merged result of both functions... no need to
        // check if parentVal is a function here because
        // it has to be a function to pass previous merges.
        return function mergedDataFn() {
            return mergeData(isFunction(childVal) ? childVal.call(this, this) : childVal, isFunction(parentVal) ? parentVal.call(this, this) : parentVal);
        };
    }
    else {
        return function mergedInstanceDataFn() {
            // instance merge
            var instanceData = isFunction(childVal)
                ? childVal.call(vm, vm)
                : childVal;
            var defaultData = isFunction(parentVal)
                ? parentVal.call(vm, vm)
                : parentVal;
            if (instanceData) {
                return mergeData(instanceData, defaultData);
            }
            else {
                return defaultData;
            }
        };
    }
}
strats.data = function (parentVal, childVal, vm) {
    if (!vm) {
        if (childVal && typeof childVal !== 'function') {
            return parentVal;
        }
        return mergeDataOrFn(parentVal, childVal);
    }
    return mergeDataOrFn(parentVal, childVal, vm);
};
/**
 * Hooks and props are merged as arrays.
 */
function mergeLifecycleHook(parentVal, childVal) {
    var res = childVal
        ? parentVal
            ? parentVal.concat(childVal)
            : isArray(childVal)
                ? childVal
                : [childVal]
        : parentVal;
    return res ? dedupeHooks(res) : res;
}
function dedupeHooks(hooks) {
    var res = [];
    for (var i = 0; i < hooks.length; i++) {
        if (res.indexOf(hooks[i]) === -1) {
            res.push(hooks[i]);
        }
    }
    return res;
}
LIFECYCLE_HOOKS.forEach(function (hook) {
    strats[hook] = mergeLifecycleHook;
});
/**
 * Assets
 *
 * When a vm is present (instance creation), we need to do
 * a three-way merge between constructor options, instance
 * options and parent options.
 */
function mergeAssets(parentVal, childVal, vm, key) {
    var res = Object.create(parentVal || null);
    if (childVal) {
        return extend(res, childVal);
    }
    else {
        return res;
    }
}
ASSET_TYPES.forEach(function (type) {
    strats[type + 's'] = mergeAssets;
});
/**
 * Watchers.
 *
 * Watchers hashes should not overwrite one
 * another, so we merge them as arrays.
 */
strats.watch = function (parentVal, childVal, vm, key) {
    // work around Firefox's Object.prototype.watch...
    //@ts-expect-error work around
    if (parentVal === nativeWatch)
        parentVal = undefined;
    //@ts-expect-error work around
    if (childVal === nativeWatch)
        childVal = undefined;
    /* istanbul ignore if */
    if (!childVal)
        return Object.create(parentVal || null);
    if (!parentVal)
        return childVal;
    var ret = {};
    extend(ret, parentVal);
    for (var key_1 in childVal) {
        var parent_1 = ret[key_1];
        var child = childVal[key_1];
        if (parent_1 && !isArray(parent_1)) {
            parent_1 = [parent_1];
        }
        ret[key_1] = parent_1 ? parent_1.concat(child) : isArray(child) ? child : [child];
    }
    return ret;
};
/**
 * Other object hashes.
 */
strats.props =
    strats.methods =
        strats.inject =
            strats.computed =
                function (parentVal, childVal, vm, key) {
                    if (childVal && "production" !== 'production') {
                        assertObjectType(key, childVal);
                    }
                    if (!parentVal)
                        return childVal;
                    var ret = Object.create(null);
                    extend(ret, parentVal);
                    if (childVal)
                        extend(ret, childVal);
                    return ret;
                };
strats.provide = function (parentVal, childVal) {
    if (!parentVal)
        return childVal;
    return function () {
        var ret = Object.create(null);
        mergeData(ret, isFunction(parentVal) ? parentVal.call(this) : parentVal);
        if (childVal) {
            mergeData(ret, isFunction(childVal) ? childVal.call(this) : childVal, false // non-recursive
            );
        }
        return ret;
    };
};
/**
 * Default strategy.
 */
var defaultStrat = function (parentVal, childVal) {
    return childVal === undefined ? parentVal : childVal;
};
/**
 * Ensure all props option syntax are normalized into the
 * Object-based format.
 */
function normalizeProps(options, vm) {
    var props = options.props;
    if (!props)
        return;
    var res = {};
    var i, val, name;
    if (isArray(props)) {
        i = props.length;
        while (i--) {
            val = props[i];
            if (typeof val === 'string') {
                name = camelize(val);
                res[name] = { type: null };
            }
        }
    }
    else if (isPlainObject(props)) {
        for (var key in props) {
            val = props[key];
            name = camelize(key);
            res[name] = isPlainObject(val) ? val : { type: val };
        }
    }
    else ;
    options.props = res;
}
/**
 * Normalize all injections into Object-based format
 */
function normalizeInject(options, vm) {
    var inject = options.inject;
    if (!inject)
        return;
    var normalized = (options.inject = {});
    if (isArray(inject)) {
        for (var i = 0; i < inject.length; i++) {
            normalized[inject[i]] = { from: inject[i] };
        }
    }
    else if (isPlainObject(inject)) {
        for (var key in inject) {
            var val = inject[key];
            normalized[key] = isPlainObject(val)
                ? extend({ from: key }, val)
                : { from: val };
        }
    }
    else ;
}
/**
 * Normalize raw function directives into object format.
 */
function normalizeDirectives$1(options) {
    var dirs = options.directives;
    if (dirs) {
        for (var key in dirs) {
            var def = dirs[key];
            if (isFunction(def)) {
                dirs[key] = { bind: def, update: def };
            }
        }
    }
}
function assertObjectType(name, value, vm) {
    if (!isPlainObject(value)) {
        warn("Invalid value for option \"".concat(name, "\": expected an Object, ") +
            "but got ".concat(toRawType(value), "."));
    }
}
/**
 * Merge two option objects into a new one.
 * Core utility used in both instantiation and inheritance.
 */
function mergeOptions(parent, child, vm) {
    if (isFunction(child)) {
        // @ts-expect-error
        child = child.options;
    }
    normalizeProps(child);
    normalizeInject(child);
    normalizeDirectives$1(child);
    // Apply extends and mixins on the child options,
    // but only if it is a raw options object that isn't
    // the result of another mergeOptions call.
    // Only merged options has the _base property.
    if (!child._base) {
        if (child.extends) {
            parent = mergeOptions(parent, child.extends, vm);
        }
        if (child.mixins) {
            for (var i = 0, l = child.mixins.length; i < l; i++) {
                parent = mergeOptions(parent, child.mixins[i], vm);
            }
        }
    }
    var options = {};
    var key;
    for (key in parent) {
        mergeField(key);
    }
    for (key in child) {
        if (!hasOwn(parent, key)) {
            mergeField(key);
        }
    }
    function mergeField(key) {
        var strat = strats[key] || defaultStrat;
        options[key] = strat(parent[key], child[key], vm, key);
    }
    return options;
}
/**
 * Resolve an asset.
 * This function is used because child instances need access
 * to assets defined in its ancestor chain.
 */
function resolveAsset(options, type, id, warnMissing) {
    /* istanbul ignore if */
    if (typeof id !== 'string') {
        return;
    }
    var assets = options[type];
    // check local registration variations first
    if (hasOwn(assets, id))
        return assets[id];
    var camelizedId = camelize(id);
    if (hasOwn(assets, camelizedId))
        return assets[camelizedId];
    var PascalCaseId = capitalize(camelizedId);
    if (hasOwn(assets, PascalCaseId))
        return assets[PascalCaseId];
    // fallback to prototype chain
    var res = assets[id] || assets[camelizedId] || assets[PascalCaseId];
    return res;
}

function validateProp(key, propOptions, propsData, vm) {
    var prop = propOptions[key];
    var absent = !hasOwn(propsData, key);
    var value = propsData[key];
    // boolean casting
    var booleanIndex = getTypeIndex(Boolean, prop.type);
    if (booleanIndex > -1) {
        if (absent && !hasOwn(prop, 'default')) {
            value = false;
        }
        else if (value === '' || value === hyphenate(key)) {
            // only cast empty string / same name to boolean if
            // boolean has higher priority
            var stringIndex = getTypeIndex(String, prop.type);
            if (stringIndex < 0 || booleanIndex < stringIndex) {
                value = true;
            }
        }
    }
    // check default value
    if (value === undefined) {
        value = getPropDefaultValue(vm, prop, key);
        // since the default value is a fresh copy,
        // make sure to observe it.
        var prevShouldObserve = shouldObserve;
        toggleObserving(true);
        observe(value);
        toggleObserving(prevShouldObserve);
    }
    return value;
}
/**
 * Get the default value of a prop.
 */
function getPropDefaultValue(vm, prop, key) {
    // no default, return undefined
    if (!hasOwn(prop, 'default')) {
        return undefined;
    }
    var def = prop.default;
    // the raw prop value was also undefined from previous render,
    // return previous default value to avoid unnecessary watcher trigger
    if (vm &&
        vm.$options.propsData &&
        vm.$options.propsData[key] === undefined &&
        vm._props[key] !== undefined) {
        return vm._props[key];
    }
    // call factory function for non-Function types
    // a value is Function if its prototype is function even across different execution context
    return isFunction(def) && getType(prop.type) !== 'Function'
        ? def.call(vm)
        : def;
}
var functionTypeCheckRE = /^\s*function (\w+)/;
/**
 * Use function string name to check built-in types,
 * because a simple equality check will fail when running
 * across different vms / iframes.
 */
function getType(fn) {
    var match = fn && fn.toString().match(functionTypeCheckRE);
    return match ? match[1] : '';
}
function isSameType(a, b) {
    return getType(a) === getType(b);
}
function getTypeIndex(type, expectedTypes) {
    if (!isArray(expectedTypes)) {
        return isSameType(expectedTypes, type) ? 0 : -1;
    }
    for (var i = 0, len = expectedTypes.length; i < len; i++) {
        if (isSameType(expectedTypes[i], type)) {
            return i;
        }
    }
    return -1;
}

var sharedPropertyDefinition = {
    enumerable: true,
    configurable: true,
    get: noop,
    set: noop
};
function proxy(target, sourceKey, key) {
    sharedPropertyDefinition.get = function proxyGetter() {
        return this[sourceKey][key];
    };
    sharedPropertyDefinition.set = function proxySetter(val) {
        this[sourceKey][key] = val;
    };
    Object.defineProperty(target, key, sharedPropertyDefinition);
}
function initState(vm) {
    var opts = vm.$options;
    if (opts.props)
        initProps$1(vm, opts.props);
    // Composition API
    initSetup(vm);
    if (opts.methods)
        initMethods(vm, opts.methods);
    if (opts.data) {
        initData(vm);
    }
    else {
        var ob = observe((vm._data = {}));
        ob && ob.vmCount++;
    }
    if (opts.computed)
        initComputed$1(vm, opts.computed);
    if (opts.watch && opts.watch !== nativeWatch) {
        initWatch(vm, opts.watch);
    }
}
function initProps$1(vm, propsOptions) {
    var propsData = vm.$options.propsData || {};
    var props = (vm._props = shallowReactive({}));
    // cache prop keys so that future props updates can iterate using Array
    // instead of dynamic object key enumeration.
    var keys = (vm.$options._propKeys = []);
    var isRoot = !vm.$parent;
    // root instance props should be converted
    if (!isRoot) {
        toggleObserving(false);
    }
    var _loop_1 = function (key) {
        keys.push(key);
        var value = validateProp(key, propsOptions, propsData, vm);
        /* istanbul ignore else */
        {
            defineReactive(props, key, value);
        }
        // static props are already proxied on the component's prototype
        // during Vue.extend(). We only need to proxy props defined at
        // instantiation here.
        if (!(key in vm)) {
            proxy(vm, "_props", key);
        }
    };
    for (var key in propsOptions) {
        _loop_1(key);
    }
    toggleObserving(true);
}
function initData(vm) {
    var data = vm.$options.data;
    data = vm._data = isFunction(data) ? getData(data, vm) : data || {};
    if (!isPlainObject(data)) {
        data = {};
    }
    // proxy data on instance
    var keys = Object.keys(data);
    var props = vm.$options.props;
    vm.$options.methods;
    var i = keys.length;
    while (i--) {
        var key = keys[i];
        if (props && hasOwn(props, key)) ;
        else if (!isReserved(key)) {
            proxy(vm, "_data", key);
        }
    }
    // observe data
    var ob = observe(data);
    ob && ob.vmCount++;
}
function getData(data, vm) {
    // #7573 disable dep collection when invoking data getters
    pushTarget();
    try {
        return data.call(vm, vm);
    }
    catch (e) {
        handleError(e, vm, "data()");
        return {};
    }
    finally {
        popTarget();
    }
}
var computedWatcherOptions = { lazy: true };
function initComputed$1(vm, computed) {
    // $flow-disable-line
    var watchers = (vm._computedWatchers = Object.create(null));
    // computed properties are just getters during SSR
    var isSSR = isServerRendering();
    for (var key in computed) {
        var userDef = computed[key];
        var getter = isFunction(userDef) ? userDef : userDef.get;
        if (!isSSR) {
            // create internal watcher for the computed property.
            watchers[key] = new Watcher(vm, getter || noop, noop, computedWatcherOptions);
        }
        // component-defined computed properties are already defined on the
        // component prototype. We only need to define computed properties defined
        // at instantiation here.
        if (!(key in vm)) {
            defineComputed(vm, key, userDef);
        }
    }
}
function defineComputed(target, key, userDef) {
    var shouldCache = !isServerRendering();
    if (isFunction(userDef)) {
        sharedPropertyDefinition.get = shouldCache
            ? createComputedGetter(key)
            : createGetterInvoker(userDef);
        sharedPropertyDefinition.set = noop;
    }
    else {
        sharedPropertyDefinition.get = userDef.get
            ? shouldCache && userDef.cache !== false
                ? createComputedGetter(key)
                : createGetterInvoker(userDef.get)
            : noop;
        sharedPropertyDefinition.set = userDef.set || noop;
    }
    Object.defineProperty(target, key, sharedPropertyDefinition);
}
function createComputedGetter(key) {
    return function computedGetter() {
        var watcher = this._computedWatchers && this._computedWatchers[key];
        if (watcher) {
            if (watcher.dirty) {
                watcher.evaluate();
            }
            if (Dep.target) {
                watcher.depend();
            }
            return watcher.value;
        }
    };
}
function createGetterInvoker(fn) {
    return function computedGetter() {
        return fn.call(this, this);
    };
}
function initMethods(vm, methods) {
    vm.$options.props;
    for (var key in methods) {
        vm[key] = typeof methods[key] !== 'function' ? noop : bind(methods[key], vm);
    }
}
function initWatch(vm, watch) {
    for (var key in watch) {
        var handler = watch[key];
        if (isArray(handler)) {
            for (var i = 0; i < handler.length; i++) {
                createWatcher(vm, key, handler[i]);
            }
        }
        else {
            createWatcher(vm, key, handler);
        }
    }
}
function createWatcher(vm, expOrFn, handler, options) {
    if (isPlainObject(handler)) {
        options = handler;
        handler = handler.handler;
    }
    if (typeof handler === 'string') {
        handler = vm[handler];
    }
    return vm.$watch(expOrFn, handler, options);
}
function stateMixin(Vue) {
    // flow somehow has problems with directly declared definition object
    // when using Object.defineProperty, so we have to procedurally build up
    // the object here.
    var dataDef = {};
    dataDef.get = function () {
        return this._data;
    };
    var propsDef = {};
    propsDef.get = function () {
        return this._props;
    };
    Object.defineProperty(Vue.prototype, '$data', dataDef);
    Object.defineProperty(Vue.prototype, '$props', propsDef);
    Vue.prototype.$set = set;
    Vue.prototype.$delete = del;
    Vue.prototype.$watch = function (expOrFn, cb, options) {
        var vm = this;
        if (isPlainObject(cb)) {
            return createWatcher(vm, expOrFn, cb, options);
        }
        options = options || {};
        options.user = true;
        var watcher = new Watcher(vm, expOrFn, cb, options);
        if (options.immediate) {
            var info = "callback for immediate watcher \"".concat(watcher.expression, "\"");
            pushTarget();
            invokeWithErrorHandling(cb, vm, [watcher.value], vm, info);
            popTarget();
        }
        return function unwatchFn() {
            watcher.teardown();
        };
    };
}

var uid = 0;
function initMixin$1(Vue) {
    Vue.prototype._init = function (options) {
        var vm = this;
        // a uid
        vm._uid = uid++;
        // a flag to mark this as a Vue instance without having to do instanceof
        // check
        vm._isVue = true;
        // avoid instances from being observed
        vm.__v_skip = true;
        // effect scope
        vm._scope = new EffectScope(true /* detached */);
        vm._scope._vm = true;
        // merge options
        if (options && options._isComponent) {
            // optimize internal component instantiation
            // since dynamic options merging is pretty slow, and none of the
            // internal component options needs special treatment.
            initInternalComponent(vm, options);
        }
        else {
            vm.$options = mergeOptions(resolveConstructorOptions(vm.constructor), options || {}, vm);
        }
        /* istanbul ignore else */
        {
            vm._renderProxy = vm;
        }
        // expose real self
        vm._self = vm;
        initLifecycle(vm);
        initEvents(vm);
        initRender(vm);
        callHook$1(vm, 'beforeCreate', undefined, false /* setContext */);
        initInjections(vm); // resolve injections before data/props
        initState(vm);
        initProvide(vm); // resolve provide after data/props
        callHook$1(vm, 'created');
        if (vm.$options.el) {
            vm.$mount(vm.$options.el);
        }
    };
}
function initInternalComponent(vm, options) {
    var opts = (vm.$options = Object.create(vm.constructor.options));
    // doing this because it's faster than dynamic enumeration.
    var parentVnode = options._parentVnode;
    opts.parent = options.parent;
    opts._parentVnode = parentVnode;
    var vnodeComponentOptions = parentVnode.componentOptions;
    opts.propsData = vnodeComponentOptions.propsData;
    opts._parentListeners = vnodeComponentOptions.listeners;
    opts._renderChildren = vnodeComponentOptions.children;
    opts._componentTag = vnodeComponentOptions.tag;
    if (options.render) {
        opts.render = options.render;
        opts.staticRenderFns = options.staticRenderFns;
    }
}
function resolveConstructorOptions(Ctor) {
    var options = Ctor.options;
    if (Ctor.super) {
        var superOptions = resolveConstructorOptions(Ctor.super);
        var cachedSuperOptions = Ctor.superOptions;
        if (superOptions !== cachedSuperOptions) {
            // super option changed,
            // need to resolve new options.
            Ctor.superOptions = superOptions;
            // check if there are any late-modified/attached options (#4976)
            var modifiedOptions = resolveModifiedOptions(Ctor);
            // update base extend options
            if (modifiedOptions) {
                extend(Ctor.extendOptions, modifiedOptions);
            }
            options = Ctor.options = mergeOptions(superOptions, Ctor.extendOptions);
            if (options.name) {
                options.components[options.name] = Ctor;
            }
        }
    }
    return options;
}
function resolveModifiedOptions(Ctor) {
    var modified;
    var latest = Ctor.options;
    var sealed = Ctor.sealedOptions;
    for (var key in latest) {
        if (latest[key] !== sealed[key]) {
            if (!modified)
                modified = {};
            modified[key] = latest[key];
        }
    }
    return modified;
}

function Vue(options) {
    this._init(options);
}
//@ts-expect-error Vue has function type
initMixin$1(Vue);
//@ts-expect-error Vue has function type
stateMixin(Vue);
//@ts-expect-error Vue has function type
eventsMixin(Vue);
//@ts-expect-error Vue has function type
lifecycleMixin(Vue);
//@ts-expect-error Vue has function type
renderMixin(Vue);

function initUse(Vue) {
    Vue.use = function (plugin) {
        var installedPlugins = this._installedPlugins || (this._installedPlugins = []);
        if (installedPlugins.indexOf(plugin) > -1) {
            return this;
        }
        // additional parameters
        var args = toArray(arguments, 1);
        args.unshift(this);
        if (isFunction(plugin.install)) {
            plugin.install.apply(plugin, args);
        }
        else if (isFunction(plugin)) {
            plugin.apply(null, args);
        }
        installedPlugins.push(plugin);
        return this;
    };
}

function initMixin(Vue) {
    Vue.mixin = function (mixin) {
        this.options = mergeOptions(this.options, mixin);
        return this;
    };
}

function initExtend(Vue) {
    /**
     * Each instance constructor, including Vue, has a unique
     * cid. This enables us to create wrapped "child
     * constructors" for prototypal inheritance and cache them.
     */
    Vue.cid = 0;
    var cid = 1;
    /**
     * Class inheritance
     */
    Vue.extend = function (extendOptions) {
        extendOptions = extendOptions || {};
        var Super = this;
        var SuperId = Super.cid;
        var cachedCtors = extendOptions._Ctor || (extendOptions._Ctor = {});
        if (cachedCtors[SuperId]) {
            return cachedCtors[SuperId];
        }
        var name = getComponentName(extendOptions) || getComponentName(Super.options);
        var Sub = function VueComponent(options) {
            this._init(options);
        };
        Sub.prototype = Object.create(Super.prototype);
        Sub.prototype.constructor = Sub;
        Sub.cid = cid++;
        Sub.options = mergeOptions(Super.options, extendOptions);
        Sub['super'] = Super;
        // For props and computed properties, we define the proxy getters on
        // the Vue instances at extension time, on the extended prototype. This
        // avoids Object.defineProperty calls for each instance created.
        if (Sub.options.props) {
            initProps(Sub);
        }
        if (Sub.options.computed) {
            initComputed(Sub);
        }
        // allow further extension/mixin/plugin usage
        Sub.extend = Super.extend;
        Sub.mixin = Super.mixin;
        Sub.use = Super.use;
        // create asset registers, so extended classes
        // can have their private assets too.
        ASSET_TYPES.forEach(function (type) {
            Sub[type] = Super[type];
        });
        // enable recursive self-lookup
        if (name) {
            Sub.options.components[name] = Sub;
        }
        // keep a reference to the super options at extension time.
        // later at instantiation we can check if Super's options have
        // been updated.
        Sub.superOptions = Super.options;
        Sub.extendOptions = extendOptions;
        Sub.sealedOptions = extend({}, Sub.options);
        // cache constructor
        cachedCtors[SuperId] = Sub;
        return Sub;
    };
}
function initProps(Comp) {
    var props = Comp.options.props;
    for (var key in props) {
        proxy(Comp.prototype, "_props", key);
    }
}
function initComputed(Comp) {
    var computed = Comp.options.computed;
    for (var key in computed) {
        defineComputed(Comp.prototype, key, computed[key]);
    }
}

function initAssetRegisters(Vue) {
    /**
     * Create asset registration methods.
     */
    ASSET_TYPES.forEach(function (type) {
        // @ts-expect-error function is not exact same type
        Vue[type] = function (id, definition) {
            if (!definition) {
                return this.options[type + 's'][id];
            }
            else {
                if (type === 'component' && isPlainObject(definition)) {
                    // @ts-expect-error
                    definition.name = definition.name || id;
                    definition = this.options._base.extend(definition);
                }
                if (type === 'directive' && isFunction(definition)) {
                    definition = { bind: definition, update: definition };
                }
                this.options[type + 's'][id] = definition;
                return definition;
            }
        };
    });
}

function _getComponentName(opts) {
    return opts && (getComponentName(opts.Ctor.options) || opts.tag);
}
function matches(pattern, name) {
    if (isArray(pattern)) {
        return pattern.indexOf(name) > -1;
    }
    else if (typeof pattern === 'string') {
        return pattern.split(',').indexOf(name) > -1;
    }
    else if (isRegExp(pattern)) {
        return pattern.test(name);
    }
    /* istanbul ignore next */
    return false;
}
function pruneCache(keepAliveInstance, filter) {
    var cache = keepAliveInstance.cache, keys = keepAliveInstance.keys, _vnode = keepAliveInstance._vnode;
    for (var key in cache) {
        var entry = cache[key];
        if (entry) {
            var name_1 = entry.name;
            if (name_1 && !filter(name_1)) {
                pruneCacheEntry(cache, key, keys, _vnode);
            }
        }
    }
}
function pruneCacheEntry(cache, key, keys, current) {
    var entry = cache[key];
    if (entry && (!current || entry.tag !== current.tag)) {
        // @ts-expect-error can be undefined
        entry.componentInstance.$destroy();
    }
    cache[key] = null;
    remove$2(keys, key);
}
var patternTypes = [String, RegExp, Array];
// TODO defineComponent
var KeepAlive = {
    name: 'keep-alive',
    abstract: true,
    props: {
        include: patternTypes,
        exclude: patternTypes,
        max: [String, Number]
    },
    methods: {
        cacheVNode: function () {
            var _a = this, cache = _a.cache, keys = _a.keys, vnodeToCache = _a.vnodeToCache, keyToCache = _a.keyToCache;
            if (vnodeToCache) {
                var tag = vnodeToCache.tag, componentInstance = vnodeToCache.componentInstance, componentOptions = vnodeToCache.componentOptions;
                cache[keyToCache] = {
                    name: _getComponentName(componentOptions),
                    tag: tag,
                    componentInstance: componentInstance
                };
                keys.push(keyToCache);
                // prune oldest entry
                if (this.max && keys.length > parseInt(this.max)) {
                    pruneCacheEntry(cache, keys[0], keys, this._vnode);
                }
                this.vnodeToCache = null;
            }
        }
    },
    created: function () {
        this.cache = Object.create(null);
        this.keys = [];
    },
    destroyed: function () {
        for (var key in this.cache) {
            pruneCacheEntry(this.cache, key, this.keys);
        }
    },
    mounted: function () {
        var _this = this;
        this.cacheVNode();
        this.$watch('include', function (val) {
            pruneCache(_this, function (name) { return matches(val, name); });
        });
        this.$watch('exclude', function (val) {
            pruneCache(_this, function (name) { return !matches(val, name); });
        });
    },
    updated: function () {
        this.cacheVNode();
    },
    render: function () {
        var slot = this.$slots.default;
        var vnode = getFirstComponentChild(slot);
        var componentOptions = vnode && vnode.componentOptions;
        if (componentOptions) {
            // check pattern
            var name_2 = _getComponentName(componentOptions);
            var _a = this, include = _a.include, exclude = _a.exclude;
            if (
            // not included
            (include && (!name_2 || !matches(include, name_2))) ||
                // excluded
                (exclude && name_2 && matches(exclude, name_2))) {
                return vnode;
            }
            var _b = this, cache = _b.cache, keys = _b.keys;
            var key = vnode.key == null
                ? // same constructor may get registered as different local components
                    // so cid alone is not enough (#3269)
                    componentOptions.Ctor.cid +
                        (componentOptions.tag ? "::".concat(componentOptions.tag) : '')
                : vnode.key;
            if (cache[key]) {
                vnode.componentInstance = cache[key].componentInstance;
                // make current key freshest
                remove$2(keys, key);
                keys.push(key);
            }
            else {
                // delay setting the cache until update
                this.vnodeToCache = vnode;
                this.keyToCache = key;
            }
            // @ts-expect-error can vnode.data can be undefined
            vnode.data.keepAlive = true;
        }
        return vnode || (slot && slot[0]);
    }
};

var builtInComponents = {
    KeepAlive: KeepAlive
};

function initGlobalAPI(Vue) {
    // config
    var configDef = {};
    configDef.get = function () { return config; };
    Object.defineProperty(Vue, 'config', configDef);
    // exposed util methods.
    // NOTE: these are not considered part of the public API - avoid relying on
    // them unless you are aware of the risk.
    Vue.util = {
        warn: warn,
        extend: extend,
        mergeOptions: mergeOptions,
        defineReactive: defineReactive
    };
    Vue.set = set;
    Vue.delete = del;
    Vue.nextTick = nextTick;
    // 2.6 explicit observable API
    Vue.observable = function (obj) {
        observe(obj);
        return obj;
    };
    Vue.options = Object.create(null);
    ASSET_TYPES.forEach(function (type) {
        Vue.options[type + 's'] = Object.create(null);
    });
    // this is used to identify the "base" constructor to extend all plain-object
    // components with in Weex's multi-instance scenarios.
    Vue.options._base = Vue;
    extend(Vue.options.components, builtInComponents);
    initUse(Vue);
    initMixin(Vue);
    initExtend(Vue);
    initAssetRegisters(Vue);
}

initGlobalAPI(Vue);
Object.defineProperty(Vue.prototype, '$isServer', {
    get: isServerRendering
});
Object.defineProperty(Vue.prototype, '$ssrContext', {
    get: function () {
        /* istanbul ignore next */
        return this.$vnode && this.$vnode.ssrContext;
    }
});
// expose FunctionalRenderContext for ssr runtime helper installation
Object.defineProperty(Vue, 'FunctionalRenderContext', {
    value: FunctionalRenderContext
});
Vue.version = version;

// these are reserved for web because they are directly compiled away
// during template compilation
var isReservedAttr = makeMap('style,class');
// attributes that should be using props for binding
var acceptValue = makeMap('input,textarea,option,select,progress');
var mustUseProp = function (tag, type, attr) {
    return ((attr === 'value' && acceptValue(tag) && type !== 'button') ||
        (attr === 'selected' && tag === 'option') ||
        (attr === 'checked' && tag === 'input') ||
        (attr === 'muted' && tag === 'video'));
};
var isEnumeratedAttr = makeMap('contenteditable,draggable,spellcheck');
var isValidContentEditableValue = makeMap('events,caret,typing,plaintext-only');
var convertEnumeratedValue = function (key, value) {
    return isFalsyAttrValue(value) || value === 'false'
        ? 'false'
        : // allow arbitrary string value for contenteditable
            key === 'contenteditable' && isValidContentEditableValue(value)
                ? value
                : 'true';
};
var isBooleanAttr = makeMap('allowfullscreen,async,autofocus,autoplay,checked,compact,controls,declare,' +
    'default,defaultchecked,defaultmuted,defaultselected,defer,disabled,' +
    'enabled,formnovalidate,hidden,indeterminate,inert,ismap,itemscope,loop,multiple,' +
    'muted,nohref,noresize,noshade,novalidate,nowrap,open,pauseonexit,readonly,' +
    'required,reversed,scoped,seamless,selected,sortable,' +
    'truespeed,typemustmatch,visible');
var xlinkNS = 'http://www.w3.org/1999/xlink';
var isXlink = function (name) {
    return name.charAt(5) === ':' && name.slice(0, 5) === 'xlink';
};
var getXlinkProp = function (name) {
    return isXlink(name) ? name.slice(6, name.length) : '';
};
var isFalsyAttrValue = function (val) {
    return val == null || val === false;
};

function genClassForVnode(vnode) {
    var data = vnode.data;
    var parentNode = vnode;
    var childNode = vnode;
    while (isDef(childNode.componentInstance)) {
        childNode = childNode.componentInstance._vnode;
        if (childNode && childNode.data) {
            data = mergeClassData(childNode.data, data);
        }
    }
    // @ts-expect-error parentNode.parent not VNodeWithData
    while (isDef((parentNode = parentNode.parent))) {
        if (parentNode && parentNode.data) {
            data = mergeClassData(data, parentNode.data);
        }
    }
    return renderClass(data.staticClass, data.class);
}
function mergeClassData(child, parent) {
    return {
        staticClass: concat(child.staticClass, parent.staticClass),
        class: isDef(child.class) ? [child.class, parent.class] : parent.class
    };
}
function renderClass(staticClass, dynamicClass) {
    if (isDef(staticClass) || isDef(dynamicClass)) {
        return concat(staticClass, stringifyClass(dynamicClass));
    }
    /* istanbul ignore next */
    return '';
}
function concat(a, b) {
    return a ? (b ? a + ' ' + b : a) : b || '';
}
function stringifyClass(value) {
    if (Array.isArray(value)) {
        return stringifyArray(value);
    }
    if (isObject(value)) {
        return stringifyObject(value);
    }
    if (typeof value === 'string') {
        return value;
    }
    /* istanbul ignore next */
    return '';
}
function stringifyArray(value) {
    var res = '';
    var stringified;
    for (var i = 0, l = value.length; i < l; i++) {
        if (isDef((stringified = stringifyClass(value[i]))) && stringified !== '') {
            if (res)
                res += ' ';
            res += stringified;
        }
    }
    return res;
}
function stringifyObject(value) {
    var res = '';
    for (var key in value) {
        if (value[key]) {
            if (res)
                res += ' ';
            res += key;
        }
    }
    return res;
}

var namespaceMap = {
    svg: 'http://www.w3.org/2000/svg',
    math: 'http://www.w3.org/1998/Math/MathML'
};
var isHTMLTag = makeMap('html,body,base,head,link,meta,style,title,' +
    'address,article,aside,footer,header,h1,h2,h3,h4,h5,h6,hgroup,nav,section,' +
    'div,dd,dl,dt,figcaption,figure,picture,hr,img,li,main,ol,p,pre,ul,' +
    'a,b,abbr,bdi,bdo,br,cite,code,data,dfn,em,i,kbd,mark,q,rp,rt,rtc,ruby,' +
    's,samp,small,span,strong,sub,sup,time,u,var,wbr,area,audio,map,track,video,' +
    'embed,object,param,source,canvas,script,noscript,del,ins,' +
    'caption,col,colgroup,table,thead,tbody,td,th,tr,' +
    'button,datalist,fieldset,form,input,label,legend,meter,optgroup,option,' +
    'output,progress,select,textarea,' +
    'details,dialog,menu,menuitem,summary,' +
    'content,element,shadow,template,blockquote,iframe,tfoot');
// this map is intentionally selective, only covering SVG elements that may
// contain child elements.
var isSVG = makeMap('svg,animate,circle,clippath,cursor,defs,desc,ellipse,filter,font-face,' +
    'foreignobject,g,glyph,image,line,marker,mask,missing-glyph,path,pattern,' +
    'polygon,polyline,rect,switch,symbol,text,textpath,tspan,use,view', true);
var isReservedTag = function (tag) {
    return isHTMLTag(tag) || isSVG(tag);
};
function getTagNamespace(tag) {
    if (isSVG(tag)) {
        return 'svg';
    }
    // basic support for MathML
    // note it doesn't support other MathML elements being component roots
    if (tag === 'math') {
        return 'math';
    }
}
var unknownElementCache = Object.create(null);
function isUnknownElement(tag) {
    /* istanbul ignore if */
    if (!inBrowser) {
        return true;
    }
    if (isReservedTag(tag)) {
        return false;
    }
    tag = tag.toLowerCase();
    /* istanbul ignore if */
    if (unknownElementCache[tag] != null) {
        return unknownElementCache[tag];
    }
    var el = document.createElement(tag);
    if (tag.indexOf('-') > -1) {
        // http://stackoverflow.com/a/28210364/1070244
        return (unknownElementCache[tag] =
            el.constructor === window.HTMLUnknownElement ||
                el.constructor === window.HTMLElement);
    }
    else {
        return (unknownElementCache[tag] = /HTMLUnknownElement/.test(el.toString()));
    }
}
var isTextInputType = makeMap('text,number,password,search,email,tel,url');

/**
 * Query an element selector if it's not an element already.
 */
function query(el) {
    if (typeof el === 'string') {
        var selected = document.querySelector(el);
        if (!selected) {
            return document.createElement('div');
        }
        return selected;
    }
    else {
        return el;
    }
}

function createElement(tagName, vnode) {
    var elm = document.createElement(tagName);
    if (tagName !== 'select') {
        return elm;
    }
    // false or null will remove the attribute but undefined will not
    if (vnode.data &&
        vnode.data.attrs &&
        vnode.data.attrs.multiple !== undefined) {
        elm.setAttribute('multiple', 'multiple');
    }
    return elm;
}
function createElementNS(namespace, tagName) {
    return document.createElementNS(namespaceMap[namespace], tagName);
}
function createTextNode(text) {
    return document.createTextNode(text);
}
function createComment(text) {
    return document.createComment(text);
}
function insertBefore(parentNode, newNode, referenceNode) {
    parentNode.insertBefore(newNode, referenceNode);
}
function removeChild(node, child) {
    node.removeChild(child);
}
function appendChild(node, child) {
    node.appendChild(child);
}
function parentNode(node) {
    return node.parentNode;
}
function nextSibling(node) {
    return node.nextSibling;
}
function tagName(node) {
    return node.tagName;
}
function setTextContent(node, text) {
    node.textContent = text;
}
function setStyleScope(node, scopeId) {
    node.setAttribute(scopeId, '');
}

var nodeOps = /*#__PURE__*/Object.freeze({
  __proto__: null,
  createElement: createElement,
  createElementNS: createElementNS,
  createTextNode: createTextNode,
  createComment: createComment,
  insertBefore: insertBefore,
  removeChild: removeChild,
  appendChild: appendChild,
  parentNode: parentNode,
  nextSibling: nextSibling,
  tagName: tagName,
  setTextContent: setTextContent,
  setStyleScope: setStyleScope
});

var ref = {
    create: function (_, vnode) {
        registerRef(vnode);
    },
    update: function (oldVnode, vnode) {
        if (oldVnode.data.ref !== vnode.data.ref) {
            registerRef(oldVnode, true);
            registerRef(vnode);
        }
    },
    destroy: function (vnode) {
        registerRef(vnode, true);
    }
};
function registerRef(vnode, isRemoval) {
    var ref = vnode.data.ref;
    if (!isDef(ref))
        return;
    var vm = vnode.context;
    var refValue = vnode.componentInstance || vnode.elm;
    var value = isRemoval ? null : refValue;
    var $refsValue = isRemoval ? undefined : refValue;
    if (isFunction(ref)) {
        invokeWithErrorHandling(ref, vm, [value], vm, "template ref function");
        return;
    }
    var isFor = vnode.data.refInFor;
    var _isString = typeof ref === 'string' || typeof ref === 'number';
    var _isRef = isRef(ref);
    var refs = vm.$refs;
    if (_isString || _isRef) {
        if (isFor) {
            var existing = _isString ? refs[ref] : ref.value;
            if (isRemoval) {
                isArray(existing) && remove$2(existing, refValue);
            }
            else {
                if (!isArray(existing)) {
                    if (_isString) {
                        refs[ref] = [refValue];
                        setSetupRef(vm, ref, refs[ref]);
                    }
                    else {
                        ref.value = [refValue];
                    }
                }
                else if (!existing.includes(refValue)) {
                    existing.push(refValue);
                }
            }
        }
        else if (_isString) {
            if (isRemoval && refs[ref] !== refValue) {
                return;
            }
            refs[ref] = $refsValue;
            setSetupRef(vm, ref, value);
        }
        else if (_isRef) {
            if (isRemoval && ref.value !== refValue) {
                return;
            }
            ref.value = value;
        }
        else ;
    }
}
function setSetupRef(_a, key, val) {
    var _setupState = _a._setupState;
    if (_setupState && hasOwn(_setupState, key)) {
        if (isRef(_setupState[key])) {
            _setupState[key].value = val;
        }
        else {
            _setupState[key] = val;
        }
    }
}

/**
 * Virtual DOM patching algorithm based on Snabbdom by
 * Simon Friis Vindum (@paldepind)
 * Licensed under the MIT License
 * https://github.com/paldepind/snabbdom/blob/master/LICENSE
 *
 * modified by Evan You (@yyx990803)
 *
 * Not type-checking this because this file is perf-critical and the cost
 * of making flow understand it is not worth it.
 */
var emptyNode = new VNode('', {}, []);
var hooks = ['create', 'activate', 'update', 'remove', 'destroy'];
function sameVnode(a, b) {
    return (a.key === b.key &&
        a.asyncFactory === b.asyncFactory &&
        ((a.tag === b.tag &&
            a.isComment === b.isComment &&
            isDef(a.data) === isDef(b.data) &&
            sameInputType(a, b)) ||
            (isTrue(a.isAsyncPlaceholder) && isUndef(b.asyncFactory.error))));
}
function sameInputType(a, b) {
    if (a.tag !== 'input')
        return true;
    var i;
    var typeA = isDef((i = a.data)) && isDef((i = i.attrs)) && i.type;
    var typeB = isDef((i = b.data)) && isDef((i = i.attrs)) && i.type;
    return typeA === typeB || (isTextInputType(typeA) && isTextInputType(typeB));
}
function createKeyToOldIdx(children, beginIdx, endIdx) {
    var i, key;
    var map = {};
    for (i = beginIdx; i <= endIdx; ++i) {
        key = children[i].key;
        if (isDef(key))
            map[key] = i;
    }
    return map;
}
function createPatchFunction(backend) {
    var i, j;
    var cbs = {};
    var modules = backend.modules, nodeOps = backend.nodeOps;
    for (i = 0; i < hooks.length; ++i) {
        cbs[hooks[i]] = [];
        for (j = 0; j < modules.length; ++j) {
            if (isDef(modules[j][hooks[i]])) {
                cbs[hooks[i]].push(modules[j][hooks[i]]);
            }
        }
    }
    function emptyNodeAt(elm) {
        return new VNode(nodeOps.tagName(elm).toLowerCase(), {}, [], undefined, elm);
    }
    function createRmCb(childElm, listeners) {
        function remove() {
            if (--remove.listeners === 0) {
                removeNode(childElm);
            }
        }
        remove.listeners = listeners;
        return remove;
    }
    function removeNode(el) {
        var parent = nodeOps.parentNode(el);
        // element may have already been removed due to v-html / v-text
        if (isDef(parent)) {
            nodeOps.removeChild(parent, el);
        }
    }
    function createElm(vnode, insertedVnodeQueue, parentElm, refElm, nested, ownerArray, index) {
        if (isDef(vnode.elm) && isDef(ownerArray)) {
            // This vnode was used in a previous render!
            // now it's used as a new node, overwriting its elm would cause
            // potential patch errors down the road when it's used as an insertion
            // reference node. Instead, we clone the node on-demand before creating
            // associated DOM element for it.
            vnode = ownerArray[index] = cloneVNode(vnode);
        }
        vnode.isRootInsert = !nested; // for transition enter check
        if (createComponent(vnode, insertedVnodeQueue, parentElm, refElm)) {
            return;
        }
        var data = vnode.data;
        var children = vnode.children;
        var tag = vnode.tag;
        if (isDef(tag)) {
            vnode.elm = vnode.ns
                ? nodeOps.createElementNS(vnode.ns, tag)
                : nodeOps.createElement(tag, vnode);
            setScope(vnode);
            createChildren(vnode, children, insertedVnodeQueue);
            if (isDef(data)) {
                invokeCreateHooks(vnode, insertedVnodeQueue);
            }
            insert(parentElm, vnode.elm, refElm);
        }
        else if (isTrue(vnode.isComment)) {
            vnode.elm = nodeOps.createComment(vnode.text);
            insert(parentElm, vnode.elm, refElm);
        }
        else {
            vnode.elm = nodeOps.createTextNode(vnode.text);
            insert(parentElm, vnode.elm, refElm);
        }
    }
    function createComponent(vnode, insertedVnodeQueue, parentElm, refElm) {
        var i = vnode.data;
        if (isDef(i)) {
            var isReactivated = isDef(vnode.componentInstance) && i.keepAlive;
            if (isDef((i = i.hook)) && isDef((i = i.init))) {
                i(vnode, false /* hydrating */);
            }
            // after calling the init hook, if the vnode is a child component
            // it should've created a child instance and mounted it. the child
            // component also has set the placeholder vnode's elm.
            // in that case we can just return the element and be done.
            if (isDef(vnode.componentInstance)) {
                initComponent(vnode, insertedVnodeQueue);
                insert(parentElm, vnode.elm, refElm);
                if (isTrue(isReactivated)) {
                    reactivateComponent(vnode, insertedVnodeQueue, parentElm, refElm);
                }
                return true;
            }
        }
    }
    function initComponent(vnode, insertedVnodeQueue) {
        if (isDef(vnode.data.pendingInsert)) {
            insertedVnodeQueue.push.apply(insertedVnodeQueue, vnode.data.pendingInsert);
            vnode.data.pendingInsert = null;
        }
        vnode.elm = vnode.componentInstance.$el;
        if (isPatchable(vnode)) {
            invokeCreateHooks(vnode, insertedVnodeQueue);
            setScope(vnode);
        }
        else {
            // empty component root.
            // skip all element-related modules except for ref (#3455)
            registerRef(vnode);
            // make sure to invoke the insert hook
            insertedVnodeQueue.push(vnode);
        }
    }
    function reactivateComponent(vnode, insertedVnodeQueue, parentElm, refElm) {
        var i;
        // hack for #4339: a reactivated component with inner transition
        // does not trigger because the inner node's created hooks are not called
        // again. It's not ideal to involve module-specific logic in here but
        // there doesn't seem to be a better way to do it.
        var innerNode = vnode;
        while (innerNode.componentInstance) {
            innerNode = innerNode.componentInstance._vnode;
            if (isDef((i = innerNode.data)) && isDef((i = i.transition))) {
                for (i = 0; i < cbs.activate.length; ++i) {
                    cbs.activate[i](emptyNode, innerNode);
                }
                insertedVnodeQueue.push(innerNode);
                break;
            }
        }
        // unlike a newly created component,
        // a reactivated keep-alive component doesn't insert itself
        insert(parentElm, vnode.elm, refElm);
    }
    function insert(parent, elm, ref) {
        if (isDef(parent)) {
            if (isDef(ref)) {
                if (nodeOps.parentNode(ref) === parent) {
                    nodeOps.insertBefore(parent, elm, ref);
                }
            }
            else {
                nodeOps.appendChild(parent, elm);
            }
        }
    }
    function createChildren(vnode, children, insertedVnodeQueue) {
        if (isArray(children)) {
            for (var i_1 = 0; i_1 < children.length; ++i_1) {
                createElm(children[i_1], insertedVnodeQueue, vnode.elm, null, true, children, i_1);
            }
        }
        else if (isPrimitive$1(vnode.text)) {
            nodeOps.appendChild(vnode.elm, nodeOps.createTextNode(String(vnode.text)));
        }
    }
    function isPatchable(vnode) {
        while (vnode.componentInstance) {
            vnode = vnode.componentInstance._vnode;
        }
        return isDef(vnode.tag);
    }
    function invokeCreateHooks(vnode, insertedVnodeQueue) {
        for (var i_2 = 0; i_2 < cbs.create.length; ++i_2) {
            cbs.create[i_2](emptyNode, vnode);
        }
        i = vnode.data.hook; // Reuse variable
        if (isDef(i)) {
            if (isDef(i.create))
                i.create(emptyNode, vnode);
            if (isDef(i.insert))
                insertedVnodeQueue.push(vnode);
        }
    }
    // set scope id attribute for scoped CSS.
    // this is implemented as a special case to avoid the overhead
    // of going through the normal attribute patching process.
    function setScope(vnode) {
        var i;
        if (isDef((i = vnode.fnScopeId))) {
            nodeOps.setStyleScope(vnode.elm, i);
        }
        else {
            var ancestor = vnode;
            while (ancestor) {
                if (isDef((i = ancestor.context)) && isDef((i = i.$options._scopeId))) {
                    nodeOps.setStyleScope(vnode.elm, i);
                }
                ancestor = ancestor.parent;
            }
        }
        // for slot content they should also get the scopeId from the host instance.
        if (isDef((i = activeInstance)) &&
            i !== vnode.context &&
            i !== vnode.fnContext &&
            isDef((i = i.$options._scopeId))) {
            nodeOps.setStyleScope(vnode.elm, i);
        }
    }
    function addVnodes(parentElm, refElm, vnodes, startIdx, endIdx, insertedVnodeQueue) {
        for (; startIdx <= endIdx; ++startIdx) {
            createElm(vnodes[startIdx], insertedVnodeQueue, parentElm, refElm, false, vnodes, startIdx);
        }
    }
    function invokeDestroyHook(vnode) {
        var i, j;
        var data = vnode.data;
        if (isDef(data)) {
            if (isDef((i = data.hook)) && isDef((i = i.destroy)))
                i(vnode);
            for (i = 0; i < cbs.destroy.length; ++i)
                cbs.destroy[i](vnode);
        }
        if (isDef((i = vnode.children))) {
            for (j = 0; j < vnode.children.length; ++j) {
                invokeDestroyHook(vnode.children[j]);
            }
        }
    }
    function removeVnodes(vnodes, startIdx, endIdx) {
        for (; startIdx <= endIdx; ++startIdx) {
            var ch = vnodes[startIdx];
            if (isDef(ch)) {
                if (isDef(ch.tag)) {
                    removeAndInvokeRemoveHook(ch);
                    invokeDestroyHook(ch);
                }
                else {
                    // Text node
                    removeNode(ch.elm);
                }
            }
        }
    }
    function removeAndInvokeRemoveHook(vnode, rm) {
        if (isDef(rm) || isDef(vnode.data)) {
            var i_3;
            var listeners = cbs.remove.length + 1;
            if (isDef(rm)) {
                // we have a recursively passed down rm callback
                // increase the listeners count
                rm.listeners += listeners;
            }
            else {
                // directly removing
                rm = createRmCb(vnode.elm, listeners);
            }
            // recursively invoke hooks on child component root node
            if (isDef((i_3 = vnode.componentInstance)) &&
                isDef((i_3 = i_3._vnode)) &&
                isDef(i_3.data)) {
                removeAndInvokeRemoveHook(i_3, rm);
            }
            for (i_3 = 0; i_3 < cbs.remove.length; ++i_3) {
                cbs.remove[i_3](vnode, rm);
            }
            if (isDef((i_3 = vnode.data.hook)) && isDef((i_3 = i_3.remove))) {
                i_3(vnode, rm);
            }
            else {
                rm();
            }
        }
        else {
            removeNode(vnode.elm);
        }
    }
    function updateChildren(parentElm, oldCh, newCh, insertedVnodeQueue, removeOnly) {
        var oldStartIdx = 0;
        var newStartIdx = 0;
        var oldEndIdx = oldCh.length - 1;
        var oldStartVnode = oldCh[0];
        var oldEndVnode = oldCh[oldEndIdx];
        var newEndIdx = newCh.length - 1;
        var newStartVnode = newCh[0];
        var newEndVnode = newCh[newEndIdx];
        var oldKeyToIdx, idxInOld, vnodeToMove, refElm;
        // removeOnly is a special flag used only by <transition-group>
        // to ensure removed elements stay in correct relative positions
        // during leaving transitions
        var canMove = !removeOnly;
        while (oldStartIdx <= oldEndIdx && newStartIdx <= newEndIdx) {
            if (isUndef(oldStartVnode)) {
                oldStartVnode = oldCh[++oldStartIdx]; // Vnode has been moved left
            }
            else if (isUndef(oldEndVnode)) {
                oldEndVnode = oldCh[--oldEndIdx];
            }
            else if (sameVnode(oldStartVnode, newStartVnode)) {
                patchVnode(oldStartVnode, newStartVnode, insertedVnodeQueue, newCh, newStartIdx);
                oldStartVnode = oldCh[++oldStartIdx];
                newStartVnode = newCh[++newStartIdx];
            }
            else if (sameVnode(oldEndVnode, newEndVnode)) {
                patchVnode(oldEndVnode, newEndVnode, insertedVnodeQueue, newCh, newEndIdx);
                oldEndVnode = oldCh[--oldEndIdx];
                newEndVnode = newCh[--newEndIdx];
            }
            else if (sameVnode(oldStartVnode, newEndVnode)) {
                // Vnode moved right
                patchVnode(oldStartVnode, newEndVnode, insertedVnodeQueue, newCh, newEndIdx);
                canMove &&
                    nodeOps.insertBefore(parentElm, oldStartVnode.elm, nodeOps.nextSibling(oldEndVnode.elm));
                oldStartVnode = oldCh[++oldStartIdx];
                newEndVnode = newCh[--newEndIdx];
            }
            else if (sameVnode(oldEndVnode, newStartVnode)) {
                // Vnode moved left
                patchVnode(oldEndVnode, newStartVnode, insertedVnodeQueue, newCh, newStartIdx);
                canMove &&
                    nodeOps.insertBefore(parentElm, oldEndVnode.elm, oldStartVnode.elm);
                oldEndVnode = oldCh[--oldEndIdx];
                newStartVnode = newCh[++newStartIdx];
            }
            else {
                if (isUndef(oldKeyToIdx))
                    oldKeyToIdx = createKeyToOldIdx(oldCh, oldStartIdx, oldEndIdx);
                idxInOld = isDef(newStartVnode.key)
                    ? oldKeyToIdx[newStartVnode.key]
                    : findIdxInOld(newStartVnode, oldCh, oldStartIdx, oldEndIdx);
                if (isUndef(idxInOld)) {
                    // New element
                    createElm(newStartVnode, insertedVnodeQueue, parentElm, oldStartVnode.elm, false, newCh, newStartIdx);
                }
                else {
                    vnodeToMove = oldCh[idxInOld];
                    if (sameVnode(vnodeToMove, newStartVnode)) {
                        patchVnode(vnodeToMove, newStartVnode, insertedVnodeQueue, newCh, newStartIdx);
                        oldCh[idxInOld] = undefined;
                        canMove &&
                            nodeOps.insertBefore(parentElm, vnodeToMove.elm, oldStartVnode.elm);
                    }
                    else {
                        // same key but different element. treat as new element
                        createElm(newStartVnode, insertedVnodeQueue, parentElm, oldStartVnode.elm, false, newCh, newStartIdx);
                    }
                }
                newStartVnode = newCh[++newStartIdx];
            }
        }
        if (oldStartIdx > oldEndIdx) {
            refElm = isUndef(newCh[newEndIdx + 1]) ? null : newCh[newEndIdx + 1].elm;
            addVnodes(parentElm, refElm, newCh, newStartIdx, newEndIdx, insertedVnodeQueue);
        }
        else if (newStartIdx > newEndIdx) {
            removeVnodes(oldCh, oldStartIdx, oldEndIdx);
        }
    }
    function findIdxInOld(node, oldCh, start, end) {
        for (var i_5 = start; i_5 < end; i_5++) {
            var c = oldCh[i_5];
            if (isDef(c) && sameVnode(node, c))
                return i_5;
        }
    }
    function patchVnode(oldVnode, vnode, insertedVnodeQueue, ownerArray, index, removeOnly) {
        if (oldVnode === vnode) {
            return;
        }
        if (isDef(vnode.elm) && isDef(ownerArray)) {
            // clone reused vnode
            vnode = ownerArray[index] = cloneVNode(vnode);
        }
        var elm = (vnode.elm = oldVnode.elm);
        if (isTrue(oldVnode.isAsyncPlaceholder)) {
            if (isDef(vnode.asyncFactory.resolved)) {
                hydrate(oldVnode.elm, vnode, insertedVnodeQueue);
            }
            else {
                vnode.isAsyncPlaceholder = true;
            }
            return;
        }
        // reuse element for static trees.
        // note we only do this if the vnode is cloned -
        // if the new node is not cloned it means the render functions have been
        // reset by the hot-reload-api and we need to do a proper re-render.
        if (isTrue(vnode.isStatic) &&
            isTrue(oldVnode.isStatic) &&
            vnode.key === oldVnode.key &&
            (isTrue(vnode.isCloned) || isTrue(vnode.isOnce))) {
            vnode.componentInstance = oldVnode.componentInstance;
            return;
        }
        var i;
        var data = vnode.data;
        if (isDef(data) && isDef((i = data.hook)) && isDef((i = i.prepatch))) {
            i(oldVnode, vnode);
        }
        var oldCh = oldVnode.children;
        var ch = vnode.children;
        if (isDef(data) && isPatchable(vnode)) {
            for (i = 0; i < cbs.update.length; ++i)
                cbs.update[i](oldVnode, vnode);
            if (isDef((i = data.hook)) && isDef((i = i.update)))
                i(oldVnode, vnode);
        }
        if (isUndef(vnode.text)) {
            if (isDef(oldCh) && isDef(ch)) {
                if (oldCh !== ch)
                    updateChildren(elm, oldCh, ch, insertedVnodeQueue, removeOnly);
            }
            else if (isDef(ch)) {
                if (isDef(oldVnode.text))
                    nodeOps.setTextContent(elm, '');
                addVnodes(elm, null, ch, 0, ch.length - 1, insertedVnodeQueue);
            }
            else if (isDef(oldCh)) {
                removeVnodes(oldCh, 0, oldCh.length - 1);
            }
            else if (isDef(oldVnode.text)) {
                nodeOps.setTextContent(elm, '');
            }
        }
        else if (oldVnode.text !== vnode.text) {
            nodeOps.setTextContent(elm, vnode.text);
        }
        if (isDef(data)) {
            if (isDef((i = data.hook)) && isDef((i = i.postpatch)))
                i(oldVnode, vnode);
        }
    }
    function invokeInsertHook(vnode, queue, initial) {
        // delay insert hooks for component root nodes, invoke them after the
        // element is really inserted
        if (isTrue(initial) && isDef(vnode.parent)) {
            vnode.parent.data.pendingInsert = queue;
        }
        else {
            for (var i_6 = 0; i_6 < queue.length; ++i_6) {
                queue[i_6].data.hook.insert(queue[i_6]);
            }
        }
    }
    // list of modules that can skip create hook during hydration because they
    // are already rendered on the client or has no need for initialization
    // Note: style is excluded because it relies on initial clone for future
    // deep updates (#7063).
    var isRenderedModule = makeMap('attrs,class,staticClass,staticStyle,key');
    // Note: this is a browser-only function so we can assume elms are DOM nodes.
    function hydrate(elm, vnode, insertedVnodeQueue, inVPre) {
        var i;
        var tag = vnode.tag, data = vnode.data, children = vnode.children;
        inVPre = inVPre || (data && data.pre);
        vnode.elm = elm;
        if (isTrue(vnode.isComment) && isDef(vnode.asyncFactory)) {
            vnode.isAsyncPlaceholder = true;
            return true;
        }
        if (isDef(data)) {
            if (isDef((i = data.hook)) && isDef((i = i.init)))
                i(vnode, true /* hydrating */);
            if (isDef((i = vnode.componentInstance))) {
                // child component. it should have hydrated its own tree.
                initComponent(vnode, insertedVnodeQueue);
                return true;
            }
        }
        if (isDef(tag)) {
            if (isDef(children)) {
                // empty element, allow client to pick up and populate children
                if (!elm.hasChildNodes()) {
                    createChildren(vnode, children, insertedVnodeQueue);
                }
                else {
                    // v-html and domProps: innerHTML
                    if (isDef((i = data)) &&
                        isDef((i = i.domProps)) &&
                        isDef((i = i.innerHTML))) {
                        if (i !== elm.innerHTML) {
                            return false;
                        }
                    }
                    else {
                        // iterate and compare children lists
                        var childrenMatch = true;
                        var childNode = elm.firstChild;
                        for (var i_7 = 0; i_7 < children.length; i_7++) {
                            if (!childNode ||
                                !hydrate(childNode, children[i_7], insertedVnodeQueue, inVPre)) {
                                childrenMatch = false;
                                break;
                            }
                            childNode = childNode.nextSibling;
                        }
                        // if childNode is not null, it means the actual childNodes list is
                        // longer than the virtual children list.
                        if (!childrenMatch || childNode) {
                            return false;
                        }
                    }
                }
            }
            if (isDef(data)) {
                var fullInvoke = false;
                for (var key in data) {
                    if (!isRenderedModule(key)) {
                        fullInvoke = true;
                        invokeCreateHooks(vnode, insertedVnodeQueue);
                        break;
                    }
                }
                if (!fullInvoke && data['class']) {
                    // ensure collecting deps for deep class bindings for future updates
                    traverse(data['class']);
                }
            }
        }
        else if (elm.data !== vnode.text) {
            elm.data = vnode.text;
        }
        return true;
    }
    return function patch(oldVnode, vnode, hydrating, removeOnly) {
        if (isUndef(vnode)) {
            if (isDef(oldVnode))
                invokeDestroyHook(oldVnode);
            return;
        }
        var isInitialPatch = false;
        var insertedVnodeQueue = [];
        if (isUndef(oldVnode)) {
            // empty mount (likely as component), create new root element
            isInitialPatch = true;
            createElm(vnode, insertedVnodeQueue);
        }
        else {
            var isRealElement = isDef(oldVnode.nodeType);
            if (!isRealElement && sameVnode(oldVnode, vnode)) {
                // patch existing root node
                patchVnode(oldVnode, vnode, insertedVnodeQueue, null, null, removeOnly);
            }
            else {
                if (isRealElement) {
                    // mounting to a real element
                    // check if this is server-rendered content and if we can perform
                    // a successful hydration.
                    if (oldVnode.nodeType === 1 && oldVnode.hasAttribute(SSR_ATTR)) {
                        oldVnode.removeAttribute(SSR_ATTR);
                        hydrating = true;
                    }
                    if (isTrue(hydrating)) {
                        if (hydrate(oldVnode, vnode, insertedVnodeQueue)) {
                            invokeInsertHook(vnode, insertedVnodeQueue, true);
                            return oldVnode;
                        }
                    }
                    // either not server-rendered, or hydration failed.
                    // create an empty node and replace it
                    oldVnode = emptyNodeAt(oldVnode);
                }
                // replacing existing element
                var oldElm = oldVnode.elm;
                var parentElm = nodeOps.parentNode(oldElm);
                // create new node
                createElm(vnode, insertedVnodeQueue, 
                // extremely rare edge case: do not insert if old element is in a
                // leaving transition. Only happens when combining transition +
                // keep-alive + HOCs. (#4590)
                oldElm._leaveCb ? null : parentElm, nodeOps.nextSibling(oldElm));
                // update parent placeholder node element, recursively
                if (isDef(vnode.parent)) {
                    var ancestor = vnode.parent;
                    var patchable = isPatchable(vnode);
                    while (ancestor) {
                        for (var i_8 = 0; i_8 < cbs.destroy.length; ++i_8) {
                            cbs.destroy[i_8](ancestor);
                        }
                        ancestor.elm = vnode.elm;
                        if (patchable) {
                            for (var i_9 = 0; i_9 < cbs.create.length; ++i_9) {
                                cbs.create[i_9](emptyNode, ancestor);
                            }
                            // #6513
                            // invoke insert hooks that may have been merged by create hooks.
                            // e.g. for directives that uses the "inserted" hook.
                            var insert_1 = ancestor.data.hook.insert;
                            if (insert_1.merged) {
                                // start at index 1 to avoid re-invoking component mounted hook
                                for (var i_10 = 1; i_10 < insert_1.fns.length; i_10++) {
                                    insert_1.fns[i_10]();
                                }
                            }
                        }
                        else {
                            registerRef(ancestor);
                        }
                        ancestor = ancestor.parent;
                    }
                }
                // destroy old node
                if (isDef(parentElm)) {
                    removeVnodes([oldVnode], 0, 0);
                }
                else if (isDef(oldVnode.tag)) {
                    invokeDestroyHook(oldVnode);
                }
            }
        }
        invokeInsertHook(vnode, insertedVnodeQueue, isInitialPatch);
        return vnode.elm;
    };
}

var directives = {
    create: updateDirectives,
    update: updateDirectives,
    destroy: function unbindDirectives(vnode) {
        // @ts-expect-error emptyNode is not VNodeWithData
        updateDirectives(vnode, emptyNode);
    }
};
function updateDirectives(oldVnode, vnode) {
    if (oldVnode.data.directives || vnode.data.directives) {
        _update(oldVnode, vnode);
    }
}
function _update(oldVnode, vnode) {
    var isCreate = oldVnode === emptyNode;
    var isDestroy = vnode === emptyNode;
    var oldDirs = normalizeDirectives(oldVnode.data.directives, oldVnode.context);
    var newDirs = normalizeDirectives(vnode.data.directives, vnode.context);
    var dirsWithInsert = [];
    var dirsWithPostpatch = [];
    var key, oldDir, dir;
    for (key in newDirs) {
        oldDir = oldDirs[key];
        dir = newDirs[key];
        if (!oldDir) {
            // new directive, bind
            callHook(dir, 'bind', vnode, oldVnode);
            if (dir.def && dir.def.inserted) {
                dirsWithInsert.push(dir);
            }
        }
        else {
            // existing directive, update
            dir.oldValue = oldDir.value;
            dir.oldArg = oldDir.arg;
            callHook(dir, 'update', vnode, oldVnode);
            if (dir.def && dir.def.componentUpdated) {
                dirsWithPostpatch.push(dir);
            }
        }
    }
    if (dirsWithInsert.length) {
        var callInsert = function () {
            for (var i = 0; i < dirsWithInsert.length; i++) {
                callHook(dirsWithInsert[i], 'inserted', vnode, oldVnode);
            }
        };
        if (isCreate) {
            mergeVNodeHook(vnode, 'insert', callInsert);
        }
        else {
            callInsert();
        }
    }
    if (dirsWithPostpatch.length) {
        mergeVNodeHook(vnode, 'postpatch', function () {
            for (var i = 0; i < dirsWithPostpatch.length; i++) {
                callHook(dirsWithPostpatch[i], 'componentUpdated', vnode, oldVnode);
            }
        });
    }
    if (!isCreate) {
        for (key in oldDirs) {
            if (!newDirs[key]) {
                // no longer present, unbind
                callHook(oldDirs[key], 'unbind', oldVnode, oldVnode, isDestroy);
            }
        }
    }
}
var emptyModifiers = Object.create(null);
function normalizeDirectives(dirs, vm) {
    var res = Object.create(null);
    if (!dirs) {
        // $flow-disable-line
        return res;
    }
    var i, dir;
    for (i = 0; i < dirs.length; i++) {
        dir = dirs[i];
        if (!dir.modifiers) {
            // $flow-disable-line
            dir.modifiers = emptyModifiers;
        }
        res[getRawDirName(dir)] = dir;
        if (vm._setupState && vm._setupState.__sfc) {
            var setupDef = dir.def || resolveAsset(vm, '_setupState', 'v-' + dir.name);
            if (typeof setupDef === 'function') {
                dir.def = {
                    bind: setupDef,
                    update: setupDef,
                };
            }
            else {
                dir.def = setupDef;
            }
        }
        dir.def = dir.def || resolveAsset(vm.$options, 'directives', dir.name);
    }
    // $flow-disable-line
    return res;
}
function getRawDirName(dir) {
    return (dir.rawName || "".concat(dir.name, ".").concat(Object.keys(dir.modifiers || {}).join('.')));
}
function callHook(dir, hook, vnode, oldVnode, isDestroy) {
    var fn = dir.def && dir.def[hook];
    if (fn) {
        try {
            fn(vnode.elm, dir, vnode, oldVnode, isDestroy);
        }
        catch (e) {
            handleError(e, vnode.context, "directive ".concat(dir.name, " ").concat(hook, " hook"));
        }
    }
}

var baseModules = [ref, directives];

function updateAttrs(oldVnode, vnode) {
    var opts = vnode.componentOptions;
    if (isDef(opts) && opts.Ctor.options.inheritAttrs === false) {
        return;
    }
    if (isUndef(oldVnode.data.attrs) && isUndef(vnode.data.attrs)) {
        return;
    }
    var key, cur, old;
    var elm = vnode.elm;
    var oldAttrs = oldVnode.data.attrs || {};
    var attrs = vnode.data.attrs || {};
    // clone observed objects, as the user probably wants to mutate it
    if (isDef(attrs.__ob__) || isTrue(attrs._v_attr_proxy)) {
        attrs = vnode.data.attrs = extend({}, attrs);
    }
    for (key in attrs) {
        cur = attrs[key];
        old = oldAttrs[key];
        if (old !== cur) {
            setAttr(elm, key, cur, vnode.data.pre);
        }
    }
    // #4391: in IE9, setting type can reset value for input[type=radio]
    // #6666: IE/Edge forces progress value down to 1 before setting a max
    /* istanbul ignore if */
    if ((isIE || isEdge) && attrs.value !== oldAttrs.value) {
        setAttr(elm, 'value', attrs.value);
    }
    for (key in oldAttrs) {
        if (isUndef(attrs[key])) {
            if (isXlink(key)) {
                elm.removeAttributeNS(xlinkNS, getXlinkProp(key));
            }
            else if (!isEnumeratedAttr(key)) {
                elm.removeAttribute(key);
            }
        }
    }
}
function setAttr(el, key, value, isInPre) {
    if (isInPre || el.tagName.indexOf('-') > -1) {
        baseSetAttr(el, key, value);
    }
    else if (isBooleanAttr(key)) {
        // set attribute for blank value
        // e.g. <option disabled>Select one</option>
        if (isFalsyAttrValue(value)) {
            el.removeAttribute(key);
        }
        else {
            // technically allowfullscreen is a boolean attribute for <iframe>,
            // but Flash expects a value of "true" when used on <embed> tag
            value = key === 'allowfullscreen' && el.tagName === 'EMBED' ? 'true' : key;
            el.setAttribute(key, value);
        }
    }
    else if (isEnumeratedAttr(key)) {
        el.setAttribute(key, convertEnumeratedValue(key, value));
    }
    else if (isXlink(key)) {
        if (isFalsyAttrValue(value)) {
            el.removeAttributeNS(xlinkNS, getXlinkProp(key));
        }
        else {
            el.setAttributeNS(xlinkNS, key, value);
        }
    }
    else {
        baseSetAttr(el, key, value);
    }
}
function baseSetAttr(el, key, value) {
    if (isFalsyAttrValue(value)) {
        el.removeAttribute(key);
    }
    else {
        // #7138: IE10 & 11 fires input event when setting placeholder on
        // <textarea>... block the first input event and remove the blocker
        // immediately.
        /* istanbul ignore if */
        if (isIE &&
            !isIE9 &&
            el.tagName === 'TEXTAREA' &&
            key === 'placeholder' &&
            value !== '' &&
            !el.__ieph) {
            var blocker_1 = function (e) {
                e.stopImmediatePropagation();
                el.removeEventListener('input', blocker_1);
            };
            el.addEventListener('input', blocker_1);
            // $flow-disable-line
            el.__ieph = true; /* IE placeholder patched */
        }
        el.setAttribute(key, value);
    }
}
var attrs = {
    create: updateAttrs,
    update: updateAttrs
};

function updateClass(oldVnode, vnode) {
    var el = vnode.elm;
    var data = vnode.data;
    var oldData = oldVnode.data;
    if (isUndef(data.staticClass) &&
        isUndef(data.class) &&
        (isUndef(oldData) ||
            (isUndef(oldData.staticClass) && isUndef(oldData.class)))) {
        return;
    }
    var cls = genClassForVnode(vnode);
    // handle transition classes
    var transitionClass = el._transitionClasses;
    if (isDef(transitionClass)) {
        cls = concat(cls, stringifyClass(transitionClass));
    }
    // set the class
    if (cls !== el._prevClass) {
        el.setAttribute('class', cls);
        el._prevClass = cls;
    }
}
var klass = {
    create: updateClass,
    update: updateClass
};

// in some cases, the event used has to be determined at runtime
// so we used some reserved tokens during compile.
var RANGE_TOKEN = '__r';
var CHECKBOX_RADIO_TOKEN = '__c';

// normalize v-model event tokens that can only be determined at runtime.
// it's important to place the event as the first in the array because
// the whole point is ensuring the v-model callback gets called before
// user-attached handlers.
function normalizeEvents(on) {
    /* istanbul ignore if */
    if (isDef(on[RANGE_TOKEN])) {
        // IE input[type=range] only supports `change` event
        var event_1 = isIE ? 'change' : 'input';
        on[event_1] = [].concat(on[RANGE_TOKEN], on[event_1] || []);
        delete on[RANGE_TOKEN];
    }
    // This was originally intended to fix #4521 but no longer necessary
    // after 2.5. Keeping it for backwards compat with generated code from < 2.4
    /* istanbul ignore if */
    if (isDef(on[CHECKBOX_RADIO_TOKEN])) {
        on.change = [].concat(on[CHECKBOX_RADIO_TOKEN], on.change || []);
        delete on[CHECKBOX_RADIO_TOKEN];
    }
}
var target;
function createOnceHandler(event, handler, capture) {
    var _target = target; // save current target element in closure
    return function onceHandler() {
        var res = handler.apply(null, arguments);
        if (res !== null) {
            remove(event, onceHandler, capture, _target);
        }
    };
}
// #9446: Firefox <= 53 (in particular, ESR 52) has incorrect Event.timeStamp
// implementation and does not fire microtasks in between event propagation, so
// safe to exclude.
var useMicrotaskFix = isUsingMicroTask && !(isFF && Number(isFF[1]) <= 53);
function add(name, handler, capture, passive) {
    // async edge case #6566: inner click event triggers patch, event handler
    // attached to outer element during patch, and triggered again. This
    // happens because browsers fire microtask ticks between event propagation.
    // the solution is simple: we save the timestamp when a handler is attached,
    // and the handler would only fire if the event passed to it was fired
    // AFTER it was attached.
    if (useMicrotaskFix) {
        var attachedTimestamp_1 = currentFlushTimestamp;
        var original_1 = handler;
        //@ts-expect-error
        handler = original_1._wrapper = function (e) {
            if (
            // no bubbling, should always fire.
            // this is just a safety net in case event.timeStamp is unreliable in
            // certain weird environments...
            e.target === e.currentTarget ||
                // event is fired after handler attachment
                e.timeStamp >= attachedTimestamp_1 ||
                // bail for environments that have buggy event.timeStamp implementations
                // #9462 iOS 9 bug: event.timeStamp is 0 after history.pushState
                // #9681 QtWebEngine event.timeStamp is negative value
                e.timeStamp <= 0 ||
                // #9448 bail if event is fired in another document in a multi-page
                // electron/nw.js app, since event.timeStamp will be using a different
                // starting reference
                e.target.ownerDocument !== document) {
                return original_1.apply(this, arguments);
            }
        };
    }
    target.addEventListener(name, handler, supportsPassive ? { capture: capture, passive: passive } : capture);
}
function remove(name, handler, capture, _target) {
    (_target || target).removeEventListener(name, 
    //@ts-expect-error
    handler._wrapper || handler, capture);
}
function updateDOMListeners(oldVnode, vnode) {
    if (isUndef(oldVnode.data.on) && isUndef(vnode.data.on)) {
        return;
    }
    var on = vnode.data.on || {};
    var oldOn = oldVnode.data.on || {};
    // vnode is empty when removing all listeners,
    // and use old vnode dom element
    target = vnode.elm || oldVnode.elm;
    normalizeEvents(on);
    updateListeners(on, oldOn, add, remove, createOnceHandler, vnode.context);
    target = undefined;
}
var events = {
    create: updateDOMListeners,
    update: updateDOMListeners,
    // @ts-expect-error emptyNode has actually data
    destroy: function (vnode) { return updateDOMListeners(vnode, emptyNode); }
};

var svgContainer;
function updateDOMProps(oldVnode, vnode) {
    if (isUndef(oldVnode.data.domProps) && isUndef(vnode.data.domProps)) {
        return;
    }
    var key, cur;
    var elm = vnode.elm;
    var oldProps = oldVnode.data.domProps || {};
    var props = vnode.data.domProps || {};
    // clone observed objects, as the user probably wants to mutate it
    if (isDef(props.__ob__) || isTrue(props._v_attr_proxy)) {
        props = vnode.data.domProps = extend({}, props);
    }
    for (key in oldProps) {
        if (!(key in props)) {
            elm[key] = '';
        }
    }
    for (key in props) {
        cur = props[key];
        // ignore children if the node has textContent or innerHTML,
        // as these will throw away existing DOM nodes and cause removal errors
        // on subsequent patches (#3360)
        if (key === 'textContent' || key === 'innerHTML') {
            if (vnode.children)
                vnode.children.length = 0;
            if (cur === oldProps[key])
                continue;
            // #6601 work around Chrome version <= 55 bug where single textNode
            // replaced by innerHTML/textContent retains its parentNode property
            if (elm.childNodes.length === 1) {
                elm.removeChild(elm.childNodes[0]);
            }
        }
        if (key === 'value' && elm.tagName !== 'PROGRESS') {
            // store value as _value as well since
            // non-string values will be stringified
            elm._value = cur;
            // avoid resetting cursor position when value is the same
            var strCur = isUndef(cur) ? '' : String(cur);
            if (shouldUpdateValue(elm, strCur)) {
                elm.value = strCur;
            }
        }
        else if (key === 'innerHTML' &&
            isSVG(elm.tagName) &&
            isUndef(elm.innerHTML)) {
            // IE doesn't support innerHTML for SVG elements
            svgContainer = svgContainer || document.createElement('div');
            svgContainer.innerHTML = "<svg>".concat(cur, "</svg>");
            var svg = svgContainer.firstChild;
            while (elm.firstChild) {
                elm.removeChild(elm.firstChild);
            }
            while (svg.firstChild) {
                elm.appendChild(svg.firstChild);
            }
        }
        else if (
        // skip the update if old and new VDOM state is the same.
        // `value` is handled separately because the DOM value may be temporarily
        // out of sync with VDOM state due to focus, composition and modifiers.
        // This  #4521 by skipping the unnecessary `checked` update.
        cur !== oldProps[key]) {
            // some property updates can throw
            // e.g. `value` on <progress> w/ non-finite value
            try {
                elm[key] = cur;
            }
            catch (e) { }
        }
    }
}
function shouldUpdateValue(elm, checkVal) {
    return (
    //@ts-expect-error
    !elm.composing &&
        (elm.tagName === 'OPTION' ||
            isNotInFocusAndDirty(elm, checkVal) ||
            isDirtyWithModifiers(elm, checkVal)));
}
function isNotInFocusAndDirty(elm, checkVal) {
    // return true when textbox (.number and .trim) loses focus and its value is
    // not equal to the updated value
    var notInFocus = true;
    // #6157
    // work around IE bug when accessing document.activeElement in an iframe
    try {
        notInFocus = document.activeElement !== elm;
    }
    catch (e) { }
    return notInFocus && elm.value !== checkVal;
}
function isDirtyWithModifiers(elm, newVal) {
    var value = elm.value;
    var modifiers = elm._vModifiers; // injected by v-model runtime
    if (isDef(modifiers)) {
        if (modifiers.number) {
            return toNumber(value) !== toNumber(newVal);
        }
        if (modifiers.trim) {
            return value.trim() !== newVal.trim();
        }
    }
    return value !== newVal;
}
var domProps = {
    create: updateDOMProps,
    update: updateDOMProps
};

var parseStyleText = cached(function (cssText) {
    var res = {};
    var listDelimiter = /;(?![^(]*\))/g;
    var propertyDelimiter = /:(.+)/;
    cssText.split(listDelimiter).forEach(function (item) {
        if (item) {
            var tmp = item.split(propertyDelimiter);
            tmp.length > 1 && (res[tmp[0].trim()] = tmp[1].trim());
        }
    });
    return res;
});
// merge static and dynamic style data on the same vnode
function normalizeStyleData(data) {
    var style = normalizeStyleBinding(data.style);
    // static style is pre-processed into an object during compilation
    // and is always a fresh object, so it's safe to merge into it
    return data.staticStyle ? extend(data.staticStyle, style) : style;
}
// normalize possible array / string values into Object
function normalizeStyleBinding(bindingStyle) {
    if (Array.isArray(bindingStyle)) {
        return toObject(bindingStyle);
    }
    if (typeof bindingStyle === 'string') {
        return parseStyleText(bindingStyle);
    }
    return bindingStyle;
}
/**
 * parent component style should be after child's
 * so that parent component's style could override it
 */
function getStyle(vnode, checkChild) {
    var res = {};
    var styleData;
    if (checkChild) {
        var childNode = vnode;
        while (childNode.componentInstance) {
            childNode = childNode.componentInstance._vnode;
            if (childNode &&
                childNode.data &&
                (styleData = normalizeStyleData(childNode.data))) {
                extend(res, styleData);
            }
        }
    }
    if ((styleData = normalizeStyleData(vnode.data))) {
        extend(res, styleData);
    }
    var parentNode = vnode;
    // @ts-expect-error parentNode.parent not VNodeWithData
    while ((parentNode = parentNode.parent)) {
        if (parentNode.data && (styleData = normalizeStyleData(parentNode.data))) {
            extend(res, styleData);
        }
    }
    return res;
}

var cssVarRE = /^--/;
var importantRE = /\s*!important$/;
var setProp = function (el, name, val) {
    /* istanbul ignore if */
    if (cssVarRE.test(name)) {
        el.style.setProperty(name, val);
    }
    else if (importantRE.test(val)) {
        el.style.setProperty(hyphenate(name), val.replace(importantRE, ''), 'important');
    }
    else {
        var normalizedName = normalize(name);
        if (Array.isArray(val)) {
            // Support values array created by autoprefixer, e.g.
            // {display: ["-webkit-box", "-ms-flexbox", "flex"]}
            // Set them one by one, and the browser will only set those it can recognize
            for (var i = 0, len = val.length; i < len; i++) {
                el.style[normalizedName] = val[i];
            }
        }
        else {
            el.style[normalizedName] = val;
        }
    }
};
var vendorNames = ['Webkit', 'Moz', 'ms'];
var emptyStyle;
var normalize = cached(function (prop) {
    emptyStyle = emptyStyle || document.createElement('div').style;
    prop = camelize(prop);
    if (prop !== 'filter' && prop in emptyStyle) {
        return prop;
    }
    var capName = prop.charAt(0).toUpperCase() + prop.slice(1);
    for (var i = 0; i < vendorNames.length; i++) {
        var name_1 = vendorNames[i] + capName;
        if (name_1 in emptyStyle) {
            return name_1;
        }
    }
});
function updateStyle(oldVnode, vnode) {
    var data = vnode.data;
    var oldData = oldVnode.data;
    if (isUndef(data.staticStyle) &&
        isUndef(data.style) &&
        isUndef(oldData.staticStyle) &&
        isUndef(oldData.style)) {
        return;
    }
    var cur, name;
    var el = vnode.elm;
    var oldStaticStyle = oldData.staticStyle;
    var oldStyleBinding = oldData.normalizedStyle || oldData.style || {};
    // if static style exists, stylebinding already merged into it when doing normalizeStyleData
    var oldStyle = oldStaticStyle || oldStyleBinding;
    var style = normalizeStyleBinding(vnode.data.style) || {};
    // store normalized style under a different key for next diff
    // make sure to clone it if it's reactive, since the user likely wants
    // to mutate it.
    vnode.data.normalizedStyle = isDef(style.__ob__) ? extend({}, style) : style;
    var newStyle = getStyle(vnode, true);
    for (name in oldStyle) {
        if (isUndef(newStyle[name])) {
            setProp(el, name, '');
        }
    }
    for (name in newStyle) {
        cur = newStyle[name];
        if (cur !== oldStyle[name]) {
            // ie9 setting to null has no effect, must use empty string
            setProp(el, name, cur == null ? '' : cur);
        }
    }
}
var style = {
    create: updateStyle,
    update: updateStyle
};

var whitespaceRE = /\s+/;
/**
 * Add class with compatibility for SVG since classList is not supported on
 * SVG elements in IE
 */
function addClass(el, cls) {
    /* istanbul ignore if */
    if (!cls || !(cls = cls.trim())) {
        return;
    }
    /* istanbul ignore else */
    if (el.classList) {
        if (cls.indexOf(' ') > -1) {
            cls.split(whitespaceRE).forEach(function (c) { return el.classList.add(c); });
        }
        else {
            el.classList.add(cls);
        }
    }
    else {
        var cur = " ".concat(el.getAttribute('class') || '', " ");
        if (cur.indexOf(' ' + cls + ' ') < 0) {
            el.setAttribute('class', (cur + cls).trim());
        }
    }
}
/**
 * Remove class with compatibility for SVG since classList is not supported on
 * SVG elements in IE
 */
function removeClass(el, cls) {
    /* istanbul ignore if */
    if (!cls || !(cls = cls.trim())) {
        return;
    }
    /* istanbul ignore else */
    if (el.classList) {
        if (cls.indexOf(' ') > -1) {
            cls.split(whitespaceRE).forEach(function (c) { return el.classList.remove(c); });
        }
        else {
            el.classList.remove(cls);
        }
        if (!el.classList.length) {
            el.removeAttribute('class');
        }
    }
    else {
        var cur = " ".concat(el.getAttribute('class') || '', " ");
        var tar = ' ' + cls + ' ';
        while (cur.indexOf(tar) >= 0) {
            cur = cur.replace(tar, ' ');
        }
        cur = cur.trim();
        if (cur) {
            el.setAttribute('class', cur);
        }
        else {
            el.removeAttribute('class');
        }
    }
}

function resolveTransition(def) {
    if (!def) {
        return;
    }
    /* istanbul ignore else */
    if (typeof def === 'object') {
        var res = {};
        if (def.css !== false) {
            extend(res, autoCssTransition(def.name || 'v'));
        }
        extend(res, def);
        return res;
    }
    else if (typeof def === 'string') {
        return autoCssTransition(def);
    }
}
var autoCssTransition = cached(function (name) {
    return {
        enterClass: "".concat(name, "-enter"),
        enterToClass: "".concat(name, "-enter-to"),
        enterActiveClass: "".concat(name, "-enter-active"),
        leaveClass: "".concat(name, "-leave"),
        leaveToClass: "".concat(name, "-leave-to"),
        leaveActiveClass: "".concat(name, "-leave-active")
    };
});
var hasTransition = inBrowser && !isIE9;
var TRANSITION = 'transition';
var ANIMATION = 'animation';
// Transition property/event sniffing
var transitionProp = 'transition';
var transitionEndEvent = 'transitionend';
var animationProp = 'animation';
var animationEndEvent = 'animationend';
if (hasTransition) {
    /* istanbul ignore if */
    if (window.ontransitionend === undefined &&
        window.onwebkittransitionend !== undefined) {
        transitionProp = 'WebkitTransition';
        transitionEndEvent = 'webkitTransitionEnd';
    }
    if (window.onanimationend === undefined &&
        window.onwebkitanimationend !== undefined) {
        animationProp = 'WebkitAnimation';
        animationEndEvent = 'webkitAnimationEnd';
    }
}
// binding to window is necessary to make hot reload work in IE in strict mode
var raf = inBrowser
    ? window.requestAnimationFrame
        ? window.requestAnimationFrame.bind(window)
        : setTimeout
    : /* istanbul ignore next */ function (/* istanbul ignore next */ fn) { return fn(); };
function nextFrame(fn) {
    raf(function () {
        // @ts-expect-error
        raf(fn);
    });
}
function addTransitionClass(el, cls) {
    var transitionClasses = el._transitionClasses || (el._transitionClasses = []);
    if (transitionClasses.indexOf(cls) < 0) {
        transitionClasses.push(cls);
        addClass(el, cls);
    }
}
function removeTransitionClass(el, cls) {
    if (el._transitionClasses) {
        remove$2(el._transitionClasses, cls);
    }
    removeClass(el, cls);
}
function whenTransitionEnds(el, expectedType, cb) {
    var _a = getTransitionInfo(el, expectedType), type = _a.type, timeout = _a.timeout, propCount = _a.propCount;
    if (!type)
        return cb();
    var event = type === TRANSITION ? transitionEndEvent : animationEndEvent;
    var ended = 0;
    var end = function () {
        el.removeEventListener(event, onEnd);
        cb();
    };
    var onEnd = function (e) {
        if (e.target === el) {
            if (++ended >= propCount) {
                end();
            }
        }
    };
    setTimeout(function () {
        if (ended < propCount) {
            end();
        }
    }, timeout + 1);
    el.addEventListener(event, onEnd);
}
var transformRE = /\b(transform|all)(,|$)/;
function getTransitionInfo(el, expectedType) {
    var styles = window.getComputedStyle(el);
    // JSDOM may return undefined for transition properties
    var transitionDelays = (styles[transitionProp + 'Delay'] || '').split(', ');
    var transitionDurations = (styles[transitionProp + 'Duration'] || '').split(', ');
    var transitionTimeout = getTimeout(transitionDelays, transitionDurations);
    var animationDelays = (styles[animationProp + 'Delay'] || '').split(', ');
    var animationDurations = (styles[animationProp + 'Duration'] || '').split(', ');
    var animationTimeout = getTimeout(animationDelays, animationDurations);
    var type;
    var timeout = 0;
    var propCount = 0;
    /* istanbul ignore if */
    if (expectedType === TRANSITION) {
        if (transitionTimeout > 0) {
            type = TRANSITION;
            timeout = transitionTimeout;
            propCount = transitionDurations.length;
        }
    }
    else if (expectedType === ANIMATION) {
        if (animationTimeout > 0) {
            type = ANIMATION;
            timeout = animationTimeout;
            propCount = animationDurations.length;
        }
    }
    else {
        timeout = Math.max(transitionTimeout, animationTimeout);
        type =
            timeout > 0
                ? transitionTimeout > animationTimeout
                    ? TRANSITION
                    : ANIMATION
                : null;
        propCount = type
            ? type === TRANSITION
                ? transitionDurations.length
                : animationDurations.length
            : 0;
    }
    var hasTransform = type === TRANSITION && transformRE.test(styles[transitionProp + 'Property']);
    return {
        type: type,
        timeout: timeout,
        propCount: propCount,
        hasTransform: hasTransform
    };
}
function getTimeout(delays, durations) {
    /* istanbul ignore next */
    while (delays.length < durations.length) {
        delays = delays.concat(delays);
    }
    return Math.max.apply(null, durations.map(function (d, i) {
        return toMs(d) + toMs(delays[i]);
    }));
}
// Old versions of Chromium (below 61.0.3163.100) formats floating pointer numbers
// in a locale-dependent way, using a comma instead of a dot.
// If comma is not replaced with a dot, the input will be rounded down (i.e. acting
// as a floor function) causing unexpected behaviors
function toMs(s) {
    return Number(s.slice(0, -1).replace(',', '.')) * 1000;
}

function enter(vnode, toggleDisplay) {
    var el = vnode.elm;
    // call leave callback now
    if (isDef(el._leaveCb)) {
        el._leaveCb.cancelled = true;
        el._leaveCb();
    }
    var data = resolveTransition(vnode.data.transition);
    if (isUndef(data)) {
        return;
    }
    /* istanbul ignore if */
    if (isDef(el._enterCb) || el.nodeType !== 1) {
        return;
    }
    var css = data.css, type = data.type, enterClass = data.enterClass, enterToClass = data.enterToClass, enterActiveClass = data.enterActiveClass, appearClass = data.appearClass, appearToClass = data.appearToClass, appearActiveClass = data.appearActiveClass, beforeEnter = data.beforeEnter, enter = data.enter, afterEnter = data.afterEnter, enterCancelled = data.enterCancelled, beforeAppear = data.beforeAppear, appear = data.appear, afterAppear = data.afterAppear, appearCancelled = data.appearCancelled, duration = data.duration;
    // activeInstance will always be the <transition> component managing this
    // transition. One edge case to check is when the <transition> is placed
    // as the root node of a child component. In that case we need to check
    // <transition>'s parent for appear check.
    var context = activeInstance;
    var transitionNode = activeInstance.$vnode;
    while (transitionNode && transitionNode.parent) {
        context = transitionNode.context;
        transitionNode = transitionNode.parent;
    }
    var isAppear = !context._isMounted || !vnode.isRootInsert;
    if (isAppear && !appear && appear !== '') {
        return;
    }
    var startClass = isAppear && appearClass ? appearClass : enterClass;
    var activeClass = isAppear && appearActiveClass ? appearActiveClass : enterActiveClass;
    var toClass = isAppear && appearToClass ? appearToClass : enterToClass;
    var beforeEnterHook = isAppear ? beforeAppear || beforeEnter : beforeEnter;
    var enterHook = isAppear ? (isFunction(appear) ? appear : enter) : enter;
    var afterEnterHook = isAppear ? afterAppear || afterEnter : afterEnter;
    var enterCancelledHook = isAppear
        ? appearCancelled || enterCancelled
        : enterCancelled;
    var explicitEnterDuration = toNumber(isObject(duration) ? duration.enter : duration);
    var expectsCSS = css !== false && !isIE9;
    var userWantsControl = getHookArgumentsLength(enterHook);
    var cb = (el._enterCb = once(function () {
        if (expectsCSS) {
            removeTransitionClass(el, toClass);
            removeTransitionClass(el, activeClass);
        }
        // @ts-expect-error
        if (cb.cancelled) {
            if (expectsCSS) {
                removeTransitionClass(el, startClass);
            }
            enterCancelledHook && enterCancelledHook(el);
        }
        else {
            afterEnterHook && afterEnterHook(el);
        }
        el._enterCb = null;
    }));
    if (!vnode.data.show) {
        // remove pending leave element on enter by injecting an insert hook
        mergeVNodeHook(vnode, 'insert', function () {
            var parent = el.parentNode;
            var pendingNode = parent && parent._pending && parent._pending[vnode.key];
            if (pendingNode &&
                pendingNode.tag === vnode.tag &&
                pendingNode.elm._leaveCb) {
                pendingNode.elm._leaveCb();
            }
            enterHook && enterHook(el, cb);
        });
    }
    // start enter transition
    beforeEnterHook && beforeEnterHook(el);
    if (expectsCSS) {
        addTransitionClass(el, startClass);
        addTransitionClass(el, activeClass);
        nextFrame(function () {
            removeTransitionClass(el, startClass);
            // @ts-expect-error
            if (!cb.cancelled) {
                addTransitionClass(el, toClass);
                if (!userWantsControl) {
                    if (isValidDuration(explicitEnterDuration)) {
                        setTimeout(cb, explicitEnterDuration);
                    }
                    else {
                        whenTransitionEnds(el, type, cb);
                    }
                }
            }
        });
    }
    if (vnode.data.show) {
        toggleDisplay && toggleDisplay();
        enterHook && enterHook(el, cb);
    }
    if (!expectsCSS && !userWantsControl) {
        cb();
    }
}
function leave(vnode, rm) {
    var el = vnode.elm;
    // call enter callback now
    if (isDef(el._enterCb)) {
        el._enterCb.cancelled = true;
        el._enterCb();
    }
    var data = resolveTransition(vnode.data.transition);
    if (isUndef(data) || el.nodeType !== 1) {
        return rm();
    }
    /* istanbul ignore if */
    if (isDef(el._leaveCb)) {
        return;
    }
    var css = data.css, type = data.type, leaveClass = data.leaveClass, leaveToClass = data.leaveToClass, leaveActiveClass = data.leaveActiveClass, beforeLeave = data.beforeLeave, leave = data.leave, afterLeave = data.afterLeave, leaveCancelled = data.leaveCancelled, delayLeave = data.delayLeave, duration = data.duration;
    var expectsCSS = css !== false && !isIE9;
    var userWantsControl = getHookArgumentsLength(leave);
    var explicitLeaveDuration = toNumber(isObject(duration) ? duration.leave : duration);
    var cb = (el._leaveCb = once(function () {
        if (el.parentNode && el.parentNode._pending) {
            el.parentNode._pending[vnode.key] = null;
        }
        if (expectsCSS) {
            removeTransitionClass(el, leaveToClass);
            removeTransitionClass(el, leaveActiveClass);
        }
        // @ts-expect-error
        if (cb.cancelled) {
            if (expectsCSS) {
                removeTransitionClass(el, leaveClass);
            }
            leaveCancelled && leaveCancelled(el);
        }
        else {
            rm();
            afterLeave && afterLeave(el);
        }
        el._leaveCb = null;
    }));
    if (delayLeave) {
        delayLeave(performLeave);
    }
    else {
        performLeave();
    }
    function performLeave() {
        // the delayed leave may have already been cancelled
        // @ts-expect-error
        if (cb.cancelled) {
            return;
        }
        // record leaving element
        if (!vnode.data.show && el.parentNode) {
            (el.parentNode._pending || (el.parentNode._pending = {}))[vnode.key] =
                vnode;
        }
        beforeLeave && beforeLeave(el);
        if (expectsCSS) {
            addTransitionClass(el, leaveClass);
            addTransitionClass(el, leaveActiveClass);
            nextFrame(function () {
                removeTransitionClass(el, leaveClass);
                // @ts-expect-error
                if (!cb.cancelled) {
                    addTransitionClass(el, leaveToClass);
                    if (!userWantsControl) {
                        if (isValidDuration(explicitLeaveDuration)) {
                            setTimeout(cb, explicitLeaveDuration);
                        }
                        else {
                            whenTransitionEnds(el, type, cb);
                        }
                    }
                }
            });
        }
        leave && leave(el, cb);
        if (!expectsCSS && !userWantsControl) {
            cb();
        }
    }
}
function isValidDuration(val) {
    return typeof val === 'number' && !isNaN(val);
}
/**
 * Normalize a transition hook's argument length. The hook may be:
 * - a merged hook (invoker) with the original in .fns
 * - a wrapped component method (check ._length)
 * - a plain function (.length)
 */
function getHookArgumentsLength(fn) {
    if (isUndef(fn)) {
        return false;
    }
    // @ts-expect-error
    var invokerFns = fn.fns;
    if (isDef(invokerFns)) {
        // invoker
        return getHookArgumentsLength(Array.isArray(invokerFns) ? invokerFns[0] : invokerFns);
    }
    else {
        // @ts-expect-error
        return (fn._length || fn.length) > 1;
    }
}
function _enter(_, vnode) {
    if (vnode.data.show !== true) {
        enter(vnode);
    }
}
var transition = inBrowser
    ? {
        create: _enter,
        activate: _enter,
        remove: function (vnode, rm) {
            /* istanbul ignore else */
            if (vnode.data.show !== true) {
                // @ts-expect-error
                leave(vnode, rm);
            }
            else {
                rm();
            }
        }
    }
    : {};

var platformModules = [attrs, klass, events, domProps, style, transition];

// the directive module should be applied last, after all
// built-in modules have been applied.
var modules = platformModules.concat(baseModules);
var patch = createPatchFunction({ nodeOps: nodeOps, modules: modules });

/**
 * Not type checking this file because flow doesn't like attaching
 * properties to Elements.
 */
/* istanbul ignore if */
if (isIE9) {
    // http://www.matts411.com/post/internet-explorer-9-oninput/
    document.addEventListener('selectionchange', function () {
        var el = document.activeElement;
        // @ts-expect-error
        if (el && el.vmodel) {
            trigger(el, 'input');
        }
    });
}
var directive = {
    inserted: function (el, binding, vnode, oldVnode) {
        if (vnode.tag === 'select') {
            // #6903
            if (oldVnode.elm && !oldVnode.elm._vOptions) {
                mergeVNodeHook(vnode, 'postpatch', function () {
                    directive.componentUpdated(el, binding, vnode);
                });
            }
            else {
                setSelected(el, binding, vnode.context);
            }
            el._vOptions = [].map.call(el.options, getValue);
        }
        else if (vnode.tag === 'textarea' || isTextInputType(el.type)) {
            el._vModifiers = binding.modifiers;
            if (!binding.modifiers.lazy) {
                el.addEventListener('compositionstart', onCompositionStart);
                el.addEventListener('compositionend', onCompositionEnd);
                // Safari < 10.2 & UIWebView doesn't fire compositionend when
                // switching focus before confirming composition choice
                // this also fixes the issue where some browsers e.g. iOS Chrome
                // fires "change" instead of "input" on autocomplete.
                el.addEventListener('change', onCompositionEnd);
                /* istanbul ignore if */
                if (isIE9) {
                    el.vmodel = true;
                }
            }
        }
    },
    componentUpdated: function (el, binding, vnode) {
        if (vnode.tag === 'select') {
            setSelected(el, binding, vnode.context);
            // in case the options rendered by v-for have changed,
            // it's possible that the value is out-of-sync with the rendered options.
            // detect such cases and filter out values that no longer has a matching
            // option in the DOM.
            var prevOptions_1 = el._vOptions;
            var curOptions_1 = (el._vOptions = [].map.call(el.options, getValue));
            if (curOptions_1.some(function (o, i) { return !looseEqual(o, prevOptions_1[i]); })) {
                // trigger change event if
                // no matching option found for at least one value
                var needReset = el.multiple
                    ? binding.value.some(function (v) { return hasNoMatchingOption(v, curOptions_1); })
                    : binding.value !== binding.oldValue &&
                        hasNoMatchingOption(binding.value, curOptions_1);
                if (needReset) {
                    trigger(el, 'change');
                }
            }
        }
    }
};
function setSelected(el, binding, vm) {
    actuallySetSelected(el, binding);
    /* istanbul ignore if */
    if (isIE || isEdge) {
        setTimeout(function () {
            actuallySetSelected(el, binding);
        }, 0);
    }
}
function actuallySetSelected(el, binding, vm) {
    var value = binding.value;
    var isMultiple = el.multiple;
    if (isMultiple && !Array.isArray(value)) {
        return;
    }
    var selected, option;
    for (var i = 0, l = el.options.length; i < l; i++) {
        option = el.options[i];
        if (isMultiple) {
            selected = looseIndexOf(value, getValue(option)) > -1;
            if (option.selected !== selected) {
                option.selected = selected;
            }
        }
        else {
            if (looseEqual(getValue(option), value)) {
                if (el.selectedIndex !== i) {
                    el.selectedIndex = i;
                }
                return;
            }
        }
    }
    if (!isMultiple) {
        el.selectedIndex = -1;
    }
}
function hasNoMatchingOption(value, options) {
    return options.every(function (o) { return !looseEqual(o, value); });
}
function getValue(option) {
    return '_value' in option ? option._value : option.value;
}
function onCompositionStart(e) {
    e.target.composing = true;
}
function onCompositionEnd(e) {
    // prevent triggering an input event for no reason
    if (!e.target.composing)
        return;
    e.target.composing = false;
    trigger(e.target, 'input');
}
function trigger(el, type) {
    var e = document.createEvent('HTMLEvents');
    e.initEvent(type, true, true);
    el.dispatchEvent(e);
}

// recursively search for possible transition defined inside the component root
function locateNode(vnode) {
    // @ts-expect-error
    return vnode.componentInstance && (!vnode.data || !vnode.data.transition)
        ? locateNode(vnode.componentInstance._vnode)
        : vnode;
}
var show = {
    bind: function (el, _a, vnode) {
        var value = _a.value;
        vnode = locateNode(vnode);
        var transition = vnode.data && vnode.data.transition;
        var originalDisplay = (el.__vOriginalDisplay =
            el.style.display === 'none' ? '' : el.style.display);
        if (value && transition) {
            vnode.data.show = true;
            enter(vnode, function () {
                el.style.display = originalDisplay;
            });
        }
        else {
            el.style.display = value ? originalDisplay : 'none';
        }
    },
    update: function (el, _a, vnode) {
        var value = _a.value, oldValue = _a.oldValue;
        /* istanbul ignore if */
        if (!value === !oldValue)
            return;
        vnode = locateNode(vnode);
        var transition = vnode.data && vnode.data.transition;
        if (transition) {
            vnode.data.show = true;
            if (value) {
                enter(vnode, function () {
                    el.style.display = el.__vOriginalDisplay;
                });
            }
            else {
                leave(vnode, function () {
                    el.style.display = 'none';
                });
            }
        }
        else {
            el.style.display = value ? el.__vOriginalDisplay : 'none';
        }
    },
    unbind: function (el, binding, vnode, oldVnode, isDestroy) {
        if (!isDestroy) {
            el.style.display = el.__vOriginalDisplay;
        }
    }
};

var platformDirectives = {
    model: directive,
    show: show
};

// Provides transition support for a single element/component.
var transitionProps = {
    name: String,
    appear: Boolean,
    css: Boolean,
    mode: String,
    type: String,
    enterClass: String,
    leaveClass: String,
    enterToClass: String,
    leaveToClass: String,
    enterActiveClass: String,
    leaveActiveClass: String,
    appearClass: String,
    appearActiveClass: String,
    appearToClass: String,
    duration: [Number, String, Object]
};
// in case the child is also an abstract component, e.g. <keep-alive>
// we want to recursively retrieve the real component to be rendered
function getRealChild(vnode) {
    var compOptions = vnode && vnode.componentOptions;
    if (compOptions && compOptions.Ctor.options.abstract) {
        return getRealChild(getFirstComponentChild(compOptions.children));
    }
    else {
        return vnode;
    }
}
function extractTransitionData(comp) {
    var data = {};
    var options = comp.$options;
    // props
    for (var key in options.propsData) {
        data[key] = comp[key];
    }
    // events.
    // extract listeners and pass them directly to the transition methods
    var listeners = options._parentListeners;
    for (var key in listeners) {
        data[camelize(key)] = listeners[key];
    }
    return data;
}
function placeholder(h, rawChild) {
    // @ts-expect-error
    if (/\d-keep-alive$/.test(rawChild.tag)) {
        return h('keep-alive', {
            props: rawChild.componentOptions.propsData
        });
    }
}
function hasParentTransition(vnode) {
    while ((vnode = vnode.parent)) {
        if (vnode.data.transition) {
            return true;
        }
    }
}
function isSameChild(child, oldChild) {
    return oldChild.key === child.key && oldChild.tag === child.tag;
}
var isNotTextNode = function (c) { return c.tag || isAsyncPlaceholder(c); };
var isVShowDirective = function (d) { return d.name === 'show'; };
var Transition = {
    name: 'transition',
    props: transitionProps,
    abstract: true,
    render: function (h) {
        var _this = this;
        var children = this.$slots.default;
        if (!children) {
            return;
        }
        // filter out text nodes (possible whitespaces)
        children = children.filter(isNotTextNode);
        /* istanbul ignore if */
        if (!children.length) {
            return;
        }
        var mode = this.mode;
        var rawChild = children[0];
        // if this is a component root node and the component's
        // parent container node also has transition, skip.
        if (hasParentTransition(this.$vnode)) {
            return rawChild;
        }
        // apply transition data to child
        // use getRealChild() to ignore abstract components e.g. keep-alive
        var child = getRealChild(rawChild);
        /* istanbul ignore if */
        if (!child) {
            return rawChild;
        }
        if (this._leaving) {
            return placeholder(h, rawChild);
        }
        // ensure a key that is unique to the vnode type and to this transition
        // component instance. This key will be used to remove pending leaving nodes
        // during entering.
        var id = "__transition-".concat(this._uid, "-");
        child.key =
            child.key == null
                ? child.isComment
                    ? id + 'comment'
                    : id + child.tag
                : isPrimitive$1(child.key)
                    ? String(child.key).indexOf(id) === 0
                        ? child.key
                        : id + child.key
                    : child.key;
        var data = ((child.data || (child.data = {})).transition =
            extractTransitionData(this));
        var oldRawChild = this._vnode;
        var oldChild = getRealChild(oldRawChild);
        // mark v-show
        // so that the transition module can hand over the control to the directive
        if (child.data.directives && child.data.directives.some(isVShowDirective)) {
            child.data.show = true;
        }
        if (oldChild &&
            oldChild.data &&
            !isSameChild(child, oldChild) &&
            !isAsyncPlaceholder(oldChild) &&
            // #6687 component root is a comment node
            !(oldChild.componentInstance &&
                oldChild.componentInstance._vnode.isComment)) {
            // replace old child transition data with fresh one
            // important for dynamic transitions!
            var oldData = (oldChild.data.transition = extend({}, data));
            // handle transition mode
            if (mode === 'out-in') {
                // return placeholder node and queue update when leave finishes
                this._leaving = true;
                mergeVNodeHook(oldData, 'afterLeave', function () {
                    _this._leaving = false;
                    _this.$forceUpdate();
                });
                return placeholder(h, rawChild);
            }
            else if (mode === 'in-out') {
                if (isAsyncPlaceholder(child)) {
                    return oldRawChild;
                }
                var delayedLeave_1;
                var performLeave = function () {
                    delayedLeave_1();
                };
                mergeVNodeHook(data, 'afterEnter', performLeave);
                mergeVNodeHook(data, 'enterCancelled', performLeave);
                mergeVNodeHook(oldData, 'delayLeave', function (leave) {
                    delayedLeave_1 = leave;
                });
            }
        }
        return rawChild;
    }
};

// Provides transition support for list items.
var props = extend({
    tag: String,
    moveClass: String
}, transitionProps);
delete props.mode;
var TransitionGroup = {
    props: props,
    beforeMount: function () {
        var _this = this;
        var update = this._update;
        this._update = function (vnode, hydrating) {
            var restoreActiveInstance = setActiveInstance(_this);
            // force removing pass
            _this.__patch__(_this._vnode, _this.kept, false, // hydrating
            true // removeOnly (!important, avoids unnecessary moves)
            );
            _this._vnode = _this.kept;
            restoreActiveInstance();
            update.call(_this, vnode, hydrating);
        };
    },
    render: function (h) {
        var tag = this.tag || this.$vnode.data.tag || 'span';
        var map = Object.create(null);
        var prevChildren = (this.prevChildren = this.children);
        var rawChildren = this.$slots.default || [];
        var children = (this.children = []);
        var transitionData = extractTransitionData(this);
        for (var i = 0; i < rawChildren.length; i++) {
            var c = rawChildren[i];
            if (c.tag) {
                if (c.key != null && String(c.key).indexOf('__vlist') !== 0) {
                    children.push(c);
                    map[c.key] = c;
                    (c.data || (c.data = {})).transition = transitionData;
                }
            }
        }
        if (prevChildren) {
            var kept = [];
            var removed = [];
            for (var i = 0; i < prevChildren.length; i++) {
                var c = prevChildren[i];
                c.data.transition = transitionData;
                // @ts-expect-error .getBoundingClientRect is not typed in Node
                c.data.pos = c.elm.getBoundingClientRect();
                if (map[c.key]) {
                    kept.push(c);
                }
                else {
                    removed.push(c);
                }
            }
            this.kept = h(tag, null, kept);
            this.removed = removed;
        }
        return h(tag, null, children);
    },
    updated: function () {
        var children = this.prevChildren;
        var moveClass = this.moveClass || (this.name || 'v') + '-move';
        if (!children.length || !this.hasMove(children[0].elm, moveClass)) {
            return;
        }
        // we divide the work into three loops to avoid mixing DOM reads and writes
        // in each iteration - which helps prevent layout thrashing.
        children.forEach(callPendingCbs);
        children.forEach(recordPosition);
        children.forEach(applyTranslation);
        // force reflow to put everything in position
        // assign to this to avoid being removed in tree-shaking
        // $flow-disable-line
        this._reflow = document.body.offsetHeight;
        children.forEach(function (c) {
            if (c.data.moved) {
                var el_1 = c.elm;
                var s = el_1.style;
                addTransitionClass(el_1, moveClass);
                s.transform = s.WebkitTransform = s.transitionDuration = '';
                el_1.addEventListener(transitionEndEvent, (el_1._moveCb = function cb(e) {
                    if (e && e.target !== el_1) {
                        return;
                    }
                    if (!e || /transform$/.test(e.propertyName)) {
                        el_1.removeEventListener(transitionEndEvent, cb);
                        el_1._moveCb = null;
                        removeTransitionClass(el_1, moveClass);
                    }
                }));
            }
        });
    },
    methods: {
        hasMove: function (el, moveClass) {
            /* istanbul ignore if */
            if (!hasTransition) {
                return false;
            }
            /* istanbul ignore if */
            if (this._hasMove) {
                return this._hasMove;
            }
            // Detect whether an element with the move class applied has
            // CSS transitions. Since the element may be inside an entering
            // transition at this very moment, we make a clone of it and remove
            // all other transition classes applied to ensure only the move class
            // is applied.
            var clone = el.cloneNode();
            if (el._transitionClasses) {
                el._transitionClasses.forEach(function (cls) {
                    removeClass(clone, cls);
                });
            }
            addClass(clone, moveClass);
            clone.style.display = 'none';
            this.$el.appendChild(clone);
            var info = getTransitionInfo(clone);
            this.$el.removeChild(clone);
            return (this._hasMove = info.hasTransform);
        }
    }
};
function callPendingCbs(c) {
    /* istanbul ignore if */
    if (c.elm._moveCb) {
        c.elm._moveCb();
    }
    /* istanbul ignore if */
    if (c.elm._enterCb) {
        c.elm._enterCb();
    }
}
function recordPosition(c) {
    c.data.newPos = c.elm.getBoundingClientRect();
}
function applyTranslation(c) {
    var oldPos = c.data.pos;
    var newPos = c.data.newPos;
    var dx = oldPos.left - newPos.left;
    var dy = oldPos.top - newPos.top;
    if (dx || dy) {
        c.data.moved = true;
        var s = c.elm.style;
        s.transform = s.WebkitTransform = "translate(".concat(dx, "px,").concat(dy, "px)");
        s.transitionDuration = '0s';
    }
}

var platformComponents = {
    Transition: Transition,
    TransitionGroup: TransitionGroup
};

// install platform specific utils
Vue.config.mustUseProp = mustUseProp;
Vue.config.isReservedTag = isReservedTag;
Vue.config.isReservedAttr = isReservedAttr;
Vue.config.getTagNamespace = getTagNamespace;
Vue.config.isUnknownElement = isUnknownElement;
// install platform runtime directives & components
extend(Vue.options.directives, platformDirectives);
extend(Vue.options.components, platformComponents);
// install platform patch function
Vue.prototype.__patch__ = inBrowser ? patch : noop;
// public mount method
Vue.prototype.$mount = function (el, hydrating) {
    el = el && inBrowser ? query(el) : undefined;
    return mountComponent(this, el, hydrating);
};
// devtools global hook
/* istanbul ignore next */
if (inBrowser) {
    setTimeout(function () {
        if (config.devtools) {
            if (devtools) {
                devtools.emit('init', Vue);
            }
        }
    }, 0);
}

/**
  * vue-class-component v7.2.6
  * (c) 2015-present Evan You
  * @license MIT
  */

function _typeof(obj) {
  if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
    _typeof = function (obj) {
      return typeof obj;
    };
  } else {
    _typeof = function (obj) {
      return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
    };
  }

  return _typeof(obj);
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _nonIterableSpread();
}

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) {
    for (var i = 0, arr2 = new Array(arr.length); i < arr.length; i++) arr2[i] = arr[i];

    return arr2;
  }
}

function _iterableToArray(iter) {
  if (Symbol.iterator in Object(iter) || Object.prototype.toString.call(iter) === "[object Arguments]") return Array.from(iter);
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance");
}

// The rational behind the verbose Reflect-feature check below is the fact that there are polyfills
// which add an implementation for Reflect.defineMetadata but not for Reflect.getOwnMetadataKeys.
// Without this check consumers will encounter hard to track down runtime errors.
function reflectionIsSupported() {
  return typeof Reflect !== 'undefined' && Reflect.defineMetadata && Reflect.getOwnMetadataKeys;
}
function copyReflectionMetadata(to, from) {
  forwardMetadata(to, from);
  Object.getOwnPropertyNames(from.prototype).forEach(function (key) {
    forwardMetadata(to.prototype, from.prototype, key);
  });
  Object.getOwnPropertyNames(from).forEach(function (key) {
    forwardMetadata(to, from, key);
  });
}

function forwardMetadata(to, from, propertyKey) {
  var metaKeys = propertyKey ? Reflect.getOwnMetadataKeys(from, propertyKey) : Reflect.getOwnMetadataKeys(from);
  metaKeys.forEach(function (metaKey) {
    var metadata = propertyKey ? Reflect.getOwnMetadata(metaKey, from, propertyKey) : Reflect.getOwnMetadata(metaKey, from);

    if (propertyKey) {
      Reflect.defineMetadata(metaKey, metadata, to, propertyKey);
    } else {
      Reflect.defineMetadata(metaKey, metadata, to);
    }
  });
}

var fakeArray = {
  __proto__: []
};
var hasProto = fakeArray instanceof Array;
function createDecorator(factory) {
  return function (target, key, index) {
    var Ctor = typeof target === 'function' ? target : target.constructor;

    if (!Ctor.__decorators__) {
      Ctor.__decorators__ = [];
    }

    if (typeof index !== 'number') {
      index = undefined;
    }

    Ctor.__decorators__.push(function (options) {
      return factory(options, key, index);
    });
  };
}
function isPrimitive(value) {
  var type = _typeof(value);

  return value == null || type !== 'object' && type !== 'function';
}

function collectDataFromConstructor(vm, Component) {
  // override _init to prevent to init as Vue instance
  var originalInit = Component.prototype._init;

  Component.prototype._init = function () {
    var _this = this;

    // proxy to actual vm
    var keys = Object.getOwnPropertyNames(vm); // 2.2.0 compat (props are no longer exposed as self properties)

    if (vm.$options.props) {
      for (var key in vm.$options.props) {
        if (!vm.hasOwnProperty(key)) {
          keys.push(key);
        }
      }
    }

    keys.forEach(function (key) {
      Object.defineProperty(_this, key, {
        get: function get() {
          return vm[key];
        },
        set: function set(value) {
          vm[key] = value;
        },
        configurable: true
      });
    });
  }; // should be acquired class property values


  var data = new Component(); // restore original _init to avoid memory leak (#209)

  Component.prototype._init = originalInit; // create plain data object

  var plainData = {};
  Object.keys(data).forEach(function (key) {
    if (data[key] !== undefined) {
      plainData[key] = data[key];
    }
  });

  return plainData;
}

var $internalHooks = ['data', 'beforeCreate', 'created', 'beforeMount', 'mounted', 'beforeDestroy', 'destroyed', 'beforeUpdate', 'updated', 'activated', 'deactivated', 'render', 'errorCaptured', 'serverPrefetch' // 2.6
];
function componentFactory(Component) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  options.name = options.name || Component._componentTag || Component.name; // prototype props.

  var proto = Component.prototype;
  Object.getOwnPropertyNames(proto).forEach(function (key) {
    if (key === 'constructor') {
      return;
    } // hooks


    if ($internalHooks.indexOf(key) > -1) {
      options[key] = proto[key];
      return;
    }

    var descriptor = Object.getOwnPropertyDescriptor(proto, key);

    if (descriptor.value !== void 0) {
      // methods
      if (typeof descriptor.value === 'function') {
        (options.methods || (options.methods = {}))[key] = descriptor.value;
      } else {
        // typescript decorated data
        (options.mixins || (options.mixins = [])).push({
          data: function data() {
            return _defineProperty({}, key, descriptor.value);
          }
        });
      }
    } else if (descriptor.get || descriptor.set) {
      // computed properties
      (options.computed || (options.computed = {}))[key] = {
        get: descriptor.get,
        set: descriptor.set
      };
    }
  });
  (options.mixins || (options.mixins = [])).push({
    data: function data() {
      return collectDataFromConstructor(this, Component);
    }
  }); // decorate options

  var decorators = Component.__decorators__;

  if (decorators) {
    decorators.forEach(function (fn) {
      return fn(options);
    });
    delete Component.__decorators__;
  } // find super


  var superProto = Object.getPrototypeOf(Component.prototype);
  var Super = superProto instanceof Vue ? superProto.constructor : Vue;
  var Extended = Super.extend(options);
  forwardStaticMembers(Extended, Component, Super);

  if (reflectionIsSupported()) {
    copyReflectionMetadata(Extended, Component);
  }

  return Extended;
}
var shouldIgnore = {
  prototype: true,
  arguments: true,
  callee: true,
  caller: true
};

function forwardStaticMembers(Extended, Original, Super) {
  // We have to use getOwnPropertyNames since Babel registers methods as non-enumerable
  Object.getOwnPropertyNames(Original).forEach(function (key) {
    // Skip the properties that should not be overwritten
    if (shouldIgnore[key]) {
      return;
    } // Some browsers does not allow reconfigure built-in properties


    var extendedDescriptor = Object.getOwnPropertyDescriptor(Extended, key);

    if (extendedDescriptor && !extendedDescriptor.configurable) {
      return;
    }

    var descriptor = Object.getOwnPropertyDescriptor(Original, key); // If the user agent does not support `__proto__` or its family (IE <= 10),
    // the sub class properties may be inherited properties from the super class in TypeScript.
    // We need to exclude such properties to prevent to overwrite
    // the component options object which stored on the extended constructor (See #192).
    // If the value is a referenced value (object or function),
    // we can check equality of them and exclude it if they have the same reference.
    // If it is a primitive value, it will be forwarded for safety.

    if (!hasProto) {
      // Only `cid` is explicitly exluded from property forwarding
      // because we cannot detect whether it is a inherited property or not
      // on the no `__proto__` environment even though the property is reserved.
      if (key === 'cid') {
        return;
      }

      var superDescriptor = Object.getOwnPropertyDescriptor(Super, key);

      if (!isPrimitive(descriptor.value) && superDescriptor && superDescriptor.value === descriptor.value) {
        return;
      }
    } // Warn if the users manually declare reserved properties

    Object.defineProperty(Extended, key, descriptor);
  });
}

function Component(options) {
  if (typeof options === 'function') {
    return componentFactory(options);
  }

  return function (Component) {
    return componentFactory(Component, options);
  };
}

Component.registerHooks = function registerHooks(keys) {
  $internalHooks.push.apply($internalHooks, _toConsumableArray(keys));
};

(undefined && undefined.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};

/** @see {@link https://github.com/vuejs/vue-class-component/blob/master/src/reflect.ts} */
var reflectMetadataIsSupported = typeof Reflect !== 'undefined' && typeof Reflect.getMetadata !== 'undefined';
function applyMetadata(options, target, key) {
    if (reflectMetadataIsSupported) {
        if (!Array.isArray(options) &&
            typeof options !== 'function' &&
            !options.hasOwnProperty('type') &&
            typeof options.type === 'undefined') {
            var type = Reflect.getMetadata('design:type', target, key);
            if (type !== Object) {
                options.type = type;
            }
        }
    }
}

/**
 * decorator of a prop
 * @param  options the options for the prop
 * @return PropertyDecorator | void
 */
function Prop(options) {
    if (options === void 0) { options = {}; }
    return function (target, key) {
        applyMetadata(options, target, key);
        createDecorator(function (componentOptions, k) {
            (componentOptions.props || (componentOptions.props = {}))[k] = options;
        })(target, key);
    };
}

/**
 * decorator of a watch function
 * @param  path the path or the expression to observe
 * @param  WatchOption
 * @return MethodDecorator
 */
function Watch(path, options) {
    if (options === void 0) { options = {}; }
    var _a = options.deep, deep = _a === void 0 ? false : _a, _b = options.immediate, immediate = _b === void 0 ? false : _b;
    return createDecorator(function (componentOptions, handler) {
        if (typeof componentOptions.watch !== 'object') {
            componentOptions.watch = Object.create(null);
        }
        var watch = componentOptions.watch;
        if (typeof watch[path] === 'object' && !Array.isArray(watch[path])) {
            watch[path] = [watch[path]];
        }
        else if (typeof watch[path] === 'undefined') {
            watch[path] = [];
        }
        watch[path].push({ handler: handler, deep: deep, immediate: immediate });
    });
}

let PFormInput = class PFormInput extends Vue {
  constructor() {
    super(...arguments);
    this.localValue = "";
  }
  onChangeValue() {
    this.localValue = this.value;
  }
  input() {
    this.$emit("input", this.localValue);
  }
};
__decorate([Prop({
  required: true
})], PFormInput.prototype, "value", void 0);
__decorate([Prop({
  required: false,
  default: ""
})], PFormInput.prototype, "label", void 0);
__decorate([Prop({
  required: false,
  default: false
})], PFormInput.prototype, "readonly", void 0);
__decorate([Watch("value", {
  immediate: true
})], PFormInput.prototype, "onChangeValue", null);
PFormInput = __decorate([Component], PFormInput);
var script$13 = PFormInput;

function normalizeComponent(template, style, script, scopeId, isFunctionalTemplate, moduleIdentifier /* server only */, shadowMode, createInjector, createInjectorSSR, createInjectorShadow) {
    if (typeof shadowMode !== 'boolean') {
        createInjectorSSR = createInjector;
        createInjector = shadowMode;
        shadowMode = false;
    }
    // Vue.extend constructor export interop.
    const options = typeof script === 'function' ? script.options : script;
    // render functions
    if (template && template.render) {
        options.render = template.render;
        options.staticRenderFns = template.staticRenderFns;
        options._compiled = true;
        // functional template
        if (isFunctionalTemplate) {
            options.functional = true;
        }
    }
    // scopedId
    if (scopeId) {
        options._scopeId = scopeId;
    }
    let hook;
    if (moduleIdentifier) {
        // server build
        hook = function (context) {
            // 2.3 injection
            context =
                context || // cached call
                    (this.$vnode && this.$vnode.ssrContext) || // stateful
                    (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext); // functional
            // 2.2 with runInNewContext: true
            if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
                context = __VUE_SSR_CONTEXT__;
            }
            // inject component styles
            if (style) {
                style.call(this, createInjectorSSR(context));
            }
            // register component module identifier for async chunk inference
            if (context && context._registeredComponents) {
                context._registeredComponents.add(moduleIdentifier);
            }
        };
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        options._ssrRegister = hook;
    }
    else if (style) {
        hook = shadowMode
            ? function (context) {
                style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot));
            }
            : function (context) {
                style.call(this, createInjector(context));
            };
    }
    if (hook) {
        if (options.functional) {
            // register for functional component in vue file
            const originalRender = options.render;
            options.render = function renderWithStyleInjection(h, context) {
                hook.call(context);
                return originalRender(h, context);
            };
        }
        else {
            // inject component registration as beforeCreate hook
            const existing = options.beforeCreate;
            options.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
    }
    return script;
}

const isOldIE = typeof navigator !== 'undefined' &&
    /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase());
function createInjector(context) {
    return (id, style) => addStyle(id, style);
}
let HEAD;
const styles = {};
function addStyle(id, css) {
    const group = isOldIE ? css.media || 'default' : id;
    const style = styles[group] || (styles[group] = { ids: new Set(), styles: [] });
    if (!style.ids.has(id)) {
        style.ids.add(id);
        let code = css.source;
        if (css.map) {
            // https://developer.chrome.com/devtools/docs/javascript-debugging
            // this makes source maps inside style tags work properly in Chrome
            code += '\n/*# sourceURL=' + css.map.sources[0] + ' */';
            // http://stackoverflow.com/a/26603875
            code +=
                '\n/*# sourceMappingURL=data:application/json;base64,' +
                    btoa(unescape(encodeURIComponent(JSON.stringify(css.map)))) +
                    ' */';
        }
        if (!style.element) {
            style.element = document.createElement('style');
            style.element.type = 'text/css';
            if (css.media)
                style.element.setAttribute('media', css.media);
            if (HEAD === undefined) {
                HEAD = document.head || document.getElementsByTagName('head')[0];
            }
            HEAD.appendChild(style.element);
        }
        if ('styleSheet' in style.element) {
            style.styles.push(code);
            style.element.styleSheet.cssText = style.styles
                .filter(Boolean)
                .join('\n');
        }
        else {
            const index = style.ids.size - 1;
            const textNode = document.createTextNode(code);
            const nodes = style.element.childNodes;
            if (nodes[index])
                style.element.removeChild(nodes[index]);
            if (nodes.length)
                style.element.insertBefore(textNode, nodes[index]);
            else
                style.element.appendChild(textNode);
        }
    }
}

/* script */
const __vue_script__$13 = script$13;

/* template */
var __vue_render__$13 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', [_vm.label != '' ? _c('div', {
    staticClass: "label-p-form-input"
  }, [_vm._v("\n    " + _vm._s(_vm.label) + "\n  ")]) : _vm._e(), _vm._v(" "), _c('div', [_c('input', {
    directives: [{
      name: "model",
      rawName: "v-model",
      value: _vm.localValue,
      expression: "localValue"
    }],
    staticClass: "input-p-form-input",
    attrs: {
      "type": "text",
      "readonly": _vm.readonly
    },
    domProps: {
      "value": _vm.localValue
    },
    on: {
      "input": [function ($event) {
        if ($event.target.composing) {
          return;
        }
        _vm.localValue = $event.target.value;
      }, _vm.input]
    }
  })])]);
};
var __vue_staticRenderFns__$13 = [];

/* style */
const __vue_inject_styles__$13 = function (inject) {
  if (!inject) return;
  inject("data-v-499716f0_0", {
    source: ".label-p-form-input[data-v-499716f0]{line-height:1.23;color:#6f6f6f}.input-p-form-input[data-v-499716f0]{border-radius:4px;border:solid 1px #e4e4e4;background-color:#fafafa;padding:10px 10px 12px 10px;color:#807f7f;width:100%}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$13 = "data-v-499716f0";
/* module identifier */
const __vue_module_identifier__$13 = undefined;
/* functional template */
const __vue_is_functional_template__$13 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$26 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$13,
  staticRenderFns: __vue_staticRenderFns__$13
}, __vue_inject_styles__$13, __vue_script__$13, __vue_scope_id__$13, __vue_is_functional_template__$13, __vue_module_identifier__$13, false, createInjector, undefined, undefined);
var __vue_component__$27 = __vue_component__$26;

let IconAdd = class IconAdd extends Vue {};
__decorate([Prop({
  default: 26
})], IconAdd.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconAdd.prototype, "color", void 0);
IconAdd = __decorate([Component], IconAdd);
var script$12 = IconAdd;

/* script */
const __vue_script__$12 = script$12;

/* template */
var __vue_render__$12 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    staticClass: "svg-icon",
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 20 20"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero"
    }
  }, [_c('path', {
    attrs: {
      "d": "M10 .128c5.452 0 9.872 4.42 9.872 9.872s-4.42 9.872-9.872 9.872S.128 15.452.128 10 4.548.128 10 .128zm0 1.795C5.54 1.923 1.923 5.54 1.923 10c0 4.46 3.616 8.077 8.077 8.077 4.46 0 8.077-3.616 8.077-8.077 0-4.46-3.616-8.077-8.077-8.077zm0 3.59c.458 0 .835.342.89.785l.007.112v2.692h2.693c.457 0 .835.343.89.785l.007.113c0 .496-.402.897-.897.897h-2.693v2.693c0 .495-.401.897-.897.897-.458 0-.835-.342-.89-.785l-.007-.112v-2.693H6.41c-.457 0-.835-.342-.89-.784L5.513 10c0-.496.402-.897.897-.897h2.692V6.41c0-.495.402-.897.898-.897z"
    }
  })])]);
};
var __vue_staticRenderFns__$12 = [];

/* style */
const __vue_inject_styles__$12 = undefined;
/* scoped */
const __vue_scope_id__$12 = undefined;
/* module identifier */
const __vue_module_identifier__$12 = undefined;
/* functional template */
const __vue_is_functional_template__$12 = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$24 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$12,
  staticRenderFns: __vue_staticRenderFns__$12
}, __vue_inject_styles__$12, __vue_script__$12, __vue_scope_id__$12, __vue_is_functional_template__$12, __vue_module_identifier__$12, false, undefined, undefined, undefined);
var __vue_component__$25 = __vue_component__$24;

let IconAsc = class IconAsc extends Vue {
  hasDefaultSlot() {
    return !!this.$slots.default;
  }
};
__decorate([Prop({
  default: 26
})], IconAsc.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconAsc.prototype, "color", void 0);
IconAsc = __decorate([Component], IconAsc);
var script$11 = IconAsc;

/* script */
const __vue_script__$11 = script$11;

/* template */
var __vue_render__$11 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 -50 600 600"
    }
  }, [_vm.hasDefaultSlot() ? _c('g', {
    attrs: {
      "fill": _vm.color
    }
  }, [_c('path', {
    attrs: {
      "d": "M 230.4 76.8 H 353 C 350 96 352 114 356 128 V 128 H 230.4 V 76.8 z M 230.4 179.2 h 154.6 C 404 199 430 212 457 215 v 15 H 231 V 179.2 z M 230.4 281.6 h 179.2 v 51.2 H 230.4 V 281.6 z M 230.4 384 h 128 v 51.2 h -128 V 384 z M 76.8 460.8 H 128 V 153.6 h 76.8 L 102.4 51.2 L 0 153.6 h 76.8 V 460.8 z M 432 98"
    }
  }), _vm._v(" "), _c('svg', {
    attrs: {
      "height": "250",
      "width": "250",
      "viewBox": "0 0 110 110",
      "x": "58%",
      "y": "-5%"
    }
  }, [_c('text', {
    attrs: {
      "dx": "50%",
      "dy": "50%",
      "fill": _vm.color,
      "font-weight": "bold",
      "font-size": "100",
      "dominant-baseline": "central",
      "text-anchor": "middle"
    }
  }, [_vm._t("default")], 2)])]) : _c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero"
    }
  }, [_c('path', {
    attrs: {
      "d": "M230.4,179.2h230.4v51.2H230.4V179.2z M230.4,281.6h179.2v51.2H230.4V281.6z M230.4,76.8H512V128H230.4V76.8z M230.4,384 h128v51.2h-128V384z M76.8,460.8H128V153.6h76.8L102.4,51.2L0,153.6h76.8V460.8z"
    }
  })])]);
};
var __vue_staticRenderFns__$11 = [];

/* style */
const __vue_inject_styles__$11 = undefined;
/* scoped */
const __vue_scope_id__$11 = undefined;
/* module identifier */
const __vue_module_identifier__$11 = undefined;
/* functional template */
const __vue_is_functional_template__$11 = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$22 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$11,
  staticRenderFns: __vue_staticRenderFns__$11
}, __vue_inject_styles__$11, __vue_script__$11, __vue_scope_id__$11, __vue_is_functional_template__$11, __vue_module_identifier__$11, false, undefined, undefined, undefined);
var __vue_component__$23 = __vue_component__$22;

let IconBalance = class IconBalance extends Vue {};
__decorate([Prop({
  default: 26
})], IconBalance.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconBalance.prototype, "color", void 0);
IconBalance = __decorate([Component], IconBalance);
var script$10 = IconBalance;

/* script */
const __vue_script__$10 = script$10;

/* template */
var __vue_render__$10 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 22 20"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero"
    }
  }, [_c('path', {
    attrs: {
      "d": "M10 2v16h2V2h-2zm3-2c.552 0 1 .448 1 1v18c0 .552-.448 1-1 1H9c-.552 0-1-.448-1-1V1c0-.552.448-1 1-1h4zm8 5c.552 0 1 .448 1 1v13c0 .552-.448 1-1 1h-4c-.552 0-1-.448-1-1V6c0-.552.448-1 1-1h4zM5 10c.552 0 1 .448 1 1v8c0 .552-.448 1-1 1H1c-.552 0-1-.448-1-1v-8c0-.552.448-1 1-1h4zm15-3h-2v11h2V7zM4 12H2v6h2v-6z"
    }
  })])]);
};
var __vue_staticRenderFns__$10 = [];

/* style */
const __vue_inject_styles__$10 = undefined;
/* scoped */
const __vue_scope_id__$10 = undefined;
/* module identifier */
const __vue_module_identifier__$10 = undefined;
/* functional template */
const __vue_is_functional_template__$10 = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$20 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$10,
  staticRenderFns: __vue_staticRenderFns__$10
}, __vue_inject_styles__$10, __vue_script__$10, __vue_scope_id__$10, __vue_is_functional_template__$10, __vue_module_identifier__$10, false, undefined, undefined, undefined);
var __vue_component__$21 = __vue_component__$20;

let IconButtonPower = class IconButtonPower extends Vue {};
__decorate([Prop({
  default: 26
})], IconButtonPower.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconButtonPower.prototype, "color", void 0);
IconButtonPower = __decorate([Component], IconButtonPower);
var script$$ = IconButtonPower;

/* script */
const __vue_script__$$ = script$$;

/* template */
var __vue_render__$$ = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 16 18"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero"
    }
  }, [_c('path', {
    attrs: {
      "d": "M13.601 4.408c-.265-.265-.694-.265-.959 0-.264.265-.264.694 0 .959 1.244 1.244 1.93 2.898 1.93 4.657 0 1.76-.686 3.414-1.93 4.658-1.284 1.284-2.97 1.926-4.656 1.926-1.687 0-3.374-.643-4.658-1.927-2.568-2.567-2.568-6.745 0-9.313.265-.265.265-.694 0-.96-.265-.264-.694-.264-.959 0-3.096 3.097-3.096 8.136 0 11.232 1.549 1.549 3.582 2.323 5.617 2.323 2.033 0 4.067-.774 5.615-2.322 1.5-1.5 2.327-3.495 2.327-5.617 0-2.121-.827-4.116-2.327-5.616z",
      "transform": "translate(-1385 -24) translate(1385 24)"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "d": "M7.985 8.783c.374 0 .678-.303.678-.678V.678C8.663.304 8.359 0 7.985 0c-.375 0-.678.304-.678.678v7.427c0 .375.303.678.678.678z",
      "transform": "translate(-1385 -24) translate(1385 24)"
    }
  })])]);
};
var __vue_staticRenderFns__$$ = [];

/* style */
const __vue_inject_styles__$$ = undefined;
/* scoped */
const __vue_scope_id__$$ = undefined;
/* module identifier */
const __vue_module_identifier__$$ = undefined;
/* functional template */
const __vue_is_functional_template__$$ = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1_ = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$$,
  staticRenderFns: __vue_staticRenderFns__$$
}, __vue_inject_styles__$$, __vue_script__$$, __vue_scope_id__$$, __vue_is_functional_template__$$, __vue_module_identifier__$$, false, undefined, undefined, undefined);
var __vue_component__$1$ = __vue_component__$1_;

let IconChevronDown = class IconChevronDown extends Vue {};
__decorate([Prop({
  default: 26
})], IconChevronDown.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconChevronDown.prototype, "color", void 0);
IconChevronDown = __decorate([Component], IconChevronDown);
var script$_ = IconChevronDown;

/* script */
const __vue_script__$_ = script$_;

/* template */
var __vue_render__$_ = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 16 16"
    }
  }, [_c('g', {
    attrs: {
      "stroke": _vm.color,
      "fill": _vm.color,
      "transform": "rotate(180 , 8, 8)"
    }
  }, [_c('path', {
    attrs: {
      "d": "M7.646 4.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 5.707l-5.646 5.647a.5.5 0 0 1-.708-.708l6-6z"
    }
  })])]);
};
var __vue_staticRenderFns__$_ = [];

/* style */
const __vue_inject_styles__$_ = undefined;
/* scoped */
const __vue_scope_id__$_ = undefined;
/* module identifier */
const __vue_module_identifier__$_ = undefined;
/* functional template */
const __vue_is_functional_template__$_ = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1Y = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$_,
  staticRenderFns: __vue_staticRenderFns__$_
}, __vue_inject_styles__$_, __vue_script__$_, __vue_scope_id__$_, __vue_is_functional_template__$_, __vue_module_identifier__$_, false, undefined, undefined, undefined);
var __vue_component__$1Z = __vue_component__$1Y;

let IconChevronLeft = class IconChevronLeft extends Vue {};
__decorate([Prop({
  default: 26
})], IconChevronLeft.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconChevronLeft.prototype, "color", void 0);
IconChevronLeft = __decorate([Component], IconChevronLeft);
var script$Z = IconChevronLeft;

/* script */
const __vue_script__$Z = script$Z;

/* template */
var __vue_render__$Z = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 16 16"
    }
  }, [_c('g', {
    attrs: {
      "stroke": _vm.color,
      "fill": _vm.color,
      "transform": "rotate(270 , 8, 8)"
    }
  }, [_c('path', {
    attrs: {
      "d": "M7.646 4.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 5.707l-5.646 5.647a.5.5 0 0 1-.708-.708l6-6z"
    }
  })])]);
};
var __vue_staticRenderFns__$Z = [];

/* style */
const __vue_inject_styles__$Z = undefined;
/* scoped */
const __vue_scope_id__$Z = undefined;
/* module identifier */
const __vue_module_identifier__$Z = undefined;
/* functional template */
const __vue_is_functional_template__$Z = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1W = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$Z,
  staticRenderFns: __vue_staticRenderFns__$Z
}, __vue_inject_styles__$Z, __vue_script__$Z, __vue_scope_id__$Z, __vue_is_functional_template__$Z, __vue_module_identifier__$Z, false, undefined, undefined, undefined);
var __vue_component__$1X = __vue_component__$1W;

let IconChevronRight = class IconChevronRight extends Vue {};
__decorate([Prop({
  default: 26
})], IconChevronRight.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconChevronRight.prototype, "color", void 0);
IconChevronRight = __decorate([Component], IconChevronRight);
var script$Y = IconChevronRight;

/* script */
const __vue_script__$Y = script$Y;

/* template */
var __vue_render__$Y = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 16 16"
    }
  }, [_c('g', {
    attrs: {
      "stroke": _vm.color,
      "fill": _vm.color,
      "transform": "rotate(90 , 8, 8)"
    }
  }, [_c('path', {
    attrs: {
      "d": "M7.646 4.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 5.707l-5.646 5.647a.5.5 0 0 1-.708-.708l6-6z"
    }
  })])]);
};
var __vue_staticRenderFns__$Y = [];

/* style */
const __vue_inject_styles__$Y = undefined;
/* scoped */
const __vue_scope_id__$Y = undefined;
/* module identifier */
const __vue_module_identifier__$Y = undefined;
/* functional template */
const __vue_is_functional_template__$Y = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1U = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$Y,
  staticRenderFns: __vue_staticRenderFns__$Y
}, __vue_inject_styles__$Y, __vue_script__$Y, __vue_scope_id__$Y, __vue_is_functional_template__$Y, __vue_module_identifier__$Y, false, undefined, undefined, undefined);
var __vue_component__$1V = __vue_component__$1U;

let IconChevronsLeft = class IconChevronsLeft extends Vue {};
__decorate([Prop({
  default: 26
})], IconChevronsLeft.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconChevronsLeft.prototype, "color", void 0);
IconChevronsLeft = __decorate([Component], IconChevronsLeft);
var script$X = IconChevronsLeft;

/* script */
const __vue_script__$X = script$X;

/* template */
var __vue_render__$X = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 32 32"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero"
    }
  }, [_c('path', {
    attrs: {
      "d": "M13.7238576,8.39052429 C14.2445567,7.86982524 15.0887767,7.86982524 15.6094757,8.39052429 C16.1301748,8.91122335 16.1301748,9.75544332 15.6094757,10.2761424 L15.6094757,10.2761424 L9.88561808,16 L15.6094757,21.7238576 C16.1301748,22.2445567 16.1301748,23.0887767 15.6094757,23.6094757 C15.0887767,24.1301748 14.2445567,24.1301748 13.7238576,23.6094757 L13.7238576,23.6094757 L7.05719096,16.942809 C6.5364919,16.42211 6.5364919,15.57789 7.05719096,15.057191 L7.05719096,15.057191 Z M23.057191,8.39052429 C23.57789,7.86982524 24.42211,7.86982524 24.942809,8.39052429 C25.4635081,8.91122335 25.4635081,9.75544332 24.942809,10.2761424 L24.942809,10.2761424 L19.2189514,16 L24.942809,21.7238576 C25.4635081,22.2445567 25.4635081,23.0887767 24.942809,23.6094757 C24.42211,24.1301748 23.57789,24.1301748 23.057191,23.6094757 L23.057191,23.6094757 L16.3905243,16.942809 C15.8698252,16.42211 15.8698252,15.57789 16.3905243,15.057191 L16.3905243,15.057191 Z"
    }
  })])]);
};
var __vue_staticRenderFns__$X = [];

/* style */
const __vue_inject_styles__$X = undefined;
/* scoped */
const __vue_scope_id__$X = undefined;
/* module identifier */
const __vue_module_identifier__$X = undefined;
/* functional template */
const __vue_is_functional_template__$X = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1S = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$X,
  staticRenderFns: __vue_staticRenderFns__$X
}, __vue_inject_styles__$X, __vue_script__$X, __vue_scope_id__$X, __vue_is_functional_template__$X, __vue_module_identifier__$X, false, undefined, undefined, undefined);
var __vue_component__$1T = __vue_component__$1S;

let IconChevronsRight = class IconChevronsRight extends Vue {};
__decorate([Prop({
  default: 26
})], IconChevronsRight.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconChevronsRight.prototype, "color", void 0);
IconChevronsRight = __decorate([Component], IconChevronsRight);
var script$W = IconChevronsRight;

/* script */
const __vue_script__$W = script$W;

/* template */
var __vue_render__$W = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 32 32"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero"
    }
  }, [_c('path', {
    attrs: {
      "d": "M16.3905243,8.39052429 C16.9112233,7.86982524 17.7554433,7.86982524 18.2761424,8.39052429 L18.2761424,8.39052429 L24.942809,15.057191 C25.4635081,15.57789 25.4635081,16.42211 24.942809,16.942809 L24.942809,16.942809 L18.2761424,23.6094757 C17.7554433,24.1301748 16.9112233,24.1301748 16.3905243,23.6094757 C15.8698252,23.0887767 15.8698252,22.2445567 16.3905243,21.7238576 L16.3905243,21.7238576 L22.1143819,16 L16.3905243,10.2761424 C15.8698252,9.75544332 15.8698252,8.91122335 16.3905243,8.39052429 Z M7.05719096,8.39052429 C7.57789001,7.86982524 8.42210999,7.86982524 8.94280904,8.39052429 L8.94280904,8.39052429 L15.6094757,15.057191 C16.1301748,15.57789 16.1301748,16.42211 15.6094757,16.942809 L15.6094757,16.942809 L8.94280904,23.6094757 C8.42210999,24.1301748 7.57789001,24.1301748 7.05719096,23.6094757 C6.5364919,23.0887767 6.5364919,22.2445567 7.05719096,21.7238576 L7.05719096,21.7238576 L12.7810486,16 L7.05719096,10.2761424 C6.5364919,9.75544332 6.5364919,8.91122335 7.05719096,8.39052429 Z"
    }
  })])]);
};
var __vue_staticRenderFns__$W = [];

/* style */
const __vue_inject_styles__$W = undefined;
/* scoped */
const __vue_scope_id__$W = undefined;
/* module identifier */
const __vue_module_identifier__$W = undefined;
/* functional template */
const __vue_is_functional_template__$W = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1Q = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$W,
  staticRenderFns: __vue_staticRenderFns__$W
}, __vue_inject_styles__$W, __vue_script__$W, __vue_scope_id__$W, __vue_is_functional_template__$W, __vue_module_identifier__$W, false, undefined, undefined, undefined);
var __vue_component__$1R = __vue_component__$1Q;

let IconChevronUp = class IconChevronUp extends Vue {};
__decorate([Prop({
  default: 26
})], IconChevronUp.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconChevronUp.prototype, "color", void 0);
IconChevronUp = __decorate([Component], IconChevronUp);
var script$V = IconChevronUp;

/* script */
const __vue_script__$V = script$V;

/* template */
var __vue_render__$V = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 16 16"
    }
  }, [_c('g', {
    attrs: {
      "stroke": _vm.color,
      "fill": _vm.color,
      "transform": "rotate(0 , 8, 8)"
    }
  }, [_c('path', {
    attrs: {
      "d": "M7.646 4.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 5.707l-5.646 5.647a.5.5 0 0 1-.708-.708l6-6z"
    }
  })])]);
};
var __vue_staticRenderFns__$V = [];

/* style */
const __vue_inject_styles__$V = undefined;
/* scoped */
const __vue_scope_id__$V = undefined;
/* module identifier */
const __vue_module_identifier__$V = undefined;
/* functional template */
const __vue_is_functional_template__$V = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1O = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$V,
  staticRenderFns: __vue_staticRenderFns__$V
}, __vue_inject_styles__$V, __vue_script__$V, __vue_scope_id__$V, __vue_is_functional_template__$V, __vue_module_identifier__$V, false, undefined, undefined, undefined);
var __vue_component__$1P = __vue_component__$1O;

let IconClipboard = class IconClipboard extends Vue {};
__decorate([Prop({
  default: 26
})], IconClipboard.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconClipboard.prototype, "color", void 0);
IconClipboard = __decorate([Component], IconClipboard);
var script$U = IconClipboard;

/* script */
const __vue_script__$U = script$U;

/* template */
var __vue_render__$U = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 24 24",
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero"
    }
  }, [_c('path', {
    attrs: {
      "d": "M15 1a2 2 0 0 1 2 2h1a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3h1a2 2 0 0 1 2-2h6zM7 5H6a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1h-1a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2zm8-2H9v2h6V3z"
    }
  })])]);
};
var __vue_staticRenderFns__$U = [];

/* style */
const __vue_inject_styles__$U = undefined;
/* scoped */
const __vue_scope_id__$U = undefined;
/* module identifier */
const __vue_module_identifier__$U = undefined;
/* functional template */
const __vue_is_functional_template__$U = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1M = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$U,
  staticRenderFns: __vue_staticRenderFns__$U
}, __vue_inject_styles__$U, __vue_script__$U, __vue_scope_id__$U, __vue_is_functional_template__$U, __vue_module_identifier__$U, false, undefined, undefined, undefined);
var __vue_component__$1N = __vue_component__$1M;

let IconCoins = class IconCoins extends Vue {};
__decorate([Prop({
  default: 26
})], IconCoins.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconCoins.prototype, "color", void 0);
IconCoins = __decorate([Component], IconCoins);
var script$T = IconCoins;

/* script */
const __vue_script__$T = script$T;

/* template */
var __vue_render__$T = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 10 16"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero"
    }
  }, [_c('path', {
    attrs: {
      "d": "M8.471.84C7.541.298 6.308 0 5.001 0 3.694 0 2.46.298 1.53.84.556 1.406.02 2.174.02 3.002v9.313c0 .828.536 1.595 1.51 2.162.93.541 2.163.84 3.47.84 1.307 0 2.54-.299 3.47-.84.974-.567 1.51-1.334 1.51-2.162V3.002c0-.828-.537-1.596-1.51-2.162zm1.011 11.475c0 .643-.448 1.258-1.262 1.731-.856.498-2 .772-3.22.772-1.22 0-2.363-.274-3.22-.772C.968 13.573.52 12.958.52 12.315v-.229c.25.31.59.593 1.011.839.931.541 2.164.84 3.47.84 1.308 0 2.54-.299 3.471-.84.422-.246.762-.53 1.011-.839v.229zm0-1.552c0 .643-.448 1.257-1.262 1.73-.856.499-2 .773-3.22.773-1.22 0-2.363-.274-3.22-.772C.968 12.02.52 11.406.52 10.763v-.23c.25.31.59.594 1.011.84.931.541 2.164.84 3.47.84 1.308 0 2.54-.299 3.471-.84.422-.246.762-.53 1.011-.84v.23zm0-1.552c0 .642-.448 1.257-1.262 1.73-.856.499-2 .773-3.22.773-1.22 0-2.363-.274-3.22-.772C.968 10.468.52 9.853.52 9.21v-.23c.25.31.59.594 1.011.84.931.54 2.164.84 3.47.84 1.308 0 2.54-.3 3.471-.84.422-.246.762-.53 1.011-.84v.23zm0-1.553c0 .643-.448 1.258-1.262 1.731-.856.499-2 .773-3.22.773-1.22 0-2.363-.274-3.22-.773C.968 8.916.52 8.301.52 7.66v-.23c.25.31.59.594 1.011.839.931.542 2.164.84 3.47.84 1.308 0 2.54-.298 3.471-.84.422-.245.762-.529 1.011-.839v.23zm0-1.552c0 .643-.448 1.258-1.262 1.731-.856.498-2 .773-3.22.773-1.22 0-2.363-.275-3.22-.773C.968 7.364.52 6.75.52 6.107v-.23c.25.31.59.593 1.011.839.931.542 2.164.84 3.47.84 1.308 0 2.54-.298 3.471-.84.422-.246.762-.53 1.011-.839v.23zm0-1.552c0 .643-.448 1.258-1.262 1.731-.856.498-2 .773-3.22.773-1.22 0-2.363-.275-3.22-.773C.968 5.812.52 5.197.52 4.555v-.23c.25.31.59.593 1.011.839.931.541 2.164.84 3.47.84 1.308 0 2.54-.299 3.471-.84.422-.246.762-.53 1.011-.839v.23zm-1.262.179c-.856.498-2 .772-3.22.772-1.22 0-2.363-.274-3.22-.772C.968 4.26.52 3.645.52 3.002S.967 1.744 1.781 1.27C2.637.773 3.78.498 5 .498c1.22 0 2.363.275 3.22.773.813.473 1.261 1.088 1.261 1.73 0 .644-.448 1.258-1.262 1.732z"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "d": "M7.843 1.535C7.066 1.134 6.056.913 5.001.913c-1.056 0-2.065.22-2.843.622C1.38 1.936.933 2.47.933 3.002c0 .53.447 1.066 1.225 1.467s1.787.622 2.843.622c1.055 0 2.065-.22 2.842-.622.778-.401 1.225-.936 1.225-1.467S8.62 1.936 7.843 1.535zm-.228 2.491c-.708.365-1.637.567-2.614.567-.978 0-1.906-.202-2.615-.567-.588-.304-.954-.696-.954-1.024 0-.328.366-.72.954-1.024.709-.366 1.637-.567 2.615-.567.977 0 1.906.201 2.614.567.589.303.954.696.954 1.024 0 .328-.365.72-.954 1.024z"
    }
  })])]);
};
var __vue_staticRenderFns__$T = [];

/* style */
const __vue_inject_styles__$T = undefined;
/* scoped */
const __vue_scope_id__$T = undefined;
/* module identifier */
const __vue_module_identifier__$T = undefined;
/* functional template */
const __vue_is_functional_template__$T = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1K = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$T,
  staticRenderFns: __vue_staticRenderFns__$T
}, __vue_inject_styles__$T, __vue_script__$T, __vue_scope_id__$T, __vue_is_functional_template__$T, __vue_module_identifier__$T, false, undefined, undefined, undefined);
var __vue_component__$1L = __vue_component__$1K;

let IconDelete = class IconDelete extends Vue {};
__decorate([Prop({
  default: 26
})], IconDelete.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconDelete.prototype, "color", void 0);
IconDelete = __decorate([Component], IconDelete);
var script$S = IconDelete;

/* script */
const __vue_script__$S = script$S;

/* template */
var __vue_render__$S = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 24 24",
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink"
    }
  }, [_c('defs', [_c('path', {
    attrs: {
      "id": "f7bcn4ycca",
      "d": "M14 1a3 3 0 0 1 2.995 2.824L17 4v1h4a1 1 0 0 1 .993.883L22 6a1 1 0 0 1-.883.993L21 7h-1v13a3 3 0 0 1-2.824 2.995L17 23H7a3 3 0 0 1-2.995-2.824L4 20V7H3a1 1 0 0 1-.993-.883L2 6a1 1 0 0 1 .883-.993L3 5h4V4a3 3 0 0 1 2.824-2.995L10 1h4zm4 6H6v13a1 1 0 0 0 .883.993L7 21h10a1 1 0 0 0 .993-.883L18 20V7zm-8 3a1 1 0 0 1 1 1v6a1 1 0 0 1-2 0v-6a1 1 0 0 1 1-1zm4 0a1 1 0 0 1 1 1v6a1 1 0 0 1-2 0v-6a1 1 0 0 1 1-1zm0-7h-4a1 1 0 0 0-.993.883L9 4v1h6V4a1 1 0 0 0-.883-.993L14 3z"
    }
  })]), _vm._v(" "), _c('g', {
    attrs: {
      "fill": "none",
      "fill-rule": "evenodd"
    }
  }, [_c('mask', {
    attrs: {
      "id": "s187xijjib",
      "fill": "#fff"
    }
  }, [_c('use', {
    attrs: {
      "xlink:href": "#f7bcn4ycca"
    }
  })]), _vm._v(" "), _c('use', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero",
      "xlink:href": "#f7bcn4ycca"
    }
  }), _vm._v(" "), _c('g', {
    attrs: {
      "mask": "url(#s187xijjib)",
      "fill": _vm.color
    }
  }, [_c('path', {
    attrs: {
      "d": "M0 0h24v24H0z"
    }
  })])])]);
};
var __vue_staticRenderFns__$S = [];

/* style */
const __vue_inject_styles__$S = undefined;
/* scoped */
const __vue_scope_id__$S = undefined;
/* module identifier */
const __vue_module_identifier__$S = undefined;
/* functional template */
const __vue_is_functional_template__$S = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1I = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$S,
  staticRenderFns: __vue_staticRenderFns__$S
}, __vue_inject_styles__$S, __vue_script__$S, __vue_scope_id__$S, __vue_is_functional_template__$S, __vue_module_identifier__$S, false, undefined, undefined, undefined);
var __vue_component__$1J = __vue_component__$1I;

let IconDesc = class IconDesc extends Vue {
  hasDefaultSlot() {
    return !!this.$slots.default;
  }
};
__decorate([Prop({
  default: 26
})], IconDesc.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconDesc.prototype, "color", void 0);
IconDesc = __decorate([Component], IconDesc);
var script$R = IconDesc;

/* script */
const __vue_script__$R = script$R;

/* template */
var __vue_render__$R = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 -50 600 600"
    }
  }, [_vm.hasDefaultSlot() ? _c('g', {
    attrs: {
      "fill": _vm.color
    }
  }, [_c('path', {
    attrs: {
      "d": "M 285 360.8 h -230.4 v -51.2 H 285 V 360.8 z M 285 258.4 h -179.2 v -51.2 H 285 V 258.4 z M 285 463.2 H 3.4 V 412 H 285 V 463.2 z M 285 156 h -128 v -51.2 h 128 V 156 z M 388 231 H 388 V 386.4 h -76.8 L 413 488.8 L 515.4 386.4 h -76.8 V 228 C 415 228 413 228 388 220 z"
    }
  }), _vm._v(" "), _c('svg', {
    attrs: {
      "height": "250",
      "width": "250",
      "viewBox": "0 0 110 110",
      "x": "58%",
      "y": "-5%"
    }
  }, [_c('text', {
    attrs: {
      "dx": "50%",
      "dy": "50%",
      "fill": _vm.color,
      "font-weight": "bold",
      "font-size": "100",
      "dominant-baseline": "central",
      "text-anchor": "middle"
    }
  }, [_vm._t("default")], 2)])]) : _c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero"
    }
  }, [_c('path', {
    attrs: {
      "d": "M 319.6 320.8 h -230.4 v -51.2 H 319.6 V 320.8 z M 319.6 218.4 h -179.2 v -51.2 H 319.6 V 218.4 z M 319.6 423.2 H 38 V 372 H 319.6 V 423.2 z M 319.6 116 h -128 v -51.2 h 128 V 116 z M 473.2 39.2 H 422 V 346.4 h -76.8 L 447.6 448.8 L 550 346.4 h -76.8 V 39.2 z"
    }
  })])]);
};
var __vue_staticRenderFns__$R = [];

/* style */
const __vue_inject_styles__$R = undefined;
/* scoped */
const __vue_scope_id__$R = undefined;
/* module identifier */
const __vue_module_identifier__$R = undefined;
/* functional template */
const __vue_is_functional_template__$R = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1G = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$R,
  staticRenderFns: __vue_staticRenderFns__$R
}, __vue_inject_styles__$R, __vue_script__$R, __vue_scope_id__$R, __vue_is_functional_template__$R, __vue_module_identifier__$R, false, undefined, undefined, undefined);
var __vue_component__$1H = __vue_component__$1G;

let IconDocument = class IconDocument extends Vue {};
__decorate([Prop({
  default: 26
})], IconDocument.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconDocument.prototype, "color", void 0);
IconDocument = __decorate([Component], IconDocument);
var script$Q = IconDocument;

/* script */
const __vue_script__$Q = script$Q;

/* template */
var __vue_render__$Q = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 18 22"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero"
    }
  }, [_c('path', {
    attrs: {
      "d": "M10 0c.043 0 .085.003.126.008L10 0c.053 0 .105.004.157.012.031.005.062.012.093.02l.03.008c.03.009.061.02.091.031l.027.012c.028.012.056.025.082.04l.032.018c.028.016.055.035.081.054l.02.015.012.01.082.073 7 7c.03.03.057.06.082.093l-.082-.093c.036.036.069.074.098.114.02.026.038.053.054.081l.018.032c.015.026.028.054.04.082l.012.027c.012.03.022.06.031.091l.008.03c.008.03.015.062.02.093l.004.031c.005.041.008.083.008.126v11c0 1.657-1.343 3-3 3H3c-1.657 0-3-1.343-3-3V3c0-1.657 1.343-3 3-3zM9 2H3c-.552 0-1 .448-1 1v16c0 .552.448 1 1 1h12c.552 0 1-.448 1-1V9h-6c-.513 0-.936-.386-.993-.883L9 8V2zm5.586 5L11 3.415V7h3.586z"
    }
  })])]);
};
var __vue_staticRenderFns__$Q = [];

/* style */
const __vue_inject_styles__$Q = undefined;
/* scoped */
const __vue_scope_id__$Q = undefined;
/* module identifier */
const __vue_module_identifier__$Q = undefined;
/* functional template */
const __vue_is_functional_template__$Q = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1E = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$Q,
  staticRenderFns: __vue_staticRenderFns__$Q
}, __vue_inject_styles__$Q, __vue_script__$Q, __vue_scope_id__$Q, __vue_is_functional_template__$Q, __vue_module_identifier__$Q, false, undefined, undefined, undefined);
var __vue_component__$1F = __vue_component__$1E;

let IconDownload = class IconDownload extends Vue {};
__decorate([Prop({
  default: 26
})], IconDownload.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconDownload.prototype, "color", void 0);
IconDownload = __decorate([Component], IconDownload);
var script$P = IconDownload;

/* script */
const __vue_script__$P = script$P;

/* template */
var __vue_render__$P = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 13 16"
    }
  }, [_c('g', {
    attrs: {
      "stroke": _vm.color,
      "fill": _vm.color,
      "fill-rule": "nonzero"
    }
  }, [_c('path', {
    attrs: {
      "d": "M13.209 7.266H.708l3.947-4.384c.194-.218.175-.553-.042-.747-.218-.195-.553-.176-.748.042L-.87 7.439l-.016.021-.024.03-.015.023-.021.034-.016.032v.163c0 .013-.037.023-.037.05 0 .026 0 0 0 0v.2l.016.031.02.035.017.023.023.03.016.02 4.736 5.263c.195.218.53.237.748.042.218-.195.237-.53.042-.747l-3.91-4.37h12.5c.292 0 .527-.237.527-.527 0-.29-.235-.526-.526-.526z",
      "transform": "translate(-1382 -710) matrix(0 -1 -1 0 1396.153 724.153)"
    }
  })])]);
};
var __vue_staticRenderFns__$P = [];

/* style */
const __vue_inject_styles__$P = undefined;
/* scoped */
const __vue_scope_id__$P = undefined;
/* module identifier */
const __vue_module_identifier__$P = undefined;
/* functional template */
const __vue_is_functional_template__$P = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1C = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$P,
  staticRenderFns: __vue_staticRenderFns__$P
}, __vue_inject_styles__$P, __vue_script__$P, __vue_scope_id__$P, __vue_is_functional_template__$P, __vue_module_identifier__$P, false, undefined, undefined, undefined);
var __vue_component__$1D = __vue_component__$1C;

let IconEdit = class IconEdit extends Vue {};
__decorate([Prop({
  default: 26
})], IconEdit.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconEdit.prototype, "color", void 0);
IconEdit = __decorate([Component], IconEdit);
var script$O = IconEdit;

/* script */
const __vue_script__$O = script$O;

/* template */
var __vue_render__$O = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 32 32"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color
    }
  }, [_c('path', {
    attrs: {
      "d": "M5.333 4h7.12c.737 0 1.334.597 1.334 1.333 0 .693-.53 1.263-1.205 1.328l-.129.006h-7.12c-.693 0-1.262.528-1.327 1.205L4 8v18.667c0 .693.529 1.262 1.205 1.327l.128.006H24c.693 0 1.263-.529 1.327-1.205l.006-.128v-7.12c0-.737.597-1.334 1.334-1.334.693 0 1.262.53 1.327 1.205l.006.129v7.12c0 2.142-1.684 3.89-3.8 3.995l-.2.005H5.333c-2.142 0-3.89-1.684-3.995-3.8l-.005-.2V8c0-2.142 1.684-3.891 3.8-3.995l.2-.005h7.12zm19.61-2.276l5.333 5.333c.52.52.52 1.365 0 1.886L16.943 22.276c-.25.25-.59.39-.943.39h-5.333c-.737 0-1.334-.596-1.334-1.333V16c0-.354.14-.693.39-.943L23.058 1.724c.52-.52 1.365-.52 1.886 0zM24 4.552l-12 12V20h3.448l12-12L24 4.552z"
    }
  })])]);
};
var __vue_staticRenderFns__$O = [];

/* style */
const __vue_inject_styles__$O = undefined;
/* scoped */
const __vue_scope_id__$O = undefined;
/* module identifier */
const __vue_module_identifier__$O = undefined;
/* functional template */
const __vue_is_functional_template__$O = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1A = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$O,
  staticRenderFns: __vue_staticRenderFns__$O
}, __vue_inject_styles__$O, __vue_script__$O, __vue_scope_id__$O, __vue_is_functional_template__$O, __vue_module_identifier__$O, false, undefined, undefined, undefined);
var __vue_component__$1B = __vue_component__$1A;

let IconEdit2 = class IconEdit2 extends Vue {};
__decorate([Prop({
  default: 26
})], IconEdit2.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconEdit2.prototype, "color", void 0);
IconEdit2 = __decorate([Component], IconEdit2);
var script$N = IconEdit2;

/* script */
const __vue_script__$N = script$N;

/* template */
var __vue_render__$N = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 16 16"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero"
    }
  }, [_c('path', {
    attrs: {
      "d": "M9.333 2.276L2.667 8.943v1.724H4.39L11.057 4 9.333 2.276zM14 14c.368 0 .667.298.667.667 0 .368-.299.666-.667.666H2c-.368 0-.667-.298-.667-.666 0-.369.299-.667.667-.667zM9.805.862l2.666 2.667c.26.26.26.682 0 .942l-7.333 7.334c-.125.125-.295.195-.471.195H2c-.368 0-.667-.298-.667-.667V8.667c0-.177.07-.347.196-.472L8.862.862c.26-.26.682-.26.943 0z"
    }
  })])]);
};
var __vue_staticRenderFns__$N = [];

/* style */
const __vue_inject_styles__$N = undefined;
/* scoped */
const __vue_scope_id__$N = undefined;
/* module identifier */
const __vue_module_identifier__$N = undefined;
/* functional template */
const __vue_is_functional_template__$N = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1y = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$N,
  staticRenderFns: __vue_staticRenderFns__$N
}, __vue_inject_styles__$N, __vue_script__$N, __vue_scope_id__$N, __vue_is_functional_template__$N, __vue_module_identifier__$N, false, undefined, undefined, undefined);
var __vue_component__$1z = __vue_component__$1y;

let IconAdministration = class IconAdministration extends Vue {};
__decorate([Prop({
  default: 26
})], IconAdministration.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconAdministration.prototype, "color", void 0);
IconAdministration = __decorate([Component], IconAdministration);
var script$M = IconAdministration;

/* script */
const __vue_script__$M = script$M;

/* template */
var __vue_render__$M = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 20 22"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero"
    }
  }, [_c('path', {
    attrs: {
      "d": "M12 2.414l-10 10V15h2.586l10-10L12 2.414zM19 20c.552 0 1 .448 1 1s-.448 1-1 1H1c-.552 0-1-.448-1-1s.448-1 1-1zM12.707.293l4 4c.39.39.39 1.024 0 1.414l-11 11C5.52 16.895 5.265 17 5 17H1c-.552 0-1-.448-1-1v-4c0-.265.105-.52.293-.707l11-11c.39-.39 1.024-.39 1.414 0z"
    }
  })])]);
};
var __vue_staticRenderFns__$M = [];

/* style */
const __vue_inject_styles__$M = undefined;
/* scoped */
const __vue_scope_id__$M = undefined;
/* module identifier */
const __vue_module_identifier__$M = undefined;
/* functional template */
const __vue_is_functional_template__$M = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1w = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$M,
  staticRenderFns: __vue_staticRenderFns__$M
}, __vue_inject_styles__$M, __vue_script__$M, __vue_scope_id__$M, __vue_is_functional_template__$M, __vue_module_identifier__$M, false, undefined, undefined, undefined);
var __vue_component__$1x = __vue_component__$1w;

let IconEnter = class IconEnter extends Vue {};
__decorate([Prop({
  default: 26
})], IconEnter.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconEnter.prototype, "color", void 0);
IconEnter = __decorate([Component], IconEnter);
var script$L = IconEnter;

/* script */
const __vue_script__$L = script$L;

/* template */
var __vue_render__$L = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 18 20"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero"
    }
  }, [_c('path', {
    attrs: {
      "d": "M10.667.833h4.166c1.326 0 2.41 1.032 2.495 2.336l.005.164v13.334c0 1.325-1.031 2.41-2.335 2.494l-.165.006h-4.166c-.46 0-.834-.373-.834-.834 0-.425.318-.775.73-.827l.104-.006h4.166c.425 0 .776-.318.827-.729l.007-.104V3.333c0-.425-.318-.775-.73-.827l-.104-.006h-4.166c-.46 0-.834-.373-.834-.833 0-.425.318-.776.73-.827l.104-.007h4.166zM8.67 6.003l.085.074 3.333 3.334.016.016.039.044-.055-.06c.032.032.06.065.086.1.013.018.026.037.037.056l.016.026c.067.12.105.26.105.407 0 .109-.02.212-.058.307l-.016.037-.029.058c-.007.014-.016.028-.024.041l-.03.045-.012.016-.016.02c-.015.019-.031.037-.048.054l-.01.011-3.334 3.334c-.326.325-.853.325-1.179 0-.298-.299-.323-.767-.074-1.094l.074-.085 1.91-1.91H1.5c-.425 0-.775-.319-.827-.73L.667 10c0-.46.373-.833.833-.833h7.988l-1.91-1.911c-.326-.326-.326-.853 0-1.179.298-.298.766-.323 1.093-.074z"
    }
  })])]);
};
var __vue_staticRenderFns__$L = [];

/* style */
const __vue_inject_styles__$L = undefined;
/* scoped */
const __vue_scope_id__$L = undefined;
/* module identifier */
const __vue_module_identifier__$L = undefined;
/* functional template */
const __vue_is_functional_template__$L = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1u = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$L,
  staticRenderFns: __vue_staticRenderFns__$L
}, __vue_inject_styles__$L, __vue_script__$L, __vue_scope_id__$L, __vue_is_functional_template__$L, __vue_module_identifier__$L, false, undefined, undefined, undefined);
var __vue_component__$1v = __vue_component__$1u;

let IconEntities = class IconEntities extends Vue {};
__decorate([Prop({
  default: 26
})], IconEntities.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconEntities.prototype, "color", void 0);
IconEntities = __decorate([Component], IconEntities);
var script$K = IconEntities;

/* script */
const __vue_script__$K = script$K;

/* template */
var __vue_render__$K = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 16 16"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero"
    }
  }, [_c('path', {
    attrs: {
      "fill-rule": "evenodd",
      "clip-rule": "evenodd",
      "d": "M5.3724 1.00239C5.17852 0.978965 5 1.13029 5 1.33354V4.49457H2.66667C2.48257 4.49457 2.33333 4.64381 2.33333 4.82791V14.6669H1.33333L1.27342 14.6722C1.11792 14.7005 1 14.8366 1 15.0002C1 15.1843 1.14924 15.3335 1.33333 15.3335H2.66553L2.66667 15.3335H8.66667L8.66781 15.3335H14.6667L14.7266 15.3282C14.8821 15.2999 15 15.1638 15 15.0002C15 14.8161 14.8508 14.6669 14.6667 14.6669H13.6667V3.6361L13.6622 3.58136C13.6414 3.45625 13.5505 3.35173 13.4255 3.31577L5.42553 1.01321L5.3724 1.00239ZM8.66667 4.49457H5.66667V1.77621L13 3.88687V14.6669H9V4.82791C9 4.64381 8.85076 4.49457 8.66667 4.49457ZM8.33333 14.6662V5.16087H3V14.6662H8.33333ZM5.06667 13.0002C5.06667 12.7793 4.88758 12.6002 4.66667 12.6002H4L3.93512 12.6054C3.74506 12.6364 3.6 12.8014 3.6 13.0002C3.6 13.2211 3.77909 13.4002 4 13.4002H4.66667L4.73155 13.395C4.92161 13.364 5.06667 13.199 5.06667 13.0002ZM7.33333 12.6002C7.55425 12.6002 7.73333 12.7793 7.73333 13.0002C7.73333 13.199 7.58827 13.364 7.39822 13.395L7.33333 13.4002H6.66667C6.44575 13.4002 6.26667 13.2211 6.26667 13.0002C6.26667 12.8014 6.41173 12.6364 6.60178 12.6054L6.66667 12.6002H7.33333ZM11.7333 13.0002C11.7333 12.7793 11.5542 12.6002 11.3333 12.6002H10.6667L10.6018 12.6054C10.4117 12.6364 10.2667 12.8014 10.2667 13.0002C10.2667 13.2211 10.4458 13.4002 10.6667 13.4002H11.3333L11.3982 13.395C11.5883 13.364 11.7333 13.199 11.7333 13.0002ZM4.66667 10.6002C4.88758 10.6002 5.06667 10.7793 5.06667 11.0002C5.06667 11.199 4.92161 11.364 4.73155 11.395L4.66667 11.4002H4C3.77909 11.4002 3.6 11.2211 3.6 11.0002C3.6 10.8014 3.74506 10.6364 3.93512 10.6054L4 10.6002H4.66667ZM7.73333 11.0002C7.73333 10.7793 7.55425 10.6002 7.33333 10.6002H6.66667L6.60178 10.6054C6.41173 10.6364 6.26667 10.8014 6.26667 11.0002C6.26667 11.2211 6.44575 11.4002 6.66667 11.4002H7.33333L7.39822 11.395C7.58827 11.364 7.73333 11.199 7.73333 11.0002ZM11.3333 10.6002C11.5542 10.6002 11.7333 10.7793 11.7333 11.0002C11.7333 11.199 11.5883 11.364 11.3982 11.395L11.3333 11.4002H10.6667C10.4458 11.4002 10.2667 11.2211 10.2667 11.0002C10.2667 10.8014 10.4117 10.6364 10.6018 10.6054L10.6667 10.6002H11.3333ZM5.06667 9.00021C5.06667 8.77929 4.88758 8.6002 4.66667 8.6002H4L3.93512 8.60544C3.74506 8.63644 3.6 8.80138 3.6 9.00021C3.6 9.22112 3.77909 9.40021 4 9.40021H4.66667L4.73155 9.39497C4.92161 9.36397 5.06667 9.19903 5.06667 9.00021ZM7.33333 8.6002C7.55425 8.6002 7.73333 8.77929 7.73333 9.00021C7.73333 9.19903 7.58827 9.36397 7.39822 9.39497L7.33333 9.40021H6.66667C6.44575 9.40021 6.26667 9.22112 6.26667 9.00021C6.26667 8.80138 6.41173 8.63644 6.60178 8.60544L6.66667 8.6002H7.33333ZM11.7333 9.00021C11.7333 8.77929 11.5542 8.6002 11.3333 8.6002H10.6667L10.6018 8.60544C10.4117 8.63644 10.2667 8.80138 10.2667 9.00021C10.2667 9.22112 10.4458 9.40021 10.6667 9.40021H11.3333L11.3982 9.39497C11.5883 9.36397 11.7333 9.19903 11.7333 9.00021ZM4.66667 6.60021C4.88758 6.60021 5.06667 6.77929 5.06667 7.00021C5.06667 7.19903 4.92161 7.36397 4.73155 7.39497L4.66667 7.4002H4C3.77909 7.4002 3.6 7.22112 3.6 7.00021C3.6 6.80138 3.74506 6.63644 3.93512 6.60544L4 6.60021H4.66667ZM7.73333 7.00021C7.73333 6.77929 7.55425 6.60021 7.33333 6.60021H6.66667L6.60178 6.60544C6.41173 6.63644 6.26667 6.80138 6.26667 7.00021C6.26667 7.22112 6.44575 7.4002 6.66667 7.4002H7.33333L7.39822 7.39497C7.58827 7.36397 7.73333 7.19903 7.73333 7.00021ZM11.3333 6.60021C11.5542 6.60021 11.7333 6.77929 11.7333 7.00021C11.7333 7.19903 11.5883 7.36397 11.3982 7.39497L11.3333 7.4002H10.6667C10.4458 7.4002 10.2667 7.22112 10.2667 7.00021C10.2667 6.80138 10.4117 6.63644 10.6018 6.60544L10.6667 6.60021H11.3333ZM11.7333 5.00021C11.7333 4.77929 11.5542 4.60021 11.3333 4.60021H10.6667L10.6018 4.60544C10.4117 4.63644 10.2667 4.80138 10.2667 5.00021C10.2667 5.22112 10.4458 5.4002 10.6667 5.4002H11.3333L11.3982 5.39497C11.5883 5.36397 11.7333 5.19903 11.7333 5.00021Z",
      "fill": _vm.color
    }
  })])]);
};
var __vue_staticRenderFns__$K = [];

/* style */
const __vue_inject_styles__$K = undefined;
/* scoped */
const __vue_scope_id__$K = undefined;
/* module identifier */
const __vue_module_identifier__$K = undefined;
/* functional template */
const __vue_is_functional_template__$K = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1s = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$K,
  staticRenderFns: __vue_staticRenderFns__$K
}, __vue_inject_styles__$K, __vue_script__$K, __vue_scope_id__$K, __vue_is_functional_template__$K, __vue_module_identifier__$K, false, undefined, undefined, undefined);
var __vue_component__$1t = __vue_component__$1s;

let IconEntity = class IconEntity extends Vue {};
__decorate([Prop({
  default: 26
})], IconEntity.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconEntity.prototype, "color", void 0);
IconEntity = __decorate([Component], IconEntity);
var script$J = IconEntity;

/* script */
const __vue_script__$J = script$J;

/* template */
var __vue_render__$J = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 24 24"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "evenodd",
      "clip-rule": "evenodd"
    }
  }, [_c('path', {
    attrs: {
      "d": "M22.809 21.796h-1.904V3.957a.591.591 0 0 0-.456-.575L10.374 1.017a.591.591 0 0 0-.726.575V5.74H4.014a.591.591 0 0 0-.592.591v15.466H1.525a.591.591 0 0 0 0 1.182h21.284a.591.591 0 0 0 0-1.182zM10.83 2.338l8.892 2.084v17.374h-5.035V6.33a.591.591 0 0 0-.591-.59H10.83V2.337zM4.605 6.922h8.9v14.874h-8.9V6.922zm11.787 13.007c0-.327.264-.592.59-.592h1.007a.591.591 0 1 1 0 1.183h-1.006a.591.591 0 0 1-.591-.595v.004zm-4.035 0a.591.591 0 0 1-.591.59H10.76a.591.591 0 0 1 0-1.182h1.005c.325 0 .59.263.591.588v.004zm0-2.73a.591.591 0 0 1-.591.592H10.76a.591.591 0 0 1 0-1.183h1.005c.324 0 .587.26.591.584v.008zm0-2.729a.591.591 0 0 1-.591.592H10.76a.591.591 0 1 1 0-1.183h1.005a.591.591 0 0 1 .591.594v-.003zm0-2.729a.591.591 0 0 1-.591.592H10.76a.591.591 0 0 1 0-1.183h1.005c.325 0 .59.263.591.588v.003zM10.17 9.012c0-.326.264-.591.59-.591h1.006a.591.591 0 0 1 0 1.182H10.76a.591.591 0 0 1-.591-.594v.003zM7.942 19.925a.591.591 0 0 1-.591.591H6.344a.591.591 0 0 1 0-1.182h1.007c.326 0 .59.265.59.591zm0-2.729a.591.591 0 0 1-.591.591H6.344a.591.591 0 0 1 0-1.182h1.007c.325 0 .589.262.59.587v.004zm0-2.73a.591.591 0 0 1-.591.592H6.344a.591.591 0 1 1 0-1.182h1.007a.591.591 0 0 1 .59.597v-.006zm0-2.728a.591.591 0 0 1-.591.591H6.344a.591.591 0 0 1 0-1.183h1.007a.591.591 0 0 1 .59.592zm0-2.73a.591.591 0 0 1-.591.592H6.344a.591.591 0 0 1 0-1.183h1.007c.326 0 .59.265.59.592zm8.45 8.184c0-.326.264-.59.59-.59h1.007a.591.591 0 1 1 0 1.182h-1.006a.591.591 0 0 1-.591-.592zm0-2.729c0-.326.264-.59.59-.59h1.007a.591.591 0 1 1 0 1.182h-1.006a.591.591 0 0 1-.591-.582v-.01zm0-2.729c0-.326.264-.591.59-.591h1.007a.591.591 0 1 1 0 1.182h-1.006a.591.591 0 0 1-.591-.587v-.004zm0-2.729c0-.326.264-.591.59-.591h1.007a.591.591 0 1 1 0 1.182h-1.006a.591.591 0 0 1-.591-.587v-.004zm0-2.73c0-.326.264-.59.59-.59h1.007a.591.591 0 1 1 0 1.182h-1.006a.591.591 0 0 1-.591-.588v-.003z"
    }
  })])]);
};
var __vue_staticRenderFns__$J = [];

/* style */
const __vue_inject_styles__$J = undefined;
/* scoped */
const __vue_scope_id__$J = undefined;
/* module identifier */
const __vue_module_identifier__$J = undefined;
/* functional template */
const __vue_is_functional_template__$J = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1q = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$J,
  staticRenderFns: __vue_staticRenderFns__$J
}, __vue_inject_styles__$J, __vue_script__$J, __vue_scope_id__$J, __vue_is_functional_template__$J, __vue_module_identifier__$J, false, undefined, undefined, undefined);
var __vue_component__$1r = __vue_component__$1q;

let IconExcel = class IconExcel extends Vue {};
__decorate([Prop({
  default: 26
})], IconExcel.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconExcel.prototype, "color", void 0);
IconExcel = __decorate([Component], IconExcel);
var script$I = IconExcel;

/* script */
const __vue_script__$I = script$I;

/* template */
var __vue_render__$I = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "viewBox": "0 0 26 26",
      "width": _vm.size,
      "height": _vm.size
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color
    }
  }, [_c('path', {
    attrs: {
      "d": "M 15 0 L 0 2.875 L 0 23.125 L 15 26 Z M 16 3 L 16 5.96875 L 19.03125 5.96875 L 19.03125 8 L 16 8 L 16 10 L 19 10 L 19 12 L 16 12 L 16 14 L 19 14 L 19 16 L 16 16 L 16 18 L 19 18 L 19 20 L 16 20 L 16 23 L 25.15625 23 C 25.617188 23 26 22.605469 26 22.125 L 26 3.875 C 26 3.394531 25.617188 3 25.15625 3 Z M 20 6 L 24 6 L 24 8 L 20 8 Z M 3.09375 7.9375 L 5.84375 7.9375 L 7.3125 11 C 7.425781 11.238281 7.535156 11.515625 7.625 11.84375 C 7.683594 11.644531 7.8125 11.359375 7.96875 10.96875 L 9.5625 7.9375 L 12.09375 7.9375 L 9.0625 12.96875 L 12.1875 18.09375 L 9.5 18.09375 L 7.75 14.78125 C 7.683594 14.660156 7.601563 14.421875 7.53125 14.09375 L 7.5 14.09375 C 7.46875 14.25 7.402344 14.496094 7.28125 14.8125 L 5.53125 18.09375 L 2.8125 18.09375 L 6.03125 13.03125 Z M 20 10 L 24 10 L 24 12 L 20 12 Z M 20 14 L 24 14 L 24 16 L 20 16 Z M 20 18 L 24 18 L 24 20 L 20 20 Z"
    }
  })])]);
};
var __vue_staticRenderFns__$I = [];

/* style */
const __vue_inject_styles__$I = undefined;
/* scoped */
const __vue_scope_id__$I = undefined;
/* module identifier */
const __vue_module_identifier__$I = undefined;
/* functional template */
const __vue_is_functional_template__$I = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1o = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$I,
  staticRenderFns: __vue_staticRenderFns__$I
}, __vue_inject_styles__$I, __vue_script__$I, __vue_scope_id__$I, __vue_is_functional_template__$I, __vue_module_identifier__$I, false, undefined, undefined, undefined);
var __vue_component__$1p = __vue_component__$1o;

let IconExit = class IconExit extends Vue {};
__decorate([Prop({
  default: 26
})], IconExit.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconExit.prototype, "color", void 0);
IconExit = __decorate([Component], IconExit);
var script$H = IconExit;

/* script */
const __vue_script__$H = script$H;

/* template */
var __vue_render__$H = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 18 20"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero"
    }
  }, [_c('path', {
    attrs: {
      "d": "M3.167.833h4.166c.46 0 .834.373.834.834 0 .425-.318.775-.73.827l-.104.006H3.167c-.425 0-.776.318-.827.729l-.007.104v13.334c0 .425.318.775.73.827l.104.006h4.166c.46 0 .834.373.834.833 0 .425-.318.776-.73.827l-.104.007H3.167c-1.326 0-2.41-1.032-2.495-2.336l-.005-.164V3.333C.667 2.008 1.698.923 3.002.84l.165-.006h4.166zm10.504 5.17l.085.074 3.333 3.334.016.016.039.044-.055-.06c.032.032.06.065.086.1.013.018.026.037.037.056l.016.026c.067.12.105.26.105.407 0 .109-.02.212-.058.307l-.016.037-.029.058c-.007.014-.016.028-.024.041l-.03.045-.012.016-.016.02c-.015.019-.031.037-.048.054l-.01.011-3.334 3.334c-.326.325-.853.325-1.179 0-.298-.299-.323-.767-.074-1.094l.074-.085 1.91-1.91H6.5c-.425 0-.775-.319-.827-.73L5.667 10c0-.46.373-.833.833-.833h7.988l-1.91-1.911c-.326-.326-.326-.853 0-1.179.298-.298.766-.323 1.093-.074z"
    }
  })])]);
};
var __vue_staticRenderFns__$H = [];

/* style */
const __vue_inject_styles__$H = undefined;
/* scoped */
const __vue_scope_id__$H = undefined;
/* module identifier */
const __vue_module_identifier__$H = undefined;
/* functional template */
const __vue_is_functional_template__$H = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1m = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$H,
  staticRenderFns: __vue_staticRenderFns__$H
}, __vue_inject_styles__$H, __vue_script__$H, __vue_scope_id__$H, __vue_is_functional_template__$H, __vue_module_identifier__$H, false, undefined, undefined, undefined);
var __vue_component__$1n = __vue_component__$1m;

let IconEye = class IconEye extends Vue {};
__decorate([Prop({
  default: 15
})], IconEye.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconEye.prototype, "color", void 0);
IconEye = __decorate([Component], IconEye);
var script$G = IconEye;

/* script */
const __vue_script__$G = script$G;

/* template */
var __vue_render__$G = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 24 24"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero"
    }
  }, [_c('path', {
    attrs: {
      "d": "M12 3c3.38 0 6.339 1.632 8.855 4.316a20.492 20.492 0 0 1 2.25 2.891c.385.596.649 1.065.79 1.346a1 1 0 0 1 0 .894c-.141.281-.405.75-.79 1.346a20.492 20.492 0 0 1-2.25 2.89C18.339 19.369 15.38 21 12 21c-3.38 0-6.339-1.632-8.855-4.316a20.492 20.492 0 0 1-2.25-2.891 15.188 15.188 0 0 1-.79-1.346 1 1 0 0 1 0-.894c.141-.281.405-.75.79-1.346a20.492 20.492 0 0 1 2.25-2.89C5.661 4.631 8.62 3 12 3zm0 2C9.255 5 6.776 6.368 4.605 8.684A18.513 18.513 0 0 0 2.14 12a18.513 18.513 0 0 0 2.464 3.316C6.776 17.632 9.255 19 12 19c2.745 0 5.224-1.368 7.395-3.684A18.513 18.513 0 0 0 21.86 12a18.513 18.513 0 0 0-2.464-3.316C17.224 6.368 14.745 5 12 5zm0 3a4 4 0 1 1 0 8 4 4 0 0 1 0-8zm0 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"
    }
  })])]);
};
var __vue_staticRenderFns__$G = [];

/* style */
const __vue_inject_styles__$G = undefined;
/* scoped */
const __vue_scope_id__$G = undefined;
/* module identifier */
const __vue_module_identifier__$G = undefined;
/* functional template */
const __vue_is_functional_template__$G = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1k = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$G,
  staticRenderFns: __vue_staticRenderFns__$G
}, __vue_inject_styles__$G, __vue_script__$G, __vue_scope_id__$G, __vue_is_functional_template__$G, __vue_module_identifier__$G, false, undefined, undefined, undefined);
var __vue_component__$1l = __vue_component__$1k;

let IconEyeClosed = class IconEyeClosed extends Vue {};
__decorate([Prop({
  default: 15
})], IconEyeClosed.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconEyeClosed.prototype, "color", void 0);
IconEyeClosed = __decorate([Component], IconEyeClosed);
var script$F = IconEyeClosed;

/* script */
const __vue_script__$F = script$F;

/* template */
var __vue_render__$F = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 24 24"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero"
    }
  }, [_c('path', {
    attrs: {
      "d": "M.293.293a1 1 0 0 1 1.414 0l22 22a1 1 0 0 1-1.414 1.414l-4.468-4.466A11.089 11.089 0 0 1 12 21c-3.38 0-6.339-1.632-8.855-4.316a20.492 20.492 0 0 1-2.25-2.891 15.188 15.188 0 0 1-.79-1.346 1 1 0 0 1 .014-.92 19.45 19.45 0 0 1 4.45-5.544L.292 1.707a1 1 0 0 1 0-1.414zm5.694 7.109a17.45 17.45 0 0 0-3.844 4.601 18.513 18.513 0 0 0 2.462 3.313C6.776 17.632 9.255 19 11.984 19a9.07 9.07 0 0 0 4.387-1.214l-2.322-2.321A4 4 0 0 1 8.535 9.95zM12 3c3.38 0 6.339 1.632 8.855 4.316a20.492 20.492 0 0 1 2.25 2.891c.385.596.649 1.065.79 1.346a1 1 0 0 1-.013.918 19.5 19.5 0 0 1-2.277 3.363 1 1 0 0 1-1.53-1.288 17.5 17.5 0 0 0 1.783-2.548 18.513 18.513 0 0 0-2.463-3.314C17.224 6.368 14.745 5 11.998 5a8.12 8.12 0 0 0-1.87.214 1 1 0 0 1-.456-1.948A10.116 10.116 0 0 1 12 3zm-1.972 8.442a1.999 1.999 0 0 0 2.53 2.53z"
    }
  })])]);
};
var __vue_staticRenderFns__$F = [];

/* style */
const __vue_inject_styles__$F = undefined;
/* scoped */
const __vue_scope_id__$F = undefined;
/* module identifier */
const __vue_module_identifier__$F = undefined;
/* functional template */
const __vue_is_functional_template__$F = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1i = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$F,
  staticRenderFns: __vue_staticRenderFns__$F
}, __vue_inject_styles__$F, __vue_script__$F, __vue_scope_id__$F, __vue_is_functional_template__$F, __vue_module_identifier__$F, false, undefined, undefined, undefined);
var __vue_component__$1j = __vue_component__$1i;

let IconFaq = class IconFaq extends Vue {};
__decorate([Prop({
  default: 26
})], IconFaq.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconFaq.prototype, "color", void 0);
IconFaq = __decorate([Component], IconFaq);
var script$E = IconFaq;

/* script */
const __vue_script__$E = script$E;

/* template */
var __vue_render__$E = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 17 17"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero"
    }
  }, [_c('path', {
    attrs: {
      "d": "M8 .667c4.05 0 7.333 3.283 7.333 7.333 0 4.05-3.283 7.333-7.333 7.333C3.95 15.333.667 12.05.667 8 .667 3.95 3.95.667 8 .667zm0 1C4.502 1.667 1.667 4.502 1.667 8S4.502 14.333 8 14.333 14.333 11.498 14.333 8 11.498 1.667 8 1.667zm-.033 8.409c.192 0 .353.065.484.195.13.131.195.292.195.484 0 .212-.068.38-.205.5-.136.122-.294.183-.474.183-.185 0-.347-.06-.485-.18s-.207-.288-.207-.503c0-.192.067-.353.2-.484.134-.13.298-.195.492-.195zm.11-5.538c.44 0 .83.082 1.166.244.338.163.598.384.782.663.184.28.276.584.276.912 0 .258-.053.484-.157.679-.105.194-.23.362-.374.503-.144.142-.403.38-.777.714-.103.094-.186.177-.249.248-.062.071-.108.137-.139.196-.03.06-.053.118-.07.178-.017.059-.042.163-.075.312-.058.316-.239.474-.543.474-.158 0-.29-.052-.398-.155-.108-.103-.162-.257-.162-.46 0-.256.04-.477.118-.664.08-.186.184-.35.315-.492.13-.141.307-.309.528-.503.195-.17.335-.299.422-.385.086-.087.16-.183.219-.29.059-.106.089-.222.089-.346 0-.243-.09-.448-.272-.615-.18-.167-.414-.251-.7-.251-.333 0-.58.084-.737.253-.158.169-.292.417-.402.745-.103.343-.299.515-.587.515-.17 0-.314-.06-.431-.18-.117-.12-.176-.25-.176-.39 0-.288.093-.58.278-.877.186-.296.456-.541.812-.736.355-.194.77-.292 1.244-.292z"
    }
  })])]);
};
var __vue_staticRenderFns__$E = [];

/* style */
const __vue_inject_styles__$E = undefined;
/* scoped */
const __vue_scope_id__$E = undefined;
/* module identifier */
const __vue_module_identifier__$E = undefined;
/* functional template */
const __vue_is_functional_template__$E = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1g = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$E,
  staticRenderFns: __vue_staticRenderFns__$E
}, __vue_inject_styles__$E, __vue_script__$E, __vue_scope_id__$E, __vue_is_functional_template__$E, __vue_module_identifier__$E, false, undefined, undefined, undefined);
var __vue_component__$1h = __vue_component__$1g;

let IconFile = class IconFile extends Vue {};
__decorate([Prop({
  default: 26
})], IconFile.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconFile.prototype, "color", void 0);
IconFile = __decorate([Component], IconFile);
var script$D = IconFile;

/* script */
const __vue_script__$D = script$D;

/* template */
var __vue_render__$D = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 32 32"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero"
    }
  }, [_c('path', {
    attrs: {
      "d": "M17.333 1.333c.047 0 .093.003.138.007l-.138-.007c.067 0 .133.005.198.015l.076.013.063.015.07.02.06.022.077.03.047.023c.122.06.234.14.332.233l.02.02 9.333 9.333.02.02c.093.097.173.21.234.333l.021.046c.012.026.022.051.032.077l.02.06c.008.023.015.047.021.07l.015.064c.005.024.01.05.013.075l.008.061c.005.045.007.09.007.137v14.667c0 2.209-1.79 4-4 4H8c-2.21 0-4-1.791-4-4V5.333c0-2.209 1.79-4 4-4zM16 4L8 4c-.736 0-1.333.597-1.333 1.333v21.334C6.667 27.403 7.264 28 8 28h16c.736 0 1.333-.597 1.333-1.333V13.333h-8c-.693 0-1.262-.528-1.327-1.205L16 12V4zm7.448 6.667l-4.782-4.782v4.783h4.782z"
    }
  })])]);
};
var __vue_staticRenderFns__$D = [];

/* style */
const __vue_inject_styles__$D = undefined;
/* scoped */
const __vue_scope_id__$D = undefined;
/* module identifier */
const __vue_module_identifier__$D = undefined;
/* functional template */
const __vue_is_functional_template__$D = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1e = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$D,
  staticRenderFns: __vue_staticRenderFns__$D
}, __vue_inject_styles__$D, __vue_script__$D, __vue_scope_id__$D, __vue_is_functional_template__$D, __vue_module_identifier__$D, false, undefined, undefined, undefined);
var __vue_component__$1f = __vue_component__$1e;

let IconFilter = class IconFilter extends Vue {};
__decorate([Prop({
  default: 26
})], IconFilter.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconFilter.prototype, "color", void 0);
IconFilter = __decorate([Component], IconFilter);
var script$C = IconFilter;

/* script */
const __vue_script__$C = script$C;

/* template */
var __vue_render__$C = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 16 16"
    }
  }, [_c('g', {
    attrs: {
      "stroke": _vm.color,
      "stroke-width": "1",
      "fill": _vm.color,
      "fill-rule": "evenodd"
    }
  }, [_c('path', {
    attrs: {
      "d": "M14.0919323,1.16666457 C14.3705023,1.16612921 14.6251252,1.32502742 14.7515163,1.57700857 C14.8644639,1.80211538 14.8594529,2.06764185 14.7432113,2.28578298 L14.69442,2.36519322 L10.2263018,8.81322715 C10.1030669,8.98386195 10.0290674,9.1828903 10.0102158,9.38988451 L10.0054131,9.49395087 L10.0054131,13.0916477 C10.0054131,13.3956684 9.84270147,13.6593448 9.5950067,13.7697712 L7.02150477,14.7739965 C6.92942169,14.8130908 6.83097454,14.8333323 6.73157663,14.8333323 C6.53451597,14.8333323 6.34589654,14.7530077 6.20761358,14.6104563 C6.0896958,14.4888987 6.01672302,14.3308878 5.99904255,14.163472 L5.99485791,14.0791056 L5.99485822,9.49427432 C5.9944515,9.28470454 5.93803034,9.08023473 5.830646,8.89975622 L5.76952306,8.80713237 L1.30615382,2.36629357 C1.14364519,2.13475853 1.1214224,1.83024416 1.24847406,1.57702785 C1.36082043,1.35304702 1.57447639,1.20259358 1.81613921,1.17230172 L1.90793241,1.16666457 L14.0919323,1.16666457 Z M13.6793333,2.16666667 L2.32,2.16666667 L6.56932705,8.22779173 C6.81459236,8.56465603 6.95970102,8.96404375 6.98955115,9.3794717 L6.9953127,9.53591829 L6.99466667,13.7566667 L9.00466667,12.9806667 L9.0046876,9.53559995 C9.00548671,9.11723101 9.12340091,8.70878977 9.34209677,8.35868103 L9.42879996,8.23021912 L13.6793333,2.16666667 Z"
    }
  })])]);
};
var __vue_staticRenderFns__$C = [];

/* style */
const __vue_inject_styles__$C = undefined;
/* scoped */
const __vue_scope_id__$C = undefined;
/* module identifier */
const __vue_module_identifier__$C = undefined;
/* functional template */
const __vue_is_functional_template__$C = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1c = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$C,
  staticRenderFns: __vue_staticRenderFns__$C
}, __vue_inject_styles__$C, __vue_script__$C, __vue_scope_id__$C, __vue_is_functional_template__$C, __vue_module_identifier__$C, false, undefined, undefined, undefined);
var __vue_component__$1d = __vue_component__$1c;

let IconGreaterThan = class IconGreaterThan extends Vue {};
__decorate([Prop({
  default: 26
})], IconGreaterThan.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconGreaterThan.prototype, "color", void 0);
IconGreaterThan = __decorate([Component], IconGreaterThan);
var script$B = IconGreaterThan;

/* script */
const __vue_script__$B = script$B;

/* template */
var __vue_render__$B = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 100 100"
    }
  }, [_c('g', {
    attrs: {
      "stroke": _vm.color,
      "fill": "none",
      "stroke-width": "10px",
      "transform": "rotate(180, 50, 50)"
    }
  }, [_c('path', {
    attrs: {
      "d": " M 95  5 L 10 50 L 95 95"
    }
  })])]);
};
var __vue_staticRenderFns__$B = [];

/* style */
const __vue_inject_styles__$B = undefined;
/* scoped */
const __vue_scope_id__$B = undefined;
/* module identifier */
const __vue_module_identifier__$B = undefined;
/* functional template */
const __vue_is_functional_template__$B = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1a = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$B,
  staticRenderFns: __vue_staticRenderFns__$B
}, __vue_inject_styles__$B, __vue_script__$B, __vue_scope_id__$B, __vue_is_functional_template__$B, __vue_module_identifier__$B, false, undefined, undefined, undefined);
var __vue_component__$1b = __vue_component__$1a;

let IconGrid = class IconGrid extends Vue {};
__decorate([Prop({
  default: 26
})], IconGrid.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconGrid.prototype, "color", void 0);
IconGrid = __decorate([Component], IconGrid);
var script$A = IconGrid;

/* script */
const __vue_script__$A = script$A;

/* template */
var __vue_render__$A = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 20 20"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero"
    }
  }, [_c('path', {
    attrs: {
      "d": "M2 2v5h5V2H2zm17 9c.552 0 1 .448 1 1v7c0 .552-.448 1-1 1h-7c-.552 0-1-.448-1-1v-7c0-.552.448-1 1-1h7zM8 11c.552 0 1 .448 1 1v7c0 .552-.448 1-1 1H1c-.552 0-1-.448-1-1v-7c0-.552.448-1 1-1h7zm10 2h-5v5h5v-5zM7 13H2v5h5v-5zM8 0c.552 0 1 .448 1 1v7c0 .552-.448 1-1 1H1c-.552 0-1-.448-1-1V1c0-.552.448-1 1-1h7zm11 0c.552 0 1 .448 1 1v7c0 .552-.448 1-1 1h-7c-.552 0-1-.448-1-1V1c0-.552.448-1 1-1h7zm-1 2h-5v5h5V2z"
    }
  })])]);
};
var __vue_staticRenderFns__$A = [];

/* style */
const __vue_inject_styles__$A = undefined;
/* scoped */
const __vue_scope_id__$A = undefined;
/* module identifier */
const __vue_module_identifier__$A = undefined;
/* functional template */
const __vue_is_functional_template__$A = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$18 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$A,
  staticRenderFns: __vue_staticRenderFns__$A
}, __vue_inject_styles__$A, __vue_script__$A, __vue_scope_id__$A, __vue_is_functional_template__$A, __vue_module_identifier__$A, false, undefined, undefined, undefined);
var __vue_component__$19 = __vue_component__$18;

let IconInbox = class IconInbox extends Vue {};
__decorate([Prop({
  default: 26
})], IconInbox.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconInbox.prototype, "color", void 0);
__decorate([Prop()], IconInbox.prototype, "msgColor", void 0);
__decorate([Prop()], IconInbox.prototype, "msgActive", void 0);
IconInbox = __decorate([Component], IconInbox);
var script$z = IconInbox;

/* script */
const __vue_script__$z = script$z;

/* template */
var __vue_render__$z = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "-1.5 -3 20 20"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero"
    }
  }, [_c('path', {
    attrs: {
      "d": "M15.333 8.667V12c0 1.105-.895 2-2 2H2.667c-1.105 0-2-.895-2-2V8.667c0-.094.02-.186.057-.271L3.037 3.19C3.358 2.466 4.075 2 4.867 2h6.266c.792 0 1.51.466 1.83 1.19l2.313 5.206.016.039c.005.013.01.026.013.04l.022.099c.004.03.006.062.006.093zM2 12c0 .368.298.667.667.667h10.666c.369 0 .667-.299.667-.667V9.333h-2.977l-1.135 1.703c-.124.186-.332.297-.555.297H6.667c-.223 0-.431-.111-.555-.297L4.977 9.333H2V12zm9.133-8.667H4.866c-.264 0-.503.156-.61.397L2.36 8h2.974c.191 0 .372.082.497.222l.058.075L7.023 10h1.954l1.135-1.703c.124-.186.332-.297.555-.297h2.974l-1.897-4.27c-.107-.241-.346-.397-.61-.397z"
    }
  }), _vm._v(" "), _vm.msgActive ? _c('circle', {
    attrs: {
      "cx": "16",
      "cy": "0",
      "r": "2",
      "fill": _vm.msgColor
    }
  }) : _vm._e()])]);
};
var __vue_staticRenderFns__$z = [];

/* style */
const __vue_inject_styles__$z = undefined;
/* scoped */
const __vue_scope_id__$z = undefined;
/* module identifier */
const __vue_module_identifier__$z = undefined;
/* functional template */
const __vue_is_functional_template__$z = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$16 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$z,
  staticRenderFns: __vue_staticRenderFns__$z
}, __vue_inject_styles__$z, __vue_script__$z, __vue_scope_id__$z, __vue_is_functional_template__$z, __vue_module_identifier__$z, false, undefined, undefined, undefined);
var __vue_component__$17 = __vue_component__$16;

let IconIsEarlyValue = class IconIsEarlyValue extends Vue {};
__decorate([Prop({
  default: 26
})], IconIsEarlyValue.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconIsEarlyValue.prototype, "color", void 0);
IconIsEarlyValue = __decorate([Component], IconIsEarlyValue);
var script$y = IconIsEarlyValue;

/* script */
const __vue_script__$y = script$y;

/* template */
var __vue_render__$y = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 80 80"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero"
    }
  }, [_c('path', {
    attrs: {
      "d": "M11.48,42a.56.56,0,0,1,.41-.94h3.66v3.38H13.93Z"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "d": "M14.86,33.58a.58.58,0,0,1-.15-.38.56.56,0,0,1,.55-.56h3.67V36H17.31Z"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "d": "M14.86,50.47a.59.59,0,0,1-.15-.39.55.55,0,0,1,.55-.55h3.67V52.9H17.31Z"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "d": "M18.24,25.14a.55.55,0,0,1,.4-.94h3.67v3.37H20.69Z"
    }
  }), _vm._v(" "), _c('rect', {
    attrs: {
      "x": "21.46",
      "y": "24.19",
      "width": "8.61",
      "height": "3.38"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "d": "M18.24,58.91a.53.53,0,0,1-.16-.38.56.56,0,0,1,.56-.56h3.67v3.38H20.69Z"
    }
  }), _vm._v(" "), _c('rect', {
    attrs: {
      "x": "21.46",
      "y": "57.97",
      "width": "8.61",
      "height": "3.38"
    }
  }), _vm._v(" "), _c('rect', {
    attrs: {
      "x": "18.63",
      "y": "32.64",
      "width": "11.45",
      "height": "3.38"
    }
  }), _vm._v(" "), _c('rect', {
    attrs: {
      "x": "18.08",
      "y": "49.53",
      "width": "11.99",
      "height": "3.38"
    }
  }), _vm._v(" "), _c('rect', {
    attrs: {
      "x": "15",
      "y": "41.09",
      "width": "15.07",
      "height": "3.38"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "d": "M44.44,46.23a4.34,4.34,0,0,1-.7-8.63,4.36,4.36,0,0,1,5,3.58h0A4.35,4.35,0,0,1,48,44.42a4.3,4.3,0,0,1-2.83,1.75A4.25,4.25,0,0,1,44.44,46.23Zm0-6.69a1.72,1.72,0,0,0-.39,0,2.34,2.34,0,1,0,.77,4.62,2.35,2.35,0,0,0,1.93-2.69h0A2.34,2.34,0,0,0,45.81,40,2.37,2.37,0,0,0,44.45,39.54Z"
    }
  }), _vm._v(" "), _c('rect', {
    attrs: {
      "x": "45.21",
      "y": "34.75",
      "width": "11.93",
      "height": "2",
      "transform": "translate(-10.77 43.5) rotate(-42.07)"
    }
  }), _vm._v(" "), _c('rect', {
    attrs: {
      "x": "42.34",
      "y": "15.19",
      "width": "2",
      "height": "7.26"
    }
  }), _vm._v(" "), _c('rect', {
    attrs: {
      "x": "61.21",
      "y": "23.18",
      "width": "3.43",
      "height": "3.38",
      "transform": "translate(-0.45 48.57) rotate(-42.07)"
    }
  }), _vm._v(" "), _c('rect', {
    attrs: {
      "x": "38.62",
      "y": "11.89",
      "width": "9.44",
      "height": "3.38"
    }
  }), _vm._v(" "), _c('rect', {
    attrs: {
      "x": "62.66",
      "y": "20.61",
      "width": "3.38",
      "height": "5.49",
      "transform": "translate(2.33 52.34) rotate(-45)"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "d": "M43.34,68.05V64.67a22,22,0,0,0,0-43.91V17.38a25.34,25.34,0,0,1,0,50.67Z"
    }
  }), _vm._v(" "), _c('rect', {
    attrs: {
      "x": "42.34",
      "y": "18.23",
      "width": "2",
      "height": "4.22"
    }
  }), _vm._v(" "), _c('rect', {
    attrs: {
      "x": "42.23",
      "y": "62.98",
      "width": "2",
      "height": "4.22"
    }
  }), _vm._v(" "), _c('rect', {
    attrs: {
      "x": "53.42",
      "y": "60.01",
      "width": "2",
      "height": "4.22",
      "transform": "translate(-23.77 35.54) rotate(-30)"
    }
  }), _vm._v(" "), _c('rect', {
    attrs: {
      "x": "61.63",
      "y": "51.84",
      "width": "2",
      "height": "4.22",
      "transform": "translate(-15.41 81.22) rotate(-60)"
    }
  }), _vm._v(" "), _c('rect', {
    attrs: {
      "x": "63.55",
      "y": "41.77",
      "width": "4.22",
      "height": "2"
    }
  }), _vm._v(" "), _c('rect', {
    attrs: {
      "x": "60.58",
      "y": "30.58",
      "width": "4.22",
      "height": "2",
      "transform": "translate(-7.39 35.57) rotate(-29.99)"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "d": "M43.41,68.11A25.6,25.6,0,0,1,35.88,67l-.18-.06-1.54-.68,1.36-3.1,1.45.64a22,22,0,1,0-2.64-41L33,19.63A25.35,25.35,0,1,1,43.41,68.11Z"
    }
  }), _vm._v(" "), _c('rect', {
    attrs: {
      "x": "52.41",
      "y": "22.36",
      "width": "4.22",
      "height": "2",
      "transform": "translate(7.03 58.9) rotate(-60.01)"
    }
  })])]);
};
var __vue_staticRenderFns__$y = [];

/* style */
const __vue_inject_styles__$y = undefined;
/* scoped */
const __vue_scope_id__$y = undefined;
/* module identifier */
const __vue_module_identifier__$y = undefined;
/* functional template */
const __vue_is_functional_template__$y = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$14 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$y,
  staticRenderFns: __vue_staticRenderFns__$y
}, __vue_inject_styles__$y, __vue_script__$y, __vue_scope_id__$y, __vue_is_functional_template__$y, __vue_module_identifier__$y, false, undefined, undefined, undefined);
var __vue_component__$15 = __vue_component__$14;

let IconLetter = class IconLetter extends Vue {};
__decorate([Prop({
  default: 26
})], IconLetter.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconLetter.prototype, "color", void 0);
__decorate([Prop({
  default: "#323232"
})], IconLetter.prototype, "colorBackground", void 0);
IconLetter = __decorate([Component], IconLetter);
var script$x = IconLetter;

/* script */
const __vue_script__$x = script$x;

/* template */
var __vue_render__$x = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "-5 -5 110 110"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.colorBackground,
      "fill-rule": "nonzero"
    }
  }, [_c('circle', {
    attrs: {
      "cx": "50",
      "cy": "50",
      "r": "50",
      "stroke": _vm.color,
      "stroke-width": "7px"
    }
  }), _vm._v(" "), _c('text', {
    attrs: {
      "x": "37",
      "y": "62",
      "fill": _vm.color,
      "font-weight": "bold",
      "font-size": "40"
    }
  }, [_vm._t("default", function () {
    return [_vm._v(_vm._s("S"))];
  })], 2)])]);
};
var __vue_staticRenderFns__$x = [];

/* style */
const __vue_inject_styles__$x = undefined;
/* scoped */
const __vue_scope_id__$x = undefined;
/* module identifier */
const __vue_module_identifier__$x = undefined;
/* functional template */
const __vue_is_functional_template__$x = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$12 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$x,
  staticRenderFns: __vue_staticRenderFns__$x
}, __vue_inject_styles__$x, __vue_script__$x, __vue_scope_id__$x, __vue_is_functional_template__$x, __vue_module_identifier__$x, false, undefined, undefined, undefined);
var __vue_component__$13 = __vue_component__$12;

let IconLocation = class IconLocation extends Vue {};
__decorate([Prop({
  default: 26
})], IconLocation.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconLocation.prototype, "color", void 0);
IconLocation = __decorate([Component], IconLocation);
var script$w = IconLocation;

/* script */
const __vue_script__$w = script$w;

/* template */
var __vue_render__$w = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 16 16"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero"
    }
  }, [_c('path', {
    attrs: {
      "d": "M8 0c3.682 0 6.667 2.985 6.667 6.667 0 2.247-1.202 4.45-3.183 6.541-.68.718-1.405 1.37-2.131 1.949-.255.203-.492.382-.705.536-.13.094-.225.16-.278.195-.224.15-.516.15-.74 0-.027-.018-.064-.043-.11-.076l-.168-.12c-.213-.153-.45-.332-.705-.535-.726-.578-1.452-1.231-2.131-1.949-1.981-2.09-3.183-4.294-3.183-6.541C1.333 2.985 4.318 0 8 0zm0 1.333c-2.946 0-5.333 2.388-5.333 5.334 0 1.835 1.048 3.757 2.817 5.625.633.668 1.314 1.28 1.994 1.822.185.148.36.282.522.401.162-.12.337-.253.522-.401.68-.542 1.36-1.154 1.994-1.822 1.769-1.868 2.817-3.79 2.817-5.625 0-2.946-2.387-5.334-5.333-5.334zM8 4c1.473 0 2.667 1.194 2.667 2.667 0 1.472-1.194 2.666-2.667 2.666-1.473 0-2.667-1.194-2.667-2.666C5.333 5.194 6.527 4 8 4zm0 1.333c-.736 0-1.333.597-1.333 1.334C6.667 7.403 7.264 8 8 8s1.333-.597 1.333-1.333c0-.737-.597-1.334-1.333-1.334z"
    }
  })])]);
};
var __vue_staticRenderFns__$w = [];

/* style */
const __vue_inject_styles__$w = undefined;
/* scoped */
const __vue_scope_id__$w = undefined;
/* module identifier */
const __vue_module_identifier__$w = undefined;
/* functional template */
const __vue_is_functional_template__$w = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$10 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$w,
  staticRenderFns: __vue_staticRenderFns__$w
}, __vue_inject_styles__$w, __vue_script__$w, __vue_scope_id__$w, __vue_is_functional_template__$w, __vue_module_identifier__$w, false, undefined, undefined, undefined);
var __vue_component__$11 = __vue_component__$10;

let IconLogic = class IconLogic extends Vue {};
__decorate([Prop({
  default: 26
})], IconLogic.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconLogic.prototype, "color", void 0);
__decorate([Prop({
  default: "#ffd102"
})], IconLogic.prototype, "color2", void 0);
IconLogic = __decorate([Component], IconLogic);
var script$v = IconLogic;

/* script */
const __vue_script__$v = script$v;

/* template */
var __vue_render__$v = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 80 80"
    }
  }, [_c('g', {
    attrs: {
      "fill": "none",
      "fill-rule": "evenodd"
    }
  }, [_c('path', {
    attrs: {
      "stroke": "#323232",
      "stroke-width": "2",
      "fill": "#D8D8D8",
      "stroke-linejoin": "round",
      "d": "M12 13h24v14H12z"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "d": "M25.036 21.856c0-.287-.077-.516-.23-.686-.154-.17-.415-.327-.783-.47a9.062 9.062 0 0 1-.953-.43 3.033 3.033 0 0 1-.686-.488c-.19-.184-.34-.4-.446-.65a2.251 2.251 0 0 1-.16-.888c0-.592.183-1.078.548-1.456.364-.379.85-.6 1.454-.663V15h.81v1.14c.598.089 1.066.347 1.403.776.338.43.507.986.507 1.67h-1.464c0-.42-.084-.735-.25-.944a.816.816 0 0 0-.672-.313c-.277 0-.491.082-.643.245-.152.163-.228.388-.228.675 0 .267.074.48.223.642.148.16.424.326.828.494.403.168.735.327.995.476.26.149.48.317.659.504.179.188.316.402.41.642.095.24.142.52.142.838 0 .596-.179 1.08-.537 1.452-.358.371-.851.59-1.48.657V25h-.805v-1.041c-.692-.077-1.228-.332-1.608-.765-.38-.433-.57-1.009-.57-1.727h1.464c0 .417.095.737.286.96.191.222.465.333.823.333.298 0 .532-.081.705-.244.172-.163.258-.383.258-.66z",
      "fill": "#323232",
      "fill-rule": "nonzero"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "d": "M56.5 48v4m-.5 2.877V57H8V31h48v14.604",
      "stroke": "#323232",
      "stroke-width": "2",
      "fill": _vm.color2,
      "stroke-linejoin": "round"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "stroke": "#323232",
      "stroke-width": "2",
      "stroke-linejoin": "round",
      "d": "M14 36h13v12H14z"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "fill": "#323232",
      "d": "M16 38h2v2h-2zM16 41h2v2h-2zM19.5 38h2v2h-2zM19.5 41h2v2h-2zM23 38h2v2h-2zM28 19h2v2h-2zM18 19h2v2h-2zM23 41h2v2h-2zM16 44h2v2h-2zM19.5 44h2v2h-2zM23 44h2v2h-2z"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "stroke": "#323232",
      "stroke-width": "2",
      "fill": "#D8D8D8",
      "stroke-linejoin": "round",
      "d": "M36.088 41H32v-5h17v5h-3.905"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "stroke": "#323232",
      "stroke-width": "2",
      "fill": "#FFF",
      "stroke-linejoin": "round",
      "d": "M35 36h11v17H35z"
    }
  }), _vm._v(" "), _c('circle', {
    attrs: {
      "stroke": "#323232",
      "stroke-width": "2",
      "fill": "#D8D8D8",
      "stroke-linejoin": "round",
      "cx": "40.5",
      "cy": "44.5",
      "r": "2.5"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "stroke": "#323232",
      "stroke-width": "2",
      "stroke-linejoin": "round",
      "d": "M39 50h-1M43 39h-1"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "d": "m46.96 16.727-1.984.917.907 1.93 2.378-1.098a7.542 7.542 0 0 0-1.301-1.749zm.064-.33 1.76-.821c-1.131-.747-2.517-.96-3.914-.619a7.587 7.587 0 0 1 2.154 1.44zm-2.165 1.002 1.887-.874a7.336 7.336 0 0 0-2.026-1.333 2.957 2.957 0 0 0-.277-.107 6.405 6.405 0 0 0-.576.224l.992 2.09zm4.319 4.234-1.909.885 1.002 2.133c.214-.096.416-.214.608-.342.022-.064.043-.128.064-.202.214-.81.299-1.642.235-2.474zm1.973-.917-1.706.789a7.683 7.683 0 0 1-.214 2.549 5.136 5.136 0 0 0 1.92-3.338zm-1.738.5 1.77-.82a5.008 5.008 0 0 0-.341-2.272l-2.005.928c.298.693.49 1.429.576 2.165zm-2.261 1.057 2.005-.928a8.058 8.058 0 0 0-.555-2.176l-2.389 1.11.939 1.994zm-4.607-3.797a7.16 7.16 0 0 1 .256-2.517 5.188 5.188 0 0 0-1.93 3.296c0-.011 1.674-.779 1.674-.779zm.554 2.389a8.004 8.004 0 0 1-.533-2.1l-1.727.799a5.088 5.088 0 0 0 .298 2.207l1.962-.906zm1.621 2.527a8.47 8.47 0 0 1-1.3-1.823l-1.974.917-.735.341a2.599 2.599 0 0 1-.15-.298 5.952 5.952 0 0 1 2.9-7.924c2.624-1.216 5.514-.565 7.178 1.642l-.747.363a4.965 4.965 0 0 0-.863-.843l-1.813.843c.512.523.938 1.109 1.28 1.749l2.484-1.152.256-.106a5.952 5.952 0 0 1-2.698 8.318c-2.623 1.215-5.513.586-7.188-1.632l.736-.373c.246.298.523.576.821.82l1.813-.842zm2.325 1.685a7.333 7.333 0 0 1-2.111-1.482l-1.75.81c1.11.758 2.475.992 3.861.672zm-1.855-1.6a7.638 7.638 0 0 0 2.068 1.408l.203.086c.192-.064.373-.128.565-.203l-1.002-2.133-1.834.843zm-1.515-2.026c.352.662.79 1.28 1.312 1.824l1.92-.885-.939-1.994-2.293 1.055zm-.842-2.815a7.94 7.94 0 0 0 .512 2.112l2.292-1.056-.906-1.93-1.898.874zm.341-2.943c-.021.064-.053.16-.085.266a7.02 7.02 0 0 0-.278 2.4l1.803-.832-.981-2.09c-.16.064-.31.16-.459.256z",
      "fill": "#323232",
      "fill-rule": "nonzero"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "d": "M56 41.092v7.012m0 4.977V57H8V9h48v26.961",
      "stroke": "#323232",
      "stroke-width": "2",
      "stroke-linejoin": "round"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "stroke": "#323232",
      "stroke-width": "2",
      "fill": "#FFF",
      "stroke-linejoin": "round",
      "d": "M54 36h4v5h-4zM54 48h4v5h-4z"
    }
  })])]);
};
var __vue_staticRenderFns__$v = [];

/* style */
const __vue_inject_styles__$v = undefined;
/* scoped */
const __vue_scope_id__$v = undefined;
/* module identifier */
const __vue_module_identifier__$v = undefined;
/* functional template */
const __vue_is_functional_template__$v = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$_ = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$v,
  staticRenderFns: __vue_staticRenderFns__$v
}, __vue_inject_styles__$v, __vue_script__$v, __vue_scope_id__$v, __vue_is_functional_template__$v, __vue_module_identifier__$v, false, undefined, undefined, undefined);
var __vue_component__$$ = __vue_component__$_;

let IconLowerThan = class IconLowerThan extends Vue {};
__decorate([Prop({
  default: 26
})], IconLowerThan.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconLowerThan.prototype, "color", void 0);
IconLowerThan = __decorate([Component], IconLowerThan);
var script$u = IconLowerThan;

/* script */
const __vue_script__$u = script$u;

/* template */
var __vue_render__$u = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 100 100"
    }
  }, [_c('g', {
    attrs: {
      "stroke": _vm.color,
      "fill": "none",
      "stroke-width": "10px"
    }
  }, [_c('path', {
    attrs: {
      "d": " M 95  5 L 10 50 L 95 95"
    }
  })])]);
};
var __vue_staticRenderFns__$u = [];

/* style */
const __vue_inject_styles__$u = undefined;
/* scoped */
const __vue_scope_id__$u = undefined;
/* module identifier */
const __vue_module_identifier__$u = undefined;
/* functional template */
const __vue_is_functional_template__$u = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$Y = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$u,
  staticRenderFns: __vue_staticRenderFns__$u
}, __vue_inject_styles__$u, __vue_script__$u, __vue_scope_id__$u, __vue_is_functional_template__$u, __vue_module_identifier__$u, false, undefined, undefined, undefined);
var __vue_component__$Z = __vue_component__$Y;

let IconMachine = class IconMachine extends Vue {};
__decorate([Prop({
  default: 26
})], IconMachine.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconMachine.prototype, "color", void 0);
IconMachine = __decorate([Component], IconMachine);
var script$t = IconMachine;

/* script */
const __vue_script__$t = script$t;

/* template */
var __vue_render__$t = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 24 24"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color
    }
  }, [_c('path', {
    attrs: {
      "d": "M20.444 2C21.302 2 22 2.698 22 3.556v16.888c0 .858-.698 1.556-1.556 1.556H3.556A1.557 1.557 0 0 1 2 20.444V3.556C2 2.698 2.698 2 3.556 2zM18.79 13.33H5.21v8.011h13.58V13.33zm1.654-10.671H3.556a.898.898 0 0 0-.897.897v16.888c0 .495.402.897.897.897h.996V5.36a.33.33 0 0 1 .329-.33h14.238a.33.33 0 0 1 .33.33v15.982h.995a.898.898 0 0 0 .897-.897V3.556a.898.898 0 0 0-.897-.897zm-4.16 12.93a.33.33 0 0 1 .33.329v1.308a.33.33 0 1 1-.66 0v-.979H14.93v3.185a.33.33 0 0 1-.33.33H9.4a.33.33 0 0 1-.33-.33v-3.185H8.045v.98a.33.33 0 1 1-.658 0v-1.31a.33.33 0 0 1 .33-.329zm-2.013.658H9.73v2.855h4.542v-2.855zM15.61 5.688H5.21v6.982H15.61V5.688zm3.18 0h-2.521v6.982h2.522V5.688zm-.94 1.714v.535h-.534v-.535h.535z"
    }
  })])]);
};
var __vue_staticRenderFns__$t = [];

/* style */
const __vue_inject_styles__$t = undefined;
/* scoped */
const __vue_scope_id__$t = undefined;
/* module identifier */
const __vue_module_identifier__$t = undefined;
/* functional template */
const __vue_is_functional_template__$t = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$W = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$t,
  staticRenderFns: __vue_staticRenderFns__$t
}, __vue_inject_styles__$t, __vue_script__$t, __vue_scope_id__$t, __vue_is_functional_template__$t, __vue_module_identifier__$t, false, undefined, undefined, undefined);
var __vue_component__$X = __vue_component__$W;

let IconMap = class IconMap extends Vue {};
__decorate([Prop({
  default: 26
})], IconMap.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconMap.prototype, "color", void 0);
IconMap = __decorate([Component], IconMap);
var script$s = IconMap;

/* script */
const __vue_script__$s = script$s;

/* template */
var __vue_render__$s = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 16 16"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero",
      "transform": "translate(-260.000000, -201.000000) translate(194.000000, 193.000000)"
    }
  }, [_c('path', {
    attrs: {
      "d": "M16 1.333V12c0 .24-.128.46-.336.579l-4.667 2.666-.016.01-.063.03.08-.04c-.037.022-.076.039-.115.052-.023.008-.046.015-.07.02l-.03.006c-.037.007-.076.01-.116.01l.05-.002-.043.002c-.03 0-.052 0-.074-.003l-.025-.003c-.035-.005-.069-.012-.102-.022l-.033-.011c-.022-.008-.042-.017-.063-.027l-.008-.004-5.015-2.507-4.357 2.49c-.444.253-.997-.067-.997-.58V4c0-.24.128-.46.336-.579L5.003.755 5.02.744c.022-.011.043-.021.066-.03l-.084.04c.036-.02.074-.038.113-.051.02-.007.038-.012.058-.017.017-.004.033-.008.05-.01.036-.006.072-.01.11-.01L5.29.669l.028-.001h.014c.027 0 .054.001.08.004l.012.002c.035.005.069.012.102.022l.017.006c.03.01.058.021.085.035l.002.001 5.015 2.507 4.357-2.49c.444-.253.997.067.997.58zM6 2.412v9.177l4 2V4.412l-4-2zm-1.334.07L1.333 4.387v9.13l3.333-1.904V2.482zm10 0l-3.333 1.905v9.13l3.334-1.904v-9.13z",
      "transform": "translate(66.000000, 8.000000)"
    }
  })])]);
};
var __vue_staticRenderFns__$s = [];

/* style */
const __vue_inject_styles__$s = undefined;
/* scoped */
const __vue_scope_id__$s = undefined;
/* module identifier */
const __vue_module_identifier__$s = undefined;
/* functional template */
const __vue_is_functional_template__$s = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$U = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$s,
  staticRenderFns: __vue_staticRenderFns__$s
}, __vue_inject_styles__$s, __vue_script__$s, __vue_scope_id__$s, __vue_is_functional_template__$s, __vue_module_identifier__$s, false, undefined, undefined, undefined);
var __vue_component__$V = __vue_component__$U;

let IconMenu = class IconMenu extends Vue {};
__decorate([Prop({
  default: 26
})], IconMenu.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconMenu.prototype, "color", void 0);
IconMenu = __decorate([Component], IconMenu);
var script$r = IconMenu;

/* script */
const __vue_script__$r = script$r;

/* template */
var __vue_render__$r = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 18 19"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero"
    }
  }, [_c('path', {
    attrs: {
      "d": "M12.574 11.333c.42 0 .76.299.76.667 0 .368-.34.667-.76.667H6.093c-.42 0-.76-.299-.76-.667 0-.368.34-.667.76-.667zm0-4c.42 0 .76.299.76.667 0 .368-.34.667-.76.667H6.093c-.42 0-.76-.299-.76-.667 0-.368.34-.667.76-.667zm0-4c.42 0 .76.299.76.667 0 .368-.34.667-.76.667H6.093c-.42 0-.76-.299-.76-.667 0-.368.34-.667.76-.667z"
    }
  }), _vm._v(" "), _c('circle', {
    attrs: {
      "cx": "3.333",
      "cy": "4",
      "r": "1"
    }
  }), _vm._v(" "), _c('circle', {
    attrs: {
      "cx": "3.333",
      "cy": "8",
      "r": "1"
    }
  }), _vm._v(" "), _c('circle', {
    attrs: {
      "cx": "3.333",
      "cy": "12",
      "r": "1"
    }
  })])]);
};
var __vue_staticRenderFns__$r = [];

/* style */
const __vue_inject_styles__$r = undefined;
/* scoped */
const __vue_scope_id__$r = undefined;
/* module identifier */
const __vue_module_identifier__$r = undefined;
/* functional template */
const __vue_is_functional_template__$r = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$S = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$r,
  staticRenderFns: __vue_staticRenderFns__$r
}, __vue_inject_styles__$r, __vue_script__$r, __vue_scope_id__$r, __vue_is_functional_template__$r, __vue_module_identifier__$r, false, undefined, undefined, undefined);
var __vue_component__$T = __vue_component__$S;

let IconMonoClient = class IconMonoClient extends Vue {};
__decorate([Prop({
  default: 26
})], IconMonoClient.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconMonoClient.prototype, "color", void 0);
IconMonoClient = __decorate([Component], IconMonoClient);
var script$q = IconMonoClient;

/* script */
const __vue_script__$q = script$q;

/* template */
var __vue_render__$q = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 18 18"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero",
      "transform": "translate(-1052 -16)"
    }
  }, [_c('path', {
    attrs: {
      "d": "m1056.021 17 .071.013 8 2.303a.333.333 0 0 1 .241.32v11.03h1c.184 0 .334.225.334.5 0 .246-.118.45-.274.493l-.06.008h-10.666c-.184 0-.334-.224-.334-.5 0-.246.118-.45.274-.492l.06-.008 1-.001V17.333c0-.197.168-.345.354-.332zm.312.776v12.89h7.334v-10.78l-7.334-2.11zm2.334 10.824c.22 0 .4.224.4.5 0 .249-.145.455-.335.493l-.065.007H1058c-.22 0-.4-.224-.4-.5 0-.249.145-.455.335-.493l.065-.007h.667zm3.333 0c.22 0 .4.224.4.5 0 .249-.145.455-.335.493l-.065.007h-.667c-.22 0-.4-.224-.4-.5 0-.249.145-.455.335-.493l.065-.007h.667zm-3.333-2c.22 0 .4.224.4.5 0 .249-.145.455-.335.493l-.065.007H1058c-.22 0-.4-.224-.4-.5 0-.249.145-.455.335-.493l.065-.007h.667zm3.333 0c.22 0 .4.224.4.5 0 .249-.145.455-.335.493l-.065.007h-.667c-.22 0-.4-.224-.4-.5 0-.249.145-.455.335-.493l.065-.007h.667zm-3.333-2c.22 0 .4.224.4.5 0 .249-.145.455-.335.493l-.065.007H1058c-.22 0-.4-.224-.4-.5 0-.249.145-.455.335-.493l.065-.007h.667zm3.333 0c.22 0 .4.224.4.5 0 .249-.145.455-.335.493l-.065.007h-.667c-.22 0-.4-.224-.4-.5 0-.249.145-.455.335-.493l.065-.007h.667zm-3.333-2c.22 0 .4.224.4.5 0 .249-.145.455-.335.493l-.065.007H1058c-.22 0-.4-.224-.4-.5 0-.249.145-.455.335-.493l.065-.007h.667zm3.333 0c.22 0 .4.224.4.5 0 .249-.145.455-.335.493l-.065.007h-.667c-.22 0-.4-.224-.4-.5 0-.249.145-.455.335-.493l.065-.007h.667zm-3.333-2c.22 0 .4.224.4.5 0 .249-.145.455-.335.493l-.065.007H1058c-.22 0-.4-.224-.4-.5 0-.249.145-.455.335-.493l.065-.007h.667zm3.333 0c.22 0 .4.224.4.5 0 .249-.145.455-.335.493l-.065.007h-.667c-.22 0-.4-.224-.4-.5 0-.249.145-.455.335-.493l.065-.007h.667z"
    }
  })])]);
};
var __vue_staticRenderFns__$q = [];

/* style */
const __vue_inject_styles__$q = undefined;
/* scoped */
const __vue_scope_id__$q = undefined;
/* module identifier */
const __vue_module_identifier__$q = undefined;
/* functional template */
const __vue_is_functional_template__$q = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$Q = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$q,
  staticRenderFns: __vue_staticRenderFns__$q
}, __vue_inject_styles__$q, __vue_script__$q, __vue_scope_id__$q, __vue_is_functional_template__$q, __vue_module_identifier__$q, false, undefined, undefined, undefined);
var __vue_component__$R = __vue_component__$Q;

let IconMore = class IconMore extends Vue {};
__decorate([Prop({
  default: 26
})], IconMore.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconMore.prototype, "color", void 0);
IconMore = __decorate([Component], IconMore);
var script$p = IconMore;

/* script */
const __vue_script__$p = script$p;

/* template */
var __vue_render__$p = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 24 24"
    }
  }, [_c('g', {
    attrs: {
      "fill": "none",
      "stroke": _vm.color,
      "stroke-width": "2"
    }
  }, [_c('path', {
    attrs: {
      "d": "M12 7C12.55 7 13 6.55 13 6C13 5.45 12.55 5 12 5C11.45 5 11 5.45 11 6C11 6.55 11.45 7 12 7Z"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "d": "M12 13C12.55 13 13 12.55 13 12C13 11.45 12.55 11 12 11C11.45 11 11 11.45 11 12C11 12.55 11.45 13 12 13Z"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "d": "M12 19C12.55 19 13 18.55 13 18C13 17.45 12.55 17 12 17C11.45 17 11 17.45 11 18C11 18.55 11.45 19 12 19Z"
    }
  })])]);
};
var __vue_staticRenderFns__$p = [];

/* style */
const __vue_inject_styles__$p = undefined;
/* scoped */
const __vue_scope_id__$p = undefined;
/* module identifier */
const __vue_module_identifier__$p = undefined;
/* functional template */
const __vue_is_functional_template__$p = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$O = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$p,
  staticRenderFns: __vue_staticRenderFns__$p
}, __vue_inject_styles__$p, __vue_script__$p, __vue_scope_id__$p, __vue_is_functional_template__$p, __vue_module_identifier__$p, false, undefined, undefined, undefined);
var __vue_component__$P = __vue_component__$O;

let IconNotes = class IconNotes extends Vue {};
__decorate([Prop({
  default: 26
})], IconNotes.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconNotes.prototype, "color", void 0);
IconNotes = __decorate([Component], IconNotes);
var script$o = IconNotes;

/* script */
const __vue_script__$o = script$o;

/* template */
var __vue_render__$o = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 18 18"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero"
    }
  }, [_c('path', {
    attrs: {
      "d": "M3.025 11.226h12.978c.796 0 1.433-.637 1.433-1.433V3.025c0-.756-.597-1.393-1.354-1.433v-.12C16.082.638 15.406 0 14.61 0H1.473C.637 0 0 .677 0 1.473V8.36c0 .836.677 1.473 1.473 1.473h.12c.04.756.676 1.393 1.432 1.393zM.597 8.36V1.513c0-.478.398-.876.876-.876H14.57c.477 0 .875.398.875.876v6.886c0 .478-.398.876-.875.876H1.473c-.478-.04-.876-.438-.876-.915zM14.57 9.833c.836 0 1.473-.677 1.473-1.473V2.19c.437.04.756.397.756.835v6.728c0 .478-.398.836-.836.836H3.025c-.438 0-.796-.358-.836-.757h12.38z"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "d": "M13.057 2.15V1.83H3.025v.279c0 .478-.398.876-.875.876H1.87v3.821h.279c.477 0 .875.398.875.876v.279h10.032v-.28c0-.477.398-.875.876-.875h.278V3.025h-.278c-.478 0-.876-.398-.876-.875zm.597 4.14c-.597.119-1.035.557-1.154 1.154H3.583c-.12-.597-.558-1.035-1.155-1.154V3.583c.597-.12 1.035-.558 1.155-1.155H12.5c.119.597.557 1.035 1.154 1.155V6.29z"
    }
  })])]);
};
var __vue_staticRenderFns__$o = [];

/* style */
const __vue_inject_styles__$o = undefined;
/* scoped */
const __vue_scope_id__$o = undefined;
/* module identifier */
const __vue_module_identifier__$o = undefined;
/* functional template */
const __vue_is_functional_template__$o = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$M = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$o,
  staticRenderFns: __vue_staticRenderFns__$o
}, __vue_inject_styles__$o, __vue_script__$o, __vue_scope_id__$o, __vue_is_functional_template__$o, __vue_module_identifier__$o, false, undefined, undefined, undefined);
var __vue_component__$N = __vue_component__$M;

let IconPencil = class IconPencil extends Vue {};
__decorate([Prop({
  default: 26
})], IconPencil.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconPencil.prototype, "color", void 0);
IconPencil = __decorate([Component], IconPencil);
var script$n = IconPencil;

/* script */
const __vue_script__$n = script$n;

/* template */
var __vue_render__$n = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 490.273 490.273"
    }
  }, [_c('g', [_c('path', {
    attrs: {
      "d": "M313.548,152.387l-230.8,230.9c-6.7,6.7-6.7,17.6,0,24.3c3.3,3.3,7.7,5,12.1,5s8.8-1.7,12.1-5l230.8-230.8\n\t\t\t\tc6.7-6.7,6.7-17.6,0-24.3C331.148,145.687,320.248,145.687,313.548,152.387z",
      "fill": _vm.color
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "d": "M431.148,191.887c4.4,0,8.8-1.7,12.1-5l25.2-25.2c29.1-29.1,29.1-76.4,0-105.4l-34.4-34.4\n\t\t\t\tc-14.1-14.1-32.8-21.8-52.7-21.8c-19.9,0-38.6,7.8-52.7,21.8l-25.2,25.2c-6.7,6.7-6.7,17.6,0,24.3l115.6,115.6\n\t\t\t\tC422.348,190.187,426.748,191.887,431.148,191.887z M352.948,45.987c7.6-7.6,17.7-11.8,28.5-11.8c10.7,0,20.9,4.2,28.5,11.8\n\t\t\t\tl34.4,34.4c15.7,15.7,15.7,41.2,0,56.9l-13.2,13.2l-91.4-91.4L352.948,45.987z",
      "fill": _vm.color
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "d": "M162.848,467.187l243.5-243.5c6.7-6.7,6.7-17.6,0-24.3s-17.6-6.7-24.3,0l-239.3,239.5l-105.6,14.2l14.2-105.6\n\t\t\t\tl228.6-228.6c6.7-6.7,6.7-17.6,0-24.3c-6.7-6.7-17.6-6.7-24.3,0l-232.6,232.8c-2.7,2.7-4.4,6.1-4.9,9.8l-18,133.6\n\t\t\t\tc-0.7,5.3,1.1,10.6,4.9,14.4c3.2,3.2,7.6,5,12.1,5c0.8,0,1.5-0.1,2.3-0.2l133.6-18\n\t\t\t\tC156.748,471.587,160.248,469.887,162.848,467.187z",
      "fill": _vm.color
    }
  })])]);
};
var __vue_staticRenderFns__$n = [];

/* style */
const __vue_inject_styles__$n = undefined;
/* scoped */
const __vue_scope_id__$n = undefined;
/* module identifier */
const __vue_module_identifier__$n = undefined;
/* functional template */
const __vue_is_functional_template__$n = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$K = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$n,
  staticRenderFns: __vue_staticRenderFns__$n
}, __vue_inject_styles__$n, __vue_script__$n, __vue_scope_id__$n, __vue_is_functional_template__$n, __vue_module_identifier__$n, false, undefined, undefined, undefined);
var __vue_component__$L = __vue_component__$K;

let IconPlus = class IconPlus extends Vue {};
__decorate([Prop({
  default: 26
})], IconPlus.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconPlus.prototype, "color", void 0);
IconPlus = __decorate([Component], IconPlus);
var script$m = IconPlus;

/* script */
const __vue_script__$m = script$m;

/* template */
var __vue_render__$m = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 16 16"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero"
    }
  }, [_c('path', {
    attrs: {
      "d": "M7.33339 7.33333L7.33339 2.34315C7.33339 1.97496 7.63186 1.67648 8.00005 1.67648C8.36824 1.67648 8.66672 1.97496 8.66672 2.34315L8.66672 7.33333L13.6569 7.33333C14.0251 7.33333 14.3236 7.63181 14.3236 8C14.3236 8.36819 14.0251 8.66667 13.6569 8.66667L8.66672 8.66667L8.66672 13.6569C8.66672 14.025 8.36824 14.3235 8.00005 14.3235C7.63186 14.3235 7.33339 14.025 7.33339 13.6569L7.33339 8.66667L2.3432 8.66667C1.97501 8.66667 1.67653 8.36819 1.67653 8C1.67653 7.63181 1.97501 7.33333 2.3432 7.33333L7.33339 7.33333Z"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "d": "M7.33339 7.33333L7.33339 2.34315C7.33339 1.97496 7.63186 1.67648 8.00005 1.67648C8.36824 1.67648 8.66672 1.97496 8.66672 2.34315L8.66672 7.33333L13.6569 7.33333C14.0251 7.33333 14.3236 7.63181 14.3236 8C14.3236 8.36819 14.0251 8.66667 13.6569 8.66667L8.66672 8.66667L8.66672 13.6569C8.66672 14.025 8.36824 14.3235 8.00005 14.3235C7.63186 14.3235 7.33339 14.025 7.33339 13.6569L7.33339 8.66667L2.3432 8.66667C1.97501 8.66667 1.67653 8.36819 1.67653 8C1.67653 7.63181 1.97501 7.33333 2.3432 7.33333L7.33339 7.33333Z"
    }
  })])]);
};
var __vue_staticRenderFns__$m = [];

/* style */
const __vue_inject_styles__$m = undefined;
/* scoped */
const __vue_scope_id__$m = undefined;
/* module identifier */
const __vue_module_identifier__$m = undefined;
/* functional template */
const __vue_is_functional_template__$m = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$I = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$m,
  staticRenderFns: __vue_staticRenderFns__$m
}, __vue_inject_styles__$m, __vue_script__$m, __vue_scope_id__$m, __vue_is_functional_template__$m, __vue_module_identifier__$m, false, undefined, undefined, undefined);
var __vue_component__$J = __vue_component__$I;

let IconPoint = class IconPoint extends Vue {};
__decorate([Prop({
  default: 26
})], IconPoint.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconPoint.prototype, "color", void 0);
__decorate([Prop({
  default: "#ffd102"
})], IconPoint.prototype, "color2", void 0);
IconPoint = __decorate([Component], IconPoint);
var script$l = IconPoint;

/* script */
const __vue_script__$l = script$l;

/* template */
var __vue_render__$l = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 80 80"
    }
  }, [_c('g', {
    attrs: {
      "id": "g89",
      "fill": "none",
      "fill-rule": "evenodd"
    }
  }, [_c('path', {
    attrs: {
      "id": "path65",
      "stroke": "#323232",
      "stroke-width": "2",
      "fill": "#D8D8D8",
      "stroke-linejoin": "round",
      "d": "M12 13h24v14H12z"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "id": "path67",
      "d": "M25.036 21.856c0-.287-.077-.516-.23-.686-.154-.17-.415-.327-.783-.47a9.062 9.062 0 0 1-.953-.43 3.033 3.033 0 0 1-.686-.488c-.19-.184-.34-.4-.446-.65a2.251 2.251 0 0 1-.16-.888c0-.592.183-1.078.548-1.456.364-.379.85-.6 1.454-.663V15h.81v1.14c.598.089 1.066.347 1.403.776.338.43.507.986.507 1.67h-1.464c0-.42-.084-.735-.25-.944a.816.816 0 0 0-.672-.313c-.277 0-.491.082-.643.245-.152.163-.228.388-.228.675 0 .267.074.48.223.642.148.16.424.326.828.494.403.168.735.327.995.476.26.149.48.317.659.504.179.188.316.402.41.642.095.24.142.52.142.838 0 .596-.179 1.08-.537 1.452-.358.371-.851.59-1.48.657V25h-.805v-1.041c-.692-.077-1.228-.332-1.608-.765-.38-.433-.57-1.009-.57-1.727h1.464c0 .417.095.737.286.96.191.222.465.333.823.333.298 0 .532-.081.705-.244.172-.163.258-.383.258-.66z",
      "fill": "#323232",
      "fill-rule": "nonzero"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "id": "path69",
      "d": "M56.5 48v4m-.5 2.877V57H8V31h48v14.604",
      "stroke": "#323232",
      "stroke-width": "2",
      "fill": _vm.color2,
      "stroke-linejoin": "round"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "id": "path71",
      "stroke": "#323232",
      "stroke-width": "2",
      "stroke-linejoin": "round",
      "d": "M14 36h13v12H14z"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "id": "path73",
      "fill": "#323232",
      "d": "M16 38h2v2h-2zM16 41h2v2h-2zM19.5 38h2v2h-2zM19.5 41h2v2h-2zM23 38h2v2h-2zM28 19h2v2h-2zM18 19h2v2h-2zM23 41h2v2h-2zM16 44h2v2h-2zM19.5 44h2v2h-2zM23 44h2v2h-2z"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "id": "path75",
      "stroke": "#323232",
      "stroke-width": "2",
      "fill": "#D8D8D8",
      "stroke-linejoin": "round",
      "d": "M36.088 41H32v-5h17v5h-3.905"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "id": "path77",
      "stroke": "#323232",
      "stroke-width": "2",
      "fill": "#FFF",
      "stroke-linejoin": "round",
      "d": "M35 36h11v17H35z"
    }
  }), _vm._v(" "), _c('circle', {
    attrs: {
      "id": "circle79",
      "stroke": "#323232",
      "stroke-width": "2",
      "fill": "#D8D8D8",
      "stroke-linejoin": "round",
      "cx": "40.5",
      "cy": "44.5",
      "r": "2.5"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "id": "path81",
      "stroke": "#323232",
      "stroke-width": "2",
      "stroke-linejoin": "round",
      "d": "M39 50h-1M43 39h-1"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "id": "path83",
      "d": "m46.96 16.727-1.984.917.907 1.93 2.378-1.098a7.542 7.542 0 0 0-1.301-1.749zm.064-.33 1.76-.821c-1.131-.747-2.517-.96-3.914-.619a7.587 7.587 0 0 1 2.154 1.44zm-2.165 1.002 1.887-.874a7.336 7.336 0 0 0-2.026-1.333 2.957 2.957 0 0 0-.277-.107 6.405 6.405 0 0 0-.576.224l.992 2.09zm4.319 4.234-1.909.885 1.002 2.133c.214-.096.416-.214.608-.342.022-.064.043-.128.064-.202.214-.81.299-1.642.235-2.474zm1.973-.917-1.706.789a7.683 7.683 0 0 1-.214 2.549 5.136 5.136 0 0 0 1.92-3.338zm-1.738.5 1.77-.82a5.008 5.008 0 0 0-.341-2.272l-2.005.928c.298.693.49 1.429.576 2.165zm-2.261 1.057 2.005-.928a8.058 8.058 0 0 0-.555-2.176l-2.389 1.11.939 1.994zm-4.607-3.797a7.16 7.16 0 0 1 .256-2.517 5.188 5.188 0 0 0-1.93 3.296c0-.011 1.674-.779 1.674-.779zm.554 2.389a8.004 8.004 0 0 1-.533-2.1l-1.727.799a5.088 5.088 0 0 0 .298 2.207l1.962-.906zm1.621 2.527a8.47 8.47 0 0 1-1.3-1.823l-1.974.917-.735.341a2.599 2.599 0 0 1-.15-.298 5.952 5.952 0 0 1 2.9-7.924c2.624-1.216 5.514-.565 7.178 1.642l-.747.363a4.965 4.965 0 0 0-.863-.843l-1.813.843c.512.523.938 1.109 1.28 1.749l2.484-1.152.256-.106a5.952 5.952 0 0 1-2.698 8.318c-2.623 1.215-5.513.586-7.188-1.632l.736-.373c.246.298.523.576.821.82l1.813-.842zm2.325 1.685a7.333 7.333 0 0 1-2.111-1.482l-1.75.81c1.11.758 2.475.992 3.861.672zm-1.855-1.6a7.638 7.638 0 0 0 2.068 1.408l.203.086c.192-.064.373-.128.565-.203l-1.002-2.133-1.834.843zm-1.515-2.026c.352.662.79 1.28 1.312 1.824l1.92-.885-.939-1.994-2.293 1.055zm-.842-2.815a7.94 7.94 0 0 0 .512 2.112l2.292-1.056-.906-1.93-1.898.874zm.341-2.943c-.021.064-.053.16-.085.266a7.02 7.02 0 0 0-.278 2.4l1.803-.832-.981-2.09c-.16.064-.31.16-.459.256z",
      "fill": "#323232",
      "fill-rule": "nonzero"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "id": "path85",
      "d": "M56 41.092v7.012m0 4.977V57H8V9h48v26.961",
      "stroke": "#323232",
      "stroke-width": "2",
      "stroke-linejoin": "round"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "id": "path87",
      "stroke": "#323232",
      "stroke-width": "2",
      "fill": "#FFF",
      "stroke-linejoin": "round",
      "d": "M54 36h4v5h-4zM54 48h4v5h-4z"
    }
  })]), _vm._v(" "), _c('g', {
    attrs: {
      "id": "g385",
      "transform": "translate(-8.5430053,-7.9469817)"
    }
  }, [_c('g', {
    attrs: {
      "id": "g375"
    }
  }, [_c('g', {
    attrs: {
      "id": "g97",
      "fill": "#ffffff",
      "fill-rule": "nonzero",
      "transform": "matrix(1.2,0,0,1.2,53.701945,52.350958)"
    }
  }, [_c('g', {
    attrs: {
      "id": "g95",
      "fill": "#292d32"
    }
  }, [_c('path', {
    attrs: {
      "id": "path91",
      "d": "M 9.94,21.65 8.93,20.3 C 8.72,20.02 8.44,19.86 8.14,19.84 7.84,19.83 7.54,19.96 7.3,20.21 L 6.73,19.7 7.28,20.21 C 5.84,21.75 4.73,21.63 4.2,21.42 3.66,21.21 2.75,20.52 2.75,18.3 V 7.04 C 2.75,2.6 4.03,1.25 8.22,1.25 h 7.56 c 4.19,0 5.47,1.35 5.47,5.79 V 18.3 c 0,2.21 -0.91,2.9 -1.45,3.12 -0.53,0.21 -1.63,0.33 -3.08,-1.21 -0.24,-0.26 -0.54,-0.4 -0.85,-0.37 -0.3,0.02 -0.59,0.18 -0.8,0.46 l -1.01,1.35 c -0.52,0.7 -1.28,1.1 -2.06,1.1 -0.78,0 -1.54,-0.4 -2.06,-1.1 z M 8.21,18.33 c 0.74,0.04 1.44,0.43 1.91,1.06 l 1.01,1.35 c 0.49,0.65 1.24,0.65 1.73,0 l 1.01,-1.35 c 0.48,-0.63 1.17,-1.02 1.92,-1.06 0.74,-0.04 1.48,0.27 2.02,0.85 0.76,0.81 1.25,0.91 1.43,0.84 0.24,-0.1 0.5,-0.68 0.5,-1.72 V 7.04 c 0,-3.61 -0.63,-4.29 -3.97,-4.29 H 8.22 C 4.88,2.75 4.25,3.43 4.25,7.04 V 18.3 c 0,1.05 0.26,1.63 0.5,1.72 0.17,0.07 0.67,-0.03 1.43,-0.84 0.54,-0.55 1.21,-0.85 1.9,-0.85 z"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "id": "path93",
      "d": "m 10.73,11.75 c -0.41,0 -0.75,-0.34 -0.75,-0.75 0,-0.41 0.34,-0.75 0.75,-0.75 h 5.5 c 0.41,0 0.75,0.34 0.75,0.75 0,0.41 -0.34,0.75 -0.75,0.75 z m 0,-4 C 10.32,7.75 9.98,7.41 9.98,7 c 0,-0.41 0.34,-0.75 0.75,-0.75 h 5.5 c 0.41,0 0.75,0.34 0.75,0.75 0,0.41 -0.34,0.75 -0.75,0.75 z M 6.78,7 c 0,-0.55 0.45,-1 1,-1 0.55,0 1,0.45 1,1 0,0.55 -0.45,1 -1,1 -0.55,0 -1,-0.45 -1,-1 z m 0,4 c 0,-0.55 0.45,-1 1,-1 0.55,0 1,0.45 1,1 0,0.55 -0.45,1 -1,1 -0.55,0 -1,-0.45 -1,-1 z",
      "fill-rule": "nonzero"
    }
  })])])]), _vm._v(" "), _c('path', {
    staticStyle: {
      "fill": "#ffffff",
      "stroke": "#ffffff",
      "stroke-width": "0.0953638"
    },
    attrs: {
      "id": "path377",
      "d": "m 54.261991,62.211035 c -0.323139,-0.09865 -0.45473,-0.233286 -1.202843,-1.230678 -0.472044,-0.629333 -0.619263,-0.802636 -0.81686,-0.961595 -0.273881,-0.220326 -0.663782,-0.418352 -0.970393,-0.492853 -0.300409,-0.07299 -0.81492,-0.06679 -1.129302,0.01361 -0.49921,0.127667 -0.780746,0.297315 -1.284549,0.774043 -0.707047,0.669049 -1.169042,0.906709 -1.403724,0.722108 -0.176407,-0.138761 -0.326511,-0.598837 -0.376589,-1.154265 -0.03965,-0.439719 -0.03948,-11.162015 1.85e-4,-12.049539 0.06389,-1.429427 0.250074,-2.084406 0.730464,-2.56966 0.338236,-0.34166 0.813004,-0.539359 1.569961,-0.65375 0.315326,-0.04765 0.833417,-0.05482 4.756498,-0.06581 4.475421,-0.01254 5.054177,-0.0017 5.674145,0.105884 1.202007,0.208656 1.751443,0.779088 1.966808,2.041968 0.111403,0.653258 0.119236,1.184669 0.108563,7.364875 l -0.01049,6.071494 -0.07043,0.262454 c -0.125874,0.469094 -0.290283,0.707077 -0.488479,0.707077 -0.264497,0 -0.650677,-0.245947 -1.170759,-0.745625 -0.483038,-0.464086 -0.698127,-0.609869 -1.112497,-0.754028 -0.58039,-0.201918 -1.292591,-0.161551 -1.818003,0.103045 -0.512514,0.258099 -0.672207,0.416419 -1.411004,1.398875 -0.336194,0.447072 -0.670608,0.859861 -0.743142,0.91731 -0.238712,0.189063 -0.559907,0.267619 -0.797569,0.195063 z m -3.488204,-8.857387 c 0.638319,-0.217497 0.835071,-1.110705 0.351239,-1.594537 -0.595631,-0.595631 -1.602529,-0.222823 -1.656182,0.613208 -0.02114,0.329487 0.04959,0.520722 0.276563,0.747695 0.111314,0.111313 0.231306,0.196711 0.320737,0.228267 0.186569,0.06583 0.52271,0.06838 0.707643,0.0054 z m 8.010101,-0.2178 c 0.515588,-0.183995 0.655466,-0.854382 0.25715,-1.232424 -0.203023,-0.192691 -0.06295,-0.184467 -3.141969,-0.184467 -3.073548,0 -2.91409,-0.0095 -3.13808,0.187156 -0.21952,0.192741 -0.293951,0.586792 -0.159789,0.845952 0.09261,0.178896 0.261419,0.330489 0.424733,0.381419 0.203635,0.0635 5.580444,0.06571 5.757955,0.0024 z m -7.946407,-3.650316 c 0.215126,-0.09035 0.45312,-0.359095 0.519148,-0.586223 0.141365,-0.48628 -0.07383,-0.970247 -0.525618,-1.182098 -0.241254,-0.113128 -0.579889,-0.113999 -0.812708,-0.0021 -0.44635,0.214547 -0.666574,0.744352 -0.505682,1.216546 0.06164,0.180891 0.293795,0.446576 0.467787,0.535341 0.253058,0.1291 0.577116,0.136104 0.857073,0.01852 z m 8.083107,-0.262768 c 0.09643,-0.05962 0.192175,-0.155373 0.251797,-0.25181 0.236856,-0.383106 0.02838,-0.945155 -0.396508,-1.068982 -0.07303,-0.02128 -1.077874,-0.02917 -2.92449,-0.02294 -2.781707,0.0094 -2.814656,0.01023 -2.940383,0.07586 -0.51395,0.268291 -0.498712,1.038113 0.02572,1.29945 l 0.163593,0.08152 2.832338,-0.0084 2.832338,-0.0084 z",
      "transform": "scale(1.25)"
    }
  })])]);
};
var __vue_staticRenderFns__$l = [];

/* style */
const __vue_inject_styles__$l = undefined;
/* scoped */
const __vue_scope_id__$l = undefined;
/* module identifier */
const __vue_module_identifier__$l = undefined;
/* functional template */
const __vue_is_functional_template__$l = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$G = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$l,
  staticRenderFns: __vue_staticRenderFns__$l
}, __vue_inject_styles__$l, __vue_script__$l, __vue_scope_id__$l, __vue_is_functional_template__$l, __vue_module_identifier__$l, false, undefined, undefined, undefined);
var __vue_component__$H = __vue_component__$G;

let IconPrint = class IconPrint extends Vue {};
__decorate([Prop({
  default: 26
})], IconPrint.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconPrint.prototype, "color", void 0);
IconPrint = __decorate([Component], IconPrint);
var script$k = IconPrint;

/* script */
const __vue_script__$k = script$k;

/* template */
var __vue_render__$k = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 18 18"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero"
    }
  }, [_c('path', {
    attrs: {
      "d": "M15.333 13h-2c-.184 0-.333-.15-.333-.333 0-.184.15-.334.333-.334h2c.552 0 1-.448 1-1V6.667c0-.552-.448-1-1-1H2.667c-.552 0-1 .448-1 1v4.666c0 .552.448 1 1 1h2c.184 0 .333.15.333.334 0 .184-.15.333-.333.333h-2C1.747 13 1 12.252 1 11.333V6.667C1 5.748 1.747 5 2.667 5h12.666C16.253 5 17 5.748 17 6.667v4.666c0 .919-.747 1.667-1.667 1.667zM10.667 15h-4c-.184 0-.334-.15-.334-.333 0-.184.15-.334.334-.334h4c.184 0 .333.15.333.334 0 .184-.15.333-.333.333zM10.667 13.667h-4c-.184 0-.334-.15-.334-.334 0-.184.15-.333.334-.333h4c.184 0 .333.15.333.333 0 .184-.15.334-.333.334z",
      "transform": "translate(-1328 -710) translate(1328 710)"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "d": "M13.333 5.667c-.184 0-.333-.15-.333-.334V2.667c0-.552-.449-1-1-1H6c-.551 0-1 .448-1 1v2.666c0 .184-.15.334-.333.334-.184 0-.334-.15-.334-.334V2.667C4.333 1.748 5.081 1 6 1h6c.92 0 1.667.748 1.667 1.667v2.666c0 .184-.15.334-.334.334zM12 17H6c-.92 0-1.667-.748-1.667-1.667V10c0-.184.15-.333.334-.333h8.666c.184 0 .334.149.334.333v5.333c0 .919-.748 1.667-1.667 1.667zm-7-6.667v5c0 .552.449 1 1 1h6c.551 0 1-.448 1-1v-5H5z",
      "transform": "translate(-1328 -710) translate(1328 710)"
    }
  })])]);
};
var __vue_staticRenderFns__$k = [];

/* style */
const __vue_inject_styles__$k = undefined;
/* scoped */
const __vue_scope_id__$k = undefined;
/* module identifier */
const __vue_module_identifier__$k = undefined;
/* functional template */
const __vue_is_functional_template__$k = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$E = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$k,
  staticRenderFns: __vue_staticRenderFns__$k
}, __vue_inject_styles__$k, __vue_script__$k, __vue_scope_id__$k, __vue_is_functional_template__$k, __vue_module_identifier__$k, false, undefined, undefined, undefined);
var __vue_component__$F = __vue_component__$E;

let IconProsegur = class IconProsegur extends Vue {};
__decorate([Prop({
  default: 26
})], IconProsegur.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconProsegur.prototype, "color", void 0);
IconProsegur = __decorate([Component], IconProsegur);
var script$j = IconProsegur;

/* script */
const __vue_script__$j = script$j;

/* template */
var __vue_render__$j = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 100 100"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero"
    }
  }, [_c('path', {
    attrs: {
      "d": "m 54.4513 22.1854 l -15.3 7.1 l 7 14.9 l 18.3 -8.5 c -2.7 -4.9 -6.1 -9.5 -10 -13.5 z m 0.5 -2.5 l 13.6 -6.3 c -8.7 -5.8 -19.4 -7.5 -30.1 -4.8 c 3.9 1.7 10.2 5.1 16.5 11.1 z m -16.6 7.7 l 14.5 -6.8 c -4.6 -4.3 -9.8 -7.8 -15.6 -10.3 c -0.9 -0.4 -1.6 -0.7 -2.1 -0.9 c -1.5 0.5 -2.9 1.1 -4.4 1.7 z m 33.2 32.8 l -14.7 6.9 l 7.7 16.5 c 1.6 -0.8 3.2 -1.7 4.6 -2.6 c 0.1 -0.5 0.3 -1 0.5 -1.6 c 1.7 -6.3 2.3 -12.8 1.9 -19.2 z m 15.1 -7.1 l -13.1 6.1 c 0.7 8.5 -0.5 15.4 -1.6 19.7 c 8.1 -6.4 13.3 -15.8 14.7 -25.8 z m -13.3 3.9 l 13.6 -6.4 c 0.4 -5.8 -0.4 -11.8 -2.6 -17.6 l -15.4 7.2 c 2.4 6 3.8 11.6 4.4 16.8 z m -17.4 8.1 l 15.4 -7.2 c -0.6 -5.8 -2.1 -11.4 -4.3 -16.8 l -18.3 8.5 z m -35.5 -29.4 c -0.5 -6.6 0.2 -13.2 2 -19.5 c -8.1 6.4 -13.3 15.6 -14.8 25.5 z m 4.3 18.5 c -2.1 -5.2 -3.5 -10.7 -4.1 -16.3 l -13.3 6.2 c -0.5 5.7 0.2 11.5 2.3 17.1 z m 12.5 19.6 c -4 -4.2 -7.3 -9 -10 -14.1 l -15.1 7.1 l -5.7 2.6 c -0.4 -0.7 -0.8 -1.5 -1.1 -2.3 c -10.8 -23.1 -0.8 -50.6 22.3 -61.4 c 20.1 -9.4 42.4 -4.4 55.2 12.7 l -5.7 2.8 c -2 -2.5 -4.2 -4.7 -6.7 -6.5 l -13.9 6.5 c 3.5 3.6 6.9 8.1 9.9 13.6 l 19.1 -8.9 l 2 -0.9 c 0.6 1 1.1 2 1.6 3 c 10.8 23.1 0.8 50.6 -22.3 61.4 c -20.2 9.4 -42.4 4.5 -55.2 -12.7 l 5.7 -2.9 c 1.9 2.4 4 4.5 6.3 6.3 z m 17.8 13.1 c -6 -2.8 -11.5 -6.8 -16.3 -11.5 l -13.4 6.3 c 8.6 5.8 19.1 7.6 29.7 5.2 z m -14.2 -12.4 c 4.6 4.5 10 8.2 15.9 10.9 c 0.6 0.3 1.1 0.5 1.6 0.7 c 1.4 -0.4 2.9 -1 4.3 -1.6 l -7.7 -16.5 z m -11.7 -15.7 c 2.7 5.1 6.1 9.9 10.1 14.1 l 14.8 -6.9 l -7.2 -15.4 z m -6.4 -21.8 c 0.5 5.6 1.9 11.1 4 16.3 l 17.6 -8.2 l -7 -14.9 z m 2.6 -22.8 c -0.2 0.5 -0.4 1.2 -0.7 2.1 c -1.8 6 -2.5 12.3 -2.1 18.5 l 13.9 -6.5 l -7.5 -16.2 c -1.3 0.7 -2.5 1.3 -3.6 2.1 z"
    }
  })])]);
};
var __vue_staticRenderFns__$j = [];

/* style */
const __vue_inject_styles__$j = undefined;
/* scoped */
const __vue_scope_id__$j = undefined;
/* module identifier */
const __vue_module_identifier__$j = undefined;
/* functional template */
const __vue_is_functional_template__$j = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$C = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$j,
  staticRenderFns: __vue_staticRenderFns__$j
}, __vue_inject_styles__$j, __vue_script__$j, __vue_scope_id__$j, __vue_is_functional_template__$j, __vue_module_identifier__$j, false, undefined, undefined, undefined);
var __vue_component__$D = __vue_component__$C;

let IconSettingAlerts = class IconSettingAlerts extends Vue {};
__decorate([Prop({
  default: 26
})], IconSettingAlerts.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconSettingAlerts.prototype, "color", void 0);
IconSettingAlerts = __decorate([Component], IconSettingAlerts);
var script$i = IconSettingAlerts;

/* script */
const __vue_script__$i = script$i;

/* template */
var __vue_render__$i = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "-0.4 -0.5 17 17"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero"
    }
  }, [_c('path', {
    attrs: {
      "d": "M9.153 13.333c.514 0 .835.557.577 1.002-.358.616-1.017.996-1.73.996-.713 0-1.372-.38-1.73-.996-.258-.445.063-1.002.577-1.002h2.306zM8 .667c1.016 0 1.965.284 2.773.776-.558.14-1.064.407-1.487.768C8.883 2.074 8.45 2 8 2 5.79 2 4 3.79 4 6v3.333c0 .486-.13.941-.357 1.334h8.714C12.13 10.274 12 9.819 12 9.333v-.681c.477-.043.927-.177 1.333-.385v1.066c0 .737.597 1.334 1.334 1.334.889 0 .889 1.333 0 1.333H1.333c-.889 0-.889-1.333 0-1.333.737 0 1.334-.597 1.334-1.334V6C2.667 3.054 5.054.667 8 .667zM11.079 2l.054.083c.124.188.283.312.455.354.053.013.096.02.137.02.028-.001.053-.005.114-.018.178-.037.342-.156.469-.34l.056-.082.097.026c.263.07.516.175.755.314l.086.05-.018.098c-.041.223-.009.425.092.575.023.036.045.063.068.086.027.027.058.051.096.076.153.098.356.127.58.082l.098-.02.051.085c.145.235.256.483.33.738l.03.102-.089.057c-.167.108-.285.246-.343.397-.03.078-.045.144-.045.208 0 .078.02.158.061.247.067.147.193.278.365.379l.089.052-.023.101c-.058.26-.147.508-.267.739l-.053.103-.112-.032c-.169-.049-.332-.056-.475-.024-.12.028-.223.082-.302.162-.09.09-.149.21-.171.35-.024.149-.008.315.049.484l.036.107-.096.059c-.221.136-.454.242-.693.317l-.118.036-.052-.111c-.072-.151-.168-.276-.283-.369-.125-.101-.259-.154-.394-.153-.146 0-.29.06-.42.173-.109.094-.202.223-.272.38l-.05.109-.116-.031c-.243-.066-.479-.162-.7-.287l-.107-.06.043-.114c.06-.158.087-.314.078-.457-.01-.173-.07-.318-.176-.424-.1-.101-.238-.16-.402-.174-.143-.013-.3.011-.458.07l-.117.043-.058-.11c-.116-.221-.204-.46-.265-.711l-.026-.111.102-.05c.156-.076.284-.18.377-.305.091-.122.139-.248.14-.376 0-.114-.038-.228-.11-.339-.081-.122-.202-.228-.355-.31l-.104-.056.035-.112c.079-.248.19-.485.329-.707l.056-.09.102.029c.185.05.363.052.513.006.099-.03.183-.08.25-.148.058-.058.102-.127.133-.207.057-.145.065-.32.022-.508l-.024-.105.095-.051c.235-.126.488-.22.755-.283L11.079 2zm.588 2c-.553 0-1 .448-1 1s.447 1 1 1c.552 0 1-.448 1-1s-.448-1-1-1z"
    }
  })])]);
};
var __vue_staticRenderFns__$i = [];

/* style */
const __vue_inject_styles__$i = undefined;
/* scoped */
const __vue_scope_id__$i = undefined;
/* module identifier */
const __vue_module_identifier__$i = undefined;
/* functional template */
const __vue_is_functional_template__$i = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$A = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$i,
  staticRenderFns: __vue_staticRenderFns__$i
}, __vue_inject_styles__$i, __vue_script__$i, __vue_scope_id__$i, __vue_is_functional_template__$i, __vue_module_identifier__$i, false, undefined, undefined, undefined);
var __vue_component__$B = __vue_component__$A;

let IconSettings = class IconSettings extends Vue {};
__decorate([Prop({
  default: 26
})], IconSettings.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconSettings.prototype, "color", void 0);
IconSettings = __decorate([Component], IconSettings);
var script$h = IconSettings;

/* script */
const __vue_script__$h = script$h;

/* template */
var __vue_render__$h = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "-1 -1 19 19"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color
    }
  }, [_c('path', {
    attrs: {
      "d": "M8,0 C9.1045695,0 10,0.8954305 10,2 L10,2.05734113 C10.0006835,2.23005543 10.1038711,2.38586865 10.2691769,2.45675828 C10.431241,2.52828361 10.6205218,2.49396346 10.7419288,2.37526215 L10.7816667,2.33552444 C11.1568032,1.95997035 11.6658482,1.74895112 12.1966667,1.74895112 C12.7274852,1.74895112 13.2365302,1.95997035 13.6111422,2.335 C13.9866963,2.71013651 14.1977155,3.2191815 14.1977155,3.75 C14.1977155,4.2808185 13.9866963,4.78986349 13.6114045,5.16473785 L13.5765751,5.19950997 C13.4527032,5.32614487 13.4183831,5.51542571 13.4899084,5.67748977 C13.50876,5.72020431 13.5230206,5.76466258 13.5325324,5.81011646 C13.6117755,5.92693737 13.7442249,5.99942745 13.8866667,6 L14,6 C15.1045695,6 16,6.8954305 16,8 C16,9.1045695 15.1045695,10 14,10 L13.9426589,10 C13.7699446,10.0006835 13.6141314,10.1038711 13.5460932,10.262621 C13.4717164,10.431241 13.5060365,10.6205218 13.6247379,10.7419288 L13.6644756,10.7816667 C14.0400296,11.1568032 14.2510489,11.6658482 14.2510489,12.1966667 C14.2510489,12.7274852 14.0400296,13.2365302 13.665,13.6111422 C13.2898635,13.9866963 12.7808185,14.1977155 12.25,14.1977155 C11.7191815,14.1977155 11.2101365,13.9866963 10.8352621,13.6114045 L10.80049,13.5765751 C10.6738551,13.4527032 10.4845743,13.4183831 10.3159543,13.4927599 C10.1572044,13.560798 10.0540169,13.7166112 10.0533333,13.8866667 L10.0533333,14 C10.0533333,15.1045695 9.15790283,16 8.05333333,16 C6.94876383,16 6.05333333,15.1045695 6.05333333,14 C6.04935475,13.7777053 5.9368759,13.6204432 5.73082311,13.5432417 C5.56875905,13.4717164 5.37947821,13.5060365 5.25807119,13.6247379 L5.21833333,13.6644756 C4.84319682,14.0400296 4.33415183,14.2510489 3.80333333,14.2510489 C3.27251484,14.2510489 2.76346985,14.0400296 2.38885777,13.665 C2.01330368,13.2898635 1.80228446,12.7808185 1.80228446,12.25 C1.80228446,11.7191815 2.01330368,11.2101365 2.38859548,10.8352621 L2.42342494,10.80049 C2.54729679,10.6738551 2.58161694,10.4845743 2.50724012,10.3159543 C2.43920198,10.1572044 2.28338876,10.0540169 2.11333333,10.0533333 L2,10.0533333 C0.8954305,10.0533333 0,9.15790283 0,8.05333333 C0,6.94876383 0.8954305,6.05333333 2,6.05333333 C2.22229471,6.04935475 2.37955679,5.9368759 2.45675828,5.73082311 C2.52828361,5.56875905 2.49396346,5.37947821 2.37526215,5.25807119 L2.33552444,5.21833333 C1.95997035,4.84319682 1.74895112,4.33415183 1.74895112,3.80333333 C1.74895112,3.27251484 1.95997035,2.76346985 2.335,2.38885777 C2.71013651,2.01330368 3.2191815,1.80228446 3.75,1.80228446 C4.2808185,1.80228446 4.78986349,2.01330368 5.16473785,2.38859548 L5.19950997,2.42342494 C5.32614487,2.54729679 5.51542571,2.58161694 5.67748977,2.51009161 C5.72020431,2.49123998 5.76466258,2.47697941 5.81011646,2.46746764 C5.92693737,2.3882245 5.99942745,2.25577513 6,2.11333333 L6,2 C6,0.8954305 6.8954305,0 8,0 Z M8,1 C7.61340068,1 7.3,1.31340068 7.3,1.7 L7.29999443,1.82179182 C7.29704565,2.56114189 6.85532351,3.22814232 6.17575201,3.51939788 C6.11660193,3.54474884 6.0543747,3.56176448 5.99085015,3.57007946 C5.32930246,3.80198663 4.58724087,3.64107002 4.07502525,3.13997475 L4.03275,3.09769934 C3.90145222,2.96625541 3.72328647,2.89239868 3.5375,2.89239868 C3.35171353,2.89239868 3.17354778,2.96625541 3.04169934,3.09825 C2.91025541,3.22954778 2.83639868,3.40771353 2.83639868,3.5935 C2.83639868,3.77928647 2.91025541,3.95745222 3.04197475,4.08902525 L3.08940381,4.13651453 C3.6196706,4.67860931 3.76658725,5.4888769 3.47697527,6.14162676 C3.21405412,6.85650078 2.54085141,7.33799676 1.763,7.356 L1.7,7.356 C1.31340068,7.356 1,7.66940068 1,8.056 C1,8.44259932 1.31340068,8.756 1.7,8.756 L1.82179182,8.75600557 C2.56114189,8.75895435 3.22814232,9.20067649 3.51640381,9.87336426 C3.82258725,10.5671231 3.6756706,11.3773907 3.13997475,11.9249747 L3.09769934,11.96725 C2.96625541,12.0985478 2.89239868,12.2767135 2.89239868,12.4625 C2.89239868,12.6482865 2.96625541,12.8264522 3.09825,12.9583007 C3.22954778,13.0897446 3.40771353,13.1636013 3.5935,13.1636013 C3.77928647,13.1636013 3.95745222,13.0897446 4.08902525,12.9580253 L4.13651453,12.9105962 C4.67860931,12.3803294 5.4888769,12.2334128 6.14162676,12.5230247 C6.85650078,12.7859459 7.33799676,13.4591486 7.356,14.237 L7.356,14.3 C7.356,14.6865993 7.66940068,15 8.056,15 C8.44259932,15 8.756,14.6865993 8.756,14.3 L8.75600557,14.1782082 C8.75895435,13.4388581 9.20067649,12.7718577 9.87336426,12.4835962 C10.5671231,12.1774128 11.3773907,12.3243294 11.9249747,12.8600253 L11.96725,12.9023007 C12.0985478,13.0337446 12.2767135,13.1076013 12.4625,13.1076013 C12.6482865,13.1076013 12.8264522,13.0337446 12.9583007,12.90175 C13.0897446,12.7704522 13.1636013,12.5922865 13.1636013,12.4065 C13.1636013,12.2207135 13.0897446,12.0425478 12.9580253,11.9109747 L12.9105962,11.8634855 C12.3803294,11.3213907 12.2334128,10.5111231 12.5395962,9.81736426 C12.8278577,9.14467649 13.4948581,8.70295435 14.237,8.7 L14.3,8.7 C14.6865993,8.7 15,8.38659932 15,8 C15,7.61340068 14.6865993,7.3 14.3,7.3 L14.1782082,7.29999443 C13.4388581,7.29704565 12.7718577,6.85532351 12.4806021,6.17575201 C12.4552512,6.11660193 12.4382355,6.0543747 12.4299205,5.99085015 C12.1980134,5.32930246 12.35893,4.58724087 12.8600253,4.07502525 L12.9023007,4.03275 C13.0337446,3.90145222 13.1076013,3.72328647 13.1076013,3.5375 C13.1076013,3.35171353 13.0337446,3.17354778 12.90175,3.04169934 C12.7704522,2.91025541 12.5922865,2.83639868 12.4065,2.83639868 C12.2207135,2.83639868 12.0425478,2.91025541 11.9109747,3.04197475 L11.8634855,3.08940381 C11.3213907,3.6196706 10.5111231,3.76658725 9.82424799,3.46339788 C9.14467649,3.17214232 8.70295435,2.50514189 8.7,1.763 L8.7,1.7 C8.7,1.31340068 8.38659932,1 8,1 Z M8,5.33333333 C9.47275933,5.33333333 10.6666667,6.52724067 10.6666667,8 C10.6666667,9.47275933 9.47275933,10.6666667 8,10.6666667 C6.52724067,10.6666667 5.33333333,9.47275933 5.33333333,8 C5.33333333,6.52724067 6.52724067,5.33333333 8,5.33333333 Z M8,9.33333333 C8.73637967,9.33333333 9.33333333,8.73637967 9.33333333,8 C9.33333333,7.26362033 8.73637967,6.66666667 8,6.66666667 C7.26362033,6.66666667 6.66666667,7.26362033 6.66666667,8 C6.66666667,8.73637967 7.26362033,9.33333333 8,9.33333333 Z"
    }
  })])]);
};
var __vue_staticRenderFns__$h = [];

/* style */
const __vue_inject_styles__$h = undefined;
/* scoped */
const __vue_scope_id__$h = undefined;
/* module identifier */
const __vue_module_identifier__$h = undefined;
/* functional template */
const __vue_is_functional_template__$h = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$y = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$h,
  staticRenderFns: __vue_staticRenderFns__$h
}, __vue_inject_styles__$h, __vue_script__$h, __vue_scope_id__$h, __vue_is_functional_template__$h, __vue_module_identifier__$h, false, undefined, undefined, undefined);
var __vue_component__$z = __vue_component__$y;

let IconSmartCash = class IconSmartCash extends Vue {};
__decorate([Prop({
  default: 26
})], IconSmartCash.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconSmartCash.prototype, "color", void 0);
IconSmartCash = __decorate([Component], IconSmartCash);
var script$g = IconSmartCash;

/* script */
const __vue_script__$g = script$g;

/* template */
var __vue_render__$g = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 80 80"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero"
    }
  }, [_c('rect', {
    attrs: {
      "x": "40.8",
      "y": "48.4",
      "width": "17",
      "height": "3.4"
    }
  }), _vm._v(" "), _c('rect', {
    attrs: {
      "x": "47.6",
      "y": "55.2",
      "width": "3.4",
      "height": "3.4"
    }
  }), _vm._v(" "), _c('rect', {
    attrs: {
      "x": "54.4",
      "y": "55.2",
      "width": "3.4",
      "height": "3.4"
    }
  }), _vm._v(" "), _c('rect', {
    attrs: {
      "x": "47.5",
      "y": "62",
      "width": "3.4",
      "height": "3.4"
    }
  }), _vm._v(" "), _c('rect', {
    attrs: {
      "x": "54.3",
      "y": "62",
      "width": "3.4",
      "height": "3.4"
    }
  }), _vm._v(" "), _c('rect', {
    attrs: {
      "x": "40.8",
      "y": "55.2",
      "width": "3.4",
      "height": "3.4"
    }
  }), _vm._v(" "), _c('rect', {
    attrs: {
      "x": "40.8",
      "y": "62",
      "width": "3.4",
      "height": "3.4"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "d": "M40,39.6c3.2,0,5.9-2.6,5.9-5.8c0-3.2-2.6-5.8-5.9-5.8s-5.9,2.6-5.9,5.8C34.2,37,36.8,39.6,40,39.6z M40,31.3 c1.4,0,2.5,1.1,2.5,2.5c0,1.4-1.1,2.5-2.5,2.5s-2.5-1.1-2.5-2.5C37.5,32.4,38.7,31.3,40,31.3z"
    }
  }), _vm._v(" "), _c('rect', {
    attrs: {
      "x": "30.6",
      "y": "11.3",
      "width": "1.7",
      "height": "6.7"
    }
  }), _vm._v(" "), _c('rect', {
    attrs: {
      "x": "30.6",
      "y": "8",
      "width": "1.7",
      "height": "1.5"
    }
  }), _vm._v(" "), _c('rect', {
    attrs: {
      "x": "47.6",
      "y": "8",
      "width": "1.7",
      "height": "1.5"
    }
  }), _vm._v(" "), _c('rect', {
    attrs: {
      "x": "39.2",
      "y": "16.4",
      "width": "1.7",
      "height": "1.6"
    }
  }), _vm._v(" "), _c('rect', {
    attrs: {
      "x": "39.2",
      "y": "4.6",
      "width": "1.7",
      "height": "10.1"
    }
  }), _vm._v(" "), _c('rect', {
    attrs: {
      "x": "47.6",
      "y": "11.3",
      "width": "1.7",
      "height": "6.7"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "d": "M23.2,61.5L23.2,61.5l-1.3,0.5l0.1,0.1c1.5,2.1,3.8,3.2,6.3,3.2c1.2,0,2.5-0.3,3.7-0.9c2-0.9,3.6-2.6,4.3-4.7\n\t\t\ts0.7-4.4-0.3-6.4c-0.1-0.2-0.2-0.4-0.3-0.6l-0.1-0.1L32,54.5c-0.4-0.8-1-1.6-1.6-2.2l2.3-1.1c0.4,0.3,0.8,0.7,1.1,1.1l0.1,0.1\n\t\t\tl1.2-0.6l-0.1-0.1c-2.3-3.1-6.3-4-10-2.3c-4.2,2-6,6.9-4,11.1c0.1,0.1,0.1,0.3,0.2,0.4l0.1,0.1l3.7-1.7c0.4,0.8,1,1.6,1.6,2.3\n\t\t\tl-2.2,1C23.9,62.3,23.5,61.9,23.2,61.5z M30.5,63.6c-0.1,0-0.1-0.1-0.2-0.1c-1-0.4-1.9-1-2.7-1.8l2.2-1l1.3,2.7\n\t\t\tC30.9,63.5,30.7,63.6,30.5,63.6z M32.4,62.5c0,0.1,0,0.2-0.1,0.2c-0.2,0.1-0.4,0.3-0.7,0.4l-1.3-2.7l2.3-1.1\n\t\t\tC32.8,60.4,32.7,61.5,32.4,62.5z M33.1,62.2c0.2-0.8,0.3-1.8,0.2-3.1l2.1-1C35.1,59.7,34.3,61.1,33.1,62.2z M35,54.5\n\t\t\tc0.3,0.9,0.5,1.9,0.4,2.9l-2.2,1c-0.1-0.9-0.4-1.9-0.7-2.7L35,54.5z M32.7,58.7l-2.6,1.2l-1.2-2.5l3-1.4\n\t\t\tC32.3,56.8,32.5,57.7,32.7,58.7z M31.4,54.7l-3,1.4l-1.1-2.4l2.5-1.2C30.4,53.2,30.9,53.9,31.4,54.7z M32,50.8l-2.1,1\n\t\t\tc-0.9-0.9-1.9-1.5-2.6-1.8C29,49.6,30.6,49.9,32,50.8z M26.3,50.2c0.1,0,0.2,0.1,0.3,0.1c0.9,0.4,1.8,1,2.6,1.7L27,53.1l-1.2-2.7\n\t\t\tC25.9,50.4,26.1,50.3,26.3,50.2z M24.6,51.3c0-0.1,0.1-0.2,0.1-0.3c0.2-0.1,0.3-0.2,0.5-0.3l1.2,2.7l-2.2,1\n\t\t\tC24.2,53.3,24.3,52.3,24.6,51.3z M23.9,51.6c-0.2,1-0.3,2-0.3,3.1l-2,0.9C21.9,54.1,22.7,52.7,23.9,51.6z M21.9,59.2\n\t\t\tc-0.3-0.9-0.4-1.8-0.4-2.8l2.2-1c0.1,0.9,0.3,1.8,0.7,2.7L21.9,59.2z M24.2,55.1l2.4-1.1l1.1,2.4l-2.9,1.4\n\t\t\tC24.6,56.9,24.3,56,24.2,55.1z M25.4,59l2.9-1.4l1.2,2.5l-2.4,1.1C26.4,60.6,25.9,59.8,25.4,59z M27,62c0.8,0.8,1.6,1.4,2.5,1.9\n\t\t\tc-1.7,0.3-3.3,0-4.7-0.9L27,62z"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "d": "M67.9,47.3c0-0.4-0.5-0.6-0.8-0.3l-2.6,2.6l0,22.5h-49v-5h-3.4v8.4h55.7L67.9,47.3z"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "d": "M12.1,63.1c0,0.4,0.5,0.6,0.8,0.3l2.6-2.6l0-24.3h6.8v6.8h35.5v-6.8h6.8v6.8h3.4V33.2H57.7H51V19.7H29.1l0,13.5h-6.8H12.1\n\t\t\tL12.1,63.1z M38.3,23.1h3.5c0.5,3.1,2.8,5.5,5.9,6v10.8H32.4l0-10.8C35.5,28.5,37.8,26.1,38.3,23.1z M54.4,36.6V40H51v-3.4H54.4z\n\t\t\tM47.6,25.6c-1.2-0.4-2.1-1.3-2.4-2.5h2.4V25.6z M34.8,23.1c-0.3,1.1-1.2,2.1-2.3,2.5l0-2.5H34.8z M29.1,36.6l0,3.4h-3.4v-3.4H29.1\n\t\t\tz"
    }
  })])]);
};
var __vue_staticRenderFns__$g = [];

/* style */
const __vue_inject_styles__$g = undefined;
/* scoped */
const __vue_scope_id__$g = undefined;
/* module identifier */
const __vue_module_identifier__$g = undefined;
/* functional template */
const __vue_is_functional_template__$g = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$w = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$g,
  staticRenderFns: __vue_staticRenderFns__$g
}, __vue_inject_styles__$g, __vue_script__$g, __vue_scope_id__$g, __vue_is_functional_template__$g, __vue_module_identifier__$g, false, undefined, undefined, undefined);
var __vue_component__$x = __vue_component__$w;

let IconSort = class IconSort extends Vue {};
__decorate([Prop({
  default: 26
})], IconSort.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconSort.prototype, "color", void 0);
IconSort = __decorate([Component], IconSort);
var script$f = IconSort;

/* script */
const __vue_script__$f = script$f;

/* template */
var __vue_render__$f = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 512 512"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero"
    }
  }, [_c('path', {
    attrs: {
      "d": "M96,512h64V128h96L128,0L0,128h96V512z M512,384h-96V0h-64v384h-96l128,128L512,384z"
    }
  })])]);
};
var __vue_staticRenderFns__$f = [];

/* style */
const __vue_inject_styles__$f = undefined;
/* scoped */
const __vue_scope_id__$f = undefined;
/* module identifier */
const __vue_module_identifier__$f = undefined;
/* functional template */
const __vue_is_functional_template__$f = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$u = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$f,
  staticRenderFns: __vue_staticRenderFns__$f
}, __vue_inject_styles__$f, __vue_script__$f, __vue_scope_id__$f, __vue_is_functional_template__$f, __vue_module_identifier__$f, false, undefined, undefined, undefined);
var __vue_component__$v = __vue_component__$u;

let IconSupport = class IconSupport extends Vue {};
__decorate([Prop({
  default: 26
})], IconSupport.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconSupport.prototype, "color", void 0);
IconSupport = __decorate([Component], IconSupport);
var script$e = IconSupport;

/* script */
const __vue_script__$e = script$e;

/* template */
var __vue_render__$e = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 491.52 491.52"
    }
  }, [_c('g', [_c('g', [_c('path', {
    attrs: {
      "d": "M460.8,230.545V215.04C460.8,96.465,364.335,0,245.76,0S30.72,96.465,30.72,215.04v15.505\n\t\t\tC12.108,244.572,0,266.789,0,291.84c0,42.35,34.45,76.8,76.8,76.8h15.36v-153.6H76.8c-8.986,0-17.579,1.631-25.6,4.48v-4.48\n\t\t\tc0-107.28,87.28-194.56,194.56-194.56s194.56,87.28,194.56,194.56v4.48c-8.021-2.849-16.614-4.48-25.6-4.48h-15.36v153.6h15.36\n\t\t\tc5.259,0,10.396-0.536,15.36-1.548v62.988c0,11.295-9.185,20.48-20.48,20.48H295.073c-4.241-11.894-15.501-20.48-28.833-20.48\n\t\t\th-30.72c-16.94,0-30.72,13.78-30.72,30.72s13.78,30.72,30.72,30.72h30.72c13.332,0,24.593-8.586,28.833-20.48H409.6\n\t\t\tc22.585,0,40.96-18.375,40.96-40.96v-70.366c24.332-12.9,40.96-38.467,40.96-67.874C491.52,266.789,479.412,244.572,460.8,230.545\n\t\t\tz M71.68,235.75v112.18c-28.665-2.595-51.2-26.76-51.2-56.09C20.48,262.51,43.015,238.345,71.68,235.75z M266.24,471.04h-30.72\n\t\t\tc-5.645,0-10.24-4.595-10.24-10.24c0-5.645,4.595-10.24,10.24-10.24h30.72c5.645,0,10.24,4.595,10.24,10.24\n\t\t\tC276.48,466.445,271.885,471.04,266.24,471.04z M419.84,347.93V235.75c28.665,2.595,51.2,26.76,51.2,56.09\n\t\t\tC471.04,321.17,448.505,345.335,419.84,347.93z",
      "fill": _vm.color
    }
  })])]), _vm._v(" "), _c('g', [_c('g', [_c('path', {
    attrs: {
      "d": "M378.88,219.265v-49.41l-31.035-5.17c-2.145-7.34-5.085-14.42-8.79-21.16L357.36,117.9l-34.93-34.94l-25.635,18.31\n\t\t\tc-6.74-3.705-13.82-6.645-21.16-8.795l-5.17-31.035H221.06l-5.17,31.035c-7.34,2.15-14.42,5.09-21.16,8.795l-25.635-18.31\n\t\t\tl-34.93,34.94l18.305,25.625c-3.705,6.74-6.645,13.82-8.795,21.16l-31.035,5.17v49.41l31.035,5.17\n\t\t\tc2.15,7.34,5.09,14.415,8.795,21.16l-18.305,25.625l34.93,34.935l25.63-18.305c6.745,3.705,13.825,6.645,21.165,8.795l5.17,31.035\n\t\t\th49.405l5.17-31.035c7.34-2.15,14.42-5.09,21.165-8.795l25.63,18.305l34.93-34.935l-18.305-25.625\n\t\t\tc3.705-6.745,6.645-13.825,8.79-21.16L378.88,219.265z M331.085,206.465l-1.495,6.755c-2.14,9.69-5.97,18.91-11.38,27.395\n\t\t\tl-3.715,5.83l16.115,22.565l-10.4,10.395l-22.555-16.11l-5.83,3.715c-8.485,5.405-17.71,9.235-27.41,11.38l-6.75,1.5l-4.55,27.31\n\t\t\tH238.41l-4.55-27.31l-6.75-1.5c-9.7-2.145-18.925-5.975-27.41-11.38l-5.83-3.715l-22.555,16.11l-10.4-10.395l16.115-22.565\n\t\t\tl-3.715-5.83c-5.405-8.48-9.24-17.7-11.385-27.4l-1.5-6.75l-27.31-4.55v-14.71l27.31-4.55l1.5-6.75\n\t\t\tc2.15-9.705,5.98-18.925,11.385-27.4l3.715-5.83l-16.115-22.565l10.4-10.4l22.555,16.115l5.83-3.715\n\t\t\tc8.485-5.405,17.71-9.235,27.41-11.38l6.75-1.5l4.55-27.31h14.705l4.55,27.31l6.75,1.5c9.7,2.145,18.925,5.975,27.41,11.38\n\t\t\tl5.83,3.715l22.555-16.115l10.4,10.4l-16.115,22.565l3.715,5.83c5.41,8.48,9.235,17.695,11.38,27.395l1.495,6.755l27.315,4.55\n\t\t\tv14.71L331.085,206.465z",
      "fill": _vm.color
    }
  })])]), _vm._v(" "), _c('g', [_c('g', [_c('path', {
    attrs: {
      "d": "M245.76,136.235c-32.16,0-58.32,26.165-58.32,58.325c0,32.16,26.16,58.325,58.32,58.325\n\t\t\tc32.16,0,58.325-26.165,58.325-58.325C304.085,162.4,277.92,136.235,245.76,136.235z M245.76,232.405\n\t\t\tc-20.865,0-37.84-16.975-37.84-37.845s16.975-37.845,37.84-37.845c20.87,0,37.845,16.975,37.845,37.845\n\t\t\tS266.63,232.405,245.76,232.405z",
      "fill": _vm.color
    }
  })])])]);
};
var __vue_staticRenderFns__$e = [];

/* style */
const __vue_inject_styles__$e = undefined;
/* scoped */
const __vue_scope_id__$e = undefined;
/* module identifier */
const __vue_module_identifier__$e = undefined;
/* functional template */
const __vue_is_functional_template__$e = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$s = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$e,
  staticRenderFns: __vue_staticRenderFns__$e
}, __vue_inject_styles__$e, __vue_script__$e, __vue_scope_id__$e, __vue_is_functional_template__$e, __vue_module_identifier__$e, false, undefined, undefined, undefined);
var __vue_component__$t = __vue_component__$s;

let IconTime = class IconTime extends Vue {};
__decorate([Prop({
  default: 26
})], IconTime.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconTime.prototype, "color", void 0);
IconTime = __decorate([Component], IconTime);
var script$d = IconTime;

/* script */
const __vue_script__$d = script$d;

/* template */
var __vue_render__$d = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 16 16"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero"
    }
  }, [_c('path', {
    attrs: {
      "d": "M8 .667c4.05 0 7.333 3.283 7.333 7.333 0 4.05-3.283 7.333-7.333 7.333C3.95 15.333.667 12.05.667 8 .667 3.95 3.95.667 8 .667zm0 1C4.502 1.667 1.667 4.502 1.667 8S4.502 14.333 8 14.333 14.333 11.498 14.333 8 11.498 1.667 8 1.667zm0 1.066c.306 0 .558.23.595.525l.005.075v4.348l2.267 2.193c.218.21.242.548.067.786l-.053.062c-.21.218-.548.242-.786.067l-.062-.053L7.4 8.19V3.333c0-.331.269-.6.6-.6z"
    }
  })])]);
};
var __vue_staticRenderFns__$d = [];

/* style */
const __vue_inject_styles__$d = undefined;
/* scoped */
const __vue_scope_id__$d = undefined;
/* module identifier */
const __vue_module_identifier__$d = undefined;
/* functional template */
const __vue_is_functional_template__$d = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$q = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$d,
  staticRenderFns: __vue_staticRenderFns__$d
}, __vue_inject_styles__$d, __vue_script__$d, __vue_scope_id__$d, __vue_is_functional_template__$d, __vue_module_identifier__$d, false, undefined, undefined, undefined);
var __vue_component__$r = __vue_component__$q;

let IconTransaction = class IconTransaction extends Vue {};
__decorate([Prop({
  default: 26
})], IconTransaction.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconTransaction.prototype, "color", void 0);
IconTransaction = __decorate([Component], IconTransaction);
var script$c = IconTransaction;

/* script */
const __vue_script__$c = script$c;

/* template */
var __vue_render__$c = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 16 16"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero"
    }
  }, [_c('path', {
    attrs: {
      "d": "M13.333 8c.053 0 .105.006.157.019l.041.01.025.01c.025.008.05.018.073.03l.02.01.058.035c.034.024.067.05.098.081l-.057-.05c.015.012.03.024.043.037l.014.013.014.015.036.042c.011.013.021.027.03.041.013.019.025.038.036.058l.01.02c.012.024.022.048.03.073l.01.025.01.04c.005.02.008.037.011.056v.003l.008.099v4c0 .368-.298.666-.667.666-.368 0-.666-.298-.666-.666l-.001-2.391-3.528 3.529c-.234.234-.6.257-.86.07l-.083-.07c-.26-.26-.26-.683 0-.943l3.528-3.529h-2.39c-.334 0-.611-.246-.66-.568l-.006-.098c0-.369.298-.667.666-.667z"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "d": "M2.565 7.992l-.055-.01-.041-.012-.025-.008-.073-.03-.02-.011-.058-.035c-.034-.024-.067-.05-.098-.081l.057.05c-.015-.012-.03-.024-.043-.037l-.014-.013-.014-.015-.036-.042c-.011-.013-.021-.027-.03-.041-.013-.019-.025-.038-.036-.058l-.01-.02c-.012-.024-.022-.048-.03-.073l-.01-.025-.01-.04C2.006 7.437 2 7.385 2 7.332v-4c0-.368.298-.666.667-.666.368 0 .666.298.666.666v2.39l3.529-3.528c.234-.234.6-.257.86-.07l.083.07c.26.26.26.683 0 .943L4.276 6.666h2.39c.335 0 .612.247.66.569l.007.098c0 .369-.298.667-.666.667h-4l-.102-.008z"
    }
  })])]);
};
var __vue_staticRenderFns__$c = [];

/* style */
const __vue_inject_styles__$c = undefined;
/* scoped */
const __vue_scope_id__$c = undefined;
/* module identifier */
const __vue_module_identifier__$c = undefined;
/* functional template */
const __vue_is_functional_template__$c = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$o = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$c,
  staticRenderFns: __vue_staticRenderFns__$c
}, __vue_inject_styles__$c, __vue_script__$c, __vue_scope_id__$c, __vue_is_functional_template__$c, __vue_module_identifier__$c, false, undefined, undefined, undefined);
var __vue_component__$p = __vue_component__$o;

let IconTransfers = class IconTransfers extends Vue {};
__decorate([Prop({
  default: 26
})], IconTransfers.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconTransfers.prototype, "color", void 0);
IconTransfers = __decorate([Component], IconTransfers);
var script$b = IconTransfers;

/* script */
const __vue_script__$b = script$b;

/* template */
var __vue_render__$b = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    staticClass: "svg-icon",
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 20 24"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero"
    }
  }, [_c('path', {
    attrs: {
      "id": "e6bu8hzksa",
      "d": "M19 12c.552 0 1 .448 1 1v2c0 2.761-2.239 5-5 5H3.414l2.293 2.293c.39.39.39 1.024 0 1.414-.36.36-.928.388-1.32.083l-.094-.083-4-4-.073-.082-.007-.008.08.09c-.036-.035-.068-.073-.097-.112-.018-.024-.034-.049-.05-.074l-.024-.042c-.01-.018-.02-.037-.028-.056l-.023-.052-.021-.06-.014-.045-.016-.065-.009-.053C.004 19.1 0 19.051 0 19l.004.09L0 19.02V19v-.02c0-.022.002-.046.004-.07l.007-.059.01-.052c.004-.022.009-.043.015-.065l.014-.046.021-.06.023-.051.03-.061.022-.037c.02-.03.04-.061.062-.09l.002-.002c.028-.035.054-.066.083-.094l4-4c.39-.39 1.024-.39 1.414 0 .36.36.388.928.083 1.32l-.083.094L3.414 18H15c1.598 0 2.904-1.249 2.995-2.824L18 15v-2c0-.552.448-1 1-1zM15.613.21l.094.083 4 4c.029.028.055.059.08.09l.017.022c.018.024.034.049.05.074l.021.037c.011.02.022.04.031.06l.023.053.021.06.014.045.016.065.009.053.008.073.001.015.001.027L20 5v.034l-.004.051c-.001.022-.004.043-.007.064l-.01.052c-.004.022-.009.043-.015.065l-.014.046-.021.06-.023.051-.03.061-.022.037c-.016.025-.032.05-.05.074l-.025.03c.004-.002.002-.001.001 0l-.013.015-.01.013-.034.036-.016.018-4 4c-.39.39-1.024.39-1.414 0-.36-.36-.388-.928-.083-1.32l.083-.094L16.585 6H5C3.402 6 2.096 7.249 2.005 8.824L2 9v2c0 .552-.448 1-1 1-.513 0-.936-.386-.993-.883L0 11V9c0-2.689 2.122-4.882 4.783-4.995L5 4h11.585l-2.292-2.293c-.36-.36-.388-.928-.083-1.32l.083-.094c.36-.36.928-.388 1.32-.083zm4.166 5.415l-.056.064.044-.049.012-.015z"
    }
  })])]);
};
var __vue_staticRenderFns__$b = [];

/* style */
const __vue_inject_styles__$b = undefined;
/* scoped */
const __vue_scope_id__$b = undefined;
/* module identifier */
const __vue_module_identifier__$b = undefined;
/* functional template */
const __vue_is_functional_template__$b = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$m = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$b,
  staticRenderFns: __vue_staticRenderFns__$b
}, __vue_inject_styles__$b, __vue_script__$b, __vue_scope_id__$b, __vue_is_functional_template__$b, __vue_module_identifier__$b, false, undefined, undefined, undefined);
var __vue_component__$n = __vue_component__$m;

let IconUpload = class IconUpload extends Vue {};
__decorate([Prop({
  default: 24
})], IconUpload.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconUpload.prototype, "color", void 0);
IconUpload = __decorate([Component], IconUpload);
var script$a = IconUpload;

/* script */
const __vue_script__$a = script$a;

/* template */
var __vue_render__$a = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    staticStyle: {
      "enable-background": "new 0 0 490 490"
    },
    attrs: {
      "id": "Capa_1",
      "version": "1.1",
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 490 490",
      "xml:space": "preserve"
    }
  }, [_c('g', [_c('g', [_c('g', [_c('path', {
    attrs: {
      "fill": _vm.color,
      "d": "M262.2,472.9V141.6l58.8,58.8c3.3,3.3,7.7,5,12.1,5c4.4,0,8.8-1.7,12.1-5c6.7-6.7,6.7-17.6,0-24.3L257.1,88\n\t\t\t\tc-6.4-6.4-17.8-6.4-24.3,0l-88.1,88.1c-6.7,6.7-6.7,17.6,0,24.3s17.6,6.7,24.3,0l58.8-58.8v331.2c0,9.5,7.7,17.2,17.1,17.2\n\t\t\t\tC254.6,490,262.2,482.4,262.2,472.9z"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "fill": _vm.color,
      "d": "M28,17.1v99.7c0,9.5,7.7,17.2,17.1,17.2c9.5,0,17.2-7.7,17.2-17.2V34.3h365.5v82.6c0,9.5,7.7,17.2,17.1,17.2\n\t\t\t\tc9.4,0,17.1-7.7,17.1-17.2V17.1C462,7.6,454.3,0,444.9,0H45.2C35.7,0,28,7.6,28,17.1z"
    }
  })])])])]);
};
var __vue_staticRenderFns__$a = [];

/* style */
const __vue_inject_styles__$a = undefined;
/* scoped */
const __vue_scope_id__$a = undefined;
/* module identifier */
const __vue_module_identifier__$a = undefined;
/* functional template */
const __vue_is_functional_template__$a = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$k = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$a,
  staticRenderFns: __vue_staticRenderFns__$a
}, __vue_inject_styles__$a, __vue_script__$a, __vue_scope_id__$a, __vue_is_functional_template__$a, __vue_module_identifier__$a, false, undefined, undefined, undefined);
var __vue_component__$l = __vue_component__$k;

let IconUserGroup = class IconUserGroup extends Vue {};
__decorate([Prop({
  default: 26
})], IconUserGroup.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconUserGroup.prototype, "color", void 0);
IconUserGroup = __decorate([Component], IconUserGroup);
var script$9 = IconUserGroup;

/* script */
const __vue_script__$9 = script$9;

/* template */
var __vue_render__$9 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 16 16"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero"
    }
  }, [_c('path', {
    attrs: {
      "d": "M6.704 1.37c.716-.716 1.876-.716 2.592 0 .305.305.48.69.525 1.088 1.76.576 3.146 1.966 3.722 3.72.396.046.782.22 1.087.526.716.716.716 1.876 0 2.592-.305.305-.69.48-1.087.525-.575 1.755-1.963 3.145-3.722 3.723-.045.396-.22.781-.525 1.086-.716.716-1.876.716-2.592 0-.305-.305-.48-.69-.525-1.087-1.757-.576-3.146-1.965-3.723-3.723-.396-.044-.781-.22-1.086-.524-.716-.716-.716-1.876 0-2.592.305-.305.69-.48 1.088-.525.575-1.756 1.962-3.145 3.722-3.723.044-.396.219-.781.524-1.086zm1.885 11.374c-.325-.325-.853-.325-1.178 0-.326.326-.326.853 0 1.179.325.325.853.325 1.178 0 .326-.326.326-.853 0-1.179zm1.065-9.287l-.06.115c-.079.14-.178.272-.298.391-.716.716-1.876.716-2.592 0-.152-.152-.271-.323-.359-.506-1.339.488-2.401 1.552-2.888 2.89.184.086.355.205.506.357.716.716.716 1.876 0 2.592-.151.152-.322.27-.505.358.487 1.339 1.55 2.402 2.888 2.89.087-.185.206-.356.358-.507.716-.716 1.876-.716 2.592 0 .151.151.27.322.358.504 1.34-.485 2.402-1.55 2.89-2.888-.185-.086-.356-.206-.507-.357-.716-.716-.716-1.876 0-2.592.151-.152.322-.271.505-.358-.486-1.337-1.548-2.4-2.888-2.889zM3.256 7.411c-.326-.326-.853-.326-1.179 0-.325.325-.325.853 0 1.178.326.326.853.326 1.179 0 .325-.325.325-.853 0-1.178zm10.667 0c-.326-.326-.853-.326-1.179 0-.325.325-.325.853 0 1.178.326.326.853.326 1.179 0 .325-.325.325-.853 0-1.178zM8.589 2.077c-.325-.325-.853-.325-1.178 0-.326.326-.326.853 0 1.179.325.325.853.325 1.178 0 .326-.326.326-.853 0-1.179z"
    }
  })])]);
};
var __vue_staticRenderFns__$9 = [];

/* style */
const __vue_inject_styles__$9 = undefined;
/* scoped */
const __vue_scope_id__$9 = undefined;
/* module identifier */
const __vue_module_identifier__$9 = undefined;
/* functional template */
const __vue_is_functional_template__$9 = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$i = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$9,
  staticRenderFns: __vue_staticRenderFns__$9
}, __vue_inject_styles__$9, __vue_script__$9, __vue_scope_id__$9, __vue_is_functional_template__$9, __vue_module_identifier__$9, false, undefined, undefined, undefined);
var __vue_component__$j = __vue_component__$i;

let IconUsers = class IconUsers extends Vue {};
__decorate([Prop({
  default: 26
})], IconUsers.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconUsers.prototype, "color", void 0);
IconUsers = __decorate([Component], IconUsers);
var script$8 = IconUsers;

/* script */
const __vue_script__$8 = script$8;

/* template */
var __vue_render__$8 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 16 16"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color
    }
  }, [_c('path', {
    attrs: {
      "d": "M8.667 9.333c1.84 0 3.333 1.493 3.333 3.334V14c0 .368-.298.667-.667.667-.368 0-.666-.299-.666-.667v-1.333c0-1.105-.896-2-2-2H3.333c-1.104 0-2 .895-2 2V14c0 .368-.298.667-.666.667C.298 14.667 0 14.368 0 14v-1.333c0-1.841 1.492-3.334 3.333-3.334zm4.02.587c.093-.356.457-.57.813-.479 1.47.38 2.499 1.706 2.5 3.226V14c0 .368-.298.667-.667.667-.368 0-.666-.299-.666-.667v-1.333c-.001-.911-.618-1.707-1.5-1.935-.357-.092-.571-.456-.48-.812zM6 1.333c1.84 0 3.333 1.493 3.333 3.334C9.333 6.507 7.841 8 6 8 4.16 8 2.667 6.508 2.667 4.667c0-1.841 1.492-3.334 3.333-3.334zm4.02.588c.092-.356.455-.571.812-.48 1.475.377 2.507 1.706 2.507 3.229 0 1.523-1.032 2.852-2.507 3.23-.357.09-.72-.125-.811-.481-.091-.357.124-.72.48-.811.885-.227 1.504-1.024 1.504-1.938 0-.914-.619-1.71-1.504-1.938-.356-.09-.571-.454-.48-.81zM6 2.667c-1.105 0-2 .895-2 2 0 1.104.895 2 2 2s2-.896 2-2c0-1.105-.895-2-2-2z"
    }
  })])]);
};
var __vue_staticRenderFns__$8 = [];

/* style */
const __vue_inject_styles__$8 = undefined;
/* scoped */
const __vue_scope_id__$8 = undefined;
/* module identifier */
const __vue_module_identifier__$8 = undefined;
/* functional template */
const __vue_is_functional_template__$8 = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$g = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$8,
  staticRenderFns: __vue_staticRenderFns__$8
}, __vue_inject_styles__$8, __vue_script__$8, __vue_scope_id__$8, __vue_is_functional_template__$8, __vue_module_identifier__$8, false, undefined, undefined, undefined);
var __vue_component__$h = __vue_component__$g;

let IconWarning = class IconWarning extends Vue {};
__decorate([Prop({
  default: 26
})], IconWarning.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconWarning.prototype, "color", void 0);
IconWarning = __decorate([Component], IconWarning);
var script$7 = IconWarning;

/* script */
const __vue_script__$7 = script$7;

/* template */
var __vue_render__$7 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 20 20"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero"
    }
  }, [_c('path', {
    attrs: {
      "d": "M10 .833c5.063 0 9.167 4.104 9.167 9.167S15.063 19.167 10 19.167.833 15.063.833 10 4.937.833 10 .833zM10 2.5c-4.142 0-7.5 3.358-7.5 7.5 0 4.142 3.358 7.5 7.5 7.5 4.142 0 7.5-3.358 7.5-7.5 0-4.142-3.358-7.5-7.5-7.5zm-.59 9.41c.326-.325.854-.325 1.18 0 .325.326.325.854 0 1.18-.326.325-.854.325-1.18 0-.325-.326-.325-.854 0-1.18zM10 5.834c.425 0 .775.318.827.73l.006.104V10c0 .46-.373.833-.833.833-.425 0-.775-.318-.827-.728L9.167 10V6.667c0-.46.373-.834.833-.834z"
    }
  })])]);
};
var __vue_staticRenderFns__$7 = [];

/* style */
const __vue_inject_styles__$7 = undefined;
/* scoped */
const __vue_scope_id__$7 = undefined;
/* module identifier */
const __vue_module_identifier__$7 = undefined;
/* functional template */
const __vue_is_functional_template__$7 = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$e = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$7,
  staticRenderFns: __vue_staticRenderFns__$7
}, __vue_inject_styles__$7, __vue_script__$7, __vue_scope_id__$7, __vue_is_functional_template__$7, __vue_module_identifier__$7, false, undefined, undefined, undefined);
var __vue_component__$f = __vue_component__$e;

let IconWizardLink = class IconWizardLink extends Vue {};
__decorate([Prop({
  default: 26
})], IconWizardLink.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconWizardLink.prototype, "color", void 0);
IconWizardLink = __decorate([Component], IconWizardLink);
var script$6 = IconWizardLink;

/* script */
const __vue_script__$6 = script$6;

/* template */
var __vue_render__$6 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 16 14"
    }
  }, [_c('g', {
    attrs: {
      "fill": "none"
    }
  }, [_c('path', {
    attrs: {
      "d": "M16 5.89424V0H10.0864V2.50672H7.88496V5.50048H5.9208V2.78608H0V8.68032H5.9208V6.21664H7.8848V11.4019H10.0864V13.701H16V7.80656H10.0864V10.6856H8.60432V3.22288H10.0864V5.89424H16Z",
      "fill": "black"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "fill-rule": "evenodd",
      "clip-rule": "evenodd",
      "d": "M0.71875 7.9642H5.20083V3.50244H0.71875V7.9642ZM15.28 8.52292H10.8052V12.9848H15.28V8.52292Z",
      "fill": "#FFDA34"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "d": "M10.8066 0.716309H15.2815V5.17823H10.8066V0.716309Z",
      "fill": "#C6C6C6"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "d": "M4.43459 4.86489L4.01059 4.44873L2.51731 5.93529L1.92611 5.34649L1.50195 5.76281L2.51731 6.77369L4.43459 4.86489Z",
      "fill": "black"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "d": "M14.5147 9.98501L14.0907 9.56885L12.5974 11.0554L12.0062 10.4666L11.582 10.8829L12.5974 11.8938L14.5147 9.98501Z",
      "fill": "black"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "d": "M12.2707 3.93004L12.9645 3.23932L13.6584 3.93004L14.0344 3.55564L13.3406 2.86492L14.0344 2.1742L13.6584 1.7998L12.9645 2.49052L12.2707 1.7998L11.8945 2.1742L12.5885 2.86492L11.8945 3.55564L12.2707 3.93004Z",
      "fill": "black"
    }
  })])]);
};
var __vue_staticRenderFns__$6 = [];

/* style */
const __vue_inject_styles__$6 = undefined;
/* scoped */
const __vue_scope_id__$6 = undefined;
/* module identifier */
const __vue_module_identifier__$6 = undefined;
/* functional template */
const __vue_is_functional_template__$6 = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$c = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$6,
  staticRenderFns: __vue_staticRenderFns__$6
}, __vue_inject_styles__$6, __vue_script__$6, __vue_scope_id__$6, __vue_is_functional_template__$6, __vue_module_identifier__$6, false, undefined, undefined, undefined);
var __vue_component__$d = __vue_component__$c;

let IconWorld = class IconWorld extends Vue {};
__decorate([Prop({
  default: 16
})], IconWorld.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconWorld.prototype, "color", void 0);
IconWorld = __decorate([Component], IconWorld);
var script$5 = IconWorld;

/* script */
const __vue_script__$5 = script$5;

/* template */
var __vue_render__$5 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 16 16"
    }
  }, [_c('path', {
    attrs: {
      "fill-rule": "evenodd",
      "clip-rule": "evenodd",
      "d": "M0 8C0 12.4089 3.59111 16 8 16C12.4089 16 16 12.4089 16 8C16 3.59111 12.4089 0 8 0C3.59111 0 0 3.59111 0 8ZM0.551111 8C0.551111 6.6669 0.903303 5.41436 1.51951 4.33055C1.56982 4.74326 1.66491 5.12303 1.76 5.44L1.77285 5.48188C1.91229 5.93702 2.083 6.49422 2.75556 6.66667C2.78222 6.67556 2.80889 6.68 2.83555 6.68444C2.86222 6.68889 2.88889 6.69333 2.91556 6.70222C3.14667 6.75556 3.27111 6.79111 3.46667 6.93333C3.55901 6.9861 3.64156 7.05844 3.72159 7.12857C3.74941 7.15296 3.77693 7.17707 3.80444 7.2C3.96444 7.34222 4.14222 7.48444 4.33778 7.60889C4.49778 7.69778 4.64 7.76889 4.76444 7.82222C5.04889 7.94667 5.19111 8.01778 5.26222 8.32C5.31556 8.49778 5.31556 8.69333 5.31556 8.90667C5.31556 9.26222 5.33333 9.61778 5.49333 9.95555C5.61778 10.24 5.83111 10.4356 6.02667 10.6133L6.03168 10.6179C6.22526 10.7939 6.40076 10.9534 6.48889 11.2178C6.57714 11.5178 6.56025 11.923 6.54261 12.3463L6.54222 12.3556L6.54133 12.3788C6.50586 13.2987 6.4633 14.4027 7.64444 14.72C7.66222 14.7378 7.69778 14.7378 7.71556 14.7378C7.76889 14.7378 7.84 14.7022 7.78667 14.72C7.85778 14.6667 7.89333 14.5778 7.87556 14.4889C7.85778 14.4 7.84 14.2933 7.82222 14.1867C7.81703 14.1503 7.8107 14.1102 7.804 14.0677C7.76487 13.8196 7.71336 13.4929 7.80444 13.3867C7.91111 13.2622 8.14222 13.1556 8.35556 13.0667L8.42563 13.035L8.42563 13.035C8.60087 12.9562 8.78338 12.8742 8.92444 12.7644C9.36889 12.4267 9.45778 12.0889 9.54667 11.6622L9.56444 11.6089C9.6 11.4844 9.70667 11.4133 9.88444 11.3067C9.92418 11.2749 9.97103 11.2431 10.0202 11.2097C10.081 11.1684 10.1455 11.1247 10.2044 11.0756C10.56 10.7733 11.1289 10.0267 11.1467 9.35111C11.1467 9.04889 11.04 8.8 10.8444 8.60444C10.6489 8.40889 10.4 8.30222 10.1511 8.19556L9.83111 8.03556C9.69547 7.9602 9.59816 7.84652 9.49586 7.727L9.49585 7.72699C9.47748 7.70553 9.45895 7.68387 9.44 7.66222C9.35111 7.53778 9.22667 7.41333 9.08444 7.28889C8.80576 7.06246 8.47589 7.02374 8.17841 6.98883L8.16 6.98667C8.03558 6.96889 7.92892 6.95112 7.82227 6.93334L7.82227 6.93334L7.82222 6.93333C7.67556 6.90667 7.52889 6.85 7.38222 6.79333C7.33333 6.77445 7.28444 6.75556 7.23556 6.73778C7.04 6.64889 6.84444 6.57778 6.63111 6.52444C6.15414 6.39287 5.73803 6.47432 5.3124 6.55764L5.20889 6.57778C5.17333 6.5837 5.13185 6.59161 5.09037 6.59951L5.09036 6.59951C5.0074 6.61531 4.92444 6.63111 4.88889 6.63111C4.87111 6.61333 4.85333 6.56 4.81778 6.41778C4.69772 5.98899 4.32944 5.8746 4.04821 5.78725L4.01778 5.77778C3.66222 5.68889 3.60889 5.67111 3.68 5.35111C3.75111 5.08444 3.94667 4.88889 4.23111 4.8C4.53333 4.71111 4.85333 4.78222 5.06667 4.97778C5.13778 5.04889 5.26222 5.06667 5.35111 5.03111C5.45778 4.99556 5.52889 4.90667 5.52889 4.8C5.56444 4.24889 5.93778 3.73333 6.27556 3.30667C6.66667 2.82667 6.88 2.79111 7.53778 2.80889H7.57333C7.76889 2.82667 8.05333 2.82667 8.21333 2.61333C8.26667 2.54222 8.33778 2.4 8.26667 2.18667C8.21333 1.99111 8.01778 1.84889 7.85778 1.74222C7.78667 1.68889 7.69778 1.61778 7.66222 1.58222C7.59111 1.47556 7.52 1.31556 7.44889 1.15557L7.44889 1.15556C7.37515 0.975317 7.29009 0.772425 7.16761 0.597337C7.44093 0.566796 7.71867 0.551111 8 0.551111C8.83937 0.551111 9.64681 0.690737 10.4003 0.947979C10.4001 0.95197 10.4 0.955977 10.4 0.96C10.3644 1.92 10.9689 2.09778 11.4667 2.24C11.6444 2.29333 11.8222 2.34667 11.9822 2.43556C11.84 2.54222 11.7156 2.66667 11.6978 2.82667C11.6622 3.11111 11.8933 3.30667 12.1067 3.48444C12.1429 3.52064 12.1806 3.55534 12.2176 3.58941C12.3078 3.67245 12.394 3.75176 12.4444 3.84C12.7671 4.34942 12.7491 5.02103 12.7314 5.68442C12.7305 5.71556 12.7297 5.74669 12.7289 5.77778C12.7111 6.18667 12.6933 6.59556 12.7467 6.96889C12.7472 6.97197 12.7478 6.97525 12.7485 6.97873C12.7879 7.19675 12.9701 8.20255 13.6 8.64C13.7778 8.76444 13.9911 8.83556 14.2044 8.83556C14.2933 8.83556 14.3822 8.81778 14.4356 8.78222C14.7463 8.71316 14.9397 8.47645 15.0972 8.28373C15.1018 8.278 15.1065 8.27231 15.1111 8.26667C15.1644 8.19556 15.2356 8.10667 15.2889 8.07111C15.3198 8.08657 15.3641 8.1289 15.4453 8.23314C15.3217 12.2324 12.0287 15.4489 8 15.4489C3.89333 15.4489 0.551111 12.1067 0.551111 8ZM15.4358 7.55627C15.2665 4.68986 13.4681 2.25367 10.956 1.16308C10.9927 1.51905 11.2004 1.58053 11.6012 1.69913C11.6096 1.70161 11.6181 1.70413 11.6267 1.70667C11.6499 1.714 11.6734 1.72133 11.6973 1.72875C12.0202 1.82926 12.3941 1.94566 12.6756 2.29333C12.7289 2.36444 12.7467 2.45333 12.7289 2.52444C12.7111 2.59556 12.6578 2.68444 12.5689 2.72C12.4175 2.78056 12.3305 2.84113 12.2751 2.87973C12.2655 2.88645 12.2568 2.8925 12.2489 2.89778C12.3022 2.95111 12.3733 3.02222 12.4444 3.07556C12.6044 3.2 12.7822 3.36 12.9067 3.55556C13.3156 4.23111 13.2978 5.03111 13.2622 5.81333L13.2616 5.82699C13.244 6.21313 13.2273 6.5815 13.28 6.89778C13.3511 7.37778 13.5644 7.98222 13.9022 8.19556C14.0444 8.28444 14.1867 8.32 14.3467 8.26667C14.4711 8.23111 14.5956 8.08889 14.7022 7.94667C14.7152 7.93082 14.7282 7.91474 14.7412 7.89858C14.889 7.71529 15.0453 7.52144 15.3067 7.53778C15.3515 7.53778 15.3946 7.54441 15.4358 7.55627ZM6.41501 0.720894C4.62859 1.10967 3.07912 2.14152 2.02232 3.56072C1.9982 4.07063 2.07237 4.60358 2.27556 5.29778C2.41778 5.81333 2.52444 6.04444 2.88 6.15111C2.90446 6.15926 2.92892 6.16368 2.95166 6.16778C2.97851 6.17263 3.00297 6.17704 3.02222 6.18667L3.0661 6.19915C3.29663 6.2646 3.50129 6.32269 3.76889 6.50667C3.89333 6.59556 4.01778 6.70222 4.12444 6.80889C4.28444 6.95111 4.42667 7.07556 4.58667 7.16444C4.69333 7.23555 4.81777 7.28889 4.94221 7.34222L4.94222 7.34222L4.9527 7.34688C5.27032 7.48798 5.63691 7.65083 5.76 8.23111C5.81333 8.46222 5.81333 8.69333 5.81333 8.92444C5.81333 9.22667 5.83111 9.51111 5.93778 9.74222C6.02667 9.93778 6.18667 10.08 6.34667 10.2222L6.37889 10.2519C6.6021 10.4573 6.85019 10.6855 6.96889 11.0756C7.09333 11.4667 7.07556 11.9467 7.05778 12.4089C7.02222 13.1378 7.04 13.6711 7.32444 13.9733C7.27111 13.6356 7.25333 13.2622 7.44889 13.0133C7.64444 12.7644 7.92889 12.64 8.21333 12.5333C8.39111 12.4622 8.55111 12.3911 8.65778 12.3022C8.94222 12.0889 8.99555 11.8933 9.06667 11.5022L9.08444 11.4489C9.17333 11.0933 9.44 10.9333 9.65333 10.8089C9.74222 10.7556 9.83111 10.7022 9.90222 10.6489C10.2044 10.3822 10.6489 9.76 10.6489 9.31556C10.6489 9.17333 10.6133 9.06667 10.5244 8.97778C10.3822 8.85333 10.1867 8.76444 9.97333 8.67556L9.97332 8.67555L9.97331 8.67554C9.84887 8.62221 9.72444 8.56889 9.6 8.49778C9.37369 8.36845 9.22087 8.19504 9.08808 8.04435L9.04889 8C8.96 7.89333 8.87111 7.78667 8.76444 7.69778C8.62312 7.58785 8.4263 7.56116 8.21076 7.53192C8.18229 7.52806 8.15349 7.52415 8.12444 7.52C8.0534 7.51112 7.98679 7.50224 7.92018 7.49336L7.92002 7.49334C7.85335 7.48445 7.78667 7.47556 7.71556 7.46667C7.51167 7.41961 7.30777 7.34489 7.1283 7.27911L7.05778 7.25333C7.00467 7.23402 6.95287 7.21471 6.90203 7.19575C6.76566 7.14491 6.63616 7.09663 6.50667 7.05778C6.13634 6.95678 5.78195 7.0311 5.38314 7.11475L5.31556 7.12889C4.8 7.23556 4.46222 7.27111 4.28444 6.57778C4.24889 6.45333 4.12444 6.4 3.87556 6.32889C3.55556 6.24 2.95111 6.06222 3.16444 5.24444C3.27111 4.8 3.62667 4.44444 4.08889 4.32C4.42667 4.21333 4.76444 4.24889 5.06667 4.37333C5.20889 3.84 5.54667 3.39556 5.84889 3.00444C6.36444 2.34667 6.75556 2.27556 7.53778 2.29333H7.57333H7.66222C7.62667 2.25778 7.57333 2.22222 7.53778 2.20444C7.51729 2.18981 7.49681 2.17566 7.47656 2.16167L7.47653 2.16166C7.37379 2.09069 7.27718 2.02395 7.21778 1.92C7.11111 1.76 7.02222 1.56444 6.95111 1.38667C6.79111 1.01333 6.66667 0.764444 6.45333 0.728889C6.44024 0.727252 6.42744 0.724559 6.41501 0.720894Z",
      "fill": _vm.color
    }
  })]);
};
var __vue_staticRenderFns__$5 = [];

/* style */
const __vue_inject_styles__$5 = undefined;
/* scoped */
const __vue_scope_id__$5 = undefined;
/* module identifier */
const __vue_module_identifier__$5 = undefined;
/* functional template */
const __vue_is_functional_template__$5 = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$a = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$5,
  staticRenderFns: __vue_staticRenderFns__$5
}, __vue_inject_styles__$5, __vue_script__$5, __vue_scope_id__$5, __vue_is_functional_template__$5, __vue_module_identifier__$5, false, undefined, undefined, undefined);
var __vue_component__$b = __vue_component__$a;

let IconX = class IconX extends Vue {};
__decorate([Prop({
  default: 26
})], IconX.prototype, "size", void 0);
__decorate([Prop({
  default: "#000"
})], IconX.prototype, "color", void 0);
IconX = __decorate([Component], IconX);
var script$4 = IconX;

/* script */
const __vue_script__$4 = script$4;

/* template */
var __vue_render__$4 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "0 0 24 24"
    }
  }, [_c('g', {
    attrs: {
      "fill": _vm.color,
      "fill-rule": "nonzero"
    }
  }, [_c('path', {
    attrs: {
      "d": "M5.293 5.293a1 1 0 0 1 1.414 0L12 10.585l5.293-5.292a1 1 0 0 1 1.32-.083l.094.083a1 1 0 0 1 0 1.414L13.415 12l5.292 5.293a1 1 0 0 1 .083 1.32l-.083.094a1 1 0 0 1-1.414 0L12 13.415l-5.293 5.292a1 1 0 0 1-1.32.083l-.094-.083a1 1 0 0 1 0-1.414L10.585 12 5.293 6.707a1 1 0 0 1-.083-1.32z"
    }
  })])]);
};
var __vue_staticRenderFns__$4 = [];

/* style */
const __vue_inject_styles__$4 = undefined;
/* scoped */
const __vue_scope_id__$4 = undefined;
/* module identifier */
const __vue_module_identifier__$4 = undefined;
/* functional template */
const __vue_is_functional_template__$4 = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$8 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$4,
  staticRenderFns: __vue_staticRenderFns__$4
}, __vue_inject_styles__$4, __vue_script__$4, __vue_scope_id__$4, __vue_is_functional_template__$4, __vue_module_identifier__$4, false, undefined, undefined, undefined);
var __vue_component__$9 = __vue_component__$8;

var icons = /*#__PURE__*/Object.freeze({
    __proto__: null,
    IconAdd: __vue_component__$25,
    IconAsc: __vue_component__$23,
    IconBalance: __vue_component__$21,
    IconButtonPower: __vue_component__$1$,
    IconChevronDown: __vue_component__$1Z,
    IconChevronLeft: __vue_component__$1X,
    IconChevronRight: __vue_component__$1V,
    IconChevronsLeft: __vue_component__$1T,
    IconChevronsRight: __vue_component__$1R,
    IconChevronUp: __vue_component__$1P,
    IconClipboard: __vue_component__$1N,
    IconCoins: __vue_component__$1L,
    IconDelete: __vue_component__$1J,
    IconDesc: __vue_component__$1H,
    IconDocument: __vue_component__$1F,
    IconDownload: __vue_component__$1D,
    IconEdit: __vue_component__$1B,
    IconEdit2: __vue_component__$1z,
    IconAdministration: __vue_component__$1x,
    IconEnter: __vue_component__$1v,
    IconEntities: __vue_component__$1t,
    IconEntity: __vue_component__$1r,
    IconExcel: __vue_component__$1p,
    IconExit: __vue_component__$1n,
    IconEye: __vue_component__$1l,
    IconEyeClosed: __vue_component__$1j,
    IconFaq: __vue_component__$1h,
    IconFile: __vue_component__$1f,
    IconView: __vue_component__$1f,
    IconFilter: __vue_component__$1d,
    IconGreaterThan: __vue_component__$1b,
    IconGrid: __vue_component__$19,
    IconInbox: __vue_component__$17,
    IconIsEarlyValue: __vue_component__$15,
    IconLetter: __vue_component__$13,
    IconLocation: __vue_component__$11,
    IconLogic: __vue_component__$$,
    IconLowerThan: __vue_component__$Z,
    IconMachine: __vue_component__$X,
    IconMap: __vue_component__$V,
    IconMenu: __vue_component__$T,
    IconMonoClient: __vue_component__$R,
    IconMore: __vue_component__$P,
    IconNotes: __vue_component__$N,
    IconPencil: __vue_component__$L,
    IconPlus: __vue_component__$J,
    IconPoint: __vue_component__$H,
    IconPrint: __vue_component__$F,
    IconProsegur: __vue_component__$D,
    IconSettingAlerts: __vue_component__$B,
    IconSettings: __vue_component__$z,
    IconSmartCash: __vue_component__$x,
    IconSort: __vue_component__$v,
    IconSupport: __vue_component__$t,
    IconTime: __vue_component__$r,
    IconTransaction: __vue_component__$p,
    IconTransfers: __vue_component__$n,
    IconUpload: __vue_component__$l,
    IconUserGroup: __vue_component__$j,
    IconUsers: __vue_component__$h,
    IconWarning: __vue_component__$f,
    IconWizardLink: __vue_component__$d,
    IconWorld: __vue_component__$b,
    IconX: __vue_component__$9
});

let PIcon = class PIcon extends Vue {
  get helpComp() {
    if (this.help) {
      return true;
    } else {
      return false;
    }
  }
  get iconsApp() {
    return Object.keys(icons);
  }
  get iconComp() {
    return this.icon;
  }
  get style() {
    return "color:" + this.color + ";font-size:" + this.size + "px;";
  }
};
__decorate([Prop({
  required: true
})], PIcon.prototype, "icon", void 0);
__decorate([Prop({
  required: false,
  default: ""
})], PIcon.prototype, "type", void 0);
__decorate([Prop({
  required: false
})], PIcon.prototype, "color", void 0);
__decorate([Prop({
  required: false,
  default: 16
})], PIcon.prototype, "size", void 0);
__decorate([Prop({
  required: false
})], PIcon.prototype, "help", void 0);
PIcon = __decorate([Component({
  components: {
    ...icons
  }
})], PIcon);
var script$3 = PIcon;

/* script */
const __vue_script__$3 = script$3;

/* template */
var __vue_render__$3 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _vm.type === 'material' ? _c('span', {
    staticClass: "material-icons",
    style: _vm.style,
    attrs: {
      "id": "icon-material"
    }
  }, [_vm._v("\n  " + _vm._s(_vm.icon) + "\n")]) : _vm.helpComp ? _c('div', {
    staticClass: "container-help-p-icon"
  }, _vm._l(_vm.iconsApp, function (key) {
    return _c('div', {
      key: key,
      staticClass: "item-help-p-icon"
    }, [_c('span', [_c(key, {
      tag: "component",
      attrs: {
        "color": _vm.color,
        "size": _vm.size
      }
    })], 1), _vm._v(" "), _c('span', [_vm._v("\n      " + _vm._s(key) + "\n    ")])]);
  }), 0) : _c(_vm.iconComp, {
    tag: "component",
    attrs: {
      "id": "icon-component",
      "color": _vm.color,
      "size": _vm.size
    }
  }, [_vm._t("default")], 2);
};
var __vue_staticRenderFns__$3 = [];

/* style */
const __vue_inject_styles__$3 = function (inject) {
  if (!inject) return;
  inject("data-v-6c560a2a_0", {
    source: ".container-help-p-icon[data-v-6c560a2a]{display:flex;flex-wrap:wrap;justify-content:space-between}.item-help-p-icon[data-v-6c560a2a]{display:flex;flex-direction:column;align-items:center;flex:0 0 20%;margin-top:20px}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$3 = "data-v-6c560a2a";
/* module identifier */
const __vue_module_identifier__$3 = undefined;
/* functional template */
const __vue_is_functional_template__$3 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$6 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$3,
  staticRenderFns: __vue_staticRenderFns__$3
}, __vue_inject_styles__$3, __vue_script__$3, __vue_scope_id__$3, __vue_is_functional_template__$3, __vue_module_identifier__$3, false, createInjector, undefined, undefined);
var __vue_component__$7 = __vue_component__$6;

let PRow = class PRow extends Vue {};
PRow = __decorate([Component], PRow);
var script$2 = PRow;

/* script */
const __vue_script__$2 = script$2;

/* template */
var __vue_render__$2 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "p-row"
  }, [_vm._t("default")], 2);
};
var __vue_staticRenderFns__$2 = [];

/* style */
const __vue_inject_styles__$2 = function (inject) {
  if (!inject) return;
  inject("data-v-3631afd8_0", {
    source: ".p-row[data-v-3631afd8]{display:flex;padding:10px;flex-wrap:wrap}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$2 = "data-v-3631afd8";
/* module identifier */
const __vue_module_identifier__$2 = undefined;
/* functional template */
const __vue_is_functional_template__$2 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$4 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$2,
  staticRenderFns: __vue_staticRenderFns__$2
}, __vue_inject_styles__$2, __vue_script__$2, __vue_scope_id__$2, __vue_is_functional_template__$2, __vue_module_identifier__$2, false, createInjector, undefined, undefined);
var __vue_component__$5 = __vue_component__$4;

let PCol = class PCol extends Vue {
  get giveMeClass() {
    if (this.md) {
      return `p-col-${this.md} p-col-padding`;
    }
    return "p-col p-col-padding";
  }
};
__decorate([Prop({
  required: false
})], PCol.prototype, "md", void 0);
PCol = __decorate([Component], PCol);
var script$1 = PCol;

/* script */
const __vue_script__$1 = script$1;

/* template */
var __vue_render__$1 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    class: _vm.giveMeClass
  }, [_vm._t("default")], 2);
};
var __vue_staticRenderFns__$1 = [];

/* style */
const __vue_inject_styles__$1 = function (inject) {
  if (!inject) return;
  inject("data-v-6e4dabce_0", {
    source: ".p-col-padding[data-v-6e4dabce]{padding:10px}.p-col[data-v-6e4dabce]{flex:0 0 100%}.p-col-1[data-v-6e4dabce]{flex:0 0 8.33%}.p-col-2[data-v-6e4dabce]{flex:0 0 16.66%}.p-col-3[data-v-6e4dabce]{flex:0 0 24.99%}.p-col-4[data-v-6e4dabce]{flex:0 0 33.32%}.p-col-5[data-v-6e4dabce]{flex:0 0 41.65%}.p-col-6[data-v-6e4dabce]{flex:0 0 49.98%}.p-col-7[data-v-6e4dabce]{flex:0 0 58.31%}.p-col-8[data-v-6e4dabce]{flex:0 0 66.64%}.p-col-9[data-v-6e4dabce]{flex:0 0 74.97%}.p-col-10[data-v-6e4dabce]{flex:0 0 83.33%}.p-col-11[data-v-6e4dabce]{flex:0 0 91.63%}.p-col-12[data-v-6e4dabce]{flex:0 0 100%}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1 = "data-v-6e4dabce";
/* module identifier */
const __vue_module_identifier__$1 = undefined;
/* functional template */
const __vue_is_functional_template__$1 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$2 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1,
  staticRenderFns: __vue_staticRenderFns__$1
}, __vue_inject_styles__$1, __vue_script__$1, __vue_scope_id__$1, __vue_is_functional_template__$1, __vue_module_identifier__$1, false, createInjector, undefined, undefined);
var __vue_component__$3 = __vue_component__$2;

let PPiePercentage = class PPiePercentage extends Vue {
  constructor() {
    super(...arguments);
    this.percentageLocal = this.percentage;
    this.colorLocal = this.color;
    this.colorBackgroundLocal = this.colorBackground;
    this.colorWarningLocal = this.colorWarning;
  }
  onChangePercentage() {
    this.percentageLocal = this.percentage;
    this.colorLocal = this.color;
    this.colorBackgroundLocal = this.colorBackground;
    if (this.percentage > 100) {
      this.percentageLocal = this.percentageLocal % 100;
      this.colorLocal = this.colorWarning;
      this.colorBackgroundLocal = this.color;
    }
  }
  getPath() {
    return this.describeArc(50, 50, 50, 0, this.getAngleFromPercentage(this.percentageLocal));
  }
  getAngleFromPercentage(percentage) {
    return percentage * 3.6;
  }
  polarToCartesian(centerX, centerY, radius, angleInDegrees) {
    const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
    return {
      x: centerX + radius * Math.cos(angleInRadians),
      y: centerY + radius * Math.sin(angleInRadians)
    };
  }
  describeArc(x, y, radius, startAngle, endAngle) {
    const start = this.polarToCartesian(x, y, radius, endAngle);
    const end = this.polarToCartesian(x, y, radius, startAngle);
    const arcSweep = endAngle - startAngle <= 180 ? "0" : "1";
    const path = `M ${start.x} ${start.y}
                A ${radius} ${radius} 0 ${arcSweep} 0 ${end.x} ${end.y}
                L ${x} ${y}
                L ${start.x} ${start.y} `;
    return path;
  }
};
__decorate([Prop({
  default: 20
})], PPiePercentage.prototype, "size", void 0);
__decorate([Prop({
  default: 65
})], PPiePercentage.prototype, "percentage", void 0);
__decorate([Prop({
  default: "#ffd102"
})], PPiePercentage.prototype, "color", void 0);
__decorate([Prop({
  default: "#997d01"
})], PPiePercentage.prototype, "colorBackground", void 0);
__decorate([Prop({
  default: "#e3483d"
})], PPiePercentage.prototype, "colorWarning", void 0);
__decorate([Watch("percentage", {
  immediate: true
})], PPiePercentage.prototype, "onChangePercentage", null);
PPiePercentage = __decorate([Component], PPiePercentage);
var script = PPiePercentage;

/* script */
const __vue_script__ = script;

/* template */
var __vue_render__ = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "xmlns:xlink": "http://www.w3.org/1999/xlink",
      "version": "1.1",
      "width": _vm.size,
      "height": _vm.size,
      "viewBox": "-5 -5 110 110"
    }
  }, [_c('g', {
    attrs: {
      "fill-rule": "nonzero"
    }
  }, [_c('circle', {
    attrs: {
      "fill": _vm.colorBackgroundLocal,
      "cx": "50",
      "cy": "50",
      "r": "40"
    }
  }), _vm._v(" "), _c('path', {
    staticClass: "shadow",
    attrs: {
      "fill": _vm.colorLocal,
      "d": _vm.getPath()
    }
  })])]);
};
var __vue_staticRenderFns__ = [];

/* style */
const __vue_inject_styles__ = function (inject) {
  if (!inject) return;
  inject("data-v-19029300_0", {
    source: ".svg-icon[data-v-19029300]{margin:2px}.shadow[data-v-19029300]{-webkit-filter:drop-shadow(0 0 4px #727272);filter:drop-shadow(0 0 4px #727272)}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__ = "data-v-19029300";
/* module identifier */
const __vue_module_identifier__ = undefined;
/* functional template */
const __vue_is_functional_template__ = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__ = /*#__PURE__*/normalizeComponent({
  render: __vue_render__,
  staticRenderFns: __vue_staticRenderFns__
}, __vue_inject_styles__, __vue_script__, __vue_scope_id__, __vue_is_functional_template__, __vue_module_identifier__, false, createInjector, undefined, undefined);
var __vue_component__$1 = __vue_component__;

var components = /*#__PURE__*/Object.freeze({
    __proto__: null,
    PFormInput: __vue_component__$27,
    PIcon: __vue_component__$7,
    IconAdd: __vue_component__$25,
    IconAsc: __vue_component__$23,
    IconBalance: __vue_component__$21,
    IconButtonPower: __vue_component__$1$,
    IconChevronDown: __vue_component__$1Z,
    IconChevronLeft: __vue_component__$1X,
    IconChevronRight: __vue_component__$1V,
    IconChevronsLeft: __vue_component__$1T,
    IconChevronsRight: __vue_component__$1R,
    IconChevronUp: __vue_component__$1P,
    IconClipboard: __vue_component__$1N,
    IconCoins: __vue_component__$1L,
    IconDelete: __vue_component__$1J,
    IconDesc: __vue_component__$1H,
    IconDocument: __vue_component__$1F,
    IconDownload: __vue_component__$1D,
    IconEdit: __vue_component__$1B,
    IconEdit2: __vue_component__$1z,
    IconAdministration: __vue_component__$1x,
    IconEnter: __vue_component__$1v,
    IconEntities: __vue_component__$1t,
    IconEntity: __vue_component__$1r,
    IconExcel: __vue_component__$1p,
    IconExit: __vue_component__$1n,
    IconEye: __vue_component__$1l,
    IconEyeClosed: __vue_component__$1j,
    IconFaq: __vue_component__$1h,
    IconFile: __vue_component__$1f,
    IconView: __vue_component__$1f,
    IconFilter: __vue_component__$1d,
    IconGreaterThan: __vue_component__$1b,
    IconGrid: __vue_component__$19,
    IconInbox: __vue_component__$17,
    IconIsEarlyValue: __vue_component__$15,
    IconLetter: __vue_component__$13,
    IconLocation: __vue_component__$11,
    IconLogic: __vue_component__$$,
    IconLowerThan: __vue_component__$Z,
    IconMachine: __vue_component__$X,
    IconMap: __vue_component__$V,
    IconMenu: __vue_component__$T,
    IconMonoClient: __vue_component__$R,
    IconMore: __vue_component__$P,
    IconNotes: __vue_component__$N,
    IconPencil: __vue_component__$L,
    IconPlus: __vue_component__$J,
    IconPoint: __vue_component__$H,
    IconPrint: __vue_component__$F,
    IconProsegur: __vue_component__$D,
    IconSettingAlerts: __vue_component__$B,
    IconSettings: __vue_component__$z,
    IconSmartCash: __vue_component__$x,
    IconSort: __vue_component__$v,
    IconSupport: __vue_component__$t,
    IconTime: __vue_component__$r,
    IconTransaction: __vue_component__$p,
    IconTransfers: __vue_component__$n,
    IconUpload: __vue_component__$l,
    IconUserGroup: __vue_component__$j,
    IconUsers: __vue_component__$h,
    IconWarning: __vue_component__$f,
    IconWizardLink: __vue_component__$d,
    IconWorld: __vue_component__$b,
    IconX: __vue_component__$9,
    PRow: __vue_component__$5,
    PCol: __vue_component__$3,
    PPiePercentage: __vue_component__$1
});

// Import vue components
// install function executed by Vue.use()
const install = function installSharedVue2UIComponentLibrary(Vue) {
  Object.entries(components).forEach(_ref => {
    let [componentName, component] = _ref;
    Vue.component(componentName, component);
  });
};

export { __vue_component__$25 as IconAdd, __vue_component__$1x as IconAdministration, __vue_component__$23 as IconAsc, __vue_component__$21 as IconBalance, __vue_component__$1$ as IconButtonPower, __vue_component__$1Z as IconChevronDown, __vue_component__$1X as IconChevronLeft, __vue_component__$1V as IconChevronRight, __vue_component__$1P as IconChevronUp, __vue_component__$1T as IconChevronsLeft, __vue_component__$1R as IconChevronsRight, __vue_component__$1N as IconClipboard, __vue_component__$1L as IconCoins, __vue_component__$1J as IconDelete, __vue_component__$1H as IconDesc, __vue_component__$1F as IconDocument, __vue_component__$1D as IconDownload, __vue_component__$1B as IconEdit, __vue_component__$1z as IconEdit2, __vue_component__$1v as IconEnter, __vue_component__$1t as IconEntities, __vue_component__$1r as IconEntity, __vue_component__$1p as IconExcel, __vue_component__$1n as IconExit, __vue_component__$1l as IconEye, __vue_component__$1j as IconEyeClosed, __vue_component__$1h as IconFaq, __vue_component__$1f as IconFile, __vue_component__$1d as IconFilter, __vue_component__$1b as IconGreaterThan, __vue_component__$19 as IconGrid, __vue_component__$17 as IconInbox, __vue_component__$15 as IconIsEarlyValue, __vue_component__$13 as IconLetter, __vue_component__$11 as IconLocation, __vue_component__$$ as IconLogic, __vue_component__$Z as IconLowerThan, __vue_component__$X as IconMachine, __vue_component__$V as IconMap, __vue_component__$T as IconMenu, __vue_component__$R as IconMonoClient, __vue_component__$P as IconMore, __vue_component__$N as IconNotes, __vue_component__$L as IconPencil, __vue_component__$J as IconPlus, __vue_component__$H as IconPoint, __vue_component__$F as IconPrint, __vue_component__$D as IconProsegur, __vue_component__$B as IconSettingAlerts, __vue_component__$z as IconSettings, __vue_component__$x as IconSmartCash, __vue_component__$v as IconSort, __vue_component__$t as IconSupport, __vue_component__$r as IconTime, __vue_component__$p as IconTransaction, __vue_component__$n as IconTransfers, __vue_component__$l as IconUpload, __vue_component__$j as IconUserGroup, __vue_component__$h as IconUsers, __vue_component__$1f as IconView, __vue_component__$f as IconWarning, __vue_component__$d as IconWizardLink, __vue_component__$b as IconWorld, __vue_component__$9 as IconX, __vue_component__$3 as PCol, __vue_component__$27 as PFormInput, __vue_component__$7 as PIcon, __vue_component__$1 as PPiePercentage, __vue_component__$5 as PRow, install as default };
